// DispatchDeck - Compliance Module
// Read-only compliance dashboard that mirrors data from roster, maintenance, and DVIR
// Included in flat $250/mo plan (all modules)

import {
  db,
  storage,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  functions,
  httpsCallable
} from '../firebase-config.js?v=1868';
// TASK-4573b — training save/delete by id in a transaction. Same SDK URL as
// app.js / hr.js, so one instance (the runTransaction precedent in app.js).
import { runTransaction } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// TASK-9843 — the registered-states rule (`window.RegisteredStates`), a
// side-effect module GENERATED from functions/src/shared/registeredStates.js by
// scripts/build-shared.cjs. crm.js imports the same file; neither ever imports
// the canonical one or each other (Pitfall #1).
import '../shared/registered-states.js?v=9843001';
// TASK-10154 — masked value + eye (window.SensitiveValue) for the partner's
// Identity & License rows. SAME ?v= as hr.js / compliance-partner.js (one instance).
import '../sensitiveValue.js?v=9531001';
// TASK-9349 — Permit queue (window.PermitQueue). Loaded ONLY here (Pitfall #1), and
// LAZILY (rel2089 perf budget): the module is fetched the first time the Permit queue
// button is pressed, then window.PermitQueue.open() runs. Same pattern as payroll.js
// _paychexUi() -> import('./payrollPaychex.js').
let _permitQueueLoad = null;
async function openPermitQueueLazy(opts) {
  if (!window.PermitQueue) {
    _permitQueueLoad = _permitQueueLoad || __ddChunkWrap(import('./permitQueue.js?v=100310295')).catch((err) => { _permitQueueLoad = null; throw err; });
    try { await _permitQueueLoad; } catch (err) {
      console.error('[Compliance] permit queue failed to load:', err);
      if (window.showToast) window.showToast('Could not open the permit queue. Check your connection and try again.', 'error');
      return;
    }
  }
  if (window.PermitQueue) window.PermitQueue.open(opts);
}
window.ComplianceOpenPermitQueue = openPermitQueueLazy;
// TASK-9582 — IFTA "Miles by state" (window.IftaMilesByState), lazy like the permit
// queue. Host authorizes (Compliance module); the module only renders + calls
// getIftaMilesByState. A failed chunk load shows a Retry, never a blank.
let _iftaMilesLoad = null; let _iftaT = 0; let _iftaWaits = 0;
// checkModuleAccess answers 'loading' while the member doc is still read: render
// the host and let the mount re-check (one timer, ~30s cap, then a Retry), so the
// section never silently vanishes.
const _iftaAccess = () => window.checkModuleAccess?.('compliance', true);
function iftaMilesHost() {
  const a = _iftaAccess();
  if (a !== true && a !== 'loading') return '';
  clearTimeout(_iftaT); _iftaWaits = 0; _iftaT = setTimeout(iftaMilesMount);
  return '<div id="ifta-miles-host"></div>';
}
function iftaMilesMount() {
  const el = document.getElementById('ifta-miles-host');
  const a = _iftaAccess();
  const fail = () => { el.innerHTML = '<p data-testid="ifta-miles-error">Miles by state did not load. <button class="btn btn-secondary btn-sm" data-testid="ifta-miles-retry" onclick="ComplianceIftaMilesMount()">Retry</button></p>'; };
  if (!el || (a !== true && a !== 'loading')) return;
  if (a !== true) return void (++_iftaWaits > 60 ? fail(_iftaWaits = 0) : (_iftaT = setTimeout(iftaMilesMount, 500)));
  (_iftaMilesLoad = _iftaMilesLoad || __ddChunkWrap(import('./iftaMilesByState.js?v=10302950')).catch((e) => { _iftaMilesLoad = null; throw e; }))
    .then(() => window.IftaMilesByState.mount(el, { tenantId: ComplianceState.tenantId, year: ComplianceState.iftaYear, quarter: ComplianceState.iftaQuarter }))
    .catch(fail);
}
window.ComplianceIftaMilesMount = iftaMilesMount;
// TASK-9571 — "Your FMCSA safety picture" on the Company tab (lazy carrierSafety.js,
// window.CarrierSafety; replaces the old crash/inspection-count card). Needs a
// USDOT number (the server reads it from the tenant doc). The server re-checks
// the Compliance module, so a 'loading' gate may mount early.
// A managing partner session (no claim on this tenant) gets the stored FMCSA
// counts instead, rendered by the same chunk with no call.
let _csLoad = null;
function carrierSafetyHost(td) {
  const a = _iftaAccess();
  const partner = !!_compPartnerTenantId();
  if (partner ? (td.fmcsaData || {}).crashTotal === undefined : (!String(td.dotNumber || '').replace(/\D/g, '') || (a !== true && a !== 'loading'))) return '';
  const opts = partner ? { stored: td.fmcsaData, syncedAt: td.fmcsaLastSync } : { tenantId: ComplianceState.tenantId };
  setTimeout(() => {
    const el = document.getElementById('carrier-safety-host');
    if (el) (_csLoad = _csLoad || __ddChunkWrap(import('./carrierSafety.js?v=9571001')).catch((e) => { _csLoad = null; throw e; }))
      .then(() => window.CarrierSafety.mount(el, opts))
      .catch(() => { el.textContent = 'Your FMCSA safety picture did not load. Reopen the Company tab to try again.'; });
  });
  return '<div id="carrier-safety-host" data-testid="carrier-safety-host"></div>';
}
// DO NOT import hr.js directly — different ?v= cache busters create duplicate
// module instances with separate state (caused recurring HR directory bug).
// Use window.HRModule.initSafetyStandalone instead.

// ============================================
// STATE
// ============================================
const ComplianceState = {
  initialized: false,
  tenantId: null,
  activeTab: 'overview',  // 'overview' | 'people' | 'assets' | 'safety'
  assetSubTab: 'all',     // 'all' | 'trucks' | 'trailers' | 'assets'
  peopleSubTab: 'all',    // 'all' | 'drivers'
  // TASK-933 — employment scope for the People tab. Defaults to 'active' (the daily view);
  // 'inactive' / 'terminated' / 'all' reach separated people whose DQ files are still under
  // DOT retention (§391.51(i) — 3 years past separation) and are exactly what an auditor asks
  // for. Mirrors the partner portal's _ALLDRIVERS_STATE.employmentFilter.
  peopleEmpFilter: 'active', // 'active' | 'inactive' | 'terminated' | 'all'
  searchQuery: '',
  filterStatus: 'all',    // 'all' | 'critical' | 'warning' | 'good'
  people: [],
  trucks: [],
  trailers: [],
  assets: [],
  hvutFilings: [],
  pretrips: [],
  maintenance: [],
  dvirRepairs: [], // TASK-9706 — maintenance source:'pretrip', any status
  dvirRepairsError: false,
  clockEntries: [],
  jobDays: [],
  dvirFlags: [],
  jsaFlags: [],
  fuelPurchases: [],
  drugTests: [],
  complianceDocs: [],
  // TASK-432 — Signals rail + 7-day trend KPIs.
  // ALL bounded to a 14-day window (current 7d + prior 7d for trend math) or
  // 7-day window — never an all-time scan. See subscribeSignalsData().
  jsaReportsRecent: [],   // jsaReports createdAt >= 14d ago
  safetyMeetingsRecent: [], // safetySignIns date >= 14d ago (tailgates)
  signedDocsRecent: [],   // signDocuments signedAt >= 7d ago
  safetyEquipmentRecent: [], // safetyEquipment (gas monitors / certs w/ expiry)
  iftaQuarter: getCurrentQuarter(),
  iftaYear: new Date().getFullYear(),
  hazmatSearchQuery: '',
  // TASK-1201 — Company Documents (Company tab). null = not loaded yet;
  // populated by _loadCompanyDocs (one-shot getDocs, refreshed after mutations).
  companyDocs: null,
  unsubscribers: [],
  // TASK-1147 — per-sub-view lazy stream registry. streamUnsubs maps stream
  // name → unsubscribe fn (subscribe-once: a stream stays live from first open
  // until module cleanup, so cross-sub-view reads never regress after a stream
  // has opened and tab-hopping never re-fetches). streamsLoaded maps stream
  // name → first snapshot (or error) arrived; renders gate on it so a freshly
  // opened sub-view shows a spinner, never a silently-empty grid.
  streamUnsubs: {},
  streamsLoaded: {},
  selectedPersonId: null,
  selectedVehicleId: null,
  // TASK-812 — when set to the open person's id, the driver-detail modal renders
  // the CDL/identity section as an editable DQ form instead of read-only rows.
  editingDriverDetailsId: null
};

// Owner/admin gate for destructive actions on regulated records. Mirrors the
// isTenantAdmin rule on drugTests delete (firestore.rules:1291). 2026-06-26 —
// the drug-test delete button was rendered to every compliance/hr-module viewer
// but the rule rejects non-admins, so a non-admin saw a button that always
// errored. Hide it to match the rule; deletion of a federal drug/alcohol record
// stays an admin break-glass action (protected results route through the
// adminBreakGlassDeleteDrugAlcoholRecord callable). Owner/legacy (no member doc)
// → true, same convention as hr.js _hrIsOwnerOrAdmin.
function _compIsOwnerOrAdmin() {
  const m = window.AppState?.memberAccess;
  if (!m) return true; // owner/legacy (no member doc)
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  return m.role === 'owner' || m.role === 'admin';
}

// TASK-1555 — can this member open a CANDIDATE-keyed BG record through
// getCheckrReportDetail? The callable's compliance-module lane (Path B of
// assertHrOrComplianceReportAccess) is person-linked ONLY — a compliance-
// module-only member gets permission-denied on a candidate-keyed record. The
// post-hire MVR fallback resolves to the candidate-keyed doc, so gate that
// button on the callers Path A admits (owner/admin or hr module) — render the
// affordance on the same predicate as the data, or don't render it.
function _compCanOpenCandidateBgReport() {
  if (_compIsOwnerOrAdmin()) return true;
  const m = window.AppState?.memberAccess;
  if (!m || ['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  return Array.isArray(m.modules) && m.modules.includes('hr');
}

// TASK-1048 (COD) — can this member WRITE roster/people data? Mirrors the server
// requireRosterWriteAccess gate on recordMvrPull (people rule = isDispatcherOrHigher
// || hasModule('roster')). Used to BLOCK an MVR-PDF upload that recordMvrPull would
// then deny: the credential Storage paths are delete-immutable (§391.51 retention,
// storage.rules `allow delete: if false`), so a client cannot reap an orphaned blob —
// the only real fix is to not create one. Owner/legacy (no member doc) → true.
function _compCanWriteRoster() {
  const m = window.AppState?.memberAccess;
  if (!m) return true;
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  if (m.role === 'owner' || m.role === 'admin' || m.role === 'dispatcher') return true;
  return Array.isArray(m.modules) && m.modules.includes('roster');
}

// TASK-1075 — best-effort server reap of a TRULY-orphaned credential upload blob.
// The driver-credential Storage paths (people/{pid}/{mvr|medExam|clearinghouse|
// annualReview|daPolicy}/) are delete-immutable client-side (§391.51, storage.rules
// `allow delete: if false`), so when an upload SUCCEEDS but the referencing
// person-doc write then FAILS, only an Admin-SDK callable can remove the orphan.
// The server RE-VERIFIES the blob is unreferenced before deleting (never reaps a
// retained doc — even a lost-response where the write actually landed is refused),
// so this is safe to call whenever we uploaded a blob and its reference did not
// land. Never throws — cleanup is best-effort.
async function _reapOrphanCredBlobCompliance(tenantId, personId, path) {
  if (!tenantId || !path) return;
  try {
    const reapFn = httpsCallable(functions, 'reapOrphanedCredentialBlob');
    await reapFn({ tenantId, path });
  } catch (err) {
    console.warn('[Compliance] orphan-blob reap failed (non-fatal):', err?.message);
  }
}

// TASK-647 — ONE effective med-card doc across HR + Compliance. The DOT med
// cert lives in two fields (medCertPdfUrl ← driver SMS self-upload / Record-Exam;
// medCardDocumentUrl ← Upload button / partner doc-intake). hr.js owns the
// canonical reader (window.HRModule.getEffectiveMedCardDoc); we delegate to it
// so partner/compliance views see self-uploads too. NOT a cross-import (no ?v=
// cache-buster duplicate-instance risk — CLAUDE.md pitfall #1). Identical inline
// fallback for the (rare) case hr.js hasn't finished loading. Keep the two impls
// in sync.
// TASK-1496-S6 — medical detail resolves through the peopleDq sibling (legacy-first).
const _medDq = (p) => (window.DqMatrix?.medDetail ? window.DqMatrix.medDetail(p, ComplianceState.tenantId) : p);
// TASK-1496-S7 — the MVR detail group, same rule.
const _mvrDq = (p) => (window.DqMatrix?.mvrDetail ? window.DqMatrix.mvrDetail(p, ComplianceState.tenantId) : p);

function effectiveMedCardDoc(emp) {
  emp = _medDq(emp);
  const shared = window.HRModule?.getEffectiveMedCardDoc;
  if (typeof shared === 'function') return shared(emp);
  // Fallback — mirror of hr.js getEffectiveMedCardDoc.
  if (!emp) return null;
  const toMs = (t) => {
    if (!t) return null;
    if (typeof t.toDate === 'function') { try { return t.toDate().getTime(); } catch (_) { return null; } }
    if (t.seconds != null) return t.seconds * 1000;
    const ms = new Date(t).getTime();
    return isNaN(ms) ? null : ms;
  };
  const certUrl = emp.medCertPdfUrl || '';
  const certPath = emp.medCertPdfPath || '';   // TASK-996 — path-only self-uploads
  const docUrl = emp.medCardDocumentUrl || '';
  const docPath = emp.medCardDocumentPath || '';
  const pendingReview = !!emp.medCardPendingReview;
  const cert = (certUrl || certPath) ? { url: certUrl || null, path: certPath || null, source: 'medCertPdfUrl', uploadedAt: (Math.max(toMs(emp.medCardSelfUploadedAt) || 0, toMs(emp.medCertPdfUploadedAt) || 0) || null), pendingReview } : null;
  const docu = (docUrl || docPath) ? { url: docUrl || null, path: docPath || null, source: 'medCardDocumentUrl', uploadedAt: toMs(emp.medCardDocumentUploadedAt), pendingReview: false } : null;
  if (cert && !docu) return cert;
  if (docu && !cert) return docu;
  if (!cert && !docu) return null;
  if (cert.uploadedAt != null && docu.uploadedAt != null) return cert.uploadedAt >= docu.uploadedAt ? cert : docu;
  if (cert.uploadedAt != null) return cert;
  if (docu.uploadedAt != null) return docu;
  return docu; // legacy, no timestamps → prefer deliberately-attached doc field
}

function getCurrentQuarter() {
  return Math.ceil((new Date().getMonth() + 1) / 3);
}

// ============================================
// HELPERS
// ============================================
// TASK-862 — delegates to shared window.escapeHtml (sharedFormat.js); escapes the
// identical set (&<>"', '->&#39;). Hoisted wrapper, window lookup deferred to call time.
function escapeHtml(str) {
  return window.escapeHtml(str);
}

// TASK-4838 — delegating wrapper for the shared JS-STRING-IN-AN-ATTRIBUTE escaper
// (sharedFormat.js). Use it for `onclick="window.Mod.fn('${…}')"`, never escapeHtml:
// the HTML parser decodes `&#39;` back to an apostrophe BEFORE the handler is
// compiled as JavaScript, so escapeHtml/escAttr give that position no protection
// at all. Hoisted wrapper, window lookup deferred to call time (Pitfall #1).
function escJsAttr(str) {
  return window.escJsAttr(str);
}

// ============================================
// ACTION ITEMS (partner-set tasks for this carrier)
// Real-time listener on tenants/{id}/actionItems. Carriers can update status
// directly (rules-gated, optimistic concurrency); notes append via callable.
// ============================================
const ActionItemsState = {
  items: [],
  unsub: null,
  showFinished: false, // TASK-10153 — finished rows fold away by default
};

function startActionItemsListener(tenantId) {
  if (ActionItemsState.unsub) { try { ActionItemsState.unsub(); } catch (_) {} }
  if (!tenantId) return;
  const q = query(collection(db, 'tenants', tenantId, 'actionItems'), where('archivedAt', '==', null));
  ActionItemsState.unsub = onSnapshot(q, (snap) => {
    ActionItemsState.items = snap.docs.map(d => ({ id: d.id, _ref: d.ref, ...d.data() }));
    if (ComplianceState.activeTab === 'overview' && document.querySelector('.compliance-content')) {
      const container = document.getElementById('action-items-section-mount');
      if (container) container.outerHTML = renderActionItemsSection();
    }
  }, (err) => {
    console.warn('[ActionItems] listener error:', err.message || err);
  });
}

function stopActionItemsListener() {
  if (ActionItemsState.unsub) { try { ActionItemsState.unsub(); } catch (_) {} ActionItemsState.unsub = null; }
}

function actionItemPartnerName() {
  return window.AppState?.tenantData?.compliancePartner || 'your compliance partner';
}

// TASK-10153 — Finished items no longer count as "open" or sort as HIGH. Open
// items sort priority-then-due; finished ones fold under a "Show N finished"
// row (collapsed by default), dimmed, with a DONE pill in place of the
// priority so a finished HIGH never reads as Highly critical.
// TASK-10154 — a row whose subject is a person opens that person's compliance
// detail (Kolsen 8/31 + 10/27). Inactive / terminated people still open.
function _actionItemSplit(items) {
  const prio = (p) => ({ high: 0, medium: 1, low: 2 }[p] ?? 3);
  const due = (d) => (d ? new Date(d).getTime() : Infinity);
  const open = [];
  const finished = [];
  (items || []).forEach((it) => { if (it) (it.status === 'finished' ? finished : open).push(it); });
  open.sort((a, b) => (prio(a.priority) - prio(b.priority)) || (due(a.dueDate) - due(b.dueDate)));
  const doneAt = (it) => {
    const t = it && (it.completedAt || it.lastUpdatedAt || it.updatedAt);
    if (!t) return 0;
    if (typeof t.toMillis === 'function') return t.toMillis();
    const n = new Date(t).getTime();
    return Number.isFinite(n) ? n : 0;
  };
  finished.sort((a, b) => doneAt(b) - doneAt(a));
  return { open, finished };
}

function _actionItemPersonId(item) {
  if (!item || item.subjectType !== 'person') return null;
  const id = item.subjectId;
  return (typeof id === 'string' && id && id.length <= 128 && !id.includes('/')) ? id : null;
}

function openActionItemSubject(personId) {
  if (!personId) return;
  if (!ComplianceState.people.some((p) => p.id === personId)) {
    if (window.showToast) window.showToast('That person is no longer on this roster.', 'info');
    return;
  }
  showPersonDetail(personId);
}

function toggleFinishedActionItems() {
  ActionItemsState.showFinished = !ActionItemsState.showFinished;
  _rerenderActionItemsSection();
}

function _actionItemRow(item, finished) {
  const overdue = !finished && item.dueDate && new Date(item.dueDate) < new Date();
  const noteCount = (item.notesThread || []).length;
  const pid = _actionItemPersonId(item);
  const taskText = escapeHtml(item.task || '');
  const taskCell = pid
    ? `<button type="button" class="action-item-open" onclick="window.ComplianceModule.openActionItemSubject('${escJsAttr(pid)}')" title="Open this person's compliance file" style="background:none;border:0;padding:0;margin:0;font:inherit;font-weight:700;color:var(--brand-blue,#3B82F6);cursor:pointer;text-align:left;text-decoration:underline;text-underline-offset:2px;">${taskText}</button>`
    : `<strong>${taskText}</strong>`;
  const prioCell = finished
    ? '<span class="priority-pill" style="background:#E2E8F0;color:#334155;">DONE</span>'
    : `<span class="priority-pill priority-${escapeHtml(item.priority || 'low')}">${escapeHtml((item.priority || 'low').toUpperCase())}</span>`;
  return `<tr${finished ? ' class="action-item-finished" style="opacity:0.7;"' : ''}>
      <td data-label="Priority">${prioCell}</td>
      <td data-label="Task">${taskCell}</td>
      <td data-label="Owner" style="color:var(--text-secondary);">${escapeHtml(item.owner || '—')}</td>
      <td data-label="Due" style="color:${overdue ? '#dc2626' : 'var(--text-secondary)'};${overdue ? 'font-weight:600;' : ''}">${item.dueDate ? escapeHtml(item.dueDate) + (overdue ? ' (overdue)' : '') : '—'}</td>
      <td data-label="Status">
        <select onchange="window.ComplianceModule.updateActionItemStatus('${escJsAttr(item.id)}', this.value)" class="status-pill status-${escapeHtml((item.status || 'not_started').replace('_','-'))}" style="border:0;padding:3px 9px;cursor:pointer;font-weight:600;font-size:0.72rem;border-radius:6px;">
          <option value="not_started" ${item.status === 'not_started' ? 'selected' : ''}>Not Started</option>
          <option value="in_progress" ${item.status === 'in_progress' ? 'selected' : ''}>In Progress</option>
          <option value="finished" ${item.status === 'finished' ? 'selected' : ''}>Finished</option>
          <option value="blocked" ${item.status === 'blocked' ? 'selected' : ''}>Blocked</option>
        </select>
      </td>
      <td data-label="Notes">
        <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.openCarrierActionNote('${escJsAttr(item.id)}')" style="display:inline-flex;align-items:center;gap:4px;font-size:0.78rem;">
          <span class="material-icons" style="font-size:14px;">forum</span> ${noteCount > 0 ? noteCount : 'Add'}
        </button>
      </td>
    </tr>`;
}

function renderActionItemsSection() {
  const items = ActionItemsState.items || [];
  if (items.length === 0) {
    return `<div id="action-items-section-mount" style="display:none;"></div>`;
  }
  const partner = escapeHtml(actionItemPartnerName());
  const { open, finished } = _actionItemSplit(items);
  const showFinished = ActionItemsState.showFinished === true;
  let rows = open.map((it) => _actionItemRow(it, false)).join('');
  if (finished.length) {
    rows += `<tr class="action-items-finished-toggle"><td colspan="6" style="padding:4px 14px;">
        <button type="button" onclick="window.ComplianceModule.toggleFinishedActionItems()" aria-expanded="${showFinished ? 'true' : 'false'}" style="background:none;border:0;padding:0;min-height:44px;font:inherit;font-size:0.8rem;font-weight:600;color:var(--text-secondary);cursor:pointer;display:inline-flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:16px;">${showFinished ? 'expand_less' : 'expand_more'}</span>${showFinished ? 'Hide' : 'Show'} ${finished.length} finished
        </button>
      </td></tr>`;
    if (showFinished) rows += finished.map((it) => _actionItemRow(it, true)).join('');
  }
  return `<div id="action-items-section-mount" style="margin-bottom:var(--spacing-lg);">
    <div style="background:var(--bg-card,#fff);border:1px solid var(--border-light,#e2e8f0);border-radius:12px;overflow:hidden;">
      <div style="padding:14px 18px;background:linear-gradient(90deg,#eff6ff,#fff);border-bottom:1px solid var(--border-light,#e2e8f0);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="color:var(--brand-blue,#3b82f6);">checklist</span>
        <strong>Action items from ${partner}</strong>
        <span style="margin-left:auto;color:var(--text-secondary);font-size:0.8rem;">${open.length} open${finished.length ? ` · ${finished.length} finished` : ''}</span>
      </div>
      <table class="action-items-table">
        <thead><tr><th>Priority</th><th>Task</th><th>Owner</th><th>Due</th><th>Status</th><th>Notes</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  </div>`;
}

// TASK-644 — re-render the action items section from current state so an
// optimistic dropdown selection snaps back to the real status when a write is
// canceled or rejected (no Firestore write = no snapshot to re-render it).
function _rerenderActionItemsSection() {
  if (ComplianceState.activeTab === 'overview' && document.querySelector('.compliance-content')) {
    const container = document.getElementById('action-items-section-mount');
    if (container) container.outerHTML = renderActionItemsSection();
  }
}

async function updateActionItemStatus(itemId, newStatus) {
  const item = ActionItemsState.items.find(i => i.id === itemId);
  if (!item || !item._ref) { _rerenderActionItemsSection(); return; }
  // TASK-644 (S1b) — route through the privileged callable instead of a direct
  // client-SDK updateDoc. The server enforces the §391.25 integrity gate (a
  // verifiable bg_check task can't be hand-flipped to "finished" without a
  // linked report) and server-stamps lastUpdatedBy. The old direct write had no
  // server validation and any new integrity field was client-forgeable.
  try {
    const call = httpsCallable(functions, 'carrierUpdateActionItemStatus');
    await call({
      tenantId: ComplianceState.tenantId,
      itemId,
      status: newStatus,
      expectedVersion: typeof item.version === 'number' ? item.version : null,
    });
    if (window.showToast) window.showToast('Status updated', 'success');
  } catch (err) {
    const raw = err?.message || '';
    // TASK-644 — VERIFIABLE_NO_ARTIFACT: finishing a bg_check task with no linked
    // report. §391.23 permits a MANUAL investigation (paper MVR, employer calls)
    // — offer the FCRA/§391.23 attestation, then retry with it.
    if (raw.includes('VERIFIABLE_NO_ARTIFACT')) {
      await _attestActionItemFinish(itemId);
      return;
    }
    console.error('[ActionItems] status update failed:', err);
    let msg = 'Could not update — partner may have edited this. Refresh to see latest.';
    if (raw.includes('version mismatch')) {
      msg = 'This task changed since you loaded it. Refresh to see the latest.';
    }
    if (window.showToast) window.showToast(msg, 'error');
    _rerenderActionItemsSection(); // snap the dropdown back to the real status
  }
}

// TASK-644 — finish a verifiable (bg_check) task that has no linked report, via
// a §391.23/FCRA attestation that a manual investigation is on file. Confirms,
// captures a required reason, then re-calls the privileged callable with the
// attestation so the server stamps completedWithoutArtifact + audit.
async function _attestActionItemFinish(itemId) {
  const item = ActionItemsState.items.find(i => i.id === itemId);
  if (!item) { _rerenderActionItemsSection(); return; }
  const ok = await (window.showConfirm
    ? window.showConfirm(
        'Close this task without a linked report?',
        'This task is tied to a background check / MVR / PSP. It completes automatically when a report returns. To close it by hand, confirm (FCRA / DOT §391.23) that you ran the screening manually and have the result on file. This is logged (who, when, why).')
    : Promise.resolve(confirm('Close this task without a linked report? Logged (FCRA / §391.23).')));
  // Revert the optimistic dropdown selection if they back out (no write fires,
  // so the snapshot listener won't re-render it on its own).
  if (!ok) { _rerenderActionItemsSection(); return; }
  const reason = (window.prompt('Confirmation note (required) — what manual screening did you run? (e.g. "Paper MVR + prior-employer calls completed 2026-06-17, on file"). Recorded in the compliance audit.') || '').trim();
  if (!reason) { if (window.showToast) window.showToast('A confirmation note is required.', 'error'); _rerenderActionItemsSection(); return; }
  try {
    const call = httpsCallable(functions, 'carrierUpdateActionItemStatus');
    await call({
      tenantId: ComplianceState.tenantId,
      itemId,
      status: 'finished',
      expectedVersion: typeof item.version === 'number' ? item.version : null,
      attestation: { confirmed: true, reason },
    });
    if (window.showToast) window.showToast('Task closed', 'success');
  } catch (e) {
    console.error('[ActionItems] attested finish failed:', e);
    if (window.showToast) window.showToast('Could not close task: ' + (e?.message || e), 'error');
  }
}

async function openCarrierActionNote(itemId) {
  const item = ActionItemsState.items.find(i => i.id === itemId);
  if (!item) return;
  const modalId = 'carrier-action-note-modal';
  let modal = document.getElementById(modalId);
  if (modal) modal.remove();
  modal = document.createElement('div');
  modal.id = modalId;
  modal.className = 'modal';
  modal.style.zIndex = '10001';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;width:100%;max-height:90vh;max-height:90dvh;display:flex;flex-direction:column;">
      <div class="modal-header">
        <h2><span class="material-icons" style="color:var(--brand-blue);">forum</span> ${escapeHtml(item.task || 'Task')}</h2>
        <button class="modal-close" onclick="document.getElementById('${modalId}').remove()">×</button>
      </div>
      <div class="modal-body" style="padding:20px;overflow-y:auto;flex:1;">
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:12px;">
          From <strong>${escapeHtml(actionItemPartnerName())}</strong>
        </div>
        <div id="carrier-notes-thread" class="notes-thread"></div>
        <div class="note-append-row">
          <textarea id="carrier-note-input" placeholder="Reply to ${escapeHtml(actionItemPartnerName())}..." maxlength="2000"></textarea>
          <button class="btn btn-primary" onclick="window.ComplianceModule.submitCarrierNote('${itemId}')" style="align-self:flex-end;">
            <span class="material-icons">send</span>
          </button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(modal);
  renderCarrierNotesThread(item.notesThread || []);
}

function renderCarrierNotesThread(thread) {
  const wrap = document.getElementById('carrier-notes-thread');
  if (!wrap) return;
  if (!thread || thread.length === 0) {
    wrap.innerHTML = `<div class="notes-thread-empty">No notes yet — add the first one below.</div>`;
    return;
  }
  wrap.innerHTML = thread.map(n => {
    const at = n.at?.toDate?.() || (n.at ? new Date(n.at) : null);
    const when = at ? at.toLocaleString() : '';
    return `<div class="note-entry note-from-${escapeHtml(n.author || 'partner')}">
      <div class="note-entry-meta">
        <span class="note-entry-author">${escapeHtml(n.name || (n.author === 'partner' ? actionItemPartnerName() : 'You'))}</span>
        <span>${escapeHtml(when)}</span>
      </div>
      <div class="note-entry-text">${escapeHtml(n.text || '')}</div>
    </div>`;
  }).join('');
  wrap.scrollTop = wrap.scrollHeight;
}

async function submitCarrierNote(itemId) {
  const input = document.getElementById('carrier-note-input');
  const text = (input?.value || '').trim();
  if (!text) return;
  try {
    const callCarrier = httpsCallable(functions, 'carrierAppendActionItemNote');
    await callCarrier({ tenantId: ComplianceState.tenantId, itemId, text });
    if (input) input.value = '';
    setTimeout(() => {
      const item = ActionItemsState.items.find(i => i.id === itemId);
      renderCarrierNotesThread(item?.notesThread || []);
    }, 400);
  } catch (err) {
    console.error('[ActionItems] carrier note failed:', err);
    if (window.showToast) window.showToast('Could not send note: ' + (err.message || 'unknown'), 'error');
  }
}

// TASK-983/541 — a bare YYYY-MM-DD parses as UTC midnight → renders the PRIOR
// calendar day in CT (and flips expiry a day early). Date-only strings parse
// LOCAL; full timestamps / Date objects stay on the existing path.
function _parseLocalDate(dateStr) {
  return (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr))
    ? new Date(dateStr + 'T00:00:00')
    : new Date(dateStr);
}

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const d = _parseLocalDate(dateStr);
  if (isNaN(d.getTime())) return escapeHtml(dateStr);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

// TASK-1326: scheme-guard for rendered hrefs / window.open. escapeHtml entity-escapes but
// does NOT neutralize a javascript:/data: scheme, and hrResources url is client-writable
// (unmasked create). Returns the url only if it's a clean http(s) URL, else '' (→ '#'/no-open).
function _cpSafeUrl(u) {
  const s = String(u == null ? '' : u).trim();
  return (/^https?:\/\//i.test(s) && !/["'<>`\s]/.test(s)) ? s : '';
}

function _sanitizeDocTitle(s) {
  // TASK-1325: drop control chars, zero-width + bidi-override codepoints (title
  // renders on the driver Doc Page + the public ?docs= page), trim, cap 120.
  const out = [];
  for (const ch of String(s == null ? '' : s)) {
    const c = ch.codePointAt(0);
    if (c < 0x20 || c === 0x7F) continue;               // control
    if (c >= 0x200B && c <= 0x200F) continue;           // zero-width
    if (c >= 0x202A && c <= 0x202E) continue;           // bidi embed/override
    if (c >= 0x2066 && c <= 0x2069) continue;           // bidi isolate
    if (c === 0xFEFF) continue;                         // BOM / zero-width no-break
    out.push(ch);
  }
  return out.join('').trim().slice(0, 120);
}

function daysUntilExpiry(dateStr) {
  if (!dateStr) return null;
  const d = _parseLocalDate(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

// ---------------------------------------------------------------------------
// Company-level credential expiry (UCR / MCS-150 / insurance)
//
// These are carrier-wide registrations on the tenant doc, not on a person or a
// vehicle, so the per-entity roll-ups never reach them. ONE source of truth,
// consumed by renderHealthSummary, getAlertCounts and renderSignalsFeed — when
// only the health card knew about these, an expired UCR appeared in the issue
// list while the Critical count stayed 0 and Signals still said "You're current."
//
// warnDays MIRRORS the Company tab's own dateField() thresholds (MCS-150 is 60
// there, everything else 30). Keep them equal or the two screens disagree: a
// 45-day-out MCS-150 was amber on Company and "All Clear" on Overview.
//
// irpCredentialsExpiry is deliberately ABSENT. The server whitelists it
// (functions/src/index.js _partnerHandlerUpdateCompanyCompliance) but no UI
// writes it — the Company tab has no IRP field — so an alert here could never
// fire, and if it did, clicking it would land on a tab with nothing to fix.
// Add it back only alongside a Company-tab field for it.
//
// Nothing here ever emits a POSITIVE status, and since TASK-9300 (4) neither
// does getExpiryStatusText() — it used to render a green "Valid (N days
// remaining)" from a stored date alone, unsafe on a §391.51 surface because a
// credential can be revoked without its expiry date moving. A stale-but-future
// date here produces SILENCE, not an assurance; there it produces a plain
// "Expires in N days".
const COMPANY_CREDENTIALS = [
  { path: 'ucrExpiry', label: 'UCR registration', warnDays: 30 },
  { path: 'mcs150NextUpdate', label: 'MCS-150 update', warnDays: 60 },
  { path: 'insurance.liabilityExpiry', label: 'Liability insurance', warnDays: 30 },
  { path: 'insurance.cargoExpiry', label: 'Cargo insurance', warnDays: 30 },
  { path: 'insurance.workersCompExpiry', label: 'Workers’ comp', warnDays: 30 },
];

function getCompanyCredentialIssues() {
  const td = window.AppState?.tenantData || {};
  const read = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
  const expired = [];
  const expiring = [];
  COMPANY_CREDENTIALS.forEach(({ path, label, warnDays }) => {
    const d = daysUntilExpiry(read(td, path));
    // null = missing OR unparseable. Deliberately skipped, not treated as an
    // issue: every tenant has these blank today, so alerting on absence would
    // light up every account overnight, and an intrastate-only carrier
    // legitimately has no UCR. Absence means UNASSESSED, never "compliant".
    if (d === null) return;
    if (d < 0) expired.push({ label, days: Math.abs(d) });
    else if (d <= warnDays) expiring.push({ label, days: d });
  });
  return { expired, expiring };
}

function formatExpiryBadge(dateStr, label) {
  if (!dateStr) {
    return `<span class="compliance-badge compliance-badge-gray" title="No ${label} on file">
      <span class="material-icons" style="font-size:14px;">help_outline</span> ${label}: N/A
    </span>`;
  }
  const days = daysUntilExpiry(dateStr);
  if (days === null) return '';
  if (days < 0) {
    return `<span class="compliance-badge compliance-badge-critical" title="${label} expired ${Math.abs(days)} days ago">
      <span class="material-icons" style="font-size:14px;">error</span> ${label}: Expired
    </span>`;
  }
  if (days <= 30) {
    return `<span class="compliance-badge compliance-badge-warning" title="${label} expires in ${days} days">
      <span class="material-icons" style="font-size:14px;">warning</span> ${label}: ${days}d
    </span>`;
  }
  return `<span class="compliance-badge compliance-badge-good" title="${label} valid (${days} days)">
    <span class="material-icons" style="font-size:14px;">check_circle</span> ${label}: OK
  </span>`;
}

// TASK-9814 follow-up — "Doesn't expire" wins: a stale date left on the doc is never read as expiring/expired.
function _vcRegExpiryOf(v) { return !v || v.registrationNoExpiry === true ? '' : (v.registrationExpiry || ''); }
// TASK-9814 — a registration card is on file but no expiry date and not marked "Doesn't expire".
function _vcRegExpiryMissing(v) {
  return !!v && Array.isArray(v.registrationDocs) && v.registrationDocs.length > 0
    && !String(v.registrationExpiry || '').trim() && v.registrationNoExpiry !== true;
}

// ============================================
// HVUT / Form 2290 helpers (tenant-side)
// ============================================
// Tax year runs Jul 1 → Jun 30, named by the July-start year.
function currentHvutTaxYear(ref) {
  const d = ref || new Date();
  const y = d.getFullYear();
  return (d.getMonth() >= 6) ? String(y) : String(y - 1);
}

const HVUT_CATEGORIES = [
  { cat: 'A', minGvwr: 55000, maxGvwr: 55999, amount: 100.00 },
  { cat: 'B', minGvwr: 56000, maxGvwr: 56999, amount: 122.00 },
  { cat: 'C', minGvwr: 57000, maxGvwr: 57999, amount: 144.00 },
  { cat: 'D', minGvwr: 58000, maxGvwr: 58999, amount: 166.00 },
  { cat: 'E', minGvwr: 59000, maxGvwr: 59999, amount: 188.00 },
  { cat: 'F', minGvwr: 60000, maxGvwr: 60999, amount: 210.00 },
  { cat: 'G', minGvwr: 61000, maxGvwr: 61999, amount: 232.00 },
  { cat: 'H', minGvwr: 62000, maxGvwr: 62999, amount: 254.00 },
  { cat: 'I', minGvwr: 63000, maxGvwr: 63999, amount: 276.00 },
  { cat: 'J', minGvwr: 64000, maxGvwr: 64999, amount: 298.00 },
  { cat: 'K', minGvwr: 65000, maxGvwr: 65999, amount: 320.00 },
  { cat: 'L', minGvwr: 66000, maxGvwr: 66999, amount: 342.00 },
  { cat: 'M', minGvwr: 67000, maxGvwr: 67999, amount: 364.00 },
  { cat: 'N', minGvwr: 68000, maxGvwr: 68999, amount: 386.00 },
  { cat: 'O', minGvwr: 69000, maxGvwr: 69999, amount: 408.00 },
  { cat: 'P', minGvwr: 70000, maxGvwr: 70999, amount: 430.00 },
  { cat: 'Q', minGvwr: 71000, maxGvwr: 71999, amount: 452.00 },
  { cat: 'R', minGvwr: 72000, maxGvwr: 72999, amount: 474.00 },
  { cat: 'S', minGvwr: 73000, maxGvwr: 73999, amount: 496.00 },
  { cat: 'T', minGvwr: 74000, maxGvwr: 74999, amount: 518.00 },
  { cat: 'V', minGvwr: 75000, maxGvwr: Infinity, amount: 550.00 },
];

function categoryForGvwr(gvwr) {
  if (!gvwr || gvwr < 55000) return null;
  return HVUT_CATEGORIES.find(c => gvwr >= c.minGvwr && gvwr <= c.maxGvwr) || null;
}

function hvutFilingFor(vehicleId, taxYear) {
  return (ComplianceState.hvutFilings || []).find(f =>
    f.vehicleId === vehicleId && String(f.taxYear) === String(taxYear)
  );
}

// 2290 section for the vehicle detail modal. Tenant-side is editable inline.
// ============================================
// TASK-4617 — COMPANY EIN (moved off the driver-readable tenant root)
// ============================================
// The carrier's federal EIN used to live on `tenants/{tid}` itself, which
// canReadTenant() exposes to every live driver-PIN session — reproduced against
// the deployed ruleset with an anonymous-auth REST probe that read `ein`
// verbatim. It now lives in `tenants/{tid}/complianceSecure/company`, gated to
// admin OR the 'compliance' module (the same audience that can already EDIT it
// via the updateCompanyProfileCarrier callable).
//
// Reads go through companyEin() so the four call sites below never touch
// tenantData.ein directly again.
//
// The tenant-root FALLBACK is deliberate and temporary: it makes this change
// order-independent with respect to the data migration, which is run
// separately by hand. Before the migration the sibling doc does not exist and
// the EIN still resolves off the root exactly as it does today; after it, the
// root field is gone and the sibling answers. Neither ordering can produce a
// blank HVUT prefill, which is the regression this has to avoid — Form 2290 is
// a real IRS deadline, not a cosmetic field.
const CompanySecureState = { tenantId: null, ein: '', loaded: false };

async function loadCompanySecure(tenantId) {
  if (!tenantId) return;
  if (CompanySecureState.loaded && CompanySecureState.tenantId === tenantId) return;
  // Claim the slot BEFORE the await so a tenant switch mid-flight is detectable
  // below. Deliberately NOT guarded by an in-flight flag: an early-return on
  // "already loading" would let tenant A's request SKIP tenant B's load
  // entirely and then land A's value under B's id — the TASK-1201 companyDocs
  // hazard, one await later.
  CompanySecureState.tenantId = tenantId;
  let ein = '';
  try {
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'complianceSecure', 'company'));
    ein = (snap.exists() ? (snap.data() || {}).ein : '') || '';
  } catch (e) {
    // Fail SOFT (never block the compliance surface on this one field) but
    // LOUD in the console. A permission-denied here means the caller holds
    // neither admin nor the 'compliance' module — which cannot happen from the
    // compliance UI, since checkModuleAccess('compliance') already gates
    // reaching it — so this is a real signal, not expected noise.
    ein = '';
    console.warn('[Compliance] complianceSecure/company unreadable; EIN falls back to the tenant root:', e?.code || e?.message || e);
  }
  // Stale-response guard: if the module re-initialised for a DIFFERENT tenant
  // while this read was in flight, drop the result on the floor. Serving
  // tenant A's EIN into tenant B's Company card is a cross-tenant data leak,
  // not just a cosmetic staleness bug.
  if (CompanySecureState.tenantId !== tenantId) return;
  CompanySecureState.ein = ein;
  CompanySecureState.loaded = true;
}

function companyEin() {
  if (CompanySecureState.loaded && CompanySecureState.ein) return CompanySecureState.ein;
  return window.AppState?.tenantData?.ein || '';
}

function renderHvutDetailSection(vehicle) {
  if (vehicle._type !== 'truck') return '';
  const gvwr = vehicle.gvwr || 0;
  const taxYear = currentHvutTaxYear();
  const suggested = categoryForGvwr(gvwr);
  const filing = hvutFilingFor(vehicle.id, taxYear);
  // 2026-05-19 — Prefill EIN from tenant.ein when this filing doesn't have its
  // own. Same EIN is used for every filing in a company fleet, so 12+ vehicles
  // × yearly = a lot of redundant entry. Skeptic-flagged risk: leased
  // owner-operator trucks file 2290 under the operator's EIN, not the
  // carrier's — hint copy below calls that out explicitly.
  const td = window.AppState?.tenantData || {};
  // TASK-4617 — sourced from complianceSecure/company (root fallback), never
  // straight off the broadcast tenant root.
  const companyEinValue = companyEin();
  const einPrefillFromTenant = !filing?.ein && !!companyEinValue;

  if (gvwr > 0 && gvwr < 55000) {
    return `
      <div class="compliance-detail-section">
        <div class="compliance-detail-section-title">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">receipt_long</span>
          Form 2290 / HVUT
        </div>
        <div style="padding:8px 0;color:var(--text-muted);font-size:0.85rem;">
          GVWR ${gvwr.toLocaleString()} lbs — under 55,000 lbs, HVUT is not required.
        </div>
      </div>
    `;
  }

  const catOptions = ['<option value="">— Select —</option>']
    .concat(HVUT_CATEGORIES.map(c => {
      const label = c.maxGvwr === Infinity ? `${c.minGvwr.toLocaleString()}+ lbs` : `${c.minGvwr.toLocaleString()}–${c.maxGvwr.toLocaleString()} lbs`;
      const selected = filing?.weightCategory === c.cat || (!filing?.weightCategory && suggested?.cat === c.cat) ? ' selected' : '';
      return `<option value="${c.cat}"${selected}>${c.cat} — ${label} ($${c.amount.toFixed(2)})</option>`;
    }))
    .join('');

  const statusOptions = [
    { v: 'not_filed', l: 'Not filed' },
    { v: 'filed', l: 'Filed (awaiting Schedule 1)' },
    { v: 'accepted', l: 'Accepted — Schedule 1 issued' },
    { v: 'not_required', l: 'Not required / Suspended' },
  ].map(o => `<option value="${o.v}"${(filing?.status || 'not_filed') === o.v ? ' selected' : ''}>${o.l}</option>`).join('');

  const defaultAmount = filing?.amountPaid != null ? filing.amountPaid : (suggested?.amount ?? '');

  const taxYearOptions = [];
  const curY = parseInt(taxYear, 10);
  for (let y = curY - 2; y <= curY + 1; y++) {
    taxYearOptions.push(`<option value="${y}"${y === curY ? ' selected' : ''}>Jul ${y} – Jun ${y+1}${y === curY ? ' (current)' : ''}</option>`);
  }

  const badge = formatHvutBadge(vehicle) || '';
  const schedule1Note = filing?.schedule1Path
    ? `<a href="#" onclick="window.ComplianceModule.viewHvutSchedule1('${escJsAttr(filing.schedule1Path)}');return false;" style="color:var(--brand-blue);font-size:0.8rem;">
        <span class="material-icons" style="font-size:14px;vertical-align:middle;">picture_as_pdf</span> View current Schedule 1
      </a>`
    : '<span style="font-size:0.8rem;color:var(--text-muted);">No Schedule 1 uploaded yet.</span>';

  return `
    <div class="compliance-detail-section" data-vehicle-id="${escapeHtml(vehicle.id)}">
      <div class="compliance-detail-section-title">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">receipt_long</span>
        Form 2290 / HVUT
        <span style="margin-left:auto;">${badge}</span>
      </div>
      <div style="padding:4px 0 12px;color:var(--text-muted);font-size:0.78rem;">
        IRS Heavy Highway Vehicle Use Tax. Tax period runs Jul 1 – Jun 30. Due Aug 31. Schedule 1 (stamped proof) required at DMV.
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
        <div class="form-group">
          <label for="hvut-t-year-${escapeHtml(vehicle.id)}" style="font-size:0.78rem;color:var(--text-secondary);">Tax year</label>
          <select id="hvut-t-year-${escapeHtml(vehicle.id)}" onchange="window.ComplianceModule.onHvutYearChange('${escapeHtml(vehicle.id)}')">
            ${taxYearOptions.join('')}
          </select>
        </div>
        <div class="form-group">
          <label for="hvut-t-status-${escapeHtml(vehicle.id)}" style="font-size:0.78rem;color:var(--text-secondary);">Status</label>
          <select id="hvut-t-status-${escapeHtml(vehicle.id)}">${statusOptions}</select>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
        <div class="form-group">
          <label for="hvut-t-cat-${escapeHtml(vehicle.id)}" style="font-size:0.78rem;color:var(--text-secondary);">Weight category</label>
          <select id="hvut-t-cat-${escapeHtml(vehicle.id)}" onchange="window.ComplianceModule.onHvutCategoryChange('${escapeHtml(vehicle.id)}')">${catOptions}</select>
        </div>
        <div class="form-group">
          <label for="hvut-t-amount-${escapeHtml(vehicle.id)}" style="font-size:0.78rem;color:var(--text-secondary);">Tax paid ($)</label>
          <input type="number" step="0.01" min="0" id="hvut-t-amount-${escapeHtml(vehicle.id)}" value="${defaultAmount !== '' ? defaultAmount : ''}" placeholder="0.00">
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;">
        <div class="form-group">
          <label for="hvut-t-ein-${escapeHtml(vehicle.id)}" style="font-size:0.78rem;color:var(--text-secondary);">EIN</label>
          <input type="text" id="hvut-t-ein-${escapeHtml(vehicle.id)}" value="${escapeHtml(filing?.ein || companyEinValue || '')}" placeholder="12-3456789" maxlength="12">
          ${einPrefillFromTenant ? `<div style="font-size:0.68rem;color:var(--text-secondary);margin-top:2px;">Pre-filled from Company Identity — override if a leased owner-operator filed 2290 under their own EIN.</div>` : ''}
        </div>
        <div class="form-group">
          <label for="hvut-t-filed-${escapeHtml(vehicle.id)}" style="font-size:0.78rem;color:var(--text-secondary);">Filed date</label>
          <input type="date" id="hvut-t-filed-${escapeHtml(vehicle.id)}" value="${escapeHtml(filing?.filedDate || '')}">
        </div>
        <div class="form-group">
          <label for="hvut-t-accepted-${escapeHtml(vehicle.id)}" style="font-size:0.78rem;color:var(--text-secondary);">Accepted date</label>
          <input type="date" id="hvut-t-accepted-${escapeHtml(vehicle.id)}" value="${escapeHtml(filing?.acceptedDate || '')}">
        </div>
      </div>

      <label style="display:flex;align-items:center;gap:8px;font-size:0.82rem;color:var(--text-primary);margin:4px 0 10px;cursor:pointer;">
        <input type="checkbox" id="hvut-t-suspended-${escapeHtml(vehicle.id)}" ${filing?.suspended ? 'checked' : ''} style="width:16px;height:16px;">
        <span>Suspended vehicle (under 5,000 miles / 7,500 for agricultural)</span>
      </label>

      <div class="form-group">
        <label for="hvut-t-sched-${escapeHtml(vehicle.id)}" style="font-size:0.78rem;color:var(--text-secondary);">Schedule 1 (PDF, optional)</label>
        <input type="file" id="hvut-t-sched-${escapeHtml(vehicle.id)}" accept="application/pdf">
        <div style="margin-top:4px;">${schedule1Note}</div>
      </div>

      <div class="form-group">
        <label for="hvut-t-notes-${escapeHtml(vehicle.id)}" style="font-size:0.78rem;color:var(--text-secondary);">Notes</label>
        <textarea id="hvut-t-notes-${escapeHtml(vehicle.id)}" rows="2" style="width:100%;padding:8px 10px;border:1px solid var(--border-medium);border-radius:6px;font-size:0.85rem;resize:vertical;font-family:inherit;">${escapeHtml(filing?.notes || '')}</textarea>
      </div>

      <div style="display:flex;align-items:center;gap:8px;margin-top:8px;">
        <span id="hvut-t-status-msg-${escapeHtml(vehicle.id)}" style="font-size:0.8rem;color:var(--text-muted);margin-right:auto;"></span>
        ${filing ? `<button class="btn btn-secondary" style="padding:6px 12px;font-size:0.8rem;color:#991B1B;border-color:#FCA5A5;background:#FEE2E2;" onclick="window.ComplianceModule.deleteHvut('${escapeHtml(vehicle.id)}')">
          <span class="material-icons" style="font-size:14px;">delete</span> Delete
        </button>` : ''}
        <button class="btn btn-primary" style="padding:6px 14px;font-size:0.8rem;" onclick="window.ComplianceModule.saveHvut('${escapeHtml(vehicle.id)}')">
          <span class="material-icons" style="font-size:14px;">check</span> Save 2290
        </button>
      </div>
    </div>
  `;
}

// TASK-749 — Registration & Insurance + Annual Inspection: editable inline (mirror
// renderHvutDetailSection's pattern). Edits + doc uploads route through the
// updateVehicleCompliance callable (same write path the partner portal uses), so
// carrier + partner stay in lockstep. Docs reuse Roster's registrationDocs[]/
// insuranceDocs[]/inspectionDocs[] arrays + truck-docs/ storage — a doc uploaded
// here shows up in Roster's edit-vehicle and vice-versa.
// TASK-1137 K5 (Kayla) — one editable row for a custom vehicle compliance date.
// Class-based inputs (not ids) so saveVehicleCompliance can querySelectorAll them;
// the stable id rides on the row's data-cci-id so edits/re-saves preserve identity.
function _vcCciRowHtml(it) {
  it = it || {};
  // Flex-wrap (not a rigid grid) so the row degrades gracefully to a stacked card on
  // narrow/mobile screens instead of collapsing (COD UX). Label + date grow; alert is
  // fixed-narrow; delete stays with them.
  return `<div class="vc-cci-row" data-cci-id="${escapeHtml(it.id || '')}" style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-bottom:6px;">
    <input class="vc-cci-label" type="text" list="vc-cci-presets" maxlength="100" value="${escapeHtml(it.label || '')}" placeholder="Label (e.g. IRP)" style="flex:1 1 150px;min-width:120px;font-size:0.85rem;padding:6px 8px;">
    <input class="vc-cci-date" type="date" value="${escapeHtml(it.expiryDate || '')}" style="flex:1 1 140px;min-width:120px;font-size:0.85rem;padding:6px 8px;">
    <input class="vc-cci-alert" type="number" min="1" max="365" value="${Number(it.alertDays) || 30}" title="Alert this many days before expiry" style="flex:0 0 64px;font-size:0.85rem;padding:6px 8px;">
    <button class="btn-icon" onclick="this.closest('.vc-cci-row').remove()" title="Remove item" style="flex:0 0 auto;"><span class="material-icons" style="font-size:18px;color:#DC2626;">delete</span></button>
  </div>`;
}

function renderVehicleComplianceSection(vehicle) {
  if (vehicle._type === 'asset') return '';
  const vid = escapeHtml(vehicle.id);
  const v = vehicle;
  const docList = (arr, kind) => {
    // COD Code-Reviewer (XSS footgun): NEVER interpolate the doc URL into an
    // inline onclick — escapeHtml encodes `'` as &#39; but the browser decodes
    // entities before executing inline JS, so a URL containing a quote breaks
    // the string. Pass the array INDEX instead (Roster's pattern, roster.js:6270)
    // and look the URL up from state in the handler.
    // TASK-10086-P — open by PATH (StorageFile: storage.rules decide; a legacy
    // link is only the office's network-error fallback). A path-only entry
    // renders; a non-Storage, non-http(s) link keeps its row (removable) but
    // never reaches the href (TASK-1326).
    const items = (Array.isArray(arr) ? arr : []).map((d, i) => {
      if (!d || (!d.url && !window.StorageFile?.fileRef?.(d))) return '';
      const name = escapeHtml(d.name || 'Document');
      return `<div style="display:flex;align-items:center;gap:6px;font-size:0.78rem;padding:2px 0;">
        <span class="material-icons" style="font-size:14px;color:var(--brand-blue);">${(d.name || '').toLowerCase().endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
        <a ${window.StorageFile.linkAttrs(d)} style="color:var(--brand-blue);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${name}</a>
        <button class="btn-icon" title="Remove" style="padding:2px;" onclick="window.ComplianceModule.removeVehicleComplianceDoc('${vid}','${kind}',${i})"><span class="material-icons" style="font-size:14px;color:#dc2626;">close</span></button>
      </div>`;
    }).join('');
    return items || '<span style="font-size:0.78rem;color:var(--text-muted);">No documents on file.</span>';
  };
  const docBlock = (label, kind, arr) => `
    <div class="form-group" style="margin-top:6px;">
      <label style="font-size:0.78rem;color:var(--text-secondary);">${label}</label>
      <div style="border:1px solid var(--border-light);border-radius:6px;padding:6px 8px;background:var(--bg-subtle, #f8fafc);">
        ${docList(arr, kind)}
        <input type="file" id="vc-${kind}-file-${vid}" accept="application/pdf,image/png,image/jpeg,image/webp" style="margin-top:6px;font-size:0.78rem;">
      </div>
    </div>`;

  return `
    <div class="compliance-detail-section" data-vehicle-id="${vid}">
      <div class="compliance-detail-section-title">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">description</span> Registration & Insurance
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
        <div class="form-group">
          <label for="vc-regnum-${vid}" style="font-size:0.78rem;color:var(--text-secondary);">Registration #</label>
          <input type="text" id="vc-regnum-${vid}" value="${escapeHtml(v.registrationNumber || '')}" placeholder="—">
        </div>
        <div class="form-group">
          <label for="vc-regexp-${vid}" style="font-size:0.78rem;color:var(--text-secondary);">Reg expiry</label>
          <input type="date" id="vc-regexp-${vid}" data-prev-value="${escapeHtml(v.registrationExpiry || '')}" value="${v.registrationNoExpiry === true ? '' : escapeHtml(v.registrationExpiry || '')}" ${v.registrationNoExpiry === true ? 'disabled' : ''}>
          <label style="display:flex;align-items:center;gap:6px;font-size:0.75rem;color:var(--text-secondary);margin-top:4px;cursor:pointer;"><input type="checkbox" id="vc-regnoexp-${vid}" data-reg-noexp-guard="compliance-vehicle" data-vid="${escapeHtml(vid)}" ${v.registrationNoExpiry === true ? 'checked' : ''} onchange="var d=this.closest('.form-group').querySelector('input[type=date]');if(d){if(this.checked){if(d.value)d.dataset.prevValue=d.value;d.value='';}else if(!d.value&&d.dataset.prevValue){d.value=d.dataset.prevValue;}d.disabled=this.checked;}" style="width:16px;height:16px;"> Doesn't expire</label>
        </div>
      </div>
      ${docBlock('Registration document', 'registration', v.registrationDocs)}
      ${v._type === 'trailer' ? '' : `<!-- TASK-9813 — IRP cab card (apportioned plate). Blank = not apportioned. -->
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:6px;">
        <div class="form-group">
          <label for="vc-irpexp-${vid}" style="font-size:0.78rem;color:var(--text-secondary);">IRP Cab Card Expires</label>
          <input type="date" id="vc-irpexp-${vid}" value="${escapeHtml(v.irpExpiry || '')}">
        </div>
      </div>
      ${docBlock('IRP cab card', 'irp', v.irpDocs)}`}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:6px;">
        <div class="form-group">
          <label for="vc-inscomp-${vid}" style="font-size:0.78rem;color:var(--text-secondary);">Insurance carrier</label>
          <input type="text" id="vc-inscomp-${vid}" value="${escapeHtml(v.insuranceCompany || '')}" placeholder="—">
        </div>
        <div class="form-group">
          <label for="vc-inspol-${vid}" style="font-size:0.78rem;color:var(--text-secondary);">Policy #</label>
          <input type="text" id="vc-inspol-${vid}" value="${escapeHtml(v.insurancePolicy || '')}" placeholder="—">
        </div>
        <div class="form-group">
          <label for="vc-insexp-${vid}" style="font-size:0.78rem;color:var(--text-secondary);">Ins expiry</label>
          <input type="date" id="vc-insexp-${vid}" value="${escapeHtml(v.insuranceExpiry || '')}">
        </div>
      </div>
      ${docBlock('Insurance card / COI', 'insurance', v.insuranceDocs)}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:6px;">
        <div class="form-group">
          <label for="vc-inspexp-${vid}" style="font-size:0.78rem;color:var(--text-secondary);">Annual inspection expiry</label>
          <input type="date" id="vc-inspexp-${vid}" value="${escapeHtml(v.inspectionExpiry || '')}">
        </div>
        <div class="form-group">
          <label for="vc-insploc-${vid}" style="font-size:0.78rem;color:var(--text-secondary);">Inspection location</label>
          <input type="text" id="vc-insploc-${vid}" value="${escapeHtml(v.inspectionLocation || '')}" placeholder="—">
        </div>
      </div>
      ${docBlock('Annual inspection certificate', 'inspection', v.inspectionDocs)}
      <!-- TASK-1137 K5 (Kayla) — custom per-vehicle compliance dates (IRP, state permits, etc.). -->
      <div style="margin-top:12px;border-top:1px solid var(--border-light);padding-top:10px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;flex-wrap:wrap;gap:6px;">
          <span style="font-size:0.82rem;font-weight:600;color:var(--text-primary);">Custom compliance dates <span style="font-weight:400;color:var(--text-muted);font-size:0.72rem;">IRP, permits, anything with an expiry</span></span>
          <button class="btn btn-sm btn-secondary" style="padding:3px 10px;font-size:0.72rem;" onclick="window.ComplianceModule.addVehicleCustomItem('${vid}')"><span class="material-icons" style="font-size:13px;">add</span> Add item</button>
        </div>
        <datalist id="vc-cci-presets"><option value="IRP Registration"><option value="State Permit"><option value="IFTA License"><option value="Oversize/Overweight Permit"><option value="Kyle/Weight Permit"><option value="Emissions Test"><option value="Toll Transponder"></datalist>
        <div id="vc-cci-rows-${vid}">${(Array.isArray(v.customComplianceItems) ? v.customComplianceItems : []).map(it => _vcCciRowHtml(it)).join('')}</div>
        <div style="font-size:0.68rem;color:var(--text-muted);margin-top:2px;">Alert = days before expiry to start emailing the compliance contacts. Blank labels are dropped on save.</div>
      </div>
      <div style="display:flex;align-items:center;gap:8px;margin-top:10px;">
        <span id="vc-msg-${vid}" style="font-size:0.8rem;color:var(--text-muted);margin-right:auto;"></span>
        <button class="btn btn-primary" style="padding:6px 14px;font-size:0.8rem;" onclick="window.ComplianceModule.saveVehicleCompliance('${vid}')">
          <span class="material-icons" style="font-size:14px;">check</span> Save
        </button>
      </div>
    </div>
  `;
}

// 2290 badge for vehicle cards. Mirrors formatExpiryBadge styling.
function formatHvutBadge(vehicle) {
  if (vehicle._type !== 'truck') return '';
  const gvwr = vehicle.gvwr || 0;
  if (gvwr > 0 && gvwr < 55000) return ''; // exempt, no badge
  if (gvwr === 0) return ''; // no GVWR, can't judge
  const taxYear = currentHvutTaxYear();
  const filing = hvutFilingFor(vehicle.id, taxYear);

  if (!filing || (filing.status !== 'filed' && filing.status !== 'accepted' && filing.status !== 'not_required' && !filing.suspended)) {
    const now = new Date();
    const aug31 = new Date(now.getFullYear(), 7, 31, 23, 59, 59);
    const isLate = now > aug31 && String(now.getFullYear()) === String(taxYear);
    const cls = isLate ? 'compliance-badge-critical' : 'compliance-badge-warning';
    const icon = isLate ? 'error' : 'warning';
    return `<span class="compliance-badge ${cls}" title="Form 2290 not filed for tax year ${taxYear}">
      <span class="material-icons" style="font-size:14px;">${icon}</span> 2290: ${isLate ? 'Overdue' : 'Due Aug 31'}
    </span>`;
  }
  if (filing.suspended || filing.status === 'not_required') {
    return `<span class="compliance-badge compliance-badge-gray" title="Form 2290 not required (suspended / low mileage)">
      <span class="material-icons" style="font-size:14px;">check_circle</span> 2290: N/R
    </span>`;
  }
  return `<span class="compliance-badge compliance-badge-good" title="Form 2290 filed for tax year ${taxYear}">
    <span class="material-icons" style="font-size:14px;">check_circle</span> 2290: ${filing.status === 'accepted' ? 'Accepted' : 'Filed'}
  </span>`;
}

// TASK-513 — required-trainings list lives in app.js (settings.safetyTrainings).
// Read the canonical active list via window._stGetCurrent so compliance and HR
// share ONE source of truth.
function getSafetyTrainingsConfig() {
  try { return (window._stGetCurrent?.() || []).filter(i => i && i.archived !== true); }
  catch (_) { return []; }
}

// State of ONE required training for ONE person. Mirrors hr.js
// computeRequiredTrainingState (recompute expiry from issueDate + recurrence;
// no-expiry → no date; missing if no linked entry). Uses the existing
// daysUntilExpiry (NO shared dateState in compliance.js — per grounding).
function requiredTrainingState(person, configItem) {
  // TASK-1353 — position-tier scoping: a training scoped (appliesTo) to other
  // tiers is 'na' for this person — evaluated-and-not-applicable, resolved
  // BEFORE the per-person exemption. 'na' is excluded from rollups exactly
  // like 'exempt' (rollupRequiredTrainings counts neither).
  if (!window.DqMatrix.trainingAppliesTo(person, configItem)) {
    return { state: 'na', date: null, days: null };
  }
  // TASK-919 — per-employee "Not Applicable" exemption short-circuits expiry logic.
  const exemptions = person.safetyTrainingExemptions || {};
  if (exemptions[configItem.itemId]) {
    return { state: 'exempt', reason: exemptions[configItem.itemId].reason, date: null, days: null };
  }
  const recurrence = Number(configItem?.recurrenceMonths) || 0;
  const entries = (person.isnetTrainings || []).filter(t => t && t.requiredTrainingId === configItem.itemId);
  if (entries.length === 0) return { state: 'missing', date: null, days: null };
  const entry = entries.slice().sort((a, b) => String(b.issueDate || '').localeCompare(String(a.issueDate || '')))[0];
  // TASK-897 — the cert's explicit expiryDate is the source of truth (matches the
  // other 3 calc sites: hr.computeRequiredTrainingState + the two compliance-partner
  // sites). recurrence is only a silent fallback for legacy certs with an issueDate
  // but no expiryDate; it no longer short-circuits a real expiryDate. Without this
  // the dashboard rollup (rollupRequiredTrainings) would still flag a genuinely
  // expired cert as "On file" whenever its training has recurrenceMonths 0.
  let expiry = entry.expiryDate || null;
  if (!expiry && entry.issueDate && recurrence > 0) {
    const d = new Date(entry.issueDate);
    if (!isNaN(d.getTime())) { d.setMonth(d.getMonth() + recurrence); expiry = d.toISOString().split('T')[0]; }
  }
  if (!expiry) return { state: 'noexpiry', date: null, days: null };
  const days = daysUntilExpiry(expiry);
  let state = 'valid';
  if (days !== null && days < 0) state = 'expired';
  else if (days !== null && days <= 30) state = 'expiring';
  return { state, date: expiry, days };
}

// TASK-1049 — reason-capture modal for marking a required training "Not
// Applicable" for one employee (compliance module). Mirrors HR's
// _promptExemptionReason. Promise resolves to the trimmed reason, or null if
// cancelled. Uses class="modal" (per lessons: NOT "modal-overlay").
function _promptExemptionReasonCompliance(trainingLabel) {
  return new Promise((resolve) => {
    const existing = document.getElementById('compliance-training-exempt-modal');
    if (existing) existing.remove();
    const modal = document.createElement('div');
    modal.id = 'compliance-training-exempt-modal';
    modal.className = 'modal';
    modal.style.cssText = 'display:flex;align-items:center;justify-content:center;';
    modal.innerHTML = `
      <div class="modal-content" style="max-width:420px;width:92%;max-height:90dvh;overflow-y:auto;">
        <div class="modal-header" style="padding:14px 16px;"><h3 style="margin:0;font-size:1rem;">Mark Not Applicable</h3></div>
        <div class="modal-body" style="padding:16px;">
          <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 10px;">Why is <strong>${escapeHtml(trainingLabel)}</strong> not applicable for this employee? This reason is recorded in the audit log.</p>
          <textarea id="compliance-training-exempt-reason" rows="3" maxlength="500" placeholder="e.g. Office/admin staff — not a field role" style="width:100%;box-sizing:border-box;"></textarea>
        </div>
        <div class="form-actions" style="display:flex;justify-content:flex-end;gap:8px;padding:12px 16px;">
          <button class="btn btn-secondary" id="compliance-training-exempt-cancel">Cancel</button>
          <button class="btn btn-primary" id="compliance-training-exempt-confirm">Mark N/A</button>
        </div>
      </div>`;
    document.body.appendChild(modal);
    const ta = modal.querySelector('#compliance-training-exempt-reason');
    setTimeout(() => ta?.focus(), 50);
    const close = (val) => { modal.remove(); resolve(val); };
    modal.querySelector('#compliance-training-exempt-cancel').onclick = () => close(null);
    modal.querySelector('#compliance-training-exempt-confirm').onclick = () => {
      const v = (ta.value || '').trim();
      if (!v) { ta.style.borderColor = '#DC2626'; ta.focus(); return; }
      close(v);
    };
    modal.addEventListener('click', (e) => { if (e.target === modal) close(null); });
  });
}

// TASK-1049 — set/clear a per-employee Required-Training exemption from the
// COMPLIANCE module, via the setTrainingExemption callable (server merges/deletes;
// safetyTrainingExemptions is SERVER-OWNED — firestore.rules blocks any direct
// client write, so we only optimistically mirror it locally). The callable admits
// a compliance-module member ADDITIVELY (owner/admin/hr still pass) — see
// functions/src/index.js setTrainingExemption. Double-submit guard keyed by
// person+item while the callable is in flight (COD lesson, TASK-1031).
const _complianceTrainingExemptionBusy = new Set();
async function toggleTrainingExemptionCompliance(personId, itemId, exempt) {
  const _busyKey = `${personId}::${itemId}`;
  if (_complianceTrainingExemptionBusy.has(_busyKey)) return; // already in flight
  // Claim the key BEFORE the reason/confirm prompt so a rapid second click can't
  // open a duplicate modal / double-fire the callable. Cleared in finally on every
  // path incl. cancel.
  _complianceTrainingExemptionBusy.add(_busyKey);
  try {
    let reason = '';
    if (exempt) {
      const cfg = getSafetyTrainingsConfig().find(i => i && i.itemId === itemId);
      reason = await _promptExemptionReasonCompliance(cfg?.label || 'this training');
      if (reason == null) return; // cancelled
    } else {
      const ok = await (window.showConfirm
        ? window.showConfirm('Clear exemption?', 'This employee will be required to have this training again.')
        : Promise.resolve(confirm('Clear exemption? This employee will be required to have this training again.')));
      if (!ok) return;
    }
    const fn = httpsCallable(functions, 'setTrainingExemption');
    await fn({ tenantId: ComplianceState.tenantId, personId, itemId, exempt: !!exempt, reason });
    // Optimistic local-state update so the re-render reflects immediately.
    // NEVER an authoritative Firestore write — the callable owns this field.
    const emp = ComplianceState.people.find(p => p.id === personId);
    if (emp) {
      emp.safetyTrainingExemptions = emp.safetyTrainingExemptions || {};
      if (exempt) emp.safetyTrainingExemptions[itemId] = { reason, exemptedAt: new Date().toISOString() };
      else delete emp.safetyTrainingExemptions[itemId];
    }
    window.showToast?.(exempt ? 'Marked Not Applicable' : 'Exemption cleared', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] setTrainingExemption', err);
    const msg = err?.code === 'functions/permission-denied'
      ? 'You do not have access to change training exemptions.'
      : 'Error updating exemption: ' + (err?.message || '');
    window.showToast?.(msg, 'error');
  } finally {
    _complianceTrainingExemptionBusy.delete(_busyKey);
  }
}

// Roll up required-training health for a person across the whole config.
// Returns { worst: 'critical'|'warning'|'good', missing, expired, expiring }.
function rollupRequiredTrainings(person) {
  const config = getSafetyTrainingsConfig();
  let missing = 0, expired = 0, expiring = 0;
  for (const c of config) {
    const st = requiredTrainingState(person, c);
    if (st.state === 'missing') missing++;
    else if (st.state === 'expired') expired++;
    else if (st.state === 'expiring') expiring++;
  }
  let worst = 'good';
  if (missing > 0 || expired > 0) worst = 'critical';
  else if (expiring > 0) worst = 'warning';
  return { worst, missing, expired, expiring };
}

function getComplianceStatus(person) {
  const today = new Date().toISOString().split('T')[0];
  // TASK-934 — DOT credential checks are gated by the shared DQ-applicability
  // matrix (window.DqMatrix, loaded once via hr.js — pitfall #1: no second
  // ?v= import). A classified Non-Driver can no longer show "compliance
  // critical" off a stale CDL/med date; required trainings + DVIRs still count
  // for everyone (company-wide, not DOT-driven).
  const dqItems = window.DqMatrix.getApplicableDqItems(person).items;
  const cdlApplies = !!dqItems.cdlValid;
  const medApplies = !!dqItems.medCard;
  const cdlDays = cdlApplies ? daysUntilExpiry(person.cdlExpiryDate) : null;
  const medDays = medApplies ? daysUntilExpiry(person.medCardExpiryDate) : null;
  // TASK-1330 Slice 1: an APPLICABLE credential with NO valid date is a GAP, not a pass.
  // daysUntilExpiry returns null for both a missing and an unparseable date, so a driver
  // with no CDL on file (cdlDays === null while the matrix says it applies) used to score
  // 'good' — better than an EXPIRED one. Missing ≠ not-applicable; the matrix already
  // decides applicability, so a null date on an applicable item is critical.
  const cdlMissing = cdlApplies && cdlDays === null;
  const medMissing = medApplies && medDays === null;
  const missedDvirs = person.missedDvirCount || 0;
  const training = rollupRequiredTrainings(person);

  // Critical: missing/expired CDL, missing/expired med card, OR a missing/expired required training
  if (cdlMissing || medMissing || (cdlDays !== null && cdlDays < 0) || (medDays !== null && medDays < 0) || training.worst === 'critical') {
    return 'critical';
  }

  // Warning: expiring within 30 days, missed DVIRs, OR an expiring required training
  if ((cdlDays !== null && cdlDays <= 30) || (medDays !== null && medDays <= 30) || missedDvirs > 0 || training.worst === 'warning') {
    return 'warning';
  }

  return 'good';
}

// TASK-4703 — the DVIR writer stores `status:'defects'` (PLURAL, canonical write
// in pretrip.js submitInspection). Every read site in this file tested the
// SINGULAR 'defect', a value the writer has never produced, so the open-defect
// signal and the vehicle-health rollup silently read clean on every tenant since
// they shipped. Probed 2026-09-01: 1,691 live DVIRs — 23 'defects', 0 'defect'.
// Mirrors inspectionHasDefects() in pretrip.js — keep the two in sync. (There is
// NO pretripHasDefects() in firestore.rules; the rules only name the defect
// fields in a comment at :1336. The pretrip.js docstring claiming a rules mirror
// is wrong — verified by grep 2026-09-01.) Tolerant by design: accepts the
// legacy singular, the hasSafetyDefect flag, and a non-empty defects[] so a
// fourth spelling can't silence the signal again (cf. TASK-593, which hit the
// same predicate drift on the pass side).
function _dvirHasDefects(p) {
  if (!p) return false;
  return p.status === 'defects' || p.status === 'defect' ||
         p.hasSafetyDefect === true ||
         (Array.isArray(p.defects) && p.defects.length > 0);
}

// TASK-9706 — a defect DVIR is open until pretrip.js dvirDefectClosure says it
// is closed (mechanic sign-off + every repair work order it spawned completed +
// next-driver review — TASK-9405, the ONE predicate the Defects tab and
// get_open_defects read). This used to test `resolved` / `workOrderId`, fields
// no app code writes, so every real defect stayed open here forever.
// Repair work orders come from the `dvirRepairs` stream (maintenance
// source:'pretrip'). Not loaded / failed / predicate missing -> OPEN: this
// screen never hides a defect it cannot prove was repaired.
let _dvirRepairIndex = { src: null, map: null };
function _dvirLinkedRepairs(dvirId) {
  const list = ComplianceState.dvirRepairs;
  if (!ComplianceState.streamsLoaded?.dvirRepairs || !Array.isArray(list)) return { state: 'loading' };
  if (ComplianceState.dvirRepairsError) return { state: 'error' };
  if (_dvirRepairIndex.src !== list) {
    const map = new Map();
    list.forEach((t) => {
      if (t?.source !== 'pretrip' || !t.sourceId) return;
      const m = map.get(t.sourceId) || { total: 0, open: 0 };
      m.total += 1;
      if (t.status !== 'completed') m.open += 1;
      map.set(t.sourceId, m);
    });
    _dvirRepairIndex = { src: list, map };
  }
  const m = _dvirRepairIndex.map.get(dvirId) || { total: 0, open: 0 };
  return { state: 'ok', total: m.total, open: m.open };
}

function _dvirIsOpenDefect(p) {
  if (!_dvirHasDefects(p)) return false;
  const closure = window.dvirDefectClosure;
  if (p.status !== 'defects') return !p.resolved && !p.workOrderId; // legacy shapes keep the old rule
  if (typeof closure !== 'function') return true;
  return closure(p, _dvirLinkedRepairs(p.id)).open !== false;
}

/** The unit name for a DVIR (the writer stores truckUnit; never show a raw doc id when a name exists). */
function _dvirUnitLabel(p) {
  const v = p?.truckId ? ([...(ComplianceState.trucks || []), ...(ComplianceState.trailers || [])].find((x) => x.id === p.truckId)) : null;
  return p?.truckUnit || p?.assetUnit || p?.trailerUnit || v?.unit || v?.nickname || p?.truck || p?.truckId || 'Vehicle';
}

// Human-readable summary of what a DVIR actually wrote up. The writer
// (pretrip.js) stores free text in `defectNotes` and the failed checklist items
// in `defects` as an array of STRINGS — probed across all live defect DVIRs
// 2026-09-01. There is no `notes` field and no `defects[].description`, so the
// old `defects?.[0]?.description || notes` chain always fell through to the
// placeholder. Kept object-tolerant in case a future writer stores richer items.
function _dvirDefectSummary(p) {
  const notes = (p?.defectNotes || p?.notes || '').trim();
  if (notes) return notes;
  const items = Array.isArray(p?.defects) ? p.defects : [];
  const labels = items
    .map(d => (typeof d === 'string' ? d : (d?.description || d?.label || d?.name || '')))
    .filter(Boolean);
  return labels.length ? labels.join(', ') : 'Reported defect';
}

// ============================================================================
// TASK-4806 — ONE definition of "this maintenance task still holds the vehicle".
//
// Probed all 284 live `maintenance` docs across 87 tenants (read-only,
// 2026-09-08). The REAL vocabulary is:
//   status   → completed 104 · Closed 84 · open 79 · Open 11 · Done 4 ·
//              in_progress 1 · pending_review 1     ("complete" — no d: ZERO)
//   priority → normal 141 · <none> 99 · critical 20 · high 8 · low 8 ·
//              medium 8                              ("safety": ZERO)
// The maintenance form only ever offers low|normal|high|critical
// (maintenance.js:373-378, :1122-1128). Capitalised statuses are a legacy
// import that lives on trippin-trucking only.
//
// TWO comparisons here were written against values nothing produces, and they
// failed in OPPOSITE directions:
//
//   • `status !== 'complete'` (no `d`). The writer is 'completed'
//     (maintenance.js:5299). Inert in practice — the COMPLIANCE_STREAMS.maintenance
//     query already restricts to ['open','in_progress','pending','parts_ordered'],
//     so a finished task never reaches this filter; measured, ZERO vehicles on
//     ZERO tenants were held by it. Fixed anyway so the guard cannot fail open
//     if that query is ever widened.
//
//   • `priority === 'safety'`. Nothing writes 'safety' either — and this gate
//     therefore MISSED 'critical', the one priority the rest of the product
//     treats as a safety hold (dispatch.js:3739, pretrip.js:2174/2208,
//     maintenance.js:5856). A truck sitting on the dispatch safety-hold board
//     with an open critical DVIR work order read "Compliant" here. That is an
//     UNDER-flag on a safety surface, the dangerous direction.
//
// The vehicle gate and the Compliance Health feed (renderHealthSummary, issue
// #10 below) also carried DIFFERENT priority sets — 'high' here, 'critical'
// there — which is exactly how the feed could report a hold the vehicle card
// denied. Same drift the TASK-1137 K5 note below warns about. They share this
// predicate now so they cannot disagree again.
//
// pending_review (TASK-4773) counts as STILL HOLDING: a driver saying the work
// is done is not an office sign-off. It cannot actually raise a hold today —
// 4773 forces priority:'normal' on that path AND the stream query excludes the
// status — this is the third belt, not a behaviour change.
//
// 'safety' is KEPT in the hold set despite matching nothing: it costs nothing,
// and removing a comparison in case an importer someday writes it is how this
// class of bug gets made. Comparisons are lowercased so the legacy
// Closed/Done/Open rows resolve the same way their lowercase twins do.
// ============================================================================
// Every value here has a REAL writer, which is the whole point of this task —
// a first draft also carried 'cancelled'/'canceled'/'dismissed' and COD's
// blast-radius lane caught them: nothing writes any of the three to a
// `maintenance` doc ('dismissed' belongs to rental items, maintenance.js:3525),
// so they were the same invented-vocabulary mistake being fixed here.
//   completed  104 live   (maintenance.js:5299, the canonical finish write)
//   closed      84 live   (as 'Closed', legacy TITS import)
//   done         4 live   (as 'Done',   legacy TITS import)
//   complete     0 live   — kept ONLY because it is the value this task was
//                          filed about; a deliberate no-op so the typo can
//                          never come back as a live hold.
const _MAINT_DONE_STATUSES = new Set(['complete', 'completed', 'closed', 'done']);
const _MAINT_HOLD_PRIORITIES = new Set(['safety', 'high', 'critical']);

/** Priority alone is high enough to hold a vehicle (used for severity colour). */
function _maintenanceIsHoldPriority(m) {
  return _MAINT_HOLD_PRIORITIES.has(String(m?.priority || '').trim().toLowerCase());
}

/** An unfinished maintenance task at a priority that holds the vehicle. */
function _maintenanceIsActiveHold(m) {
  if (!m) return false;
  if (_MAINT_DONE_STATUSES.has(String(m.status || '').trim().toLowerCase())) return false;
  return _maintenanceIsHoldPriority(m);
}

function getVehicleComplianceStatus(vehicle, pretrips, maintenanceTasks) {
  const vehiclePretrips = pretrips.filter(p =>
    p.truckId === vehicle.id || p.trailerId === vehicle.id ||
    p.truck === vehicle.unit || p.trailer === vehicle.unit
  );

  // Check for open safety defects in pretrips
  const hasOpenDefects = vehiclePretrips.some(_dvirIsOpenDefect);

  // Check maintenance holds
  const activeHolds = maintenanceTasks.filter(m =>
    (m.truckId === vehicle.id || m.trailerId === vehicle.id ||
     m.truck === vehicle.unit || m.trailer === vehicle.unit) &&
    _maintenanceIsActiveHold(m)
  );

  // Check expired registration, insurance, or inspection
  const regDays = daysUntilExpiry(_vcRegExpiryOf(vehicle));
  const insDays = daysUntilExpiry(vehicle.insuranceExpiry);
  const inspDays = daysUntilExpiry(vehicle.inspectionExpiry);
  // TASK-9813 — IRP cab card (trucks only; blank = not apportioned). 60-day soon window = the cron's first alert.
  const irpDays = vehicle._type === 'trailer' ? null : daysUntilExpiry(vehicle.irpExpiry);
  // TASK-1137 K5 — custom compliance items feed the SAME health rollup (else a badge
  // could read "Expired" while the dashboard counts the vehicle "good" — COD). Each
  // custom item uses its OWN alertDays threshold for the "expiring soon" window.
  const _cciItems = Array.isArray(vehicle.customComplianceItems) ? vehicle.customComplianceItems : [];
  const cciExpired = _cciItems.some(it => { const d = daysUntilExpiry(it.expiryDate); return d !== null && d < 0; });
  const cciSoon = _cciItems.some(it => { const d = daysUntilExpiry(it.expiryDate); return d !== null && d >= 0 && d <= (Number(it.alertDays) || 30); });
  const anyExpired = (regDays !== null && regDays < 0) || (insDays !== null && insDays < 0) || (inspDays !== null && inspDays < 0) || (irpDays !== null && irpDays < 0) || cciExpired;
  const anyExpiringSoon = (regDays !== null && regDays >= 0 && regDays <= 30) || (insDays !== null && insDays >= 0 && insDays <= 30) || (inspDays !== null && inspDays >= 0 && inspDays <= 30) || (irpDays !== null && irpDays >= 0 && irpDays <= 60) || cciSoon;

  if (hasOpenDefects || activeHolds.length > 0 || anyExpired) return 'critical';

  // Check for recent defects (last 7 days)
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const recentDefects = vehiclePretrips.filter(p => {
    const pDate = p.date ? new Date(p.date) : (p.createdAt?.toDate?.() || null);
    return pDate && pDate > weekAgo && _dvirHasDefects(p);
  });

  if (recentDefects.length > 0 || anyExpiringSoon) return 'warning';

  return 'good';
}

function getAlertCounts() {
  const today = new Date().toISOString().split('T')[0];
  let critical = 0;
  let warning = 0;
  let good = 0;

  // People alerts
  const activePeople = ComplianceState.people.filter(p => p.active !== false);
  activePeople.forEach(p => {
    const status = getComplianceStatus(p);
    if (status === 'critical') critical++;
    else if (status === 'warning') warning++;
    else good++;
  });

  // Vehicle alerts
  const allVehicles = [...ComplianceState.trucks, ...ComplianceState.trailers];
  allVehicles.forEach(v => {
    const status = getVehicleComplianceStatus(v, ComplianceState.pretrips, ComplianceState.maintenance);
    if (status === 'critical') critical++;
    else if (status === 'warning') warning++;
    else good++;
  });

  // Company-level credentials. Counted here so the Critical/Warning chips can
  // never read 0 while renderHealthSummary is showing an expired UCR. Not added
  // to `good` when healthy — the company is not an entity in this roll-up's
  // denominator, and inflating it would move the compliance percentage.
  const companyCreds = getCompanyCredentialIssues();
  critical += companyCreds.expired.length;
  warning += companyCreds.expiring.length;

  return { critical, warning, good };
}

// ============================================
// DATA SUBSCRIPTIONS (read-only)
//
// TASK-1147 — streams subscribe lazily PER SUB-VIEW instead of ~12 listeners
// firing on module load. Each named stream subscribes the first time a
// sub-view (or detail modal) that reads it becomes visible, then STAYS live
// until module cleanup — subscribe-once, so a cross-sub-view read never
// regresses after its stream has opened, and tab-hopping never re-fetches.
// renderComplianceContent gates each tab body on its required streams' first
// snapshot (loading spinner, never a silently-empty grid). Every stream def
// marks itself loaded on error too (empty array), so a rules denial can't
// hang a tab on the spinner.
// ============================================

function _markStreamLoaded(name) {
  ComplianceState.streamsLoaded[name] = true;
  _drainStreamWaiters();
}

function _setStream(name, docs) {
  // TASK-9359 — resolve the identity kit (DOB / licence number / state / expiry
  // / CDL number) from the peopleIdentity vault onto the people stream in place.
  if (name === 'people') { try { window.PersonIdentity?.overlayList?.(docs); } catch (_) { /* legacy values stay */ } }
  ComplianceState[name] = docs;
  _markStreamLoaded(name);
  renderIfReady();
  // TASK-1623 — Kolsen's ask: the supervisor should be TOLD, on arrival, that a
  // §391.25(b) review is waiting on their signature, not have to go looking.
  if (name === 'people') _compMaybePromptMvrReviews();
}

// ============================================================================
// TASK-1623 — the arrival prompt for outstanding §391.25(b) reviews.
//
// Fires once per browser session per tenant, the first time the people stream
// lands with at least one driver carrying a review flag. It is INFORMATIONAL:
// it never blocks the module, never writes anything, and dismissing it costs
// nothing — the flags stay on the driver cards either way.
//
// Deliberately keyed off the SAME derived state the cards render
// (`_compMvrReviewState`), so the prompt and the badges can never disagree —
// the count-says-1 / list-shows-0 drift that bit the partner urgency feed.
// Drivers whose state is `indeterminate` are listed SEPARATELY rather than
// folded into the count: "we cannot tell" is not "review required".
// ============================================================================
function _compPendingMvrReviews() {
  const out = { needsReview: [], indeterminate: [] };
  const people = Array.isArray(ComplianceState.people) ? ComplianceState.people : [];
  for (const p of people) {
    if (!p || p.active === false || p.terminated === true || p.terminationDate) continue;
    // Same driver test the after-MVR prompt uses (crewType || role || roles[0]).
    const role = String(p.crewType || p.role || (p.roles && p.roles[0]) || '').toLowerCase();
    if (!/driver|cdl/.test(role)) continue;
    const st = _compMvrReviewState(p);
    if (st.code === 'new_violation' || st.code === 'new_mvr') out.needsReview.push({ p, st });
    else if (st.code === 'indeterminate') out.indeterminate.push({ p, st });
  }
  // Violations first — those are the ones Crown asks the motor carrier to sign.
  out.needsReview.sort((a, b) => (b.st.flagB ? 1 : 0) - (a.st.flagB ? 1 : 0));
  return out;
}

function _compMaybePromptMvrReviews() {
  try {
    const tenantId = ComplianceState.tenantId;
    if (!tenantId) return;
    const key = 'dd_mvr_review_prompt_' + tenantId;
    let already = false;
    try { already = sessionStorage.getItem(key) === '1'; } catch (_) { already = false; }
    if (already) return;

    const { needsReview, indeterminate } = _compPendingMvrReviews();
    if (needsReview.length === 0 && indeterminate.length === 0) return;
    try { sessionStorage.setItem(key, '1'); } catch (_) { /* private mode — prompt once per load instead */ }

    const existing = document.getElementById('comp-mvr-review-prompt');
    if (existing) existing.remove();

    const rows = needsReview.slice(0, 8).map(({ p, st }) => `
      <div style="display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:1px solid var(--border-light);">
        <span class="material-icons" style="font-size:16px;color:${st.flagB ? '#DC2626' : '#D97706'};">${st.flagB ? 'gavel' : 'rate_review'}</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${escapeHtml(p.name || 'Driver')}</div>
          <div style="font-size:0.72rem;color:var(--text-secondary);">${escapeHtml(st.flagB ? 'New violation found on MVR' : 'New MVR on file')} &bull; pulled ${escapeHtml(String(st.mvrDate || '').slice(0, 10))}</div>
        </div>
        <button class="btn btn-sm" onclick="document.getElementById('comp-mvr-review-prompt')?.remove();window.ComplianceModule.openAnnualReviewModal('${escapeHtml(String(p.id))}')" style="padding:3px 10px;font-size:0.7rem;background:#2563EB;color:#fff;border:none;">Sign review</button>
      </div>`).join('');

    const indetHtml = indeterminate.length ? `
      <div style="margin-top:10px;background:#F1F5F9;border:1px solid #CBD5E1;border-radius:8px;padding:10px 12px;font-size:0.75rem;color:#334155;line-height:1.5;">
        <strong>${indeterminate.length} driver${indeterminate.length === 1 ? '' : 's'} could not be assessed.</strong>
        A date on ${indeterminate.length === 1 ? 'that record' : 'those records'} is unreadable, so DispatchDeck will not tell you the review is either done or outstanding. Open ${indeterminate.length === 1 ? 'the driver' : 'those drivers'} and fix the date.
      </div>` : '';

    const modal = document.createElement('div');
    modal.id = 'comp-mvr-review-prompt';
    modal.className = 'modal';
    modal.innerHTML = `
      <div class="modal-content" style="max-width:520px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
        <div class="modal-header" style="background:linear-gradient(135deg,#1D4ED8,#3B82F6);color:#fff;flex-shrink:0;">
          <h3 style="color:#fff;display:flex;align-items:center;gap:8px;"><span class="material-icons">rate_review</span> Driving-record reviews are waiting</h3>
          <button class="modal-close" onclick="document.getElementById('comp-mvr-review-prompt').remove()" style="color:#fff;"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body" style="padding:18px;overflow-y:auto;flex:1;min-height:0;">
          ${needsReview.length ? `
            <div style="font-size:0.85rem;color:var(--text-secondary);line-height:1.55;margin-bottom:12px;">
              <strong>${needsReview.length} driver${needsReview.length === 1 ? ' has' : 's have'} an MVR on file that nobody has signed off on yet.</strong>
              49 CFR §391.25(b) requires the motor carrier to review the record and decide whether the driver still meets minimum safe-driving standards; §391.25(c)(2) requires a note in the driver qualification file naming who did the review and when.
            </div>
            ${rows}
            ${needsReview.length > 8 ? `<div style="font-size:0.72rem;color:var(--text-secondary);padding:6px 0;">+ ${needsReview.length - 8} more on the People tab.</div>` : ''}
          ` : ''}
          ${indetHtml}
        </div>
        <div class="form-actions" style="padding:14px 18px;border-top:1px solid var(--border-light);display:flex;gap:8px;justify-content:flex-end;flex-shrink:0;">
          <button class="btn btn-secondary" onclick="document.getElementById('comp-mvr-review-prompt').remove()">Later</button>
        </div>
      </div>`;
    document.body.appendChild(modal);
    modal.classList.remove('hidden');
  } catch (e) {
    // A nag must never be able to break the module it nags in.
    console.warn('[Compliance] MVR review prompt failed (non-fatal):', e);
  }
}

// Deferred callbacks for detail modals opened before their streams' first
// snapshot (e.g. an Overview health-summary row click → vehicle detail).
const _streamWaiters = [];
function _streamsLoadedAll(names) {
  return names.every(n => ComplianceState.streamsLoaded[n]);
}
function _whenStreamsLoaded(names, cb) {
  if (_streamsLoadedAll(names)) { cb(); return; }
  _streamWaiters.push({ names, cb });
}
function _drainStreamWaiters() {
  for (let i = _streamWaiters.length - 1; i >= 0; i--) {
    if (_streamsLoadedAll(_streamWaiters[i].names)) {
      const { cb } = _streamWaiters.splice(i, 1)[0];
      try { cb(); } catch (e) { console.error('[Compliance] Stream waiter failed:', e); }
    }
  }
}

const STREAM_DEFS = {
  // People
  people: (tenantId) => {
    // TASK-9359 — the identity kit lives in the peopleIdentity vault; re-overlay
    // + re-render whenever it lands (it can arrive after the people stream).
    window.PersonIdentity?.subscribe?.(tenantId, {
      key: 'compliance',
      onUpdate: () => {
        try {
          window.PersonIdentity.overlayList(ComplianceState.people);
          if (Array.isArray(ComplianceState.people) && ComplianceState.people.length) renderIfReady();
        } catch (_) { /* view may be mid-teardown */ }
      },
    });
    return onSnapshot(
      query(collection(db, 'tenants', tenantId, 'people'), orderBy('name')),
      (snap) => _setStream('people', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => _setStream('people', [])
    );
  },

  // Trucks
  trucks: (tenantId) => onSnapshot(
    collection(db, 'tenants', tenantId, 'trucks'),
    (snap) => _setStream('trucks', snap.docs.map(d => ({ id: d.id, ...d.data(), _type: 'truck' }))),
    () => _setStream('trucks', [])
  ),

  // Trailers
  trailers: (tenantId) => onSnapshot(
    collection(db, 'tenants', tenantId, 'trailers'),
    (snap) => _setStream('trailers', snap.docs.map(d => ({ id: d.id, ...d.data(), _type: 'trailer' }))),
    () => _setStream('trailers', [])
  ),

  // Assets (non-consumable equipment) — Assets tab only
  assets: (tenantId) => onSnapshot(
    collection(db, 'tenants', tenantId, 'assets'),
    (snap) => _setStream('assets', snap.docs.map(d => ({ id: d.id, ...d.data(), _type: 'asset' }))),
    () => _setStream('assets', [])
  ),

  // HVUT / Form 2290 filings — vehicle cards + vehicle detail only
  hvutFilings: (tenantId) => onSnapshot(
    collection(db, 'tenants', tenantId, 'hvutFilings'),
    (snap) => _setStream('hvutFilings', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => _setStream('hvutFilings', [])
  ),

  // Pretrips (last 90 days for DVIR history)
  pretrips: (tenantId) => {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    return onSnapshot(
      query(
        collection(db, 'tenants', tenantId, 'pretrips'),
        where('createdAt', '>=', Timestamp.fromDate(ninetyDaysAgo))
      ),
      (snap) => _setStream('pretrips', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => _setStream('pretrips', [])
    );
  },

  // TASK-9706 — every work order a DVIR spawned (source:'pretrip', any status),
  // so _dvirIsOpenDefect can see a COMPLETED repair; `maintenance` below is
  // active-only. A failed listener flags dvirRepairsError -> defects stay open.
  dvirRepairs: (tenantId) => onSnapshot(
    query(
      collection(db, 'tenants', tenantId, 'maintenance'),
      where('source', '==', 'pretrip')
    ),
    (snap) => { ComplianceState.dvirRepairsError = false; _setStream('dvirRepairs', snap.docs.map(d => ({ id: d.id, ...d.data() }))); },
    () => { ComplianceState.dvirRepairsError = true; _setStream('dvirRepairs', []); }
  ),

  // Maintenance tasks (active only)
  maintenance: (tenantId) => onSnapshot(
    query(
      collection(db, 'tenants', tenantId, 'maintenance'),
      where('status', 'in', ['open', 'in_progress', 'pending', 'parts_ordered'])
    ),
    (snap) => _setStream('maintenance', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => _setStream('maintenance', [])
  ),

  // Clock entries (last 90 days for driver scorecard) — drivers sub-view only
  clockEntries: (tenantId) => {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    return onSnapshot(
      query(
        collection(db, 'tenants', tenantId, 'clockEntries'),
        where('clockIn', '>=', Timestamp.fromDate(ninetyDaysAgo))
      ),
      (snap) => _setStream('clockEntries', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => _setStream('clockEntries', [])
    );
  },

  // Job days — drivers sub-view only. TASK-1147: now BOUNDED to the last 90
  // days (mirrors the pretrips/clockEntries window; the only consumer here is
  // the drivers scorecard, which uses a 30-day assignment window). jobDays.date
  // is a Timestamp on dispatch-written docs (dispatch.js windows admins on the
  // same field — TASK-424 precedent). One-shot no-regression fallback to the
  // old unbounded stream if the windowed query errors, same pattern as
  // dispatch.js jobDays.
  jobDays: (tenantId) => {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    const publish = (snap) => _setStream('jobDays', snap.docs.map(d => ({ id: d.id, ...d.data() })));
    let unsub = onSnapshot(
      query(
        collection(db, 'tenants', tenantId, 'jobDays'),
        where('date', '>=', Timestamp.fromDate(ninetyDaysAgo))
      ),
      publish,
      (err) => {
        console.warn('[Compliance] Windowed jobDays query failed — falling back to unbounded stream.', err?.code || err);
        unsub = onSnapshot(
          collection(db, 'tenants', tenantId, 'jobDays'),
          publish,
          () => _setStream('jobDays', [])
        );
      }
    );
    return () => unsub();
  },

  // Drug tests
  drugTests: (tenantId) => onSnapshot(
    query(collection(db, 'tenants', tenantId, 'drugTests'), orderBy('testDate', 'desc')),
    (snap) => _setStream('drugTests', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => _setStream('drugTests', [])
  ),

  // Compliance program docs (TPA attestations, period reports, pool roster snapshots, blank CCFs)
  complianceDocs: (tenantId) => onSnapshot(
    query(collection(db, 'tenants', tenantId, 'complianceDocs'), orderBy('createdAt', 'desc')),
    (snap) => _setStream('complianceDocs', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => _setStream('complianceDocs', [])
  ),

  // Fuel purchases (IFTA)
  fuelPurchases: (tenantId) => onSnapshot(
    query(collection(db, 'tenants', tenantId, 'fuelPurchases'), orderBy('date', 'desc')),
    (snap) => _setStream('fuelPurchases', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => _setStream('fuelPurchases', [])
  ),

  // DVIR accountability flags (all statuses for detail view). Feature-gated:
  // when the setting is off there is nothing to stream — mark loaded with an
  // empty array (matches the pre-1147 behavior of never subscribing).
  dvirFlags: (tenantId) => {
    if (!window.AppState?.settings?.dvirAccountability) {
      ComplianceState.dvirFlags = [];
      _markStreamLoaded('dvirFlags'); // no renderIfReady — we're inside a render pass
      return () => {};
    }
    return onSnapshot(
      collection(db, 'tenants', tenantId, 'dvirAccountability'),
      (snap) => _setStream('dvirFlags', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => _setStream('dvirFlags', [])
    );
  },

  // JSA accountability flags (all statuses for detail view) — same gate.
  jsaFlags: (tenantId) => {
    if (!window.AppState?.settings?.jsaAccountability) {
      ComplianceState.jsaFlags = [];
      _markStreamLoaded('jsaFlags'); // no renderIfReady — we're inside a render pass
      return () => {};
    }
    return onSnapshot(
      collection(db, 'tenants', tenantId, 'jsaAccountability'),
      (snap) => _setStream('jsaFlags', snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => _setStream('jsaFlags', [])
    );
  },

  // TASK-432 — Signals/Trend rail (4 window-bounded listeners; Overview only).
  // Marked loaded immediately: the KPI tiles/feed render zero-safe and
  // self-heal per snapshot, so the tab gate never waits on them.
  signals: (tenantId) => {
    subscribeSignalsData(tenantId); // pushes its unsubs onto ComplianceState.unsubscribers
    _markStreamLoaded('signals');
    return () => {};
  }
};

// Which streams each sub-view actually reads (traced call-graph, TASK-1147):
//   overview — getAlertCounts / renderComplianceStats / renderHealthSummary
//     read people + trucks + trailers + pretrips + maintenance; signals rail;
//     random-pool + clearinghouse sections read people.
//   profile — renderDAndAProgramSection + quarterly random strip read
//     complianceDocs (rest is tenantData/FMCSA, not streams).
//   people — person cards read people + drugTests (badge) + dvirFlags/jsaFlags
//     (flag badges); the person detail modal reads the same set (drug-test
//     section, DVIR/JSA accountability section, DQ-scorecard pre-employment
//     flag). complianceDocs kept per spec (program-doc affordances).
//   people/drivers — the scorecard additionally reads pretrips + jobDays +
//     clockEntries.
//   assets — grids/cards/detail read trucks + trailers + assets + hvutFilings
//     + pretrips + maintenance.
//   ifta — fuelPurchases + trucks/trailers (IFTA-vehicle join + fuel modal).
//   hazmat — people only.
//   safety / dotAdvisor / permitPete — delegated modules with their own
//     listeners; nothing from ComplianceState.
const VIEW_STREAMS = {
  overview: ['people', 'trucks', 'trailers', 'pretrips', 'dvirRepairs', 'maintenance', 'signals'],
  profile: ['complianceDocs'],
  people: ['people', 'drugTests', 'complianceDocs', 'dvirFlags', 'jsaFlags'],
  assets: ['trucks', 'trailers', 'assets', 'hvutFilings', 'pretrips', 'dvirRepairs', 'maintenance'],
  ifta: ['fuelPurchases', 'trucks', 'trailers'],
  hazmat: ['people'],
  safety: [],
  dotAdvisor: [],
  permitPete: [],
  activity: ['people'] // TASK-10095 S1b — names for the rows
};
const PEOPLE_DRIVERS_EXTRA = ['pretrips', 'dvirRepairs', 'jobDays', 'clockEntries'];
// Detail modals can open from sub-views that never subscribed their streams
// (Overview health-summary / signals rows) — see showPersonDetail /
// showVehicleDetail on-demand gates.
const PERSON_DETAIL_STREAMS = ['people', 'drugTests', 'dvirFlags', 'jsaFlags'];
const VEHICLE_DETAIL_STREAMS = ['trucks', 'trailers', 'pretrips', 'dvirRepairs', 'maintenance', 'hvutFilings'];

function _requiredStreamsForActiveView() {
  const base = VIEW_STREAMS[ComplianceState.activeTab] || VIEW_STREAMS.overview;
  if (ComplianceState.activeTab === 'people' && ComplianceState.peopleSubTab === 'drivers') {
    return [...base, ...PEOPLE_DRIVERS_EXTRA];
  }
  return base;
}

// Idempotent — at most one live listener per stream name.
function ensureStreams(names) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId) return;
  names.forEach((name) => {
    if (ComplianceState.streamUnsubs[name]) return;
    const def = STREAM_DEFS[name];
    if (!def) return;
    // Claim the slot BEFORE invoking the def: some defs mark themselves loaded
    // synchronously (signals, feature-gated flags), which can re-enter a
    // render pass → ensureStreams before this assignment would otherwise land.
    ComplianceState.streamUnsubs[name] = () => {};
    ComplianceState.streamUnsubs[name] = def(tenantId) || (() => {});
  });
}

// ============================================
// TASK-432 — SIGNALS RAIL + 7-DAY TREND KPIs (data subscriptions)
//
// 🔴 SCALE GUARD (COD Architect/Blast): every query below is hard-bounded to a
// rolling window — JSA + safety meetings to the last 14 days (the 7d trend needs
// the prior 7d too), signed PDFs to the last 7 days. We never scan the whole
// collection. As tenants grow, the future path is the denormalized "top-level
// stamp for fast filter queries" pattern (functions/src/index.js:~40891) — noted,
// NOT built now (premature at current scale per Skeptic).
//
// 🔴 PARTNER VISIBILITY (COD AuthZ): this module only ever runs in the CARRIER's
// OWN authenticated session (initCompliance is called with window.AppState.tenant
// — the logged-in user's own tenant; app.js:8282). It is NEVER rendered in a
// partner-impersonation context. The JSA paying-gate
// ([[feedback-jsa-partner-visibility-paying-gate]]) lives on the PARTNER surface
// (compliance-manager.html), which aggregates other carriers' JSA data. A carrier
// always sees its own data. So these reads cannot leak a non-paying carrier's
// signals to a partner — there is no partner here.
// ============================================
function _windowStart(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d;
}

function subscribeSignalsData(tenantId) {
  // 14-day window covers current-7d + prior-7d for the trend arrows.
  const fourteenDaysAgo = _windowStart(14);
  const sevenDaysAgo = _windowStart(7);

  // JSA reports (date within 14d) — KPI: JSA trend. Bound on `date` (a Timestamp
  // present on every JSA — jsa.js:2198/2264 + demoSeeder.js:2027) rather than
  // createdAt, which the demo seed omits.
  const unsubJsaReports = onSnapshot(
    query(
      collection(db, 'tenants', tenantId, 'jsaReports'),
      where('date', '>=', Timestamp.fromDate(fourteenDaysAgo))
    ),
    (snap) => {
      ComplianceState.jsaReportsRecent = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      renderIfReady();
    },
    () => { ComplianceState.jsaReportsRecent = []; }
  );
  ComplianceState.unsubscribers.push(unsubJsaReports);

  // Safety meetings / tailgates (safetySignIns). Doc id === 'YYYY-MM-DD' and the
  // `date` string sorts chronologically, so a string range is the window bound.
  const dateStr14 = fourteenDaysAgo.toISOString().split('T')[0];
  const unsubMeetings = onSnapshot(
    query(
      collection(db, 'tenants', tenantId, 'safetySignIns'),
      where('date', '>=', dateStr14)
    ),
    (snap) => {
      ComplianceState.safetyMeetingsRecent = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      renderIfReady();
    },
    () => { ComplianceState.safetyMeetingsRecent = []; }
  );
  ComplianceState.unsubscribers.push(unsubMeetings);

  // Signed PDFs this week (signDocuments signedAt within 7d) — Signals feed only.
  const unsubSigned = onSnapshot(
    query(
      collection(db, 'tenants', tenantId, 'signDocuments'),
      where('signedAt', '>=', Timestamp.fromDate(sevenDaysAgo))
    ),
    (snap) => {
      ComplianceState.signedDocsRecent = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      renderIfReady();
    },
    () => { ComplianceState.signedDocsRecent = []; }
  );
  ComplianceState.unsubscribers.push(unsubSigned);

  // Safety equipment (gas monitors / certs). Not date-bounded because the Signals
  // feed reads EXPIRY/CALIBRATION-DUE dates off each record — but it is naturally
  // bounded by fleet size (one row per issued item, small), not by time/all-history
  // growth. We only surface rows that carry an expiry/calibration date.
  const unsubEquip = onSnapshot(
    collection(db, 'tenants', tenantId, 'safetyEquipment'),
    (snap) => {
      ComplianceState.safetyEquipmentRecent = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      renderIfReady();
    },
    () => { ComplianceState.safetyEquipmentRecent = []; }
  );
  ComplianceState.unsubscribers.push(unsubEquip);
}

function renderIfReady() {
  if (!ComplianceState.initialized) return;
  const container = document.getElementById('compliance-content');
  if (!container) return;
  renderComplianceContent();
  // TASK-879 — an OPEN person-detail modal showed STALE data after a Roster /
  // license edit: the people onSnapshot re-rendered the list behind the modal but
  // not the modal itself. Re-render the open modal so it reflects the new data.
  // GUARD against clobbering in-progress user input:
  //  - editingDriverDetailsId: the inline Driver-Details edit form is open.
  //  - sub-forms (Add Drug Test / Training / Fuel / MVR) reuse #compliance-detail-modal
  //    but do NOT render the data-detail="person" layout → selector excludes them.
  //  - the Vehicle-detail modal shares .compliance-detail-header but not
  //    data-detail="person" → excluded too.
  //  - a focused field inside the modal (user mid-type) → skip this cycle.
  // Scroll position is preserved so the refresh isn't jarring.
  if (ComplianceState.selectedPersonId && !ComplianceState.editingDriverDetailsId) {
    const modal = document.getElementById('compliance-detail-modal');
    const personContent = modal && !modal.classList.contains('hidden')
      ? modal.querySelector('.modal-content[data-detail="person"]')
      : null;
    const active = document.activeElement;
    const editingInModal = active && modal && modal.contains(active) &&
      /^(INPUT|SELECT|TEXTAREA)$/.test(active.tagName || '');
    if (personContent && !editingInModal) {
      const prevScroll = personContent.scrollTop;
      showPersonDetail(ComplianceState.selectedPersonId);
      const next = modal.querySelector('.modal-content[data-detail="person"]');
      if (next) next.scrollTop = prevScroll;
    }
  }
}

// ============================================
// RENDER
// ============================================
function renderComplianceContent() {
  const container = document.getElementById('compliance-content');
  if (!container) return;

  // TASK-1147 — subscribe this sub-view's streams on demand, then gate the tab
  // body on their first snapshot so a just-opened tab shows a spinner instead
  // of a silently-empty grid. The tab bar stays rendered + clickable during
  // the load; the arriving snapshots re-enter via renderIfReady.
  const requiredStreams = _requiredStreamsForActiveView();
  ensureStreams(requiredStreams);
  if (!_streamsLoadedAll(requiredStreams)) {
    container.innerHTML = `
      ${renderTabBar()}
      <div style="display:flex;align-items:center;justify-content:center;padding:60px 0;">
        <div class="loading-spinner"></div>
      </div>
    `;
    return;
  }

  const isOverview = ComplianceState.activeTab === 'overview';
  const isProfile = ComplianceState.activeTab === 'profile';
  const hideSearch = /^(overview|profile|safety|ifta|hazmat|dotAdvisor|permitPete|activity)$/.test(ComplianceState.activeTab) || (ComplianceState.activeTab === 'people' && ComplianceState.peopleSubTab === 'drivers');

  container.innerHTML = `
    ${renderTabBar()}
    ${isOverview ? renderOverviewTab() : ''}
    ${!isOverview && !hideSearch ? renderSearchBar() : ''}
    ${!isOverview ? renderTabContent() : ''}
  `;

  // Wire up events
  wireEvents();

  // TASK-1201 — populate the Company Documents section (async; the tab HTML
  // renders a host div synchronously, this fills it once hrResources load).
  if (isProfile) {
    renderCompanyDocsSection();
  }

  // Init safety tab content after DOM is ready
  if (ComplianceState.activeTab === 'safety') {
    const safetyContainer = document.getElementById('compliance-safety-container');
    if (safetyContainer) {
      window.HRModule?.initSafetyStandalone?.(safetyContainer, ComplianceState.tenantId);
    }
  }

  // TASK-10095 S1b — Activity: LAZY views/complianceActivity.js renders; this host
  // authorizes by handing it ONLY the getComplianceActivity callable (server gate:
  // owner, or compliance/hr module) — the renderer never decides access.
  const actEl = ComplianceState.activeTab === 'activity' && document.getElementById('compliance-activity-host');
  const refresh = ComplianceState.actFresh; // read synchronously; mount runs after the import
  ComplianceState.actFresh = 0;
  if (actEl) __ddChunkWrap(import('./complianceActivity.js?v=10095202')).then((m) => m.mount(actEl, { key: window.AppState?.user?.uid && ComplianceState.tenantId + '|' + window.AppState.user.uid, refresh, people: ComplianceState.people, escapeHtml, load: (a) => httpsCallable(functions, 'getComplianceActivity')({ ...a, tenantId: ComplianceState.tenantId }).then((r) => r.data) })).catch(() => { actEl.textContent = 'Could not load Activity.'; });

  // Init DOT Advisor tab content after DOM is ready (uses window.DotAdvisorModule
  // to avoid cross-module ?v= cache-buster import duplication — see CLAUDE.md).
  if (ComplianceState.activeTab === 'dotAdvisor') {
    const advisorContainer = document.getElementById('compliance-dot-advisor-container');
    if (advisorContainer) {
      window.DotAdvisorModule?.init?.(advisorContainer, ComplianceState.tenantId);
    }
  }

  // Init Permit Pete tab (uses window.PermitPeteModule to avoid cross-module
  // ?v= cache-buster import duplication — see CLAUDE.md).
  if (ComplianceState.activeTab === 'permitPete') {
    const peteContainer = document.getElementById('compliance-permit-pete-container');
    if (peteContainer) {
      window.PermitPeteModule?.init?.(peteContainer, ComplianceState.tenantId);
    }
  }
}

// ============================================
// TASK-432 — SIGNALS RAIL + 7-DAY TREND KPIs (render)
//
// Turns the compliance Overview from a filing cabinet into a "what's about to
// bite you" feed. Two pieces:
//   (1) KPI tiles with prior-7d trend arrows — JSA, Tailgates, Hazard ID,
//       Incidents. Count for the last 7 days vs the prior 7 days.
//   (2) A live Signals feed — expiring certifications, outstanding
//       hazard/action items, signed PDFs this week, equipment calibration/expiry.
//
// ALL counts derive from the window-bounded subscriptions in subscribeSignalsData
// (last 14d / 7d) + already-loaded carrier-own data (people, pretrips,
// actionItems). No precomputed counters, no all-time scans. FieldSafe-inspired.
// ============================================

// Normalize a Firestore Timestamp | Date | 'YYYY-MM-DD' | ISO string to a Date.
function _toDate(val) {
  if (!val) return null;
  if (typeof val.toDate === 'function') { try { return val.toDate(); } catch (_) { return null; } }
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

// Split a list of {date} records into [current 7d count, prior 7d count].
function _trendSplit(items, getDate) {
  const now = new Date();
  const day = 24 * 60 * 60 * 1000;
  const sevenAgo = now.getTime() - 7 * day;
  const fourteenAgo = now.getTime() - 14 * day;
  let current = 0, prior = 0;
  (items || []).forEach(it => {
    const d = getDate(it);
    if (!d) return;
    const t = d.getTime();
    if (t >= sevenAgo) current++;
    else if (t >= fourteenAgo) prior++;
  });
  return { current, prior };
}

function _trendArrowHtml(current, prior, upIsGood) {
  // Context-aware colour (COD UX catch): "up" is GOOD for completed safety work
  // (more JSAs / tailgates) but BAD for hazards / DVIR defects. Green = the
  // good direction, amber = the bad direction, grey = flat / no data.
  if (prior === 0 && current === 0) {
    return `<span style="font-size:0.72rem;color:var(--text-muted);">no activity</span>`;
  }
  const delta = current - prior;
  if (delta === 0) {
    return `<span style="display:inline-flex;align-items:center;gap:2px;font-size:0.72rem;color:var(--text-muted);"><span class="material-icons" style="font-size:15px;">trending_flat</span> same as last 7d</span>`;
  }
  const up = delta > 0;
  const good = up === !!upIsGood; // up & upIsGood, or down & !upIsGood
  const color = good ? '#10B981' : '#EF4444';
  const icon = up ? 'trending_up' : 'trending_down';
  const pct = prior === 0 ? '' : ` ${Math.round((Math.abs(delta) / prior) * 100)}%`;
  return `<span style="display:inline-flex;align-items:center;gap:2px;font-size:0.72rem;color:${color};font-weight:600;" title="${current} in last 7 days vs ${prior} the prior 7 days">
    <span class="material-icons" style="font-size:15px;">${icon}</span>${up ? '+' : '−'}${Math.abs(delta)}${pct} vs prior 7d
  </span>`;
}

function _kpiTile(icon, iconColor, value, label, current, prior, upIsGood) {
  return `
    <div class="crm-stat-card bi-stat-vertical" style="cursor:default;">
      <div class="crm-stat-icon" style="background:${iconColor}15;color:${iconColor};">
        <span class="material-icons">${icon}</span>
      </div>
      <div class="crm-stat-value">${value}</div>
      <div class="crm-stat-label">${label}</div>
      <div style="margin-top:4px;">${_trendArrowHtml(current, prior, upIsGood)}</div>
    </div>`;
}

function renderSignalsTrendKpis() {
  // JSA reports completed (createdAt within window).
  const jsa = _trendSplit(ComplianceState.jsaReportsRecent, r => _toDate(r.date) || _toDate(r.createdAt));
  // Tailgates / safety meetings (safetySignIns date string).
  const tailgates = _trendSplit(ComplianceState.safetyMeetingsRecent, r => _toDate(r.date) || _toDate(r.createdAt));
  // Hazard ID — action items created (the existing actionItems lifecycle is the
  // canonical hazard/action model per COD; no new collection). Count by createdAt.
  const hazards = _trendSplit(ActionItemsState.items, r => _toDate(r.createdAt));
  // DVIR Defects — safety defects reported in the window. NOT labeled
  // "Incidents": in FMCSA terms an incident is a recordable accident/injury
  // (§390.15), whereas a DVIR defect is an inspection write-up. Mislabeling
  // would corrupt a carrier's SMS/audit narrative (COD Domain catch).
  // pretrips already loaded (90d); bounded by createdAt to the trend window here.
  const defects = (ComplianceState.pretrips || []).filter(_dvirHasDefects);
  const dvirDefects = _trendSplit(defects, r => _toDate(r.createdAt));

  return `
    <div style="margin-bottom:var(--spacing-md);">
      <div class="crm-section-title" style="margin-bottom:10px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="color:var(--brand-blue);font-size:20px;">insights</span>
        Activity — last 7 days
        <span style="font-weight:400;font-size:0.78rem;color:var(--text-muted);margin-left:4px;">vs the 7 days before</span>
      </div>
      <div class="crm-stats-grid">
        ${_kpiTile('fact_check', '#3B82F6', jsa.current, 'JSAs', jsa.current, jsa.prior, true)}
        ${_kpiTile('groups', '#8B5CF6', tailgates.current, 'Tailgates', tailgates.current, tailgates.prior, true)}
        ${_kpiTile('report_problem', '#F59E0B', hazards.current, 'Hazard IDs', hazards.current, hazards.prior, false)}
        ${_kpiTile('warning', '#EF4444', dvirDefects.current, 'DVIR Defects', dvirDefects.current, dvirDefects.prior, false)}
      </div>
    </div>`;
}

function renderSignalsFeed() {
  const signals = [];
  const today = new Date();
  const activePeople = ComplianceState.people.filter(p => p.active !== false);

  // 1. Expiring OR expired certifications (CDL / med card). Already-expired
  //    (d < 0) MUST stay visible — a CDL that lapsed yesterday is the highest-
  //    severity FMCSA risk (§391.41 per-trip violation); dropping it the day
  //    after expiry defeats the rail (COD Domain catch). Surface <= 30d out
  //    AND anything overdue.
  activePeople.forEach(p => {
    [
      { field: 'cdlExpiryDate', label: 'CDL' },
      { field: 'medCardExpiryDate', label: 'Med card' },
    ].forEach(({ field, label }) => {
      const d = daysUntilExpiry(p[field]);
      if (d !== null && d <= 30) {
        const expired = d < 0;
        signals.push({
          icon: expired ? 'gpp_bad' : 'badge', color: (expired || d <= 7) ? '#EF4444' : '#F59E0B',
          title: `${escapeHtml(p.name || 'Driver')} — ${label} ${expired ? `EXPIRED ${Math.abs(d)}d ago` : `expires in ${d}d`}`,
          sub: p[field], sortKey: expired ? -2000 + d : d, action: 'people', refId: p.id,
        });
      }
    });
  });

  // 2. Gas monitor / safety equipment calibration or expiry due soon. The
  //    safetyEquipment record only carries an expiry/recal date if the tenant
  //    captured one — read it if present, never fabricate. Bounded to <=30d out.
  (ComplianceState.safetyEquipmentRecent || []).forEach(eq => {
    if (eq && eq.voidedAt) return; // TASK-9719 — a voided issue was never handed out
    const dueRaw = eq.recalDue || eq.calibrationDue || eq.nextCalibration || eq.expiresAt || eq.expiryDate;
    const due = dueRaw && typeof dueRaw.toDate === 'function' ? dueRaw.toDate().toISOString().split('T')[0] : dueRaw;
    const d = daysUntilExpiry(due);
    if (d !== null && d <= 30) {
      const isGas = /gas|h2s|monitor/i.test(eq.equipmentType || '');
      signals.push({
        icon: isGas ? 'sensors' : 'health_and_safety', color: d < 0 ? '#EF4444' : (d <= 7 ? '#EF4444' : '#F59E0B'),
        title: `${escapeHtml(eq.equipmentType || 'Equipment')}${eq.personName ? ' (' + escapeHtml(eq.personName) + ')' : ''} — ${isGas ? 'calibration' : 'expiry'} ${d < 0 ? Math.abs(d) + 'd overdue' : 'in ' + d + 'd'}`,
        sub: due, sortKey: d, action: 'safety',
      });
    }
  });

  // 3. Outstanding hazard / action items (open partner-set tasks, not finished).
  (ActionItemsState.items || []).forEach(item => {
    if (item.status === 'finished') return;
    const overdue = item.dueDate && new Date(item.dueDate) < today;
    signals.push({
      icon: 'checklist', color: overdue ? '#EF4444' : (item.priority === 'high' ? '#F59E0B' : '#3B82F6'),
      title: `${escapeHtml(item.task || 'Action item')}${overdue ? ' — overdue' : (item.dueDate ? ' — due ' + escapeHtml(item.dueDate) : '')}`,
      sub: `Priority: ${escapeHtml((item.priority || 'low').toUpperCase())}`, _subSafe: true,
      sortKey: overdue ? -1000 : ({ high: 0, medium: 1, low: 2 }[item.priority] ?? 3),
      action: null, // full management lives in the Action Items section above
    });
  });

  // 4. Open DVIR safety defects (hazards on the equipment side).
  const openDefects = (ComplianceState.pretrips || []).filter(_dvirIsOpenDefect);
  openDefects.slice(0, 5).forEach(def => {
    signals.push({
      icon: 'car_crash', color: '#EF4444',
      title: `DVIR defect — ${escapeHtml(_dvirUnitLabel(def))}`,
      sub: escapeHtml(_dvirDefectSummary(def).substring(0, 70)), _subSafe: true,
      sortKey: -500, action: 'assets',
    });
  });

  // 5. Signed PDFs this week (positive signal — closed loops).
  const signedThisWeek = (ComplianceState.signedDocsRecent || []).filter(d => _toDate(d.signedAt));
  if (signedThisWeek.length > 0) {
    signals.push({
      icon: 'task_alt', color: '#10B981', positive: true,
      title: `${signedThisWeek.length} document${signedThisWeek.length > 1 ? 's' : ''} signed this week`,
      sub: signedThisWeek.slice(0, 3).map(d => escapeHtml(d.title || 'Document')).join(', ') + (signedThisWeek.length > 3 ? '…' : ''), _subSafe: true,
      sortKey: 9000, action: 'safety',
    });
  }

  // 6. Company-level credentials (UCR / MCS-150 / insurance). Without these the
  // empty state below could claim "You're current" with an expired UCR on file.
  const _coCreds = getCompanyCredentialIssues();
  _coCreds.expired.forEach(c => {
    signals.push({
      icon: 'gpp_bad', color: '#EF4444',
      title: `${escapeHtml(c.label)} expired`,
      sub: `${c.days} day${c.days === 1 ? '' : 's'} ago — company credential`, _subSafe: true,
      sortKey: -900, action: 'profile',
    });
  });
  _coCreds.expiring.forEach(c => {
    signals.push({
      icon: 'schedule', color: '#F59E0B',
      title: `${escapeHtml(c.label)} expires in ${c.days} day${c.days === 1 ? '' : 's'}`,
      sub: 'Company credential', _subSafe: true,
      sortKey: -400, action: 'profile',
    });
  });

  // Sort: most urgent (lowest sortKey) first; positive/info last.
  signals.sort((a, b) => a.sortKey - b.sortKey);

  const header = `
    <div class="crm-section-title" style="margin-bottom:12px;display:flex;align-items:center;gap:6px;">
      <span class="material-icons" style="color:var(--brand-blue);font-size:20px;">notifications_active</span>
      Signals
      <span style="font-weight:400;font-size:0.78rem;color:var(--text-muted);margin-left:4px;">what's about to bite you</span>
    </div>`;

  if (signals.length === 0) {
    return `<div class="crm-section-card" style="margin-top:16px;">
      ${header}
      <div class="bi-alert-item" style="border-left-color:#10B981;cursor:default;">
        <span class="material-icons" style="color:#10B981;font-size:20px;">check_circle</span>
        <div>
          <div style="font-weight:600;font-size:13px;color:var(--text-primary);">No active signals</div>
          <div style="font-size:12px;color:var(--text-secondary);">Nothing expiring, no open hazards or action items. You're current.</div>
        </div>
      </div>
    </div>`;
  }

  const rows = signals.slice(0, 12).map(s => {
    const clickAction = s.action ? `onclick="window.ComplianceModule.handleIssueClick('${s.action}', '${s.refId || ''}')" style="cursor:pointer;"` : 'style="cursor:default;"';
    return `<div class="bi-alert-item" style="border-left-color:${s.color};" ${clickAction}>
      <span class="material-icons" style="color:${s.color};font-size:20px;">${s.icon}</span>
      <div style="min-width:0;">
        <div style="font-weight:600;font-size:13px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${s.title}</div>
        <div style="font-size:12px;color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${s._subSafe ? s.sub : escapeHtml(s.sub || '')}</div>
      </div>
    </div>`;
  }).join('');

  return `<div class="crm-section-card" style="margin-top:16px;">
    ${header}
    ${rows}
    ${signals.length > 12 ? `<div style="text-align:center;font-size:0.78rem;color:var(--text-muted);margin-top:8px;">+${signals.length - 12} more</div>` : ''}
  </div>`;
}

function renderOverviewTab() {
  const alerts = getAlertCounts();
  // Tier-aware audit button. Compliance-only tenants get the Driver
  // Qualification audit (391.51 driver-side data they HAVE), with an
  // inline upsell banner explaining what the full audit unlocks. Full
  // subscribers get the complete DOT audit (DVIRs + work orders +
  // citations + driver qualification).
  const isComplianceOnly = !!window.isComplianceOnlyTenant?.();
  const auditButton = isComplianceOnly
    ? `<button class="btn btn-primary btn-sm" onclick="window.ComplianceModule.showDotAuditModal()" style="display:inline-flex;align-items:center;gap:6px;">
         <span class="material-icons" style="font-size:18px;">assignment_ind</span> Driver Qualification Audit
       </button>`
    : `<button class="btn btn-primary btn-sm" onclick="window.ComplianceModule.showDotAuditModal()" style="display:inline-flex;align-items:center;gap:6px;">
         <span class="material-icons" style="font-size:18px;">assignment</span> Full DOT Audit Report
       </button>`;

  const upsellBanner = isComplianceOnly
    ? `<div style="margin-top:var(--spacing-md);padding:14px 18px;background:linear-gradient(135deg,#EFF6FF,#F0F9FF);border:1px solid #BFDBFE;border-radius:var(--radius-md);">
         <div style="display:flex;align-items:flex-start;gap:12px;">
           <span class="material-icons" style="font-size:22px;color:var(--brand-blue);margin-top:1px;">lock_open</span>
           <div style="flex:1;">
             <div style="font-weight:600;font-size:0.92rem;color:var(--text-primary);margin-bottom:4px;">Unlock the full DOT audit</div>
             <div style="font-size:0.82rem;color:var(--text-secondary);line-height:1.5;">
               Upgrade to DispatchDeck ($250/mo flat) to add the vehicle-side audit:
               DVIR records (49 CFR 396.11), 12-month work order history (396.3), and
               citation/DVIR pairing for proof-of-repair (396). The driver qualification
               audit above stays included.
             </div>
           </div>
           <button class="btn btn-primary btn-sm" onclick="window.showFeatureUpgradeModal?.('base') || (window.location.href='/#/billing')" style="white-space:nowrap;align-self:flex-start;">Upgrade</button>
         </div>
       </div>`
    : "";

  // TASK-972 S4 — DOT auditor access links (time-boxed read-only). Owner/admin
  // only, mirroring the server's requireTenantAdmin gate on mint/list/revoke.
  // The modal implementation is shared with HR: window.HRModule.openDotAuditAccess
  // (window.* at call time — never a second ?v= import, pitfall #1).
  const auditAccessButton = _compIsOwnerOrAdmin()
    ? `<button class="btn btn-secondary btn-sm" onclick="window.ComplianceModule.openDotAuditAccess()" style="display:inline-flex;align-items:center;gap:6px;">
         <span class="material-icons" style="font-size:18px;">policy</span> DOT Audit Access
       </button>`
    : '';

  return `
    ${renderActionItemsSection()}
    ${renderComplianceStats(alerts)}
    ${renderSignalsTrendKpis()}
    ${renderSignalsFeed()}
    <div style="margin:var(--spacing-md) 0;display:flex;justify-content:center;gap:10px;flex-wrap:wrap;">
      ${auditButton}
      ${auditAccessButton}
    </div>
    ${upsellBanner}
    ${renderClearinghouseConsentsCarrierSection()}
    ${renderRandomPoolRosterSection()}
    ${renderHealthSummary()}
  `;
}

// ============================================
// TASK-DA-v1 follow-up — §382.305(j) random-pool roster (read-only)
//
// Roster-level view of who's in the FMCSA random selection pool. Person-level
// edit toggle lives in hr.js People → Compliance tab; we mirror the readout
// here so the compliance advisor / fleet manager sees pool composition at a
// glance from the Overview without drilling into each driver.
//
// Predicate matches the audit-packet manifest at
// functions/src/index.js:31015-31021 — driver = role contains 'driver' OR
// cdlNumber present; default in-pool when isDriver && hasCdl unless
// person.inRandomPool is an explicit boolean. hasCdl falls back to cdlClass
// presence so legacy roster docs without a CDL number still classify.
// ============================================
// TASK-343 Slice 4 — daCategory enum + UI helpers.
//   routine        — standard random-pool driver (default).
//   sap_process    — driver currently engaged with a Substance Abuse
//                    Professional after a violation (§40.281–289). Per FMCSA
//                    guidance, carrier must track separately from routine
//                    random pool; driver remains in pool but cannot perform
//                    safety-sensitive functions.
//   rtd_followup   — driver who completed Return-to-Duty process
//                    (§40.305) and is on the follow-up testing schedule
//                    set by the SAP (min 6 unannounced tests in 12 months).
const DA_CATEGORY_ROUTINE     = 'routine';
const DA_CATEGORY_SAP_PROCESS = 'sap_process';
const DA_CATEGORY_RTD_FOLLOWUP = 'rtd_followup';

const DA_CATEGORY_META = {
  [DA_CATEGORY_ROUTINE]: {
    label: 'Routine',
    chipBg: '#EFF6FF', chipColor: '#1D4ED8',
    sectionLabel: 'Routine random pool',
    sectionHint: 'Standard random-pool drivers (§382.305).',
  },
  [DA_CATEGORY_SAP_PROCESS]: {
    label: 'SAP process',
    chipBg: '#FEF3C7', chipColor: '#92400E',
    sectionLabel: 'SAP process (49 CFR §40.281–289)',
    sectionHint: 'Driver is engaged with a Substance Abuse Professional after a violation. Track separately from routine pool; driver cannot perform safety-sensitive functions until RTD complete.',
  },
  [DA_CATEGORY_RTD_FOLLOWUP]: {
    label: 'RTD follow-up',
    chipBg: '#FCE7F3', chipColor: '#9D174D',
    sectionLabel: 'Return-to-duty follow-up (49 CFR §40.305)',
    sectionHint: 'Driver completed Return-to-Duty process and is on SAP-prescribed follow-up testing schedule (min 6 unannounced tests in 12 months).',
  },
};

function _normalizeDaCategory(value) {
  if (value === DA_CATEGORY_SAP_PROCESS || value === DA_CATEGORY_RTD_FOLLOWUP) return value;
  return DA_CATEGORY_ROUTINE;
}

function renderRandomPoolRosterSection() {
  // TASK-1320 SIDE FIX (confirmed live) — this filtered on `active` ONLY, and a
  // TERMINATED person can carry `active` unset/true (the terminate paths set it,
  // but legacy + imported rows don't), so a terminated driver still rendered
  // "In pool" on the §382.305 roster readout. Match the partner-side predicate
  // (compliance-partner.js getEmploymentStatus:265) exactly: terminated is a
  // first-class state, not a flavor of inactive.
  //
  // 🟢 DELIBERATELY NOT auto-flipping `inRandomPool = false` on termination (COD
  // Domain + Blast; COD UX Lead dissented and wanted the flip — Domain + Blast's
  // reasoning is stronger and this note records the split rather than silently
  // resolving it). That field has EIGHT other readers
  // (functions/src/index.js :9427/:32695/:57474/:62322/:62334/:72806,
  // compliance-manager.html:12513, compliance-partner.js:3226) and flipping it
  // erases the state the new randomPoolHistory subcollection exists to hold.
  // Fix the render filter, not the field.
  const activePeople = ComplianceState.people.filter(
    p => p.active !== false && p.terminated !== true && !p.terminationDate);

  const classified = [];
  for (const p of activePeople) {
    // TASK-9696 — §382.305 / §199.105 pool composition FOLLOWS COVERAGE through the
    // ONE predicate (window.DqMatrix.randomPoolStatusFor — twin of the server's
    // services/testingProgram.js; hr.js and the partner portal read the same one).
    // Covered (CDL tier or PHMSA flag) → listed, in by default, the explicit
    // boolean is the opt-out. Not covered → not listed — EXCEPT a stale
    // `inRandomPool:true`, which is listed as a conflict for a human to clear
    // (the old filter hid exactly these people, so nobody saw them).
    const st = window.DqMatrix.randomPoolStatusFor(p);
    if (!st.covered && !st.conflict) continue;
    const hasCdl = !!(p.cdlNumber || p.hasCdlNumber === true || (p.cdlClass && p.cdlClass !== '' && p.cdlClass !== 'none'));
    const daCategory = _normalizeDaCategory(p.daCategory);
    classified.push({ person: p, inPool: st.inPool, conflict: st.conflict, hasCdl, daCategory, coverage: st.coverage });
  }

  if (classified.length === 0) return '';

  const inPoolCount = classified.filter(r => r.inPool).length;
  const conflictCount = classified.filter(r => r.conflict).length;
  const excludedCount = classified.length - inPoolCount - conflictCount;

  // TASK-343 Slice 4 — per-category counts (in-pool drivers only — excluded
  // drivers are surfaced separately and don't carry a meaningful category).
  const categoryCounts = {
    [DA_CATEGORY_ROUTINE]: 0,
    [DA_CATEGORY_SAP_PROCESS]: 0,
    [DA_CATEGORY_RTD_FOLLOWUP]: 0,
  };
  for (const r of classified) {
    if (r.inPool) categoryCounts[r.daCategory] = (categoryCounts[r.daCategory] || 0) + 1;
  }

  // TASK-343 Slice 4 — filter chip honors ComplianceState.daCategoryFilter
  // ('all' | 'routine' | 'sap_process' | 'rtd_followup'). Default 'all'.
  const activeFilter = ComplianceState.daCategoryFilter || 'all';

  // Sort in-pool first, then alphabetically by name
  classified.sort((a, b) => {
    if (a.inPool !== b.inPool) return a.inPool ? -1 : 1;
    return (a.person.name || '').localeCompare(b.person.name || '');
  });

  const filtered = activeFilter === 'all'
    ? classified
    : classified.filter(r => r.inPool && r.daCategory === activeFilter);

  // Group in-pool drivers by category for grouped rendering when 'all' filter
  // is active. Excluded drivers render in their own trailing group.
  const groupOrder = [DA_CATEGORY_ROUTINE, DA_CATEGORY_SAP_PROCESS, DA_CATEGORY_RTD_FOLLOWUP];
  const groupedInPool = { [DA_CATEGORY_ROUTINE]: [], [DA_CATEGORY_SAP_PROCESS]: [], [DA_CATEGORY_RTD_FOLLOWUP]: [] };
  const excluded = [];
  const conflicts = []; // TASK-9696
  for (const r of filtered) {
    if (r.conflict) conflicts.push(r);
    else if (!r.inPool) excluded.push(r);
    else groupedInPool[r.daCategory].push(r);
  }

  const renderRow = ({ person, inPool, daCategory, conflict, coverage }) => {
    const progTitle = escapeHtml(window.DqMatrix.coverageLabel(coverage));
    const chip = conflict
      ? `<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#FEF3C7;color:#92400E;" title="inRandomPool is true, but no DOT program covers this person (${progTitle}). Open the person in HR → Compliance to review or clear the flag.">Flag set, not covered</span>`
      : inPool
      ? `<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#ECFDF5;color:#059669;" title="${progTitle}">In pool</span>`
      : `<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#F1F5F9;color:#475569;" title="Covered (${progTitle}) but opted out (inRandomPool: false)">Excluded</span>`;
    const catOptions = Object.entries(DA_CATEGORY_META).map(([key, meta]) =>
      `<option value="${key}"${key === daCategory ? ' selected' : ''}>${escapeHtml(meta.label)}</option>`
    ).join('');
    const catSelect = inPool
      ? `<select onchange="window.ComplianceModule.setDaCategory('${escapeHtml(person.id)}', this.value)" style="font-size:0.74rem;padding:2px 6px;border-radius:6px;border:1px solid var(--border-light);background:#fff;cursor:pointer;">${catOptions}</select>`
      : `<span style="font-size:0.72rem;color:var(--text-muted);">—</span>`;
    return `<tr>
      <td style="font-weight:600;">${escapeHtml(person.name || '—')}</td>
      <td style="color:var(--text-secondary);">${escapeHtml(person.cdlNumber || '—')}</td>
      <td style="color:var(--text-secondary);">${escapeHtml(person.cdlState || '')}</td>
      <td>${chip}</td>
      <td>${catSelect}</td>
    </tr>`;
  };

  let bodyHtml;
  if (activeFilter !== 'all') {
    bodyHtml = filtered.map(renderRow).join('') || `<tr><td colspan="5" style="text-align:center;color:var(--text-secondary);font-size:0.78rem;padding:18px 14px;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">filter_list_off</span>No drivers in this category.</td></tr>`;
  } else {
    const sections = [];
    for (const cat of groupOrder) {
      const rows = groupedInPool[cat];
      if (rows.length === 0 && cat === DA_CATEGORY_ROUTINE && excluded.length === 0) continue;
      if (rows.length === 0) continue;
      const meta = DA_CATEGORY_META[cat];
      sections.push(`<tr style="background:${meta.chipBg};"><td colspan="5" style="padding:6px 10px;font-size:0.74rem;font-weight:700;color:${meta.chipColor};">${escapeHtml(meta.sectionLabel)} (${rows.length})</td></tr>`);
      sections.push(rows.map(renderRow).join(''));
    }
    if (excluded.length > 0) {
      sections.push(`<tr style="background:#F1F5F9;"><td colspan="5" style="padding:6px 10px;font-size:0.74rem;font-weight:700;color:#475569;">Excluded — covered, opted out (${excluded.length})</td></tr>`);
      sections.push(excluded.map(renderRow).join(''));
    }
    // TASK-9696 — stale "in pool" flags on people no DOT program covers. Listed
    // for review, never counted as members, never auto-cleared.
    if (conflicts.length > 0) {
      sections.push(`<tr style="background:#FEF3C7;"><td colspan="5" style="padding:6px 10px;font-size:0.74rem;font-weight:700;color:#92400E;">Flagged "in pool" but not in a DOT testing program — review (${conflicts.length})</td></tr>`);
      sections.push(conflicts.map(renderRow).join(''));
    }
    bodyHtml = sections.join('');
  }

  // TASK-343 Slice 4 — filter chip strip
  const filterChip = (key, label, count) => {
    const isActive = activeFilter === key;
    const meta = key === 'all' ? { chipBg: '#E0E7FF', chipColor: '#3730A3' } : DA_CATEGORY_META[key];
    return `<button onclick="window.ComplianceModule.setDaCategoryFilter('${escapeHtml(key)}')" style="padding:4px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;border:${isActive ? '2px solid ' + meta.chipColor : '1px solid var(--border-light)'};background:${isActive ? meta.chipBg : '#fff'};color:${isActive ? meta.chipColor : 'var(--text-secondary)'};cursor:pointer;">${escapeHtml(label)} (${count})</button>`;
  };

  const filterStripHtml = `
    <div style="padding:6px 18px 10px;display:flex;flex-wrap:wrap;gap:6px;align-items:center;">
      <span style="font-size:0.72rem;color:var(--text-muted);margin-right:4px;">Filter:</span>
      ${filterChip('all', 'All', inPoolCount)}
      ${filterChip(DA_CATEGORY_ROUTINE, DA_CATEGORY_META[DA_CATEGORY_ROUTINE].label, categoryCounts[DA_CATEGORY_ROUTINE])}
      ${filterChip(DA_CATEGORY_SAP_PROCESS, DA_CATEGORY_META[DA_CATEGORY_SAP_PROCESS].label, categoryCounts[DA_CATEGORY_SAP_PROCESS])}
      ${filterChip(DA_CATEGORY_RTD_FOLLOWUP, DA_CATEGORY_META[DA_CATEGORY_RTD_FOLLOWUP].label, categoryCounts[DA_CATEGORY_RTD_FOLLOWUP])}
    </div>`;

  return `
    <div class="crm-card" style="margin-bottom:var(--spacing-md);">
      <div style="display:flex;align-items:center;gap:10px;padding:14px 18px 6px;">
        <span class="material-icons" style="color:var(--brand-blue);">shuffle</span>
        <div style="flex:1;">
          <div style="font-weight:600;font-size:0.95rem;color:var(--text-primary);">Random Selection Pool</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);line-height:1.4;">Drivers subject to FMCSA random drug &amp; alcohol testing<br><span style="font-size:0.72rem;color:var(--text-muted);">49 CFR §382.305(j) · §40.281–305 SAP/RTD tracking</span></div>
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end;">
          ${inPoolCount ? `<span style="padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#ECFDF5;color:#059669;">${inPoolCount} in pool</span>` : ''}
          ${excludedCount ? `<span style="padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#F1F5F9;color:#475569;">${excludedCount} excluded</span>` : ''}
          ${conflictCount ? `<span style="padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#FEF3C7;color:#92400E;" title="inRandomPool is true on people no DOT program covers">${conflictCount} to review</span>` : ''}
        </div>
      </div>
      ${filterStripHtml}
      <div style="overflow-x:auto;max-height:340px;overflow-y:auto;">
        <table class="crm-table" style="width:100%;font-size:0.85rem;">
          <thead>
            <tr>
              <th>Driver</th>
              <th>CDL #</th>
              <th>State</th>
              <th>Pool Status</th>
              <th>D&amp;A Category</th>
            </tr>
          </thead>
          <tbody>${bodyHtml}</tbody>
        </table>
      </div>
      <div style="padding:10px 18px 14px;display:flex;flex-wrap:wrap;gap:8px;align-items:center;border-top:1px solid var(--border-light);">
        <button class="btn btn-secondary btn-sm" onclick="window.ComplianceModule.showAddProgramDoc('RANDOM_SELECTION_NOTICE')" style="font-size:0.78rem;">
          <span class="material-icons" style="font-size:14px;">upload</span> Upload selection notice
        </button>
        <a href="#/hr" class="compliance-source-link" style="font-size:0.78rem;color:var(--brand-blue);text-decoration:none;font-weight:600;">
          Manage in HR &rsaquo;
        </a>
        <span style="margin-left:auto;font-size:0.72rem;color:var(--text-muted);line-height:1.4;">
          Toggle a driver in/out from the HR People &rarr; Compliance tab.<br>Set SAP/RTD category inline above. Upload your C/TPA's quarterly selection notice (PDF).
        </span>
      </div>
    </div>
  `;
}

// TASK-343 Slice 4 — write handler. Persists `daCategory` on the person doc
// + audit-logs the change for §40.305 follow-up audit trail. Called from the
// inline <select onchange> in the pool roster table.
async function setPersonDaCategory(personId, newCategory) {
  if (!personId) return;
  const normalized = _normalizeDaCategory(newCategory);
  const person = (ComplianceState.people || []).find(p => p.id === personId);
  if (!person) {
    window.showToast?.('Driver not found', 'error');
    return;
  }
  const previous = _normalizeDaCategory(person.daCategory);
  if (previous === normalized) return; // no-op
  const tenantId = ComplianceState.tenantId;
  if (!tenantId) {
    window.showToast?.('Tenant not loaded', 'error');
    return;
  }
  try {
    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
      daCategory: normalized,
      daCategoryUpdatedAt: Timestamp.now(),
    });
    person.daCategory = normalized;
    person.daCategoryUpdatedAt = Timestamp.now();
    // Audit log per `feedback_use_audit_log_for_tech_support.md` — partner +
    // tech support can answer "who moved Driver X to SAP process?" in seconds.
    if (typeof window.logAudit === 'function') {
      window.logAudit('compliance', 'PERSON_DA_CATEGORY_CHANGE', {
        entityType: 'person',
        entityId: personId,
        personName: person.name || null,
        before: { daCategory: previous },
        after: { daCategory: normalized },
        cfrCitation: '49 CFR §40.281–305',
      });
    }
    window.showToast?.(`Set to ${DA_CATEGORY_META[normalized].label}`, 'success');
    renderComplianceContent();
  } catch (err) {
    console.error('[Compliance] setPersonDaCategory error:', err);
    window.showToast?.(`Error: ${err.message}`, 'error');
  }
}

function setDaCategoryFilter(value) {
  const allowed = new Set(['all', DA_CATEGORY_ROUTINE, DA_CATEGORY_SAP_PROCESS, DA_CATEGORY_RTD_FOLLOWUP]);
  ComplianceState.daCategoryFilter = allowed.has(value) ? value : 'all';
  renderComplianceContent();
}

// ============================================
// TASK-264 — Carrier-side FMCSA Clearinghouse consent status (read-only mirror)
//
// Mirrors the partner-portal status table per spec §4 Phase 3. Carrier sees
// the same data scoped to their own tenant. No batch-send button on the
// carrier side - sending is a compliance-partner action. If a carrier needs
// to capture consent themselves they can stamp it manually via the existing
// driver detail's Driver Consent on File (date) field (TASK-212).
// ============================================
function _chParseDateC(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'object' && typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  return null;
}

function _chClassifyConsent(person) {
  const validUntil = _chParseDateC(person.clearinghouseConsentValidUntil);
  const signedAt = _chParseDateC(person.clearinghouseConsentSignedAt);
  const requestedAt = _chParseDateC(person.clearinghouseConsentRequestedAt);
  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  if (validUntil && validUntil > in30) {
    return { code: 'valid', label: 'Valid', color: '#059669', bg: '#ECFDF5', signedAt, validUntil };
  }
  if (validUntil && validUntil > now && validUntil <= in30) {
    const daysLeft = Math.max(0, Math.round((validUntil - now) / (24 * 60 * 60 * 1000)));
    return { code: 'expiring', label: `Expires in ${daysLeft}d`, color: '#D97706', bg: '#FFFBEB', signedAt, validUntil };
  }
  if (requestedAt && (!signedAt || (validUntil && validUntil < now))) {
    const hoursAgo = Math.max(0, Math.round((now - requestedAt) / (60 * 60 * 1000)));
    const ago = hoursAgo < 24 ? `${hoursAgo}h ago` : `${Math.round(hoursAgo / 24)}d ago`;
    return { code: 'requested', label: `Requested ${ago}`, color: '#475569', bg: '#F1F5F9', signedAt, validUntil };
  }
  return { code: 'missing', label: 'Missing', color: '#DC2626', bg: '#FEF2F2', signedAt, validUntil };
}

function _chFmtDateCarrier(d) {
  if (!d) return '—';
  try {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) { return '—'; }
}

function renderClearinghouseConsentsCarrierSection() {
  const activePeople = ComplianceState.people.filter(p => p.active !== false);
  const drivers = activePeople.filter(p => {
    // TASK-934 — Clearinghouse consent is a CDL-tier item (§382.701): an
    // explicit classification wins; legacy (unclassified) keeps the
    // role/cdlClass heuristic.
    const explicitPt = window.DqMatrix.explicitPositionType(p);
    if (explicitPt) return explicitPt === 'cdl';
    const role = String(p.crewType || p.role || p.roles?.[0] || '').toLowerCase();
    return role === 'cdl_driver' || role === 'driver' || role === 'cdldriver' || (p.cdlClass && p.cdlClass !== 'none');
  });
  if (drivers.length === 0) return '';

  const classified = drivers.map(d => ({ person: d, status: _chClassifyConsent(d) }));
  const validCount = classified.filter(r => r.status.code === 'valid').length;
  const expiringCount = classified.filter(r => r.status.code === 'expiring').length;
  const requestedCount = classified.filter(r => r.status.code === 'requested').length;
  const missingCount = classified.filter(r => r.status.code === 'missing').length;

  // Hide entirely if every driver is valid — nothing to nag about
  if (missingCount === 0 && expiringCount === 0 && requestedCount === 0) return '';

  // Sort: missing -> expiring -> requested -> valid
  const order = { missing: 0, expiring: 1, requested: 2, valid: 3 };
  classified.sort((a, b) => order[a.status.code] - order[b.status.code]);

  // Limit to first 12 rows in the overview card; "View all" link could be added later
  const visible = classified.slice(0, 12);
  const moreCount = classified.length - visible.length;

  const rowsHtml = visible.map(({ person, status }) => {
    const lastQuery = _chParseDateC(person.clearinghouseLastQueryDate);
    return `<tr>
      <td style="font-weight:600;">${escapeHtml(person.name || '—')}</td>
      <td><span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:${status.bg};color:${status.color};">${escapeHtml(status.label)}</span></td>
      <td>${_chFmtDateCarrier(status.signedAt)}</td>
      <td>${_chFmtDateCarrier(status.validUntil)}</td>
      <td>${_chFmtDateCarrier(lastQuery)}</td>
    </tr>`;
  }).join('');

  return `
    <div class="crm-card" style="margin-bottom:var(--spacing-md);">
      <div style="display:flex;align-items:center;gap:10px;padding:14px 18px 6px;">
        <span class="material-icons" style="color:var(--brand-blue);">verified_user</span>
        <div style="flex:1;">
          <div style="font-weight:600;font-size:0.95rem;color:var(--text-primary);">FMCSA Clearinghouse Consents</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);line-height:1.4;">Limited-query consent status<br><span style="font-size:0.72rem;color:var(--text-muted);">49 CFR §382.701(b)(3) — consent may cover more than one year</span></div>
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end;">
          ${validCount ? `<span style="padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#ECFDF5;color:#059669;">${validCount} valid</span>` : ''}
          ${expiringCount ? `<span style="padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#FFFBEB;color:#D97706;">${expiringCount} expiring</span>` : ''}
          ${requestedCount ? `<span style="padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#F1F5F9;color:#475569;">${requestedCount} pending</span>` : ''}
          ${missingCount ? `<span style="padding:3px 10px;border-radius:999px;font-size:0.74rem;font-weight:600;background:#FEF2F2;color:#DC2626;">${missingCount} missing</span>` : ''}
        </div>
      </div>
      <div style="overflow-x:auto;">
        <table class="crm-table" style="width:100%;font-size:0.85rem;">
          <thead>
            <tr>
              <th>Driver</th>
              <th>Consent Status</th>
              <th>Signed On</th>
              <th>Valid Until</th>
              <th>Last Query</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
      ${moreCount > 0 ? `<div style="padding:8px 18px 14px;font-size:0.78rem;color:var(--text-muted);">+${moreCount} more driver${moreCount === 1 ? '' : 's'} — open the People tab for the full list.</div>` : ''}
      <div style="padding:10px 18px 14px;font-size:0.78rem;color:var(--text-muted);line-height:1.5;border-top:1px solid var(--border-light);">
        Your compliance advisor can send consent links to drivers from their partner portal. If you collect signed paper consents in-office, stamp the Driver Consent on File date in each driver's detail.
      </div>
    </div>
  `;
}

function renderComplianceStats(alerts) {
  const activePeople = ComplianceState.people.filter(p => p.active !== false);
  const allVehicles = [...ComplianceState.trucks, ...ComplianceState.trailers];
  const totalItems = activePeople.length + allVehicles.length;
  const complianceRate = totalItems > 0 ? Math.round((alerts.good / totalItems) * 100) : 100;
  const rateColor = complianceRate >= 90 ? '#10B981' : complianceRate >= 70 ? '#F59E0B' : '#EF4444';

  return `
    <div class="crm-stats-grid" style="margin-bottom:var(--spacing-md);">
      <div class="crm-stat-card bi-stat-vertical bi-stat-clickable ${ComplianceState.filterStatus === 'all' ? 'active' : ''}"
        onclick="window.ComplianceModule.setFilter('all')">
        <div class="crm-stat-icon" style="background:${rateColor}15;color:${rateColor};">
          <span class="material-icons">verified_user</span>
        </div>
        <div class="crm-stat-value" style="color:${rateColor};">${complianceRate}%</div>
        <div class="crm-stat-label">Compliance Rate</div>
      </div>
      <div class="crm-stat-card bi-stat-vertical bi-stat-clickable ${ComplianceState.filterStatus === 'critical' ? 'active' : ''}"
        onclick="window.ComplianceModule.setFilter('critical')">
        <div class="crm-stat-icon" style="background:#FEE2E2;color:#EF4444;">
          <span class="material-icons">error</span>
        </div>
        <div class="crm-stat-value" style="color:#EF4444;">${alerts.critical}</div>
        <div class="crm-stat-label">Critical</div>
      </div>
      <div class="crm-stat-card bi-stat-vertical bi-stat-clickable ${ComplianceState.filterStatus === 'warning' ? 'active' : ''}"
        onclick="window.ComplianceModule.setFilter('warning')">
        <div class="crm-stat-icon" style="background:#FEF3C7;color:#F59E0B;">
          <span class="material-icons">warning</span>
        </div>
        <div class="crm-stat-value" style="color:#F59E0B;">${alerts.warning}</div>
        <div class="crm-stat-label">Warning</div>
      </div>
      <div class="crm-stat-card bi-stat-vertical bi-stat-clickable ${ComplianceState.filterStatus === 'good' ? 'active' : ''}"
        onclick="window.ComplianceModule.setFilter('good')">
        <div class="crm-stat-icon" style="background:#ECFDF5;color:#10B981;">
          <span class="material-icons">check_circle</span>
        </div>
        <div class="crm-stat-value" style="color:#10B981;">${alerts.good}</div>
        <div class="crm-stat-label">Compliant</div>
      </div>
    </div>
  `;
}

function renderHealthSummary() {
  const activePeople = ComplianceState.people.filter(p => p.active !== false);
  const allVehicles = [...ComplianceState.trucks, ...ComplianceState.trailers];
  const issues = [];

  // 1. CDL drivers missing med cards
  const cdlDrivers = activePeople.filter(p => {
    // TASK-934 — explicit classification wins; legacy keeps the heuristic.
    const explicitPt = window.DqMatrix.explicitPositionType(p);
    if (explicitPt) return explicitPt === 'cdl';
    const role = (p.crewType || p.role || p.roles?.[0] || '').toLowerCase();
    return role === 'cdl_driver' || (p.cdlClass && p.cdlClass !== 'none');
  });
  const missingMedCard = cdlDrivers.filter(p => !p.medCardExpiryDate);
  if (missingMedCard.length > 0) {
    issues.push({
      severity: 'critical', icon: 'badge',
      title: `${missingMedCard.length} CDL driver${missingMedCard.length > 1 ? 's' : ''} missing med card`,
      detail: missingMedCard.map(p => p.name).join(', '),
      action: 'people', personId: missingMedCard[0]?.id
    });
  }

  // 2. Expired CDL
  // TASK-934 — DOT credential issues are gated by the shared DQ-applicability
  // matrix so a classified Non-Driver's stale dates never surface here.
  const expiredCDL = activePeople.filter(p => {
    if (!window.DqMatrix.getApplicableDqItems(p).items.cdlValid) return false;
    const d = daysUntilExpiry(p.cdlExpiryDate);
    return d !== null && d < 0;
  });
  if (expiredCDL.length > 0) {
    issues.push({
      severity: 'critical', icon: 'error',
      title: `${expiredCDL.length} expired CDL${expiredCDL.length > 1 ? 's' : ''}`,
      detail: expiredCDL.map(p => `${p.name} (${Math.abs(daysUntilExpiry(p.cdlExpiryDate))}d ago)`).join(', '),
      action: 'people', personId: expiredCDL[0]?.id
    });
  }

  // 2b. Missing CDL — TASK-1330: an applicable driver with NO CDL on file is a gap, not a pass
  // (mirrors getComplianceStatus; daysUntilExpiry===null = missing or unparseable date).
  const missingCDL = activePeople.filter(p => {
    if (!window.DqMatrix.getApplicableDqItems(p).items.cdlValid) return false;
    return daysUntilExpiry(p.cdlExpiryDate) === null;
  });
  if (missingCDL.length > 0) {
    issues.push({
      severity: 'critical', icon: 'badge',
      title: `${missingCDL.length} driver${missingCDL.length > 1 ? 's' : ''} missing CDL`,
      detail: missingCDL.map(p => p.name).join(', '),
      action: 'people', personId: missingCDL[0]?.id
    });
  }

  // 3. Expired med cards
  const expiredMed = activePeople.filter(p => {
    if (!window.DqMatrix.getApplicableDqItems(p).items.medCard) return false;
    const d = daysUntilExpiry(p.medCardExpiryDate);
    return d !== null && d < 0;
  });
  if (expiredMed.length > 0) {
    issues.push({
      severity: 'critical', icon: 'error',
      title: `${expiredMed.length} expired med card${expiredMed.length > 1 ? 's' : ''}`,
      detail: expiredMed.map(p => `${p.name} (${Math.abs(daysUntilExpiry(p.medCardExpiryDate))}d ago)`).join(', '),
      action: 'people', personId: expiredMed[0]?.id
    });
  }

  // 4. CDL expiring within 30 days
  const expiringCDL = activePeople.filter(p => {
    if (!window.DqMatrix.getApplicableDqItems(p).items.cdlValid) return false;
    const d = daysUntilExpiry(p.cdlExpiryDate);
    return d !== null && d >= 0 && d <= 30;
  });
  if (expiringCDL.length > 0) {
    issues.push({
      severity: 'warning', icon: 'schedule',
      title: `${expiringCDL.length} CDL${expiringCDL.length > 1 ? 's' : ''} expiring within 30 days`,
      detail: expiringCDL.map(p => `${p.name} (${daysUntilExpiry(p.cdlExpiryDate)}d)`).join(', '),
      action: 'people', personId: expiringCDL[0]?.id
    });
  }

  // 5. Med cards expiring within 30 days
  const expiringMed = activePeople.filter(p => {
    if (!window.DqMatrix.getApplicableDqItems(p).items.medCard) return false;
    const d = daysUntilExpiry(p.medCardExpiryDate);
    return d !== null && d >= 0 && d <= 30;
  });
  if (expiringMed.length > 0) {
    issues.push({
      severity: 'warning', icon: 'schedule',
      title: `${expiringMed.length} med card${expiringMed.length > 1 ? 's' : ''} expiring within 30 days`,
      detail: expiringMed.map(p => `${p.name} (${daysUntilExpiry(p.medCardExpiryDate)}d)`).join(', '),
      action: 'people', personId: expiringMed[0]?.id
    });
  }

  // 6. Open safety defects (DVIR)
  const openDefects = ComplianceState.pretrips.filter(_dvirIsOpenDefect);
  if (openDefects.length > 0) {
    issues.push({
      severity: 'critical', icon: 'report_problem',
      title: `${openDefects.length} open DVIR defect${openDefects.length > 1 ? 's' : ''}`,
      detail: openDefects.slice(0, 5).map(d => `${_dvirUnitLabel(d)}: ${_dvirDefectSummary(d).substring(0, 60)}`).join('; '),
      action: 'assets'
    });
  }

  // 7. Missed DVIRs
  const missedDvirs = activePeople.filter(p => (p.missedDvirCount || 0) > 0);
  if (missedDvirs.length > 0) {
    const totalMissed = missedDvirs.reduce((s, p) => s + (p.missedDvirCount || 0), 0);
    issues.push({
      severity: 'warning', icon: 'assignment_late',
      title: `${totalMissed} missed DVIR${totalMissed > 1 ? 's' : ''} across ${missedDvirs.length} driver${missedDvirs.length > 1 ? 's' : ''}`,
      detail: missedDvirs.map(p => `${p.name} (${p.missedDvirCount})`).join(', '),
      action: 'drivers'
    });
  }

  // 8. Expired vehicle docs
  const expiredDocs = [];
  const expiredVehicleIds = [];
  allVehicles.forEach(v => {
    const unit = v.unit || v.nickname || v.name || v.id;
    // TASK-9813 — IRP cab card rides the same lists (trucks only).
    ['registrationExpiry', 'insuranceExpiry', 'inspectionExpiry', ...(v._type === 'trailer' ? [] : ['irpExpiry'])].forEach(field => {
      const d = daysUntilExpiry(field === 'registrationExpiry' ? _vcRegExpiryOf(v) : v[field]);
      if (d !== null && d < 0) {
        expiredDocs.push({ unit, field: field === 'irpExpiry' ? 'IRP cab card' : field.replace('Expiry', ''), days: Math.abs(d) });
        if (!expiredVehicleIds.includes(v.id)) expiredVehicleIds.push(v.id);
      }
    });
  });
  if (expiredDocs.length > 0) {
    issues.push({
      severity: 'critical', icon: 'gpp_bad',
      title: `${expiredDocs.length} expired vehicle document${expiredDocs.length > 1 ? 's' : ''}`,
      detail: expiredDocs.slice(0, 5).map(d => `${d.unit} ${d.field} (${d.days}d ago)`).join(', '),
      action: 'assets', vehicleId: expiredVehicleIds[0]
    });
  }

  // 9. Vehicle docs expiring within 30 days
  const expiringDocs = [];
  const expiringVehicleIds = [];
  allVehicles.forEach(v => {
    const unit = v.unit || v.nickname || v.name || v.id;
    ['registrationExpiry', 'insuranceExpiry', 'inspectionExpiry', ...(v._type === 'trailer' ? [] : ['irpExpiry'])].forEach(field => {
      const d = daysUntilExpiry(field === 'registrationExpiry' ? _vcRegExpiryOf(v) : v[field]);
      if (d !== null && d >= 0 && d <= (field === 'irpExpiry' ? 60 : 30)) { // COD 10/14 — IRP soon = 60, same as the rollup
        expiringDocs.push({ unit, field: field === 'irpExpiry' ? 'IRP cab card' : field.replace('Expiry', ''), days: d });
        if (!expiringVehicleIds.includes(v.id)) expiringVehicleIds.push(v.id);
      }
    });
  });
  if (expiringDocs.length > 0) {
    issues.push({
      severity: 'warning', icon: 'schedule',
      title: `${expiringDocs.length} vehicle document${expiringDocs.length > 1 ? 's' : ''} expiring within 30 days`,
      detail: expiringDocs.slice(0, 5).map(d => `${d.unit} ${d.field} (${d.days}d)`).join(', '),
      action: 'assets', vehicleId: expiringVehicleIds[0]
    });
  }

  // 10. Active high-priority maintenance holds.
  // TASK-4806 — shares _maintenanceIsActiveHold with getVehicleComplianceStatus.
  // This list used to test (safety|critical) while the vehicle gate tested
  // (safety|high), so the feed and the vehicle card could contradict each other
  // on the same task. Renamed off `safetyHolds` because the set is now every
  // hold-grade priority, not just the safety ones.
  const activeHolds = ComplianceState.maintenance.filter(_maintenanceIsActiveHold);
  if (activeHolds.length > 0) {
    issues.push({
      severity: 'critical', icon: 'build',
      title: `${activeHolds.length} high-priority maintenance hold${activeHolds.length > 1 ? 's' : ''}`,
      detail: activeHolds.slice(0, 5).map(m => `${m.title || m.description || 'Task'}`).join(', '),
      action: 'maintenance'
    });
  }

  // 11. Company-level credentials (UCR / MCS-150 / insurance)
  // Shared with getAlertCounts + renderSignalsFeed — see getCompanyCredentialIssues().
  const { expired: expiredCompany, expiring: expiringCompany } = getCompanyCredentialIssues();

  if (expiredCompany.length > 0) {
    issues.push({
      severity: 'critical', icon: 'gpp_bad',
      title: `${expiredCompany.length} expired company credential${expiredCompany.length > 1 ? 's' : ''}`,
      detail: expiredCompany.map(c => `${c.label} (${c.days}d ago)`).join(', '),
      action: 'profile'
    });
  }
  if (expiringCompany.length > 0) {
    issues.push({
      severity: 'warning', icon: 'schedule',
      title: `${expiringCompany.length} company credential${expiringCompany.length > 1 ? 's' : ''} expiring within 30 days`,
      detail: expiringCompany.map(c => `${c.label} (${c.days}d)`).join(', '),
      action: 'profile'
    });
  }

  // Sort: critical first, then warning
  issues.sort((a, b) => (a.severity === 'critical' ? 0 : 1) - (b.severity === 'critical' ? 0 : 1));

  // Filter by active chip
  const filtered = ComplianceState.filterStatus === 'all' ? issues
    : issues.filter(i => i.severity === ComplianceState.filterStatus);

  const borderColors = { critical: '#EF4444', warning: '#F59E0B', info: '#3B82F6', good: '#10B981' };

  if (filtered.length === 0) {
    return `<div class="crm-section-card" style="margin-top:16px;">
      <div class="crm-section-title" style="margin-bottom:12px;">
        <span class="material-icons" style="color:var(--text-secondary);font-size:20px;">verified_user</span>
        Compliance Health
      </div>
      <div class="bi-alert-item" style="border-left-color:#10B981;cursor:default;">
        <span class="material-icons" style="color:#10B981;font-size:20px;">check_circle</span>
        <div>
          <div style="font-weight:600;font-size:13px;color:var(--text-primary);">${ComplianceState.filterStatus === 'all' ? 'All Clear' : `No ${ComplianceState.filterStatus} issues`}</div>
          <div style="font-size:12px;color:var(--text-secondary);">No compliance issues detected. All documents current, no open defects.</div>
        </div>
      </div>
    </div>`;
  }

  return `<div class="crm-section-card" style="margin-top:16px;">
    <div class="crm-section-title" style="margin-bottom:12px;">
      <span class="material-icons" style="color:var(--text-secondary);font-size:20px;">verified_user</span>
      Compliance Health
    </div>
    ${filtered.map(issue => {
      const bc = borderColors[issue.severity] || '#94A3B8';
      const clickData = issue.personId ? `data-person="${issue.personId}"` : (issue.vehicleId ? `data-vehicle="${issue.vehicleId}"` : '');
      const clickAction = issue.action ? `onclick="window.ComplianceModule.handleIssueClick('${issue.action}', '${issue.personId || issue.vehicleId || ''}')"` : '';
      return `
        <div class="bi-alert-item" style="border-left-color:${bc};cursor:pointer;" ${clickAction} ${clickData}>
          <span class="material-icons" style="color:${bc};font-size:20px;">${issue.icon}</span>
          <div style="flex:1;">
            <div style="font-weight:600;font-size:13px;color:var(--text-primary);">${escapeHtml(issue.title)}</div>
            <div style="font-size:12px;color:var(--text-secondary);">${escapeHtml(issue.detail)}</div>
          </div>
          <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">chevron_right</span>
        </div>`;
    }).join('')}
  </div>`;
}

function renderTabBar() {
  const tabs = [
    { key: 'overview', icon: 'dashboard', label: 'Overview' },
    { key: 'profile', icon: 'business', label: 'Company' },
    { key: 'people', icon: 'people', label: 'People' },
    { key: 'assets', icon: 'local_shipping', label: 'Assets' },
    { key: 'safety', icon: 'health_and_safety', label: 'Safety' },
    { key: 'ifta', icon: 'local_gas_station', label: 'IFTA' },
    { key: 'hazmat', icon: 'science', label: 'Hazmat' },
    { key: 'dotAdvisor', icon: 'policy', label: 'DOT Advisor' },
    { key: 'permitPete', icon: 'local_shipping', label: 'Permit Pete' },
    { key: 'activity', icon: 'history', label: 'Activity' }
  ];
  return `
    <div class="crm-tabs" style="margin-bottom:var(--spacing-md);">
      ${tabs.map(t => t.soon
        ? `<button class="crm-tab" disabled style="opacity:0.4;cursor:not-allowed;">
            <span class="material-icons">${t.icon}</span> ${t.label}
            <span style="font-size:0.6rem;background:var(--bg-secondary);padding:1px 5px;border-radius:4px;font-weight:700;color:var(--text-secondary);">SOON</span>
          </button>`
        : `<button class="crm-tab ${ComplianceState.activeTab === t.key ? 'active' : ''}"
            onclick="window.ComplianceModule.setTab('${t.key}')">
            <span class="material-icons">${t.icon}</span> ${t.label}
          </button>`
      ).join('')}
    </div>
  `;
}

function renderSearchBar() {
  return `
    <div class="compliance-search-bar">
      <span class="material-icons" style="color:var(--text-muted);font-size:20px;">search</span>
      <input type="text" id="compliance-search" class="compliance-search-input"
        placeholder="Search ${ComplianceState.activeTab === 'people' ? 'people' : 'assets'}..."
        value="${escapeHtml(ComplianceState.searchQuery)}">
      ${ComplianceState.searchQuery ? `<button class="btn-icon" onclick="window.ComplianceModule.clearSearch()" title="Clear search">
        <span class="material-icons" style="font-size:18px;color:var(--text-muted);">close</span>
      </button>` : ''}
    </div>
  `;
}

function renderTabContent() {
  if (ComplianceState.activeTab === 'profile') {
    return renderCompanyProfileTab();
  }
  if (ComplianceState.activeTab === 'people') {
    return renderPeopleTab();
  }
  if (ComplianceState.activeTab === 'safety') {
    return '<div id="compliance-safety-container"></div>';
  }
  if (ComplianceState.activeTab === 'ifta') {
    return renderIftaTab();
  }
  if (ComplianceState.activeTab === 'hazmat') {
    return renderHazmatTab();
  }
  if (ComplianceState.activeTab === 'dotAdvisor') {
    // .dot-advisor-page uses height:100%, which resolves only against a HEIGHT:
    // a min-height alone left the chat ~150px tall (TASK-10149). The class in
    // styles.css sets a dvh height with a vh fallback (iOS address bar).
    return '<div id="compliance-dot-advisor-container" class="compliance-chat-host"></div>';
  }
  if (ComplianceState.activeTab === 'activity') return '<div id="compliance-activity-host"></div>';
  if (ComplianceState.activeTab === 'permitPete') {
    // Same host as DOT Advisor (TASK-10149).
    return '<div id="compliance-permit-pete-container" class="compliance-chat-host"></div>';
  }
  return renderAssetsTab();
}

// ============================================
// COMPANY PROFILE TAB
// ============================================
function renderCompanyProfileTab() {
  const td = window.AppState?.tenantData || {};
  const partner = td.compliancePartner || '';
  const partnerLogo = td.compliancePartnerLogo || '';
  const partnerEmail = td.compliancePartnerEmail || '';
  const partnerPhone = td.compliancePartnerPhone || '';
  const partnerWebsite = td.compliancePartnerWebsite || '';
  const isComplianceOnly = td.complianceOnly === true;

  // FMCSA cached data
  const fmcsa = td.fmcsaData || {};
  const hasFmcsa = !!fmcsa.legalName;
  const fmcsaSyncDate = td.fmcsaLastSync ? new Date(td.fmcsaLastSync).toLocaleDateString() : null;

  // Compliance dates
  const mcs150Next = td.mcs150NextUpdate || '';
  const ucrExpiry = td.ucrExpiry || '';
  const ucrYear = td.ucrYear || '';
  const boc3Agent = td.boc3Agent || '';
  const consortium = td.drugAlcoholConsortium || '';
  const clearinghouseId = td.clearinghouseCompanyId || '';
  // TASK-4617 — from complianceSecure/company (tenant-root fallback), not the
  // broadcast root doc.
  const ein = companyEin();

  // Insurance
  const ins = td.insurance || {};

  // Partner branding banner
  const partnerBanner = partner ? `
    <div style="background:linear-gradient(135deg,#0F172A 0%,#1E293B 100%);border-radius:var(--radius-lg);padding:24px;margin-bottom:var(--spacing-lg);display:flex;align-items:center;gap:20px;flex-wrap:wrap;">
      ${partnerLogo ? `<img src="${escapeHtml(partnerLogo)}" alt="" style="width:64px;height:64px;border-radius:12px;object-fit:contain;background:#fff;padding:4px;">` : `<div style="width:64px;height:64px;border-radius:12px;background:linear-gradient(135deg,#06B6D4,#3B82F6);display:flex;align-items:center;justify-content:center;"><span class="material-icons" style="font-size:32px;color:#fff;">verified_user</span></div>`}
      <div style="flex:1;min-width:200px;">
        <div style="font-size:0.72rem;text-transform:uppercase;letter-spacing:1.5px;color:#06B6D4;font-weight:700;margin-bottom:4px;">Compliance Managed By</div>
        <div style="font-size:1.25rem;font-weight:700;color:#fff;">${escapeHtml(partner)}</div>
        <div style="display:flex;gap:16px;margin-top:8px;flex-wrap:wrap;">
          ${partnerEmail ? `<a href="mailto:${escapeHtml(partnerEmail)}" style="font-size:0.78rem;color:#94A3B8;text-decoration:none;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:14px;">email</span>${escapeHtml(partnerEmail)}</a>` : ''}
          ${partnerPhone ? `<a href="tel:${escapeHtml(partnerPhone)}" style="font-size:0.78rem;color:#94A3B8;text-decoration:none;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:14px;">phone</span>${escapeHtml(partnerPhone)}</a>` : ''}
          ${partnerWebsite ? `<a href="${escapeHtml(_cpSafeUrl(partnerWebsite) || '#')}" target="_blank" rel="noopener" style="font-size:0.78rem;color:#94A3B8;text-decoration:none;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:14px;">language</span>${escapeHtml(partnerWebsite)}</a>` : ''}
        </div>
      </div>
      ${isComplianceOnly ? `<div style="background:rgba(6,182,212,0.15);border:1px solid rgba(6,182,212,0.3);border-radius:8px;padding:8px 14px;text-align:center;">
        <div style="font-size:0.68rem;color:#06B6D4;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Your Partner</div>
        <div style="font-size:0.78rem;color:#94A3B8;margin-top:2px;">Questions? Contact them directly.</div>
      </div>` : ''}
    </div>` : '';

  // FMCSA verified badge
  const fmcsaBadge = hasFmcsa ? `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:var(--spacing-md);">
      <div style="display:flex;align-items:center;gap:6px;background:#ECFDF5;border:1px solid #A7F3D0;border-radius:6px;padding:4px 12px;">
        <span class="material-icons" style="font-size:16px;color:#059669;">verified</span>
        <span style="font-size:0.78rem;font-weight:600;color:#059669;">FMCSA Verified</span>
      </div>
      ${fmcsaSyncDate ? `<span style="font-size:0.72rem;color:var(--text-muted);">Last synced: ${fmcsaSyncDate}</span>` : ''}
      <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.syncFmcsa()" id="btn-fmcsa-sync" style="font-size:0.72rem;padding:3px 10px;margin-left:auto;">
        <span class="material-icons" style="font-size:14px;">sync</span> Sync
      </button>
    </div>` : '';

  // Build field helper
  const field = (label, value, icon, editable, fieldKey) => {
    const val = value || '';
    const editableAttr = editable ? `contenteditable="false"` : '';
    return `<div style="display:flex;align-items:flex-start;gap:10px;padding:10px 0;border-bottom:1px solid var(--border-light);">
      <span class="material-icons" style="font-size:18px;color:var(--brand-blue);margin-top:1px;">${icon}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">${label}</div>
        <div style="font-size:0.88rem;color:var(--text-primary);margin-top:2px;${!val ? 'font-style:italic;color:var(--text-muted);' : ''}">${val ? escapeHtml(val) : 'Not set'}</div>
      </div>
      ${editable ? `<button class="btn-icon" onclick="window.ComplianceModule.editProfileField('${fieldKey}')" title="Edit" style="padding:4px;"><span class="material-icons" style="font-size:16px;color:var(--text-secondary);">edit</span></button>` : ''}
    </div>`;
  };

  const dateField = (label, value, icon, fieldKey, warnDays) => {
    const val = value || '';
    let statusColor = 'var(--text-primary)';
    let statusIcon = '';
    if (val) {
      const d = new Date(val);
      const now = new Date();
      const daysLeft = Math.floor((d - now) / 86400000);
      if (daysLeft < 0) { statusColor = '#DC2626'; statusIcon = '<span class="material-icons" style="font-size:14px;color:#DC2626;margin-left:6px;">error</span>'; }
      else if (daysLeft < (warnDays || 30)) { statusColor = '#D97706'; statusIcon = '<span class="material-icons" style="font-size:14px;color:#D97706;margin-left:6px;">warning</span>'; }
      else { statusColor = '#059669'; }
    }
    return `<div style="display:flex;align-items:flex-start;gap:10px;padding:10px 0;border-bottom:1px solid var(--border-light);">
      <span class="material-icons" style="font-size:18px;color:var(--brand-blue);margin-top:1px;">${icon}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">${label}</div>
        <div style="font-size:0.88rem;color:${statusColor};margin-top:2px;display:flex;align-items:center;">${val ? escapeHtml(val) : '<span style="font-style:italic;color:var(--text-muted);">Not set</span>'}${statusIcon}</div>
      </div>
      <button class="btn-icon" onclick="window.ComplianceModule.editProfileField('${fieldKey}')" title="Edit" style="padding:4px;"><span class="material-icons" style="font-size:16px;color:var(--text-secondary);">edit</span></button>
    </div>`;
  };

  const card = (title, icon, content) => `
    <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-lg);overflow:hidden;margin-bottom:var(--spacing-md);">
      <div style="padding:14px 16px;border-bottom:1px solid var(--border-light);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">${icon}</span>
        <span style="font-size:0.92rem;font-weight:700;color:var(--text-primary);">${title}</span>
      </div>
      <div style="padding:6px 16px;">${content}</div>
    </div>`;

  // Company Identity card
  const identityFields =
    field('Company Name', td.companyName || td.name, 'business', true, 'companyName') +
    field('DOT Number', td.dotNumber, 'badge', true, 'dotNumber') +
    field('MC Number', fmcsa.mcNumber || td.mcNumber, 'confirmation_number', !hasFmcsa, 'mcNumber') +
    field('EIN', ein, 'account_balance', true, 'ein') +
    field('Operating Status', fmcsa.operatingStatus, 'toggle_on', false) +
    field('Safety Rating', fmcsa.safetyRating ? `${fmcsa.safetyRating}${fmcsa.safetyRatingDate ? ` (${fmcsa.safetyRatingDate})` : ''}` : '', 'security', false) +
    field('Entity Type', fmcsa.entityType, 'category', false) +
    field('Fleet Size', fmcsa.totalPowerUnits ? `${fmcsa.totalPowerUnits} power units, ${fmcsa.totalDrivers} drivers` : (td.fleetSize ? `${td.fleetSize} vehicles` : ''), 'local_shipping', !hasFmcsa, 'fleetSize');

  // Contact Info card
  const contactFields =
    field('Phone', td.phone || (td.companyPhones?.[0]?.phone), 'phone', true, 'phone') +
    field('Email', td.email || (td.companyEmails?.[0]?.email), 'email', true, 'email') +
    field('Address', td.address || (td.companyAddresses?.[0]?.address) || (fmcsa.phyStreet ? `${fmcsa.phyStreet}, ${fmcsa.phyCity}, ${fmcsa.phyState} ${fmcsa.phyZipcode}` : ''), 'location_on', true, 'address');

  // Compliance Dates card (the "silent killers")
  const complianceDateFields =
    dateField('MCS-150 Next Update', mcs150Next, 'event', 'mcs150NextUpdate', 60) +
    dateField('UCR Expiration', ucrExpiry, 'calendar_month', 'ucrExpiry', 30) +
    field('UCR Year', ucrYear, 'date_range', true, 'ucrYear') +
    field('BOC-3 Process Agent', boc3Agent, 'gavel', true, 'boc3Agent');

  // TASK-9843 — the states / provinces the company is registered in. CRM
  // reads the same list for its "Not registered in ST" heads-up.
  const registeredStatesCard = card('Registered States & Provinces', 'map', renderRegisteredStatesCardBody(td.registeredStates));

  // Drug & Alcohol card
  const drugAlcoholFields =
    field('Consortium / Testing Provider', consortium, 'science', true, 'drugAlcoholConsortium') +
    field('Clearinghouse Company ID', clearinghouseId, 'fingerprint', true, 'clearinghouseCompanyId') +
    renderDAndAProgramSection();

  // Insurance card
  const insuranceFields =
    field('Liability Policy #', ins.liabilityPolicy, 'policy', true, 'insurance.liabilityPolicy') +
    dateField('Liability Expiry', ins.liabilityExpiry, 'event_busy', 'insurance.liabilityExpiry', 30) +
    field('Liability Limit', ins.liabilityLimit, 'attach_money', true, 'insurance.liabilityLimit') +
    field('Cargo Policy #', ins.cargoPolicy, 'policy', true, 'insurance.cargoPolicy') +
    dateField('Cargo Expiry', ins.cargoExpiry, 'event_busy', 'insurance.cargoExpiry', 30) +
    field('Cargo Limit', ins.cargoLimit, 'attach_money', true, 'insurance.cargoLimit') +
    field('Workers Comp Policy #', ins.workersCompPolicy, 'policy', true, 'insurance.workersCompPolicy') +
    dateField('Workers Comp Expiry', ins.workersCompExpiry, 'event_busy', 'insurance.workersCompExpiry', 30) +
    (hasFmcsa && fmcsa.bipdInsuranceOnFile ? field('BIPD Insurance (FMCSA)', fmcsa.bipdInsuranceOnFile, 'check_circle', false) : '') +
    (hasFmcsa && fmcsa.cargoInsuranceOnFile ? field('Cargo Insurance (FMCSA)', fmcsa.cargoInsuranceOnFile, 'check_circle', false) : '');


  // DOT number prompt for non-verified companies
  const dotPrompt = !hasFmcsa && td.dotNumber ? `
    <div style="background:#FEF3C7;border:1px solid #FDE68A;border-radius:var(--radius-md);padding:14px 16px;margin-bottom:var(--spacing-md);display:flex;align-items:center;gap:12px;">
      <span class="material-icons" style="font-size:24px;color:#D97706;">info</span>
      <div style="flex:1;">
        <div style="font-size:0.85rem;font-weight:600;color:#92400E;">Verify your DOT number to auto-fill company data from FMCSA</div>
        <div style="font-size:0.78rem;color:#A16207;margin-top:2px;">Safety rating, insurance status, fleet size, and more will be pulled automatically.</div>
      </div>
      <button class="btn btn-sm" onclick="window.ComplianceModule.syncFmcsa()" style="background:#D97706;color:#fff;border:none;font-size:0.78rem;white-space:nowrap;">
        <span class="material-icons" style="font-size:14px;">sync</span> Verify Now
      </button>
    </div>` : !td.dotNumber ? `
    <div style="background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:14px 16px;margin-bottom:var(--spacing-md);display:flex;align-items:center;gap:12px;">
      <span class="material-icons" style="font-size:24px;color:var(--text-muted);">badge</span>
      <div style="flex:1;">
        <div style="font-size:0.85rem;font-weight:600;color:var(--text-primary);">Add your DOT number to get started</div>
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;">We'll pull your company info directly from FMCSA.</div>
      </div>
      <button class="btn btn-sm btn-primary" onclick="window.ComplianceModule.editProfileField('dotNumber')" style="font-size:0.78rem;white-space:nowrap;">
        <span class="material-icons" style="font-size:14px;">add</span> Add DOT #
      </button>
    </div>` : '';

  return `
    ${partnerBanner}
    ${fmcsaBadge}
    ${dotPrompt}
    ${card('Company Identity', 'business', identityFields)}
    ${carrierSafetyHost(td)}
    ${card('Contact Information', 'contacts', contactFields)}
    ${card('Compliance Dates', 'event_available', complianceDateFields)}
    ${registeredStatesCard}
    ${card('Drug & Alcohol Program', 'science', drugAlcoholFields)}
    ${card('Insurance', 'shield', insuranceFields)}
    <div id="compliance-company-docs"></div>
  `;
}

// ============================================
// TASK-1201 — COMPANY DOCUMENTS (Company tab)
//
// Company-level documents with expirations (business license, TERO, IFTA,
// insurance certs, permits). REUSES the existing HR company-documents store —
// tenants/{tid}/hrResources + Storage tenants/{tid}/hr/company-documents/*
// (the COD 🔴 binding decision: NO new collection, so driver-PIN reads stay
// behind the TASK-483 publiclyShared gate). New optional fields on hrResources
// docs: `category` (one of COMPANY_DOC_CATEGORIES) + `expiresAt` (YYYY-MM-DD).
// A doc shows here when category ∈ the company enum OR it has an expiresAt;
// docs the HR tab files under its own free-form categories stay in HR only.
// "Visible to drivers" = publiclyShared — flips the SAME flag the HR share
// toggle and the driver Doc Page read, so flagged docs surface there with no
// new driver view. Expirations ride the existing daily compliance-alert rail
// (functions dailyInspectionDueNotifications → companyDoc type + partner fanout).
// ============================================
const COMPANY_DOC_CATEGORIES = ['Business License', 'TERO', 'IFTA', 'Insurance cert', 'Permit', 'Other'];

function _isCompanyDoc(r) {
  return r.companyDoc === true || COMPANY_DOC_CATEGORIES.includes((r.category || '').trim()) || !!r.expiresAt;
}

async function _loadCompanyDocs(force = false) {
  if (!force && Array.isArray(ComplianceState.companyDocs)) return ComplianceState.companyDocs;
  try {
    const snap = await getDocs(collection(db, 'tenants', ComplianceState.tenantId, 'hrResources'));
    ComplianceState.companyDocs = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(_isCompanyDoc)
      .sort((a, b) => {
        const am = a.createdAt?.toMillis?.() || 0;
        const bm = b.createdAt?.toMillis?.() || 0;
        return bm - am;
      });
  } catch (err) {
    console.error('[Compliance] company docs load error:', err);
    ComplianceState.companyDocs = [];
  }
  return ComplianceState.companyDocs;
}

function _companyDocExpiryHtml(expiresAt) {
  if (!expiresAt) return '<span style="font-size:0.78rem;color:var(--text-muted);font-style:italic;">No expiration</span>';
  const d = new Date(expiresAt);
  if (isNaN(d.getTime())) return '';
  const daysLeft = Math.floor((d - new Date()) / 86400000);
  let color = '#059669'; let icon = '';
  if (daysLeft < 0) { color = '#DC2626'; icon = '<span class="material-icons" style="font-size:14px;vertical-align:-2px;">error</span> '; }
  else if (daysLeft < 30) { color = '#D97706'; icon = '<span class="material-icons" style="font-size:14px;vertical-align:-2px;">warning</span> '; }
  const label = daysLeft < 0 ? `Expired ${expiresAt}` : `Expires ${expiresAt}`;
  return `<span style="font-size:0.78rem;font-weight:600;color:${color};">${icon}${escapeHtml(label)}</span>`;
}

async function renderCompanyDocsSection() {
  const host = document.getElementById('compliance-company-docs');
  if (!host) return;
  if (!Array.isArray(ComplianceState.companyDocs)) {
    host.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;padding:24px 0;"><div class="loading-spinner"></div></div>`;
    await _loadCompanyDocs();
    // Tab may have changed while loading — re-check the host is still mounted.
    if (!document.getElementById('compliance-company-docs')) return;
  }
  const docs = ComplianceState.companyDocs || [];

  const rows = docs.map(r => {
    const cat = (r.category || '').trim();
    const shared = r.publiclyShared === true;
    const isLink = r.type !== 'document';
    const icon = isLink ? 'link' : (r.isPdf ? 'picture_as_pdf' : 'description');
    return `<div style="display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid var(--border-light);flex-wrap:wrap;">
      <span class="material-icons" style="font-size:22px;color:${r.isPdf ? '#DC2626' : 'var(--brand-blue)'};flex-shrink:0;">${icon}</span>
      <div style="flex:1;min-width:180px;">
        <div style="font-size:0.88rem;font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;">${escapeHtml(r.title || r.fileName || 'Document')}</div>
        <div style="display:flex;gap:10px;align-items:center;margin-top:3px;flex-wrap:wrap;">
          ${cat ? `<span style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;background:var(--bg-secondary);color:var(--text-secondary);padding:2px 8px;border-radius:10px;">${escapeHtml(cat)}</span>` : ''}
          ${_companyDocExpiryHtml(r.expiresAt)}
          ${r.source === 'partner' ? '<span style="font-size:0.7rem;color:var(--text-muted);">Added by your compliance partner</span>' : ''}
        </div>
      </div>
      <div style="display:flex;gap:2px;flex-shrink:0;align-items:center;">
        <button class="btn-icon" onclick="window.ComplianceModule.toggleCompanyDocShare('${r.id}', ${shared ? 'false' : 'true'})" title="${shared ? 'Visible to drivers on their Doc Page — click to hide' : 'Hidden from drivers — click to show on the driver Doc Page'}">
          <span class="material-icons" style="font-size:18px;color:${shared ? 'var(--brand-blue)' : 'var(--text-muted)'};">${shared ? 'visibility' : 'visibility_off'}</span>
        </button>
        <button class="btn-icon" onclick="window.ComplianceModule.openCompanyDoc('${r.id}')" title="Open"><span class="material-icons" style="font-size:18px;color:var(--text-secondary);">open_in_new</span></button>
        <button class="btn-icon" onclick="window.ComplianceModule.editCompanyDoc('${r.id}')" title="Edit category / expiration"><span class="material-icons" style="font-size:18px;color:var(--text-secondary);">edit</span></button>
        <button class="btn-icon" onclick="window.ComplianceModule.deleteCompanyDoc('${r.id}')" title="Remove"><span class="material-icons" style="font-size:18px;color:var(--text-muted);">delete_outline</span></button>
      </div>
    </div>`;
  }).join('');

  host.innerHTML = `
    <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-lg);overflow:hidden;margin-bottom:var(--spacing-md);">
      <div style="padding:14px 16px;border-bottom:1px solid var(--border-light);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">folder_special</span>
        <span style="font-size:0.92rem;font-weight:700;color:var(--text-primary);">Company Documents</span>
        <button class="btn btn-sm btn-primary" onclick="window.ComplianceModule.openCompanyDocUpload()" style="margin-left:auto;font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">upload_file</span> Upload
        </button>
      </div>
      <div style="padding:6px 16px;">
        ${docs.length ? rows : `<div style="padding:18px 0;color:var(--text-secondary);font-size:0.85rem;">No company documents yet. Upload your business license, TERO certificate, IFTA license, insurance certificates, or permits — add an expiration and DispatchDeck reminds you (and your compliance partner) before it lapses.</div>`}
        <div style="padding:8px 0 10px;font-size:0.72rem;color:var(--text-muted);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:14px;">visibility</span>
          Docs marked visible to drivers appear on their in-app Doc Page. Everything else stays office-only.
        </div>
      </div>
    </div>`;
}

function openCompanyDocUpload() {
  const modal = document.getElementById('compliance-detail-modal');
  if (!modal) return;
  const catOptions = COMPANY_DOC_CATEGORIES.map(c => `<option value="${escapeHtml(c)}"${c === 'Other' ? ' selected' : ''}>${escapeHtml(c)}</option>`).join('');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h3 style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="color:var(--brand-blue);">upload_file</span>
          Upload Company Document
        </h3>
        <button class="btn-icon" onclick="window.ComplianceModule.closeDetail()"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:var(--spacing-lg);display:flex;flex-direction:column;gap:14px;">
        <div>
          <label style="font-size:0.82rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">File *</label>
          <input type="file" id="company-doc-file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv" style="font-size:13px;width:100%;">
          <div style="font-size:0.72rem;color:var(--text-muted);margin-top:4px;">PDF, Word, Excel, image, or CSV. 25 MB max.</div>
        </div>
        <div>
          <label style="font-size:0.82rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">Category</label>
          <select id="company-doc-category" class="form-control" style="font-size:14px;">${catOptions}</select>
        </div>
        <div>
          <label style="font-size:0.82rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">Expiration date</label>
          <input type="date" id="company-doc-expiry" class="form-control" style="font-size:14px;">
          <div style="font-size:0.72rem;color:var(--text-muted);margin-top:4px;">Optional — set it and you'll get expiration reminders on the compliance alert emails.</div>
        </div>
        <label style="display:flex;align-items:center;gap:8px;font-size:0.85rem;color:var(--text-primary);cursor:pointer;">
          <input type="checkbox" id="company-doc-share" style="width:16px;height:16px;">
          Visible to drivers (shows on their in-app Doc Page)
        </label>
      </div>
      <div class="form-actions" style="padding:12px var(--spacing-lg) var(--spacing-lg);border-top:1px solid var(--border-light);">
        <button class="btn btn-secondary" onclick="window.ComplianceModule.closeDetail()">Cancel</button>
        <button class="btn btn-primary" id="company-doc-upload-btn" onclick="window.ComplianceModule.submitCompanyDocUpload()">
          <span class="material-icons" style="font-size:16px;">upload</span> Upload
        </button>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
}

async function submitCompanyDocUpload() {
  const fileEl = document.getElementById('company-doc-file');
  const file = fileEl?.files?.[0];
  if (!file) { window.showToast?.('Pick a file first', 'error'); return; }
  if (file.size > 25 * 1024 * 1024) { window.showToast?.('File too large (25MB max)', 'error'); return; }
  const category = document.getElementById('company-doc-category')?.value || 'Other';
  const expiresAt = document.getElementById('company-doc-expiry')?.value || '';
  const share = document.getElementById('company-doc-share')?.checked === true;

  const btn = document.getElementById('company-doc-upload-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Uploading...'; }
  try {
    const tenantId = ComplianceState.tenantId;
    // SAME Storage prefix + doc shape as hr.js _uploadCompanyDocumentFile —
    // ONE store, every surface (HR Documents, this tab, partner portal, driver
    // Doc Page when shared). Kept local (no cross-import — CLAUDE.md pitfall #1).
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `tenants/${tenantId}/hr/company-documents/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file, { contentType: _cpInferContentType(file) });
    const url = await getDownloadURL(storageRef);
    const title = file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');
    await addDoc(collection(db, 'tenants', tenantId, 'hrResources'), {
      title,
      fileName: file.name,
      url,
      storagePath,
      type: 'document',
      mimeType: file.type,
      isPdf: file.type === 'application/pdf',
      sizeBytes: file.size,
      icon: file.type.includes('pdf') ? 'picture_as_pdf' : 'description',
      category,
      companyDoc: true, // rules discriminator — compliance-arm edits scoped to company docs only
      ...(expiresAt ? { expiresAt } : {}),
      publiclyShared: share,
      uploadedAt: Timestamp.now(),
      uploadedBy: window.AppState?.userName || 'Unknown',
      createdAt: Timestamp.now()
    });
    window.ComplianceModule.closeDetail();
    window.showToast?.('Document uploaded', 'success');
    await _loadCompanyDocs(true);
    renderCompanyDocsSection();
  } catch (err) {
    console.error('[Compliance] company doc upload error:', err);
    window.showToast?.('Upload failed: ' + (err.message || 'Unknown error'), 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">upload</span> Upload'; }
  }
}

function editCompanyDoc(resourceId) {
  const r = (ComplianceState.companyDocs || []).find(x => x.id === resourceId);
  if (!r) return;
  const modal = document.getElementById('compliance-detail-modal');
  if (!modal) return;
  const cur = (r.category || '').trim();
  const catOptions = COMPANY_DOC_CATEGORIES.map(c => `<option value="${escapeHtml(c)}"${c === cur ? ' selected' : ''}>${escapeHtml(c)}</option>`).join('');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:400px;">
      <div class="modal-header">
        <h3 style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="color:var(--brand-blue);">edit</span>
          Edit ${escapeHtml(r.title || 'Document')}
        </h3>
        <button class="btn-icon" onclick="window.ComplianceModule.closeDetail()"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:var(--spacing-lg);display:flex;flex-direction:column;gap:14px;">
        <div>
          <label style="font-size:0.82rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">Document name</label>
          <input type="text" id="company-doc-name" class="form-control" value="${escapeHtml(r.title || r.fileName || '')}" maxlength="120" placeholder="e.g. Employee Handbook" style="font-size:14px;">
        </div>
        <div>
          <label style="font-size:0.82rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">Category</label>
          <select id="company-doc-category" class="form-control" style="font-size:14px;">${catOptions}</select>
        </div>
        <div>
          <label style="font-size:0.82rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">Expiration date</label>
          <input type="date" id="company-doc-expiry" class="form-control" value="${escapeHtml(r.expiresAt || '')}" style="font-size:14px;">
        </div>
      </div>
      <div class="form-actions" style="padding:12px var(--spacing-lg) var(--spacing-lg);border-top:1px solid var(--border-light);">
        <button class="btn btn-secondary" onclick="window.ComplianceModule.closeDetail()">Cancel</button>
        <button class="btn btn-primary" onclick="window.ComplianceModule.saveCompanyDocMeta('${r.id}')">
          <span class="material-icons" style="font-size:16px;">save</span> Save
        </button>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
}

async function saveCompanyDocMeta(resourceId) {
  const category = document.getElementById('company-doc-category')?.value || 'Other';
  const expiresAt = document.getElementById('company-doc-expiry')?.value || '';
  // TASK-1325: rename support. Sanitize; if blank, keep the previous title (never write '').
  const prev = (ComplianceState.companyDocs || []).find(x => x.id === resourceId);
  const prevTitle = prev?.title || '';
  const title = _sanitizeDocTitle(document.getElementById('company-doc-name')?.value || '');
  try {
    const payload = { category, expiresAt: expiresAt || null };
    if (title && title !== prevTitle) payload.title = title;
    await updateDoc(doc(db, 'tenants', ComplianceState.tenantId, 'hrResources', resourceId), payload);
    if (payload.title) {
      // Audit "who renamed this document" (non-blocking) — DOT/compliance evidence.
      // TASK-1495 — through the shared writer (window.logAudit), not a direct
      // addDoc: it stamps category/action/timestamp/user and strips undefined, so
      // this row is guaranteed to carry the `timestamp` the "who changed X" viewer
      // range-filters on. `type` stays in the payload so old and new rows share a
      // filter key. hr.js:renameHrResource is the twin of this write and logs the
      // same action under category 'hr'.
      try {
        await window.logAudit?.('compliance', 'company_doc_renamed', {
          type: 'company_doc_renamed', resourceId, oldTitle: prevTitle, newTitle: title,
          editedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown',
        });
      } catch (auditErr) { console.warn('[Compliance] rename audit failed (non-blocking):', auditErr); }
    }
    window.ComplianceModule.closeDetail();
    window.showToast?.('Updated', 'success');
    await _loadCompanyDocs(true);
    renderCompanyDocsSection();
  } catch (err) {
    console.error('[Compliance] company doc meta save error:', err);
    window.showToast?.('Failed to save: ' + (err.message || 'Unknown error'), 'error');
  }
}

async function toggleCompanyDocShare(resourceId, share) {
  try {
    await updateDoc(doc(db, 'tenants', ComplianceState.tenantId, 'hrResources', resourceId), {
      publiclyShared: share === true || share === 'true'
    });
    window.showToast?.(share === true || share === 'true' ? 'Now visible on the driver Doc Page' : 'Hidden from drivers', 'success');
    await _loadCompanyDocs(true);
    renderCompanyDocsSection();
  } catch (err) {
    console.error('[Compliance] company doc share toggle error:', err);
    window.showToast?.('Error updating', 'error');
  }
}

async function openCompanyDoc(resourceId) {
  const r = (ComplianceState.companyDocs || []).find(x => x.id === resourceId);
  if (!r) return;
  try {
    let url = r.url || '';
    // Partner-uploaded rows may carry only a storagePath (no token URL) —
    // resolve a download URL client-side (carrier members can read the path).
    if (!url && r.storagePath) url = await getDownloadURL(ref(storage, r.storagePath));
    const safe = _cpSafeUrl(url);
    if (safe) window.open(safe, '_blank', 'noopener');
    else if (url) window.showToast?.('This entry has an unsupported link and cannot be opened', 'error');
    else window.showToast?.('No file attached to this entry', 'error');
  } catch (err) {
    console.error('[Compliance] company doc open error:', err);
    window.showToast?.('Could not open the document', 'error');
  }
}

async function deleteCompanyDoc(resourceId) {
  const r = (ComplianceState.companyDocs || []).find(x => x.id === resourceId);
  if (!r) return;
  if (!confirm(`Remove "${r.title || 'this document'}"?`)) return;
  try {
    if (r.storagePath) {
      try { await deleteObject(ref(storage, r.storagePath)); } catch (e) { /* file may already be gone */ }
    }
    await deleteDoc(doc(db, 'tenants', ComplianceState.tenantId, 'hrResources', resourceId));
    window.showToast?.('Removed', 'success');
    await _loadCompanyDocs(true);
    renderCompanyDocsSection();
  } catch (err) {
    console.error('[Compliance] company doc delete error:', err);
    window.showToast?.('Error removing document', 'error');
  }
}

async function syncFmcsaData() {
  const td = window.AppState?.tenantData || {};
  const dotNumber = td.dotNumber;
  if (!dotNumber || !/^\d{1,10}$/.test(dotNumber.replace(/\D/g, ''))) {
    window.showToast?.('Enter a valid DOT number first', 'error');
    return;
  }

  const btn = document.getElementById('btn-fmcsa-sync');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Syncing...';
  }

  try {
    const lookup = httpsCallable(functions, 'fmcsaCarrierLookup');
    const cleanDot = dotNumber.replace(/\D/g, '');
    const { data } = await lookup({ dotNumber: cleanDot });

    // Save FMCSA data to tenant doc
    const tenantRef = doc(db, 'tenants', ComplianceState.tenantId);
    const fmcsaData = {
      legalName: data.legalName || '',
      dbaName: data.dbaName || '',
      mcNumber: data.mcNumber || '',
      operatingStatus: data.operatingStatus || '',
      allowedToOperate: data.allowedToOperate || '',
      safetyRating: data.safetyRating || '',
      safetyRatingDate: data.safetyRatingDate || '',
      entityType: data.entityType || '',
      totalDrivers: data.totalDrivers || 0,
      totalPowerUnits: data.totalPowerUnits || 0,
      phyStreet: data.phyStreet || '',
      phyCity: data.phyCity || '',
      phyState: data.phyState || '',
      phyZipcode: data.phyZipcode || '',
      phone: data.phone || '',
      bipdInsuranceRequired: data.bipdInsuranceRequired || '',
      bipdInsuranceOnFile: data.bipdInsuranceOnFile || '',
      cargoInsuranceRequired: data.cargoInsuranceRequired || '',
      cargoInsuranceOnFile: data.cargoInsuranceOnFile || '',
      crashTotal: data.crashTotal || 0,
      fatalCrash: data.fatalCrash || 0,
      injCrash: data.injCrash || 0,
      towCrash: data.towCrash || 0,
      driverInsp: data.driverInsp || 0,
      driverOosInsp: data.driverOosInsp || 0,
      vehicleInsp: data.vehicleInsp || 0,
      vehicleOosInsp: data.vehicleOosInsp || 0,
    };

    const updates = {
      fmcsaData,
      fmcsaLastSync: new Date().toISOString(),
    };

    // Also set mcNumber at root if not set
    if (data.mcNumber && !td.mcNumber) updates.mcNumber = data.mcNumber;

    // Auto-fill address if empty
    if (!td.address && !td.companyAddresses?.length && data.phyStreet) {
      updates.address = `${data.phyStreet}, ${data.phyCity}, ${data.phyState} ${data.phyZipcode}`;
      updates.companyAddresses = [{ label: 'Main', address: `${data.phyStreet}, ${data.phyCity}, ${data.phyState} ${data.phyZipcode}` }];
    }

    // Auto-fill phone if empty
    if (!td.phone && !td.companyPhones?.length && data.phone) {
      updates.phone = data.phone;
      updates.companyPhones = [{ label: 'Main', phone: data.phone }];
    }

    await updateDoc(tenantRef, updates);

    // Update local state
    window.AppState.tenantData.fmcsaData = fmcsaData;
    window.AppState.tenantData.fmcsaLastSync = updates.fmcsaLastSync;
    if (updates.mcNumber) window.AppState.tenantData.mcNumber = updates.mcNumber;
    if (updates.address) window.AppState.tenantData.address = updates.address;
    if (updates.companyAddresses) window.AppState.tenantData.companyAddresses = updates.companyAddresses;
    if (updates.phone) window.AppState.tenantData.phone = updates.phone;
    if (updates.companyPhones) window.AppState.tenantData.companyPhones = updates.companyPhones;

    window.showToast?.('FMCSA data synced successfully', 'success');
    renderComplianceContent();
  } catch (err) {
    console.error('[Compliance] FMCSA sync error:', err);
    const msg = err.message || 'Failed to sync';
    window.showToast?.(msg.includes('not-found') ? 'Carrier not found — check DOT number' : msg, 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:14px;">sync</span> Sync';
    }
  }
}

// ============================================
// TASK-9843 — REGISTERED STATES (Compliance → Company)
// ============================================
// The states / provinces the company is registered in, each with a KIND
// (business registration, contractor license, intrastate authority, IRP/IFTA,
// other) and an optional expiry, stored on the tenant doc as
// `registeredStates` (firestore.rules t9843RegisteredStatesOk bounds it: a list
// of <= 320). CRM reads the same list to show a small red "! Not registered in
// ST" under a customer whose address state is known and not on it — a heads-up
// that never blocks anything. The RULE is window.RegisteredStates (the shared
// mirror); this file only renders the card, the editor and the write.
// Not routed through updateCompanyProfileCarrier: that callable's whitelist is
// string/date keyed (COMPANY_PROFILE_PARTNER_FIELDS) and this is a list; the
// direct tenant-doc write is the same path syncFmcsaData takes, with the
// module/admin arms of the tenant update rule as the fence.
function _rsLib() { return window.RegisteredStates || null; }

function _rsTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function renderRegisteredStatesCardBody(raw) {
  const RS = _rsLib();
  const list = RS ? RS.normalizeList(raw) : [];
  const editBtn = `<div style="padding:10px 0 6px;display:flex;justify-content:flex-end;">
      <button class="btn btn-secondary btn-sm" id="btn-rs-edit" onclick="window.ComplianceModule.openRegisteredStatesEditor()" style="font-size:0.78rem;padding:4px 12px;">
        <span class="material-icons" style="font-size:16px;">edit</span> ${list.length ? 'Edit' : 'Add states'}
      </button>
    </div>`;
  if (!RS) {
    return `<div style="padding:10px 0;font-size:0.85rem;color:var(--text-secondary);">Registered states could not load — reload the page.</div>`;
  }
  if (!list.length) {
    return `<div style="padding:10px 0;font-size:0.85rem;color:var(--text-secondary);line-height:1.5;">
        No registered states yet. List the states and provinces your company is registered to work in, and CRM will show a small red
        <span style="display:inline-flex;align-items:center;justify-content:center;width:14px;height:14px;border-radius:50%;background:var(--action-danger);color:#fff;font-size:10px;font-weight:700;vertical-align:-2px;">!</span>
        under any customer in a state that isn't listed. A heads-up only — nothing is blocked.
      </div>` + editBtn;
  }
  const today = _rsTodayKey();
  const byState = {};
  list.forEach((r) => { (byState[r.state] = byState[r.state] || []).push(r); });
  const rows = Object.keys(byState).map((code) => {
    const kinds = byState[code].map((r) => {
      let exp = '';
      if (r.expiry) {
        const dl = Math.round((Date.parse(r.expiry + 'T00:00:00Z') - Date.parse(today + 'T00:00:00Z')) / 864e5);
        const color = dl < 0 ? 'var(--action-danger)' : (dl <= 30 ? 'var(--action-warning)' : 'var(--text-secondary)');
        const icon = dl < 0 ? '<span class="material-icons" style="font-size:13px;vertical-align:-2px;">error</span> ' : (dl <= 30 ? '<span class="material-icons" style="font-size:13px;vertical-align:-2px;">warning</span> ' : '');
        exp = ` · <span style="color:${color};font-weight:${dl <= 30 ? 600 : 400};">${icon}${dl < 0 ? 'expired ' : 'expires '}${escapeHtml(r.expiry)}</span>`;
      }
      return `<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;">${escapeHtml(RS.kindLabel(r.kind))}${r.note ? ' · ' + escapeHtml(r.note) : ''}${exp}</div>`;
    }).join('');
    return `<div style="display:flex;align-items:flex-start;gap:10px;padding:10px 0;border-bottom:1px solid var(--border-light);">
      <span class="material-icons" style="font-size:18px;color:var(--brand-blue);margin-top:1px;">place</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:0.88rem;font-weight:600;color:var(--text-primary);">${escapeHtml(code)} <span style="font-weight:400;color:var(--text-secondary);">${escapeHtml(RS.NAMES[code] || '')}</span></div>
        ${kinds}
      </div>
    </div>`;
  }).join('');
  return rows + editBtn;
}

let _rsRowSeq = 0;

function openRegisteredStatesEditor() {
  const RS = _rsLib();
  if (!RS) { window.showToast?.('Registered states could not load — reload the page', 'error'); return; }
  const list = RS.normalizeList(window.AppState?.tenantData?.registeredStates);
  const modal = document.getElementById('compliance-detail-modal');
  if (!modal) return;
  modal.innerHTML = `
    <div class="modal-content" style="max-width:680px;">
      <div class="modal-header">
        <h3 style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="color:var(--brand-blue);">map</span>
          Registered States & Provinces
        </h3>
        <button class="btn-icon" onclick="window.ComplianceModule.closeDetail()"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:var(--spacing-lg);max-height:60vh;overflow:auto;">
        <p style="font-size:0.82rem;color:var(--text-secondary);margin:0 0 12px;line-height:1.5;">
          Where your company is registered to do business. CRM shows a small red "!" under a customer in a state that isn't listed here — a heads-up only, nothing is blocked.
          "Registered" means different things: contractor work across a state line usually hinges on the business or contractor registration, not the DOT number. Add one row per registration.
        </p>
        <div id="rs-editor-rows" style="display:flex;flex-direction:column;gap:6px;"></div>
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.ComplianceModule.rsEditorAddRow()" style="margin-top:10px;padding:4px 12px;font-size:12px;">
          <span class="material-icons" style="font-size:14px;">add</span> Add state
        </button>
      </div>
      <div class="form-actions" style="padding:12px var(--spacing-lg) var(--spacing-lg);border-top:1px solid var(--border-light);">
        <button class="btn btn-secondary" onclick="window.ComplianceModule.closeDetail()">Cancel</button>
        <button class="btn btn-primary" id="rs-editor-save" onclick="window.ComplianceModule.saveRegisteredStates()">
          <span class="material-icons" style="font-size:16px;">save</span> Save
        </button>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
  if (list.length) list.forEach((r) => rsEditorAddRow(r.state, r.kind, r.expiry || '', r.note || ''));
  else rsEditorAddRow('', 'sos', '');
}

function rsEditorAddRow(state, kind, expiry, note) {
  const RS = _rsLib();
  const box = document.getElementById('rs-editor-rows');
  if (!RS || !box) return;
  if (box.querySelectorAll('.rs-editor-row').length >= RS.MAX_ENTRIES) {
    window.showToast?.(`That's the most rows the list can hold (${RS.MAX_ENTRIES})`, 'error');
    return;
  }
  const id = `rs-row-${++_rsRowSeq}`;
  const row = document.createElement('div');
  row.className = 'rs-editor-row';
  row.id = id;
  row.style.cssText = 'display:flex;gap:6px;align-items:center;flex-wrap:wrap;';
  const stateOpts = ['<option value="">State / province…</option>']
    .concat(RS.JURISDICTIONS.map((c) => `<option value="${c}" ${c === state ? 'selected' : ''}>${c} — ${escapeHtml(RS.NAMES[c] || c)}</option>`)).join('');
  const kindOpts = RS.KINDS.map((k) => `<option value="${k.id}" ${k.id === RS.kind(kind) ? 'selected' : ''}>${escapeHtml(k.label)}</option>`).join('');
  row.innerHTML = `
    <select class="form-control rs-state" aria-label="State or province" style="flex:1 1 170px;min-width:150px;font-size:13px;padding:6px 8px;">${stateOpts}</select>
    <select class="form-control rs-kind" aria-label="Kind of registration" style="flex:2 1 220px;min-width:180px;font-size:13px;padding:6px 8px;">${kindOpts}</select>
    <input type="date" class="form-control rs-expiry" aria-label="Expires (optional)" title="Expires (optional)" value="${escapeHtml(expiry || '')}" style="flex:1 1 140px;min-width:130px;font-size:13px;padding:6px 8px;">
    <input type="text" class="form-control rs-note" aria-label="Note (optional)" placeholder="Note / license # (optional)" maxlength="${RS.NOTE_MAX}" value="${escapeHtml(note || '')}" style="flex:2 1 180px;min-width:150px;font-size:13px;padding:6px 8px;">
    <button type="button" class="btn-icon" onclick="window.ComplianceModule.rsEditorRemoveRow('${id}')" title="Remove" style="padding:4px;"><span class="material-icons" style="font-size:16px;color:var(--text-secondary);">close</span></button>
  `;
  box.appendChild(row);
  if (!state) setTimeout(() => row.querySelector('.rs-state')?.focus(), 50);
}

function rsEditorRemoveRow(id) {
  document.getElementById(id)?.remove();
}

async function saveRegisteredStates() {
  const RS = _rsLib();
  if (!RS) return;
  const rows = [...document.querySelectorAll('#rs-editor-rows .rs-editor-row')];
  const raw = rows.map((r) => ({
    state: r.querySelector('.rs-state')?.value || '',
    kind: r.querySelector('.rs-kind')?.value || '',
    expiry: r.querySelector('.rs-expiry')?.value || '',
    note: r.querySelector('.rs-note')?.value || '',
  })).filter((r) => r.state);
  const skipped = rows.length - raw.length;
  const list = RS.normalizeList(raw);
  const btn = document.getElementById('rs-editor-save');
  if (btn) btn.disabled = true;
  try {
    const old = RS.normalizeList(window.AppState?.tenantData?.registeredStates);
    await updateDoc(doc(db, 'tenants', ComplianceState.tenantId), { registeredStates: list });
    if (window.AppState?.tenantData) window.AppState.tenantData.registeredStates = list;
    // Reversible trail: old + new (state:kind) on a company-wide setting.
    window.logAudit?.('compliance', 'COMPANY_REGISTERED_STATES_CHANGE', {
      old: old.map((r) => r.state + ':' + r.kind),
      new: list.map((r) => r.state + ':' + r.kind),
      count: list.length,
    });
    window.ComplianceModule.closeDetail();
    const codes = RS.registeredCodes(list);
    window.showToast?.(
      (codes.length ? `Registered in ${codes.join(', ')}` : 'Registered states cleared — CRM will not flag any customer')
      + (skipped ? ` (${skipped} row${skipped === 1 ? '' : 's'} without a state skipped)` : ''),
      'success'
    );
    renderComplianceContent();
  } catch (err) {
    console.error('[Compliance] Save registered states error:', err);
    const denied = /permission|insufficient/i.test(err?.code || '') || /permission|insufficient/i.test(err?.message || '');
    window.showToast?.(denied
      ? 'Not saved — only the company owner, an admin or a Compliance-module member can change registered states'
      : 'Failed to save: ' + (err?.message || 'Unknown error'), 'error');
    if (btn) btn.disabled = false;
  }
}

// TASK-1225 — the ONLY company-profile keys the carrier Company tab may edit.
// Client mirror of the server whitelist in updateCompanyProfileCarrier
// (functions/src/index.js COMPANY_PROFILE_PARTNER_FIELDS — shared with the
// partner twin updateCarrierCompanyProfile). Every editProfileField call site
// in renderCompanyProfileTab passes one of these; anything else is rejected
// here AND server-side (the callable whitelist is the real boundary — this
// list just fails fast with a toast instead of a server round-trip).
const COMPANY_PROFILE_EDITABLE_KEYS = new Set([
  'companyName', 'dotNumber', 'mcNumber', 'ein', 'fleetSize',
  'phone', 'email', 'address',
  'mcs150NextUpdate', 'ucrExpiry', 'ucrYear', 'boc3Agent',
  'drugAlcoholConsortium', 'clearinghouseCompanyId',
  'insurance.liabilityPolicy', 'insurance.liabilityExpiry', 'insurance.liabilityLimit',
  'insurance.cargoPolicy', 'insurance.cargoExpiry', 'insurance.cargoLimit',
  'insurance.workersCompPolicy', 'insurance.workersCompExpiry',
]);

async function editProfileField(fieldKey) {
  if (!COMPANY_PROFILE_EDITABLE_KEYS.has(fieldKey)) {
    window.showToast?.('This field can\'t be edited here', 'error');
    return;
  }
  const td = window.AppState?.tenantData || {};

  // Resolve current value (supports nested keys like "insurance.liabilityPolicy")
  let currentValue = '';
  if (fieldKey.includes('.')) {
    const parts = fieldKey.split('.');
    let obj = td;
    for (const p of parts) obj = obj?.[p];
    currentValue = obj || '';
  } else {
    // TASK-4617 (integrated-diff COD) — `ein` no longer lives on the tenant root.
    // Seeding this dialog from td[fieldKey] would show BLANK once the migration
    // strips the root copy, and saving without retyping would push '' through the
    // callable and DESTROY the stored EIN. A federal tax ID, wiped by opening a
    // dialog and clicking Save. companyEin() carries the legacy root fallback, so
    // this reads correctly on BOTH sides of the migration.
    currentValue = (fieldKey === 'ein' ? companyEin() : td[fieldKey]) || '';
  }

  // Determine input type
  const isDate = fieldKey.includes('Expiry') || fieldKey.includes('Update') || fieldKey === 'ucrExpiry' || fieldKey === 'mcs150NextUpdate';
  const label = fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).replace('insurance.', '').replace('Insurance ', 'Insurance ');

  const modal = document.getElementById('compliance-detail-modal');
  if (!modal) return;

  modal.innerHTML = `
    <div class="modal-content" style="max-width:400px;">
      <div class="modal-header">
        <h3 style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="color:var(--brand-blue);">edit</span>
          Edit ${escapeHtml(label)}
        </h3>
        <button class="btn-icon" onclick="window.ComplianceModule.closeDetail()"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:var(--spacing-lg);">
        <label style="font-size:0.82rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:6px;">${escapeHtml(label)}</label>
        <input type="${isDate ? 'date' : 'text'}" id="profile-field-input" class="form-control"
          value="${escapeHtml(currentValue)}" style="font-size:14px;"
          ${fieldKey === 'dotNumber' ? 'placeholder="1234567" pattern="[0-9]*"' : ''}>
      </div>
      <div class="form-actions" style="padding:12px var(--spacing-lg) var(--spacing-lg);border-top:1px solid var(--border-light);">
        <button class="btn btn-secondary" onclick="window.ComplianceModule.closeDetail()">Cancel</button>
        <button class="btn btn-primary" onclick="window.ComplianceModule.saveProfileField('${fieldKey}')">
          <span class="material-icons" style="font-size:16px;">save</span> Save
        </button>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
  setTimeout(() => document.getElementById('profile-field-input')?.focus(), 100);
}

async function saveProfileField(fieldKey) {
  const input = document.getElementById('profile-field-input');
  if (!input) return;
  // TASK-1225 — allowlist fence + server-validated write. This used to be a
  // client-SDK updateDoc of an arbitrary fieldKey on the tenant ROOT doc (any
  // key the rules denylist didn't name was settable). Now it goes through the
  // updateCompanyProfileCarrier callable: 22-key whitelist, date/length
  // validation, and a COMPANY_PROFILE_CARRIER_EDIT audit row. Also fixes the
  // legit flow the old path broke — a non-admin member with the compliance
  // module got a rules permission-denied on the direct write.
  if (!COMPANY_PROFILE_EDITABLE_KEYS.has(fieldKey)) {
    window.showToast?.('This field can\'t be edited here', 'error');
    return;
  }
  const value = input.value.trim();

  try {
    await httpsCallable(functions, 'updateCompanyProfileCarrier')({
      tenantId: ComplianceState.tenantId,
      fields: { [fieldKey]: value },
    });

    // Update local state — mirror the server normalization (trim done above,
    // <> stripped server-side) so the cached tenant doc matches what persisted.
    const stored = value.replace(/[<>]/g, '') || null;
    if (fieldKey.includes('.')) {
      const parts = fieldKey.split('.');
      if (!window.AppState.tenantData[parts[0]]) window.AppState.tenantData[parts[0]] = {};
      window.AppState.tenantData[parts[0]][parts[1]] = stored;
    } else if (fieldKey === 'ein') {
      // TASK-4617 — the EIN did NOT land on the tenant root; the callable wrote
      // it to complianceSecure/company. Mirroring it into tenantData here would
      // (a) re-create the exact root-doc copy this task exists to remove, from
      // the client's own cache, and (b) leave CompanySecureState holding the
      // OLD value — which companyEin() prefers, so the edit would appear not to
      // have taken until a reload. Update the secure-state mirror instead and
      // drop any stale root copy.
      CompanySecureState.ein = stored || '';
      CompanySecureState.loaded = true;
      if (window.AppState.tenantData) delete window.AppState.tenantData.ein;
    } else {
      window.AppState.tenantData[fieldKey] = stored;
    }

    window.ComplianceModule.closeDetail();
    window.showToast?.('Updated', 'success');
    renderComplianceContent();
  } catch (err) {
    console.error('[Compliance] Save profile field error:', err);
    window.showToast?.('Failed to save: ' + (err.message || 'Unknown error'), 'error');
  }
}

// TASK-933 — employment standing of one person. Same three buckets and the same
// precedence as the partner portal's getEmploymentStatus (compliance-partner.js:290):
// terminated wins over inactive, because `terminated` people are also `active:false`
// but carry the §391.51 retention record that plain inactive does not.
function getPersonEmploymentStatus(p) {
  if (!p) return 'active';
  if (p.terminated === true || p.terminationDate) return 'terminated';
  if (p.active === false) return 'inactive';
  return 'active';
}

// TASK-9533 — the person-detail header tint. TASK-933 made the roster CARD neutral for a
// separated person (no compliance standing → no red/green), but the detail modal's header kept
// `compliance-detail-header-good` — a green "all good" band over someone no longer employed.
// Employment wins, same precedence as renderPersonCard and the partner header
// (compliance-partner.js _cpDriverHeaderStatus). A rehire clears terminated + terminationDate,
// so a rehired person tints by compliance again. Returns the modifier class or '' (neutral).
function _compDetailHeaderClass(person, status) {
  return getPersonEmploymentStatus(person) === 'active' ? `compliance-detail-header-${status}` : '';
}

// TASK-933 — separation dates arrive as either a 'YYYY-MM-DD' string (the terminate form)
// or a Firestore Timestamp (server-stamped). Normalize to a plain date string; anything
// else returns '' so a raw object can never reach the DOM as "[object Object]".
function _sepDateStr(v) {
  if (!v) return '';
  if (typeof v === 'string') return v;
  try {
    const d = v.toDate?.() || (v.seconds != null ? new Date(v.seconds * 1000) : null);
    if (!d || isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch (_) { return ''; }
}

// True when this person is in the CURRENT employment scope of the People tab.
function _inPeopleEmpScope(p) {
  const ef = ComplianceState.peopleEmpFilter;
  return ef === 'all' || getPersonEmploymentStatus(p) === ef;
}

function renderPeopleSubPills() {
  const sub = ComplianceState.peopleSubTab;
  const ef = ComplianceState.peopleEmpFilter;
  // Counts run over the FULL roster (not the current scope) so the pills always show
  // what's behind each one — same as the partner portal's empCounts.
  const empCounts = { active: 0, inactive: 0, terminated: 0, all: ComplianceState.people.length };
  ComplianceState.people.forEach(p => { empCounts[getPersonEmploymentStatus(p)]++; });
  const allCount = ComplianceState.people.filter(p => _inPeopleEmpScope(p)).length;
  const driverCount = ComplianceState.people.filter(p => {
    if (!_inPeopleEmpScope(p)) return false;
    const role = (p.crewType || p.role || p.roles?.[0] || '').toLowerCase();
    return role.includes('driver') || (p.cdlClass && p.cdlClass !== 'none');
  }).length;
  // TASK-933 — employment pills. Shown only on the 'all' sub-tab: the Drivers scorecard
  // scores 30-day safety performance, which stays an active-only measure, so a control
  // that didn't move it would just be lying.
  const empPill = (k, label, n) => `
        <button class="compliance-sub-pill ${ef === k ? 'active' : ''}" onclick="window.ComplianceModule.setPeopleEmpFilter('${k}')">
          ${escapeHtml(label)} <span class="compliance-pill-count">${n}</span>
        </button>`;
  const empPills = sub === 'drivers' ? '' : `
      <div class="compliance-sub-pills" style="margin-top:8px;" title="Separated employees stay reachable here — DOT record retention runs past the last day worked (§391.51(i)).">
        ${empPill('active', 'Active', empCounts.active)}
        ${empPill('inactive', 'Inactive', empCounts.inactive)}
        ${empPill('terminated', 'No Longer Employed', empCounts.terminated)}
        ${empPill('all', 'All', empCounts.all)}
      </div>`;
  return `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;">
      <div class="compliance-sub-pills">
        <button class="compliance-sub-pill ${sub === 'all' ? 'active' : ''}" onclick="window.ComplianceModule.setPeopleSubTab('all')">
          All <span class="compliance-pill-count">${allCount}</span>
        </button>
        <button class="compliance-sub-pill ${sub === 'drivers' ? 'active' : ''}" onclick="window.ComplianceModule.setPeopleSubTab('drivers')">
          Drivers <span class="compliance-pill-count">${driverCount}</span>
        </button>
      </div>
      <button class="btn btn-primary btn-sm" onclick="window.ComplianceModule.openAddPerson()" style="white-space:nowrap;">
        <span class="material-icons" style="font-size:16px;">add</span> Add Person
      </button>
    </div>
    ${empPills}
  `;
}

function renderPeopleTab() {
  if (ComplianceState.peopleSubTab === 'drivers') {
    return renderPeopleSubPills() + renderDriversScorecard();
  }
  return renderPeopleSubPills() + renderPeopleGrid();
}

function renderAssetSubPills() {
  const sub = ComplianceState.assetSubTab;
  const truckCount = ComplianceState.trucks.filter(v => v.active !== false && (v.unit || v.nickname || v.name || v.make)).length;
  const trailerCount = ComplianceState.trailers.filter(v => v.active !== false && (v.unit || v.nickname || v.name || v.make)).length;
  const assetCount = ComplianceState.assets.filter(a => a.active !== false && !a.consumable).length;
  // Context-aware add button — matches the current subtab
  let addBtn = '';
  if (sub === 'trailers') {
    addBtn = `<button class="btn btn-primary btn-sm" onclick="window.ComplianceModule.openAddTrailer()" style="white-space:nowrap;">
      <span class="material-icons" style="font-size:16px;">add</span> Add Trailer
    </button>`;
  } else if (sub !== 'assets') {
    // 'all' or 'trucks' (Vehicles) — default to adding a vehicle
    addBtn = `<button class="btn btn-primary btn-sm" onclick="window.ComplianceModule.openAddVehicle()" style="white-space:nowrap;">
      <span class="material-icons" style="font-size:16px;">add</span> Add Vehicle
    </button>`;
  }
  return `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:var(--spacing-md);">
      <div class="compliance-sub-pills">
        <button class="compliance-sub-pill ${sub === 'all' ? 'active' : ''}" onclick="window.ComplianceModule.setAssetSubTab('all')">
          All <span class="compliance-pill-count">${truckCount + trailerCount + assetCount}</span>
        </button>
        <button class="compliance-sub-pill ${sub === 'trucks' ? 'active' : ''}" onclick="window.ComplianceModule.setAssetSubTab('trucks')">
          Vehicles <span class="compliance-pill-count">${truckCount}</span>
        </button>
        <button class="compliance-sub-pill ${sub === 'trailers' ? 'active' : ''}" onclick="window.ComplianceModule.setAssetSubTab('trailers')">
          Trailers <span class="compliance-pill-count">${trailerCount}</span>
        </button>
        <button class="compliance-sub-pill ${sub === 'assets' ? 'active' : ''}" onclick="window.ComplianceModule.setAssetSubTab('assets')">
          Equipment <span class="compliance-pill-count">${assetCount}</span>
        </button>
      </div>
      ${addBtn}
    </div>
  `;
}

function renderAssetsTab() {
  return renderAssetSubPills() + renderVehiclesGrid();
}

// ============================================
// PEOPLE TAB
// ============================================
function renderPeopleGrid() {
  // TASK-933 — employment scope FIRST (default 'active'). Separated people are no longer
  // dropped outright: their DQ file has to stay reachable while DOT retention runs.
  let people = ComplianceState.people.filter(p => _inPeopleEmpScope(p));

  // Search filter
  const matchesSearch = (p, q) =>
    (p.name || '').toLowerCase().includes(q) ||
    (p.crewType || p.role || '').toLowerCase().includes(q);
  // TASK-933 — how many people OUTSIDE the current employment scope match the search. A
  // separated driver searched for by name would otherwise come back "not found" with no
  // hint that the record exists one pill away.
  let outOfScopeMatches = 0;
  if (ComplianceState.searchQuery) {
    const q = ComplianceState.searchQuery.trim().toLowerCase();
    outOfScopeMatches = ComplianceState.people.filter(p => !_inPeopleEmpScope(p) && matchesSearch(p, q)).length;
    people = people.filter(p => matchesSearch(p, q));
  }

  // Status filter. EFFECTIVE severity: an inactive/terminated person has no compliance
  // standing (the scoreboard counts and the rate are active-only), so they never match a
  // critical/warning/good filter — same rule the partner portal uses so a chip count can't
  // disagree with what's rendered.
  const effStatus = p => (getPersonEmploymentStatus(p) === 'active' ? getComplianceStatus(p) : getPersonEmploymentStatus(p));
  if (ComplianceState.filterStatus !== 'all') {
    people = people.filter(p => effStatus(p) === ComplianceState.filterStatus);
  }

  if (people.length === 0) {
    const scopeLabel = { active: 'active', inactive: 'inactive', terminated: 'separated' }[ComplianceState.peopleEmpFilter] || '';
    const emptyMsg = scopeLabel ? `No ${scopeLabel} people in the roster.` : 'No people in the roster.';
    const outOfScopeHint = outOfScopeMatches > 0 ? `
        <div style="margin-top:10px;font-size:0.82rem;color:var(--text-primary);">
          ${outOfScopeMatches} match${outOfScopeMatches === 1 ? '' : 'es'} outside the current employment filter.
          <button class="btn btn-sm btn-secondary" style="margin-left:8px;font-size:0.75rem;padding:4px 10px;" onclick="window.ComplianceModule.setPeopleEmpFilter('all')">Search all employment statuses</button>
        </div>` : '';
    return `
      <div class="empty-state" style="margin-top:var(--spacing-xl);">
        <div class="empty-state-icon"><span class="material-icons" style="font-size:48px;color:var(--text-muted);">person_search</span></div>
        <div class="empty-state-title">No People Found</div>
        <div class="empty-state-text">${ComplianceState.filterStatus !== 'all' ? 'Try changing your filter.' : escapeHtml(emptyMsg)}</div>
        ${outOfScopeHint}
      </div>
    `;
  }

  // Sort: critical first, then warning, then good — separated people sort last (no severity).
  const statusOrder = { critical: 0, warning: 1, good: 2, inactive: 3, terminated: 4 };
  people.sort((a, b) => statusOrder[effStatus(a)] - statusOrder[effStatus(b)]);

  // TASK-9145 — roster-level bulk Send for missing §382.601(d) receipts.
  const _daMissing = _compDaBulkMissingCount();
  const daBulkBanner = _daMissing > 0 ? `
    <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding:12px 14px;margin-bottom:var(--spacing-md);background:#F5F3FF;border:1px solid #DDD6FE;border-radius:var(--radius-lg);">
      <span class="material-icons" style="color:#7c3aed;font-size:22px;">assignment_turned_in</span>
      <div style="flex:1;min-width:200px;font-size:0.88rem;color:var(--text-primary);"><strong>${_daMissing} ${_daMissing === 1 ? 'driver has' : 'drivers have'} no signed D&amp;A policy receipt</strong> (49 CFR §382.601(d)).</div>
      <button class="btn btn-sm btn-primary" style="min-height:40px;" onclick="window.ComplianceModule.openDaReceiptBulkSend()"><span class="material-icons" style="font-size:16px;">send</span> Send links</button>
    </div>` : '';

  return `
    ${daBulkBanner}
    <div class="compliance-grid">
      ${people.map(p => renderPersonCard(p)).join('')}
    </div>
  `;
}

// TASK-980 — flag chip tooltip. 🔒 The reason is never on the person doc; the
// chip shows ONLY who + when. TASK-1207: the reason lives in the server-only
// driverFlags doc — owner/admin see it in the detail callout below.
function formatFlagTooltip(person) {
  const by = String(person?.flaggedByEmail || '').trim();
  let when = '';
  try {
    const d = person?.flaggedAt?.toDate?.() || (person?.flaggedAt ? new Date(person.flaggedAt) : null);
    if (d && !isNaN(d.getTime())) when = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) {}
  return 'Flagged for follow-up' + (by ? ' · by ' + by : '') + (when ? ' · on ' + when : '');
}

// TASK-1207 / TASK-9424 — the flag panel at the top of the compliance person
// detail. A driver can now carry SEVERAL flags, each cleared on its own with a
// required reason, and the history (every flag and every clear, with why) stays.
// The shell renders synchronously; _hydrateCompFlagReason fills it from the
// getDriverFlags callable, which returns reasons ONLY to owner/admin (the same
// set the driverFlags read rule admits) — a module holder sees who/when and a
// "contact your admin" line, and can still pick WHICH flag to clear.
function _buildCompFlagReasonCalloutHTML(person) {
  if (person?.flagged !== true) return '<div id="comp-flag-panel"></div>';
  const meta = formatFlagTooltip(person).replace(/^Flagged for follow-up/, ''); // '' or ' · by X · on date'
  return `
    <div id="comp-flag-panel">
    <div id="comp-flag-reason-callout" style="background:#FEF2F2;border:1.5px solid #FECACA;border-left:5px solid #DC2626;border-radius:8px;padding:12px 14px;margin-bottom:12px;">
      <div style="display:flex;align-items:center;gap:6px;font-weight:800;color:#B91C1C;font-size:0.9rem;">
        <span class="material-icons" style="font-size:18px;">flag</span> Flagged for follow-up${escapeHtml(meta)}
      </div>
      <div id="comp-flag-reason-text" style="margin-top:6px;font-size:0.82rem;color:#991B1B;">Loading flags…</div>
    </div>
    </div>`;
}

// Who may press Clear… — the host's call (Pitfall #8), mirroring the setDriverFlag
// WRITE gate: owner/admin, or an hr/compliance module holder who is not view/upload-only.
// The server re-checks; this only decides whether the button is drawn.
function _compCanWriteFlags() {
  const m = window.AppState?.memberAccess;
  if (!m) return true; // owner/legacy (no member doc)
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  if (m.role === 'owner' || m.role === 'admin') return true;
  const mods = Array.isArray(m.modules) ? m.modules : [];
  return (mods.includes('hr') || mods.includes('compliance')) && m.viewUploadOnly !== true;
}

// TASK-9424 — hydrate the panel from getDriverFlags. Runs for EVERY person (not
// only flagged ones) so a cleared flag's history — including why it was cleared —
// is still visible after the last flag goes. A partner-owned flag (`source`, set
// server-side, TASK-9227) gets a note instead of a Clear button: only the partner
// can clear it. Free text is escaped by the shared renderer; status copy goes
// through textContent.
async function _hydrateCompFlagReason(person) {
  const host = document.getElementById('comp-flag-panel');
  if (!host || !person?.id) return;
  const PS = window.PersonSections;
  let res;
  try {
    const fn = httpsCallable(functions, 'getDriverFlags');
    res = (await fn({ tenantId: ComplianceState.tenantId, personId: person.id }))?.data || {};
  } catch (err) {
    const el = document.getElementById('comp-flag-reason-text');
    if (el) {
      el.textContent = String(err?.code || '').endsWith('permission-denied')
        ? 'Flag details are visible to HR / Compliance and the owner/admin — contact your admin.'
        : 'Could not load this driver\'s flags — reopen to try again.';
    }
    return;
  }
  if (document.getElementById('comp-flag-panel') !== host) return; // detail re-rendered meanwhile
  const canWrite = _compCanWriteFlags();
  const pid = String(person.id);
  const items = PS.driverFlagPanelItems(res.flags, {
    noteFor: (f) => (f.source === 'partner' ? 'Set by your compliance partner — only they can clear it.'
      : (f.reason == null ? 'Only an owner/admin (who can read the reason) can clear it.' : '')),
  });
  host.innerHTML = PS.renderFlagPanel(items, {
    hiddenReasonText: 'Reason visible to the owner/admin only — contact your admin.',
    emptyReasonText: 'No reason on file — this flag was set before reasons were recorded.',
    renderAction: (f) => ((f.source === 'partner' || !canWrite || f.reasonHidden) ? '' : `<button class="btn btn-sm" onclick="window.ComplianceModule.clearDriverFlag('${escJsAttr(pid)}','${escJsAttr(f.flagId || '')}')" style="background:#fff;border:1.5px solid #DC2626;color:#DC2626;font-weight:600;" title="Clear this flag — you will be asked why">Clear…</button>`),
  }) + PS.renderFlagHistory(PS.driverFlagHistoryItems(res.history), {
    hiddenReasonText: 'Reasons visible to the owner/admin only.',
  });
}

// TASK-933 Slice B — read-only separation block for the person detail modal. Mirrors the
// partner portal's termination rows (compliance-partner.js:12750) with the fields the
// carrier already stores on the person doc. READ-ONLY on purpose: off-boarding stays a
// Roster/HR action, and an auditor pulling a retained DQ file must not be able to edit it
// from here. Renders nothing for a currently-employed person.
// TASK-1594 — the reason is NOT on the person doc any more (that doc is
// broadcast to every driver-PIN session). It lives in peoplePii/{id}, readable
// by admin / compliance manager / hr-directory; fill the placeholder after
// render. textContent, never innerHTML — the reason is free text.
function _fillSeparationReason(person) {
  const pid = String(person?.id || '');
  const tid = ComplianceState.tenantId || window.AppState?.tenant;
  if (!pid || !tid) return;
  const set = (txt) => {
    document.querySelectorAll(`[data-sep-reason-for="${CSS.escape(pid)}"]`).forEach((el) => { el.textContent = txt; });
  };
  getDoc(doc(db, 'tenants', tid, 'peoplePii', pid)).then((snap) => {
    const sib = snap.exists() ? (snap.data() || {}) : {};
    const v = typeof sib.terminationReason === 'string' ? sib.terminationReason : (person.terminationReason || '');
    set(v || '—');
  }).catch(() => set('Visible to admins and HR Directory only'));
}

function _buildSeparationSectionHTML(person) {
  const emp = getPersonEmploymentStatus(person);
  if (emp === 'active') return '';
  if (emp === 'inactive') {
    return `
      <div class="compliance-detail-section">
        <div class="compliance-detail-section-title">
          <span class="material-icons" style="font-size:18px;color:#64748B;">pause_circle</span> Inactive
        </div>
        <div style="padding:6px 0;font-size:0.82rem;color:var(--text-secondary);">
          Still employed, temporarily not working. Compliance deadlines keep running — they are excluded from the active scoreboard only.
        </div>
      </div>`;
  }
  const row = (label, value) => `
    <div style="display:flex;justify-content:space-between;gap:12px;padding:5px 0;font-size:0.82rem;">
      <span style="color:var(--text-secondary);">${escapeHtml(label)}</span>
      <span style="color:var(--text-primary);font-weight:600;text-align:right;">${escapeHtml(value || '—')}</span>
    </div>`;
  const termAt = person.terminationDate || person.terminatedAt || person.deactivatedAt;
  setTimeout(() => _fillSeparationReason(person), 0);
  return `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title">
        <span class="material-icons" style="font-size:18px;color:#64748B;">person_off</span> No Longer Employed
      </div>
      ${row('Last day worked', formatDateShort(_sepDateStr(person.lastDayWorked)))}
      <div style="display:flex;justify-content:space-between;gap:12px;padding:5px 0;font-size:0.82rem;">
        <span style="color:var(--text-secondary);">Reason</span>
        <span data-sep-reason-for="${escapeHtml(String(person.id || ''))}" style="color:var(--text-primary);font-weight:600;text-align:right;">…</span>
      </div>
      ${row('Terminated at', formatDateShort(_sepDateStr(termAt)))}
      <div style="padding:8px 0 0;font-size:0.75rem;color:var(--text-secondary);line-height:1.5;border-top:1px solid var(--border-light);margin-top:6px;">
        This driver qualification file stays on record for 3 years past the separation date (49 CFR §391.51(i)). Off-boarding details are edited in Roster, not here.
      </div>
    </div>`;
}

function renderPersonCard(person) {
  const status = getComplianceStatus(person);
  const role = person.crewType || person.role || person.roles?.[0] || '';
  const roleLabel = role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  // TASK-934 — explicit classification wins; legacy (unclassified) people keep
  // the role/cdlClass heuristic for the CDL badge.
  const _ptExplicit = window.DqMatrix.explicitPositionType(person);
  const isNonDriver = window.DqMatrix.NO_DQ_FILE_TIERS.includes(_ptExplicit); // TASK-9696 — drives_no_cmv too
  const isCdl = _ptExplicit
    ? _ptExplicit === 'cdl'
    : (role === 'cdl_driver' || (person.cdlClass && person.cdlClass !== 'none'));
  const missedDvirs = person.missedDvirCount || 0;
  const trainings = person.isnetTrainings || [];
  const hasDvirFlag = ComplianceState.dvirFlags.some(f => f.driverId === person.id && f.status === 'flagged');
  const hasJsaFlag = ComplianceState.jsaFlags.some(f => f.driverId === person.id && f.status === 'flagged');

  const statusConfig = {
    critical: { icon: 'error', color: '#DC2626', label: 'Action Required' },
    warning: { icon: 'warning', color: '#D97706', label: 'Expiring Soon' },
    good: { icon: 'check_circle', color: '#059669', label: 'Compliant' }
  };
  // TASK-933 — a separated person has NO compliance standing (they're out of the scoreboard
  // counts and the rate), so the card shows their employment state in neutral gray instead of
  // a red "Action Required" that nothing on the dashboard is counting. Slate #64748B on the
  // light card, never --text-muted.
  const emp = getPersonEmploymentStatus(person);
  const empConfig = {
    inactive: { icon: 'pause_circle', color: '#64748B', label: 'Inactive — still employed' },
    terminated: { icon: 'person_off', color: '#64748B', label: 'No longer employed — record retained' }
  };
  const sc = empConfig[emp] || statusConfig[status];
  const cardClass = empConfig[emp] ? 'compliance-card' : `compliance-card compliance-card-${status}`;
  const cardStyle = empConfig[emp] ? ' style="border-left-color:#64748B;"' : '';
  const sepDate = emp === 'terminated' ? (_sepDateStr(person.lastDayWorked) || _sepDateStr(person.terminationDate)) : '';

  return `
    <div class="${cardClass}"${cardStyle} onclick="window.ComplianceModule.showPersonDetail('${person.id}')">
      <div class="compliance-card-header">
        <div style="display:flex;align-items:center;justify-content:space-between;width:100%;">
          <div>
            <div style="display:flex;align-items:center;gap:8px;">
              <div class="compliance-card-name">${escapeHtml(person.name || 'Unknown')}</div>
              ${isCdl ? '<span class="compliance-type-badge compliance-type-truck">CDL</span>' : ''}
            </div>
            ${roleLabel ? `<div class="compliance-card-role">${escapeHtml(roleLabel)}</div>` : ''}
          </div>
          <div class="compliance-status-indicator" style="color:${sc.color};" title="${_escAttr(sc.label)}">
            <span class="material-icons" style="font-size:22px;">${sc.icon}</span>
          </div>
        </div>
      </div>
      <div class="compliance-card-badges">
        ${emp === 'terminated' ? `<span class="compliance-badge compliance-badge-gray" title="Separated employee — the DQ file stays on record (§391.51(i), 3 years past separation)">
          <span class="material-icons" style="font-size:14px;">person_off</span> No Longer Employed${sepDate ? ' · ' + formatDateShort(sepDate) : ''}
        </span>` : ''}
        ${emp === 'inactive' ? `<span class="compliance-badge compliance-badge-gray" title="Inactive — still employed, temporarily not working">
          <span class="material-icons" style="font-size:14px;">pause_circle</span> Inactive
        </span>` : ''}
        ${person.flagged === true ? `<span class="compliance-badge compliance-badge-critical" title="${escapeHtml(formatFlagTooltip(person))}">🚩 Flagged</span>` : ''}
        ${isNonDriver ? window.DqMatrix.nonDriverChipHtml('', _ptExplicit) : ''}
        ${hasDvirFlag ? `<span class="compliance-badge compliance-badge-critical" style="animation:dvir-flag-pulse 1.5s ease-in-out infinite;">
          <span class="material-icons" style="font-size:14px;">warning</span> Missing DVIR
        </span>` : ''}
        ${hasJsaFlag ? `<span class="compliance-badge" style="background:#FEF3C7;color:#D97706;border-color:#F59E0B40;animation:dvir-flag-pulse 1.5s ease-in-out infinite;">
          <span class="material-icons" style="font-size:14px;">health_and_safety</span> Missing JSA
        </span>` : ''}
        ${isCdl ? formatExpiryBadge(person.cdlExpiryDate, 'CDL') : ''}
        ${isNonDriver ? '' : formatExpiryBadge(person.medCardExpiryDate, 'Med Card')}
        ${missedDvirs > 0 && !hasDvirFlag ? `<span class="compliance-badge compliance-badge-critical" title="${missedDvirs} missed DVIRs">
          <span class="material-icons" style="font-size:14px;">report_problem</span> ${missedDvirs} Missed DVIR${missedDvirs > 1 ? 's' : ''}
        </span>` : ''}
        ${trainings.length > 0 ? `<span class="compliance-badge compliance-badge-info" title="${trainings.length} training cert(s)">
          <span class="material-icons" style="font-size:14px;">school</span> ${trainings.length} Cert${trainings.length > 1 ? 's' : ''}
        </span>` : ''}
        ${(() => {
          const tr = rollupRequiredTrainings(person);
          if (tr.missing > 0 || tr.expired > 0) {
            const n = tr.missing + tr.expired;
            return `<span class="compliance-badge compliance-badge-critical" title="${tr.missing} missing, ${tr.expired} expired required training(s)">
              <span class="material-icons" style="font-size:14px;">verified_user</span> ${n} Training Gap${n > 1 ? 's' : ''}
            </span>`;
          }
          if (tr.expiring > 0) {
            return `<span class="compliance-badge compliance-badge-warning" title="${tr.expiring} required training(s) expiring soon">
              <span class="material-icons" style="font-size:14px;">verified_user</span> ${tr.expiring} Training Expiring
            </span>`;
          }
          return '';
        })()}
        ${renderDrugTestBadge(person.id)}
      </div>
    </div>
  `;
}

// TASK-9458 — the carrier Compliance person-modal DQ scorecard (TASK-1144 shared
// renderer: the renderer renders, this host authorizes and supplies the data).
// Split out of showPersonDetail so hydrateComplianceBgReport can re-render it once
// the peopleBackgroundChecks doc is cached — the first open renders before it lands.
// The cached bg doc goes in as `bgCheck`, exactly as the Compliance MVR row does
// (30d14244f): a Checkr MVR linked only on that doc IS the MVR on file, so the
// §391.25 / §391.51(b) rows must not read "not on file" beside a working View MVR
// report button. Only the shared predicate (DqMatrix.mvrRecordIsOnFile) reads it.
function _compDqScorecardHtml(person, dqApp) {
  const personId = person.id;
  const _bgDoc = _compBgDocCache[personId];
  return window.PersonSections.renderDqScorecard({ ...person, bgCheck: _bgDoc || person.bgCheck }, {
        mode: 'carrier',
        dqApplicability: dqApp,
        // TASK-1319 — accepts pre_employment spelling + requires a passing result.
        // TASK-1320 — `person` is the third arg so the shared predicate can apply
        // the §382.301 Break-in-Service date gate off the server-stamped,
        // rules-locked anchor. Owner 4 of 4; must stay in lockstep with
        // hr.js:30671 and the two server owners (ATOMIC-LANDING rule).
        hasPreEmploymentDrugTest: window.PersonSections.hasPassingPreEmploymentTest(ComplianceState.drugTests, personId, person),
        auditDateLabel: person.dqFileLastAudit ? formatDateShort(person.dqFileLastAudit) : null,
        runAuditOnClick: null,
        // TASK-9295 — "View MVR report" on the MVR row. The onclick is built by the
        // CARRIER host helper (hr.js, statically imported by app.js → window.HRModule),
        // which also decides access (owner / hr / compliance module; ?client= partner)
        // and returns null (no button) otherwise. This tenant is passed explicitly so the
        // Checkr leg never depends on HRState having been initialised.
        // _compBgDocCache holds this person's BG record once hydrated (else undefined →
        // the server decides the MVR-only-package rule for a compliance-module session).
        ...(() => {
          const b = (window.HRModule && typeof window.HRModule.hrMvrReportButton === 'function')
            ? (window.HRModule.hrMvrReportButton(person, ComplianceState.tenantId, _compBgDocCache[personId]) || {}) : {};
          return { mvrViewOnClick: b.onClick || null, mvrViewDisabledReason: b.disabledReason || null };
        })(),
        // TASK-1320 — work-during-the-break opener; gate + opener live in hr.js
        // (window.HRModule, Pitfall #1) so both carrier surfaces share one table.
        bisGapAccountOnClick: _compCanRecordBisGap() ? `window.ComplianceModule.openBisGapAccount('${window.escJsAttr(personId)}')` : null,
      });
}

function showPersonDetail(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) {
    // Don't strand the TASK-1147 loading shell if the person vanished between
    // the deferred open and the streams landing.
    if (ComplianceState.selectedPersonId === personId) window.ComplianceModule?.closeDetail?.();
    return;
  }

  // TASK-1147 — the person detail reads streams the current sub-view may not
  // have opened (drug-test section/badge, DVIR/JSA accountability flags, the
  // DQ-scorecard pre-employment flag) — e.g. opened straight from an Overview
  // health-summary row. Subscribe on demand and hold a spinner until their
  // first snapshot lands; the TASK-879 modal re-render machinery in
  // renderIfReady keeps it fresh afterwards. Never render those sections
  // silently empty off a never-subscribed stream.
  ensureStreams(PERSON_DETAIL_STREAMS);
  if (!_streamsLoadedAll(PERSON_DETAIL_STREAMS)) {
    const modalEl = document.getElementById('compliance-detail-modal');
    if (!modalEl) return;
    ComplianceState.selectedPersonId = personId;
    modalEl.innerHTML = `
      <div class="modal-content" data-detail="person" style="max-width:560px;">
        <div style="display:flex;align-items:center;justify-content:center;padding:60px 0;">
          <div class="loading-spinner"></div>
        </div>
      </div>`;
    modalEl.classList.remove('hidden');
    _whenStreamsLoaded(PERSON_DETAIL_STREAMS, () => {
      if (ComplianceState.selectedPersonId === personId && !modalEl.classList.contains('hidden')) {
        showPersonDetail(personId);
      }
    });
    return;
  }

  // TASK-973 — fire-and-forget "who opened this person's compliance file" log. NEVER
  // blocks the render; the server derives the actor from auth, verifies tenant
  // membership + that the person exists, and rate-limits re-opens (source='carrier').
  // Swallow every error — logging is best-effort.
  try {
    const _viewTid = ComplianceState.tenantId;
    if (_viewTid) httpsCallable(functions, 'logDriverFileView')({ tenantId: _viewTid, personId }).catch(() => {});
  } catch (_) {}

  ComplianceState.selectedPersonId = personId;
  // A partner-managed NON-PAYING (compliance-only) carrier has HR paywalled, so
  // an "Edit in HR" escape hatch routes them to a screen they can't reach. For
  // those tenants the per-section "Edit" buttons (CDL/MVR/etc., TASK-812) ARE
  // the edit surface — so we hide the HR link for them. Paying carriers keep it.
  const complianceOnly = !!window.isComplianceOnlyTenant?.();

  const modal = document.getElementById('compliance-detail-modal');
  if (!modal) return;

  const status = getComplianceStatus(person);
  const role = person.crewType || person.role || person.roles?.[0] || '';
  // TASK-934 — explicit classification wins; legacy (unclassified) keeps the
  // role/cdlClass heuristic.
  const _ptExplicitDetail = window.DqMatrix.explicitPositionType(person);
  const isNonDriver = window.DqMatrix.NO_DQ_FILE_TIERS.includes(_ptExplicitDetail); // TASK-9696 — drives_no_cmv too
  const isCdl = _ptExplicitDetail
    ? _ptExplicitDetail === 'cdl'
    : (role === 'cdl_driver' || (person.cdlClass && person.cdlClass !== 'none'));
  // TASK-1096 — ONE applicability resolve for the whole detail modal. The partner
  // portal (_renderDqChecklist) and HR (buildComplianceTabHTML DQ checklist)
  // already gate per DqMatrix.getApplicableDqItems; this brings the carrier
  // compliance person-detail into parity. UNCLASSIFIED people resolve to 'cdl'
  // (full checklist — fail-safe, TASK-934); only an explicit classification can
  // remove sections.
  const _dqApp = window.DqMatrix.getApplicableDqItems(person);
  const _dqItems = _dqApp.items;
  const _dqNonDriver = !window.DqMatrix.hasDqFile(_dqApp.positionType); // TASK-9696 — no DQ file (non_driver OR drives_no_cmv)
  const missedDvirs = person.missedDvirCount || 0;
  const trainings = person.isnetTrainings || [];

  // CDL section
  // TASK-4605 Slice 2 — shared front/back licence card (Pitfall #8: renderer
  // renders, host authorizes). checkModuleAccess('compliance') already gates
  // reaching this modal at all (driver-PIN mode never grants the 'compliance'
  // module — app.js checkModuleAccess), and the legacy single-slot code above
  // never gated edit any tighter than "can open this modal" — so canEdit is
  // unconditionally true here, exactly matching prior behavior. Markup only;
  // the click wiring is bound below via bindLicenseImages (real functions, not
  // inline onclick — TASK-4605's own fix landed after a Storage path with an
  // apostrophe broke out of an HTML-escaped onclick string).
  // TASK-4605 (integrated-diff COD) — was `canEdit: true`. This file already has
  // `_compCanWriteRoster()` (:125), which mirrors the AUTHORITATIVE server gate
  // (requireRosterWriteAccess / firestore.rules people:update). Passing true
  // unconditionally hands a view/upload-only member an Upload button whose write
  // is denied downstream — the dead-button complaint this repo already has
  // history with. The whole point of "renderer renders, host authorizes" is that
  // the host passes ITS gate; a hardcoded true makes the parameter decorative.
  const dlDocHtml = window.PersonSections.renderLicenseImages(person, { canEdit: _compCanWriteRoster(), canOpen: true })
    + (person.dlDocumentPath ? `<div style="padding:2px 0 0;text-align:right;">
        <a href="#" onclick="event.preventDefault();window.viewCredDocHistory('${personId}','license',null,this);return false;" title="All uploaded versions of this document" style="font-size:0.72rem;color:var(--text-secondary);display:inline-flex;align-items:center;gap:3px;cursor:pointer;"><span class="material-icons" style="font-size:14px;">history</span> History</a>
      </div>` : '');

  // TASK-812 — Driver-qualification identity + CDL data entry, mirroring the HR
  // Compliance-tab edit form (hr.js saveComplianceFields, ids comp-*). Writes the
  // EXACT SAME person-doc keys HR writes (dob, dlState, dlNumber, dlExpiryDate,
  // cdlClass, cdlEndorsements, cdlIssueDate, cdlExpiryDate) via the compliance-
  // (+ cdlRestrictions since TASK-9300 (5) — carrier-editable here and in the roster form;
  // the partner portal already read/wrote it, the carrier side simply never showed it) —
  // local updateDoc path — no HR delegation (TASK-781 trap), no copy/sync (HR,
  // Roster, Compliance are three views over ONE people/{id} doc). Lets a
  // partner-managed NON-PAYING carrier (HR paywalled) type the data John needs to
  // run an MVR + build a DQ file, instead of only attaching a license picture.
  const isEditingDriver = ComplianceState.editingDriverDetailsId === personId;
  // TASK-9359 — this viewer cannot read the peopleIdentity vault (and the person
  // doc carries no legacy copy): show "Restricted" and lock the inputs, so a
  // blank is never saved over a value hidden from this user.
  // TASK-10154 — a managing partner reads / edits through the server instead:
  // display rows come from _compPartnerIdRows, and the Edit form is unlocked
  // only once the audited full-value fetch for this person has landed.
  const _partnerId10154 = !!_compPartnerTenantId();
  const _partnerEditVals = _partnerId10154 ? (_CompPartnerId.edit.get(personId) || null) : null;
  const _idLock9359 = _partnerId10154
    ? !_partnerEditVals
    : !!(window.PersonIdentity && window.PersonIdentity.identityUnknown(person));
  const _idLockAttr9359 = _idLock9359 ? ' disabled title="Restricted — your login cannot view this"' : '';
  // The values the Edit form shows: the partner's audited fetch, else the person.
  const _idv = _partnerEditVals || person;
  const cdlSection = isEditingDriver
    ? `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">badge</span> Driver Details &amp; CDL
        </div>
      </div>
      <div style="padding:8px 0;">
        ${/* TASK-934 — Driver Classification (canonical DQ gate). Same shared
             control + same setDriverClassification callable as the HR Edit
             Person surface — Surface B for compliance-only carriers whose HR
             is paywalled. Definitional, so it renders first. */
          window.DqMatrix.classificationControlHtml({
            person,
            name: 'comp-dq-position-type',
            attributionLabel: (window.AppState?.tenantData?.compliancePartner || '').trim() || null,
          })}
        <div style="font-weight:600;font-size:0.78rem;color:var(--text-secondary);margin:4px 0 6px;text-transform:uppercase;letter-spacing:0.02em;">License &amp; Identity</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 12px;">
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.78rem;font-weight:600;">Date of Birth</label>
            <input type="date" id="comp-dq-dob" class="form-control" value="${_idLock9359 ? '' : escapeHtml(_idv.dob || '')}" style="font-size:0.85rem;"${_idLockAttr9359}>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.78rem;font-weight:600;">DL State</label>
            <input type="text" id="comp-dq-dl-state" class="form-control" maxlength="2" placeholder="${_idLock9359 ? 'Restricted' : 'ND'}" value="${_idLock9359 ? '' : escapeHtml(_idv.dlState || '')}" style="font-size:0.85rem;text-transform:uppercase;"${_idLockAttr9359}>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.78rem;font-weight:600;">DL Number</label>
            <input type="text" id="comp-dq-dl-number" class="form-control" placeholder="${_idLock9359 ? 'Restricted' : 'D123456789'}" value="${_idLock9359 ? '' : escapeHtml(_idv.dlNumber || '')}" style="font-size:0.85rem;"${_idLockAttr9359}>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.78rem;font-weight:600;">DL Expiry</label>
            <input type="date" id="comp-dq-dl-expiry" class="form-control" value="${_idLock9359 ? '' : escapeHtml(_idv.dlExpiryDate || '')}" style="font-size:0.85rem;"${_idLockAttr9359}>
          </div>
        </div>
        <div style="font-weight:600;font-size:0.78rem;color:var(--text-secondary);margin:12px 0 6px;text-transform:uppercase;letter-spacing:0.02em;">CDL</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 12px;">
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.78rem;font-weight:600;">CDL Class</label>
            <select id="comp-dq-cdl-class" class="form-control" style="font-size:0.85rem;">
              <option value=""${!person.cdlClass || person.cdlClass === 'none' ? ' selected' : ''}>None</option>
              <option value="A"${person.cdlClass === 'A' ? ' selected' : ''}>Class A</option>
              <option value="B"${person.cdlClass === 'B' ? ' selected' : ''}>Class B</option>
              <option value="C"${person.cdlClass === 'C' ? ' selected' : ''}>Class C</option>
            </select>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.78rem;font-weight:600;">Endorsements</label>
            <input type="text" id="comp-dq-cdl-endorsements" class="form-control" placeholder="H, N, T, X" value="${escapeHtml(person.cdlEndorsements || '')}" style="font-size:0.85rem;">
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.78rem;font-weight:600;">Restrictions</label>
            <input type="text" id="comp-dq-cdl-restrictions" class="form-control" placeholder="e.g. L, Z" value="${escapeHtml(_cdlRestrText(person.cdlRestrictions))}" style="font-size:0.85rem;">
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.78rem;font-weight:600;">CDL Issue Date</label>
            <input type="date" id="comp-dq-cdl-issue" class="form-control" value="${escapeHtml(person.cdlIssueDate || '')}" style="font-size:0.85rem;">
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.78rem;font-weight:600;">CDL Expiry</label>
            <input type="date" id="comp-dq-cdl-expiry" class="form-control" value="${escapeHtml(person.cdlExpiryDate || '')}" style="font-size:0.85rem;">
          </div>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
          <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.cancelDriverDetailsEdit('${personId}')" style="font-size:0.78rem;">Cancel</button>
          <button class="btn btn-sm btn-primary" id="comp-dq-save-btn" onclick="window.ComplianceModule.saveDriverDetails('${personId}')" style="font-size:0.78rem;">
            <span class="material-icons" style="font-size:14px;">save</span> Save
          </button>
        </div>
        ${dlDocHtml}
      </div>
    </div>
  `
    : `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">badge</span> CDL Information
        </div>
        <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.editDriverDetails('${personId}')" style="font-size:0.72rem;padding:3px 8px;" title="Edit driver's license, DOB and CDL details">
          <span class="material-icons" style="font-size:14px;">edit</span> Edit
        </button>
      </div>
      <div class="compliance-detail-fields">
        ${_partnerId10154 ? _compPartnerIdRows(personId) : `
        ${detailRow('Date of Birth', _idLock9359 ? 'Restricted' : (formatDateShort(person.dob) || 'Not on file'))}
        ${detailRow('DL Number', _idLock9359 ? 'Restricted' : (person.dlNumber ? escapeHtml(person.dlNumber) : 'Not on file'))}
        ${detailRow('DL State', _idLock9359 ? 'Restricted' : (person.dlState ? escapeHtml(person.dlState) : 'N/A'))}
        ${detailRow('DL Expiry', _idLock9359 ? 'Restricted' : (formatDateShort(person.dlExpiryDate) || 'N/A'))}
        ${person.dlExpiryDate ? detailRow('DL Status', getExpiryStatusText(person.dlExpiryDate)) : ''}`}
        ${detailRow('CDL Class', person.cdlClass && person.cdlClass !== 'none' ? 'Class ' + escapeHtml(person.cdlClass) : 'Not on file')}
        ${detailRow('Endorsements', person.cdlEndorsements ? escapeHtml(person.cdlEndorsements) : 'None')}
        ${detailRow('Restrictions', _cdlRestrText(person.cdlRestrictions) ? escapeHtml(_cdlRestrText(person.cdlRestrictions)) : 'None')}
        ${detailRow('CDL Issue Date', formatDateShort(person.cdlIssueDate) || 'N/A')}
        ${detailRow('CDL Expiry', formatDateShort(person.cdlExpiryDate) || 'N/A')}
        ${person.cdlExpiryDate ? detailRow('CDL Status', getExpiryStatusText(person.cdlExpiryDate)) : ''}
        ${dlDocHtml}
      </div>
    </div>
  `;

  // Medical card section — with upload, editable expiry, document view.
  // TASK-647 — read the EFFECTIVE med-card doc (most-recent of medCertPdfUrl /
  // medCardDocumentUrl) via the shared HR reader so this compliance/partner view
  // ALSO sees a driver's SMS self-upload (medCertPdfUrl) — previously it read
  // only medCardDocumentUrl, so self-uploads were invisible here and the DQ file
  // read stale/empty. Falls back to an identical inline impl if hr.js hasn't
  // loaded yet (no cross-import ?v= cache-buster — CLAUDE.md pitfall #1).
  const effMed = effectiveMedCardDoc(person);
  const medDocHtml = effMed
    ? `<div style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-top:1px solid var(--border-light);margin-top:4px;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:16px;color:#059669;">check_circle</span>
          <a href="#" onclick="event.preventDefault();window.viewComplianceDoc('${escJsAttr(effMed.path || '')}','${escJsAttr(effMed.url || '')}','medical-card');return false;" style="font-size:0.8rem;color:var(--brand-blue);font-weight:500;cursor:pointer;">View Medical Card${effMed.source === 'medCertPdfUrl' ? ' <span style="color:var(--text-secondary);font-weight:400;">(self-uploaded)</span>' : ''}</a>
          <a href="#" onclick="event.preventDefault();window.viewCredDocHistory('${personId}','medCard',null,this);return false;" title="All uploaded versions of this document" style="font-size:0.72rem;color:var(--text-secondary);display:inline-flex;align-items:center;gap:3px;cursor:pointer;"><span class="material-icons" style="font-size:14px;">history</span> History</a>
        </div>
        <button class="btn-icon" onclick="event.stopPropagation();window.ComplianceModule.${effMed.source === 'medCertPdfUrl' ? 'removeMedCertPdf' : 'removeMedCardDocument'}('${personId}')" title="Remove file" style="padding:2px;">
          <span class="material-icons" style="font-size:16px;color:#DC2626;">delete</span>
        </button>
      </div>`
    : `<div style="padding:8px 0;border-top:1px solid var(--border-light);margin-top:4px;text-align:center;">
        <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.uploadMedCardDocument('${personId}')" style="font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">upload_file</span> Upload Medical Card
        </button>
      </div>`;

  // Editable expiry date for med card
  const medExpiryHtml = `
    <div class="compliance-detail-row">
      <span class="compliance-detail-label">Expiry Date</span>
      <span class="compliance-detail-value" style="display:flex;align-items:center;gap:6px;">
        <input type="date" value="${person.medCardExpiryDate || ''}"
          onchange="window.ComplianceModule.updateMedCardExpiry('${personId}', this.value)"
          style="border:1px solid var(--border-light);border-radius:6px;padding:4px 8px;font-size:0.82rem;background:var(--bg-primary);color:var(--text-primary);" />
      </span>
    </div>`;

  const medSection = `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#059669;">medical_services</span> Medical Card
          ${effMed && effMed.pendingReview ? '<span style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;margin-left:6px;background:#FEF3C7;color:#92400E;border-radius:6px;font-size:0.7rem;font-weight:600;"><span class="material-icons" style="font-size:12px;">pending</span> Pending review</span>' : ''}
        </div>
        <div style="display:flex;align-items:center;gap:6px;flex-shrink:0;">
          <button class="btn btn-sm" onclick="window.ComplianceModule.openMedExamModalCompliance('${personId}')" style="padding:3px 8px;font-size:0.72rem;background:#047857;color:#fff;border:none;" title="Record the full DOT medical examiner's certificate — examiner, NRCME #, restrictions, cert PDF">
            <span class="material-icons" style="font-size:14px;">medical_services</span> Record Exam
          </button>
          <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.sendMedUploadLinkCompliance('${personId}')" style="padding:3px 8px;font-size:0.72rem;" title="Text the driver a one-tap link to upload a photo of their new medical card">
            <span class="material-icons" style="font-size:14px;">sms</span> Send Upload Link
          </button>
        </div>
      </div>
      <div class="compliance-detail-fields">
        ${_medDq(person).medCardNumber ? detailRow('Provider', escapeHtml(_medDq(person).medCardNumber)) : ''}
        ${medExpiryHtml}
        ${person.medCardExpiryDate ? detailRow('Status', getExpiryStatusText(person.medCardExpiryDate)) : ''}
        ${medDocHtml}
      </div>
    </div>
  `;

  // ISNet section (if data exists)
  const hasIsnet = person.isnetNickname || person.isnetEmployeeType || person.isnetEmployeeId || person.isnetSsn || person.isnetDob || person.isnetDlState || person.isnetDlNumber;
  const isnetSection = hasIsnet ? `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title">
        <span class="material-icons" style="font-size:18px;color:#7c3aed;">verified_user</span> ISNet Profile
      </div>
      <div class="compliance-detail-fields">
        ${person.isnetNickname ? detailRow('ISNetworld #', escapeHtml(person.isnetNickname)) : ''}
        ${person.isnetEmployeeType ? detailRow('Employee Type', escapeHtml(person.isnetEmployeeType)) : ''}
        ${person.isnetEmployeeId ? detailRow('Employee ID', escapeHtml(person.isnetEmployeeId)) : ''}
        ${person.isnetSsn ? detailRow('SSN', maskSSN(person.isnetSsn)) : ''}
        ${person.isnetDob ? detailRow('Date of Birth', formatDateShort(person.isnetDob)) : ''}
        ${person.isnetDlState ? detailRow('DL State', escapeHtml(person.isnetDlState)) : ''}
        ${person.isnetDlNumber ? detailRow('DL Number', escapeHtml(person.isnetDlNumber)) : ''}
      </div>
    </div>
  ` : '';

  // TASK-513 — Required Trainings (Safety Roster) required-vs-has matrix.
  const requiredConfig = getSafetyTrainingsConfig();
  // TASK-9911 M2 — rendered by the shared PersonSections.renderSkillsAndCerts
  // (same section as Roster's person form + HR). Compliance is the host: its
  // own state calc (requiredTrainingState) and its own Mark N/A / Undo actions.
  const requiredSection = (() => {
    const PS = window.PersonSections;
    if (!PS || typeof PS.renderSkillsAndCerts !== 'function') return '';
    const skillRows = PS.skillsCertsSkillRows(person);
    // COD r1: escJsAttr or no buttons — escapeHtml is not JS-string safe.
    const jsq = (v) => window.escJsAttr(String(v));
    const canJs = typeof window.escJsAttr === 'function';
    const body = PS.renderSkillsAndCerts({
      skills: skillRows ? { rows: skillRows, editable: false, today: PS.skillsCertsToday(), note: 'No card needed. Ticked in Roster → Skills.' } : null,
      certs: requiredConfig.length ? {
        rows: requiredConfig.map(c => ({ itemId: c.itemId, label: c.label, ...requiredTrainingState(person, c) })),
        formatDate: formatDateShort,
        actionsHtml: (r) => {
          // TASK-1353 — tier-scoped: nothing to exempt.
          if (r.state === 'na' || !canJs) return '';
          // TASK-1049 — worded per-employee exemption / Undo.
          return r.state === 'exempt'
            ? `<button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.toggleTrainingExemptionCompliance('${jsq(personId)}','${jsq(r.itemId)}', false)" style="font-size:0.7rem;padding:2px 8px;">Undo</button>`
            : `<button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.toggleTrainingExemptionCompliance('${jsq(personId)}','${jsq(r.itemId)}', true)" style="font-size:0.7rem;padding:2px 8px;" title="Mark this training not applicable for this employee">Mark N/A</button>`;
        },
      } : null,
    });
    if (!body) return '';
    return `
    <div class="compliance-detail-section" id="compliance-skills-certs">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;color:#7c3aed;">verified_user</span> Skills &amp; certifications
      </div>
      <div class="compliance-detail-fields">${body}</div>
    </div>`;
  })();

  // Show any logged training entries that link to a now-archived/removed
  // required item as "no longer required" (do NOT delete the entry — spec).
  const activeIds = new Set(requiredConfig.map(c => c.itemId));
  const orphanLinked = trainings.filter(t => t.requiredTrainingId && !activeIds.has(t.requiredTrainingId));

  // Training certs section — with add/edit/delete + file upload
  const trainingSection = `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#7c3aed;">school</span> Training & Certifications
          ${trainings.length > 0 ? `<span class="compliance-badge compliance-badge-info" style="margin-left:4px;">${trainings.length}</span>` : ''}
        </div>
        <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.addTraining('${personId}')" style="font-size:0.72rem;padding:3px 8px;">
          <span class="material-icons" style="font-size:14px;">add</span> Add
        </button>
      </div>
      ${trainings.length > 0 ? `
        <div class="compliance-timeline">
          ${trainings.map((t, idx) => `
            <div class="compliance-timeline-item">
              <div class="compliance-timeline-icon">
                <span class="material-icons" style="font-size:16px;color:#7c3aed;">school</span>
              </div>
              <div class="compliance-timeline-content" style="flex:1;">
                <div style="display:flex;align-items:start;justify-content:space-between;">
                  <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${escapeHtml(t.trainingName || 'Untitled')}</div>
                  <div style="display:flex;gap:2px;flex-shrink:0;">
                    <button class="btn-icon" onclick="event.stopPropagation();window.ComplianceModule.editTraining('${personId}',${idx},'${escJsAttr(t.id || '')}')" title="Edit" style="padding:2px;">
                      <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">edit</span>
                    </button>
                    <button class="btn-icon" onclick="event.stopPropagation();window.ComplianceModule.deleteTraining('${personId}',${idx},'${escJsAttr(t.id || '')}')" title="Delete" style="padding:2px;">
                      <span class="material-icons" style="font-size:14px;color:#DC2626;">delete</span>
                    </button>
                  </div>
                </div>
                <div style="font-size:0.78rem;color:var(--text-secondary);">
                  ${t.organization ? escapeHtml(t.organization) + ' &bull; ' : ''}${t.issueDate ? formatDateShort(t.issueDate) : ''}${t.expiryDate ? ' — ' + formatDateShort(t.expiryDate) : ''}
                </div>
                ${(t.requiredTrainingId && !activeIds.has(t.requiredTrainingId)) ? `<div style="font-size:0.72rem;color:#92400E;margin-top:2px;display:flex;align-items:center;gap:3px;"><span class="material-icons" style="font-size:13px;">info</span> No longer a required training</div>` : ''}
                ${t.certCourseNumber ? `<div style="font-size:0.75rem;color:var(--text-secondary);">Cert #: ${escapeHtml(t.certCourseNumber)}</div>` : ''}
                ${t.expiryDate ? `<div style="font-size:0.75rem;margin-top:2px;">${getExpiryStatusText(t.expiryDate)}</div>` : ''}
                ${(t.scannedCopyPath || t.scannedCopyUrl) ? `<div style="display:flex;align-items:center;gap:10px;margin-top:3px;"><a href="#" onclick="event.preventDefault();window.viewComplianceDoc('${escJsAttr(t.scannedCopyPath || '')}','${escJsAttr(t.scannedCopyUrl || '')}','certificate');return false;" style="font-size:0.75rem;color:var(--brand-blue);display:inline-flex;align-items:center;gap:3px;cursor:pointer;">
                  <span class="material-icons" style="font-size:14px;">attach_file</span> View Certificate
                </a>${t.id ? `<a href="#" onclick="event.preventDefault();window.viewCredDocHistory('${personId}','training','${escapeHtml(t.id)}',this);return false;" title="All uploaded versions of this certificate" style="font-size:0.72rem;color:var(--text-secondary);display:inline-flex;align-items:center;gap:3px;cursor:pointer;"><span class="material-icons" style="font-size:14px;">history</span> History</a>` : ''}</div>` : ''}
              </div>
            </div>
          `).join('')}
        </div>
      ` : `<div style="padding:12px;color:var(--text-muted);font-size:0.85rem;text-align:center;">No training records on file</div>`}
    </div>
  `;

  // TASK-811 — MVR card. Mirrors HR's MVR section so a partner-managed NON-PAYING
  // carrier (HR paywalled) can record an existing pull (FREE, server-authored via
  // recordMvrPull) and request a new pull from their DOT partner — both
  // previously HR-only and unreachable behind the paywall.
  // TASK-1096 — matrix-gated: only render the DOT sections this classification
  // actually requires (previously all rendered unconditionally for everyone).
  const mvrSection = _dqItems.mvr ? buildComplianceMvrSection(person) : '';

  // TASK-1045 (818-S1) — §391.25 annual review + FMCSA Clearinghouse cards.
  // Mirror HR's person-doc data (same fields, single record); writes go through
  // the compliance-local save paths below.
  const annualReviewSection = _dqItems.annualReview ? buildComplianceAnnualReviewSection(person) : '';

  // TASK-1623 — §383.31 conviction notification. Gated on the MVR item because
  // §383.31 binds CDL holders; the block self-hides unless a violation flag is
  // live or a notification is already in flight / on file, so a clean driver's
  // card never grows a section that has nothing to say. Deliberately SEPARATE
  // from the annual review above: the driver's notification and the carrier's
  // review are two records with two signers (§383.31 vs §391.25(c)(2)), and only
  // the review note is a §391.51(b)(5) DQ-file requirement.
  const convictionNoticeSection = _dqItems.mvr ? _compConvictionNoticeBlock(person) : '';
  // TASK-1090 — the builder is classification-aware: CDL (or unclassified) gets
  // the actionable card; an explicit Non-CDL Driver gets a "Clearinghouse N/A"
  // state (you cannot run a Clearinghouse query on a non-CDL person — §382.701);
  // a Non-Driver returns '' (the TASK-1096 explainer covers it).
  const clearinghouseSection = buildComplianceClearinghouseSection(person);

  // TASK-832 — Background Check / criminal report section (hidden until the async
  // hydrate confirms a report exists on peopleBackgroundChecks/{personId}).
  // TASK-1096 — no matrix key for BG screening; hide for classified Non-Drivers
  // only (a non-CDL driver still legitimately carries MVR/criminal screening).
  const bgSection = _dqNonDriver ? '' : buildComplianceBgSection(person);

  // TASK-1096 — classified Non-Driver: the DOT sections above collapse into one
  // shared "DQ N/A" explainer (same chip + wording as the partner portal's
  // Core-Credential card and HR's DQ-file card). Company trainings/documents
  // below still apply and render normally.
  const dqNaExplainer = _dqNonDriver ? `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#94A3B8;">folder_special</span> DOT Driver Qualification
        </div>
        ${window.DqMatrix.nonDriverChipHtml('', _dqApp.positionType)}
      </div>
      <div style="padding:8px 4px;font-size:0.8rem;color:var(--text-secondary);line-height:1.5;">
        Classified <strong>${escapeHtml(window.DqMatrix.LABELS[_dqApp.positionType] || 'Non-Driver')}</strong> — DOT driver-qualification items (§391 / §382: medical card,
        MVR, FMCSA Clearinghouse, annual review, drug &amp; alcohol program, background screening) do not
        apply. Company required trainings still apply and are tracked below.
      </div>
    </div>` : '';

  // TASK-1103 — post-hire access to the completed §391.21 employment
  // application. person.candidateId (stamped by HR Approve-to-Roster) links to
  // the candidates doc; the modal itself is owned by HRModule (single source —
  // window.HRModule per pitfall #1). Renders only when a digital application
  // exists; the candidates read is rules-gated admin | partner | hasModule('hr'),
  // so a compliance-only member without HR/admin gets a clear toast from HR.
  const applicationSection = person.candidateId ? `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#2563EB;">description</span> Employment Application
        </div>
        <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.viewPersonApplication('${escapeHtml(personId)}')" style="font-size:0.72rem;padding:4px 10px;display:inline-flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:14px;">open_in_new</span> View / Print
        </button>
      </div>
      <div style="padding:6px 4px 2px;font-size:0.78rem;color:var(--text-secondary);line-height:1.5;">
        Completed DispatchDeck application (FMCSR 391.21) from hiring — opens the full application; print or save as PDF from there.
      </div>
      <!-- TASK-1099 — §391.23 Safety Performance History Investigation (per-employer
           contact status, response capture, N/A + Restore, printable sheet). The
           pop-out is the shared HRModule modal keyed by person.candidateId. -->
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;padding:8px 4px 2px;border-top:1px solid var(--border-light);margin-top:8px;">
        <div style="font-size:0.78rem;color:var(--text-secondary);line-height:1.5;">
          <strong style="color:var(--text-primary);">Safety Performance History Investigation</strong> — 49 CFR §391.23 prior-employer inquiries: who's been contacted, what they reported, N/A exemptions, printable status sheet.
        </div>
        <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.openPersonPeiInvestigation('${escapeHtml(personId)}')" style="font-size:0.72rem;padding:4px 10px;display:inline-flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:14px;">travel_explore</span> Open Investigation
        </button>
      </div>
    </div>` : '';

  // DVIR + JSA accountability (full management)
  const dvirSection = buildDvirJsaSection(person);

  // TASK-1144 — DQ File completeness score card + §-grouped regulatory checklist,
  // via the shared window.PersonSections.renderDqScorecard (renderer renders /
  // host authorizes; window.* global read at call time, never a cross-?v= import
  // — pitfall #1). Brings the carrier compliance driver detail into visual +
  // scoring parity with HR (previously the ONLY surface showing the rollup DQ
  // score + four-file grouping — the TASK-1096 drift-bug class). Host-supplied
  // inputs: the already-resolved _dqApp; the pre-employment drug-test flag off
  // ComplianceState.drugTests (the CARRIER's store, not HRState); the carrier's
  // formatted last-audit date. runAuditOnClick is null — the carrier has no
  // runDqFileAudit callable, so the "Run AI Audit" button is omitted (the host
  // authorizes the affordance by choosing whether to pass a callable). Skipped
  // for classified Non-Drivers: dqNaExplainer above already renders their
  // "DQ N/A" card, so calling the renderer's Non-Driver branch too would
  // dual-render (COD UX note — dispatchers would read it as duplicated/broken).
  // TASK-1304 (guardrail 3) — §395.8(j)(2) 7-day on-duty statement DQ flag
  // (missing / requested / on-file + stale-aware), via the shared renderer so it
  // reads identically here, in HR, and (mirrored) in the partner portal. Drivers
  // only — a Non-Driver logs no HOS.
  const sevenDaySection = _dqNonDriver
    ? ''
    : window.PersonSections.renderSevenDayStatementFlag(person);

  // TASK-9458 — built by _compDqScorecardHtml (above) so hydrateComplianceBgReport
  // can re-render it once the bg doc lands; wrapped so it has a slot to replace.
  const dqScorecardSection = _dqNonDriver
    ? ''
    : `<div id="comp-dq-scorecard-${escapeHtml(personId)}">${_compDqScorecardHtml(person, _dqApp)}</div>`;

  // TASK-9438 — CDLIS row on the driver card. The carrier host lives in hr.js
  // (window.HRModule, Pitfall #1) so HR and Compliance share one gate and one
  // data path: it returns '' for a viewer who may not read the candidate record
  // (a compliance-module-only member never sees CDLIS here), and reads the
  // record from candidates/{person.candidateId}, never from the people doc.
  // TASK-9698 — every CMV driver (CDL + Non-CDL Driver), same gate as hr.js.
  const cdlisCardSection = (window.DqMatrix.hasDqFile(person) && window.HRModule && typeof window.HRModule.hrCdlisCardHtml === 'function')
    ? window.HRModule.hrCdlisCardHtml(person, ComplianceState.tenantId)
    : '';

  modal.innerHTML = `
    <div class="modal-content" data-detail="person" style="max-width:560px;max-height:90dvh;overflow-y:auto;">
      <div class="compliance-detail-header ${_compDetailHeaderClass(person, status)}">
        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div>
            <div style="display:flex;align-items:center;gap:8px;">
              <h3 style="margin:0;font-size:1.1rem;font-weight:700;color:var(--text-primary);">${escapeHtml(person.name || 'Unknown')}</h3>
              ${isCdl ? '<span class="compliance-type-badge compliance-type-truck">CDL</span>' : ''}
              ${isNonDriver ? window.DqMatrix.nonDriverChipHtml('', _ptExplicitDetail) : ''}
              ${person.flagged === true ? `<span class="compliance-badge compliance-badge-critical" title="${escapeHtml(formatFlagTooltip(person))}">🚩 Flagged</span>` : ''}
            </div>
          </div>
          <button class="btn-icon" onclick="window.ComplianceModule.closeDetail()" title="Close">
            <span class="material-icons">close</span>
          </button>
        </div>
      </div>
      <div style="padding:var(--spacing-md);">
        ${_buildCompFlagReasonCalloutHTML(person)}
        ${_buildSeparationSectionHTML(person)}
        ${cdlSection}
        ${dqNaExplainer}
        ${_dqItems.medCard ? (window.DqMatrix.intrastateNoteHtml(person, { fmtDate: formatDateShort }) + medSection) : (_dqApp.intrastateExemption ? `<div class="compliance-section-card"><div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:18px;color:#059669;">medical_services</span> Medical Card <span class="compliance-cite-badge">49 CFR §391.43</span></div>${window.DqMatrix.intrastateNoteHtml(person, { fmtDate: formatDateShort })}<div style="font-size:0.75rem;color:var(--text-secondary);">Set or remove it from HR → the person's Compliance tab.</div></div>` : '')}
        ${mvrSection}
        ${annualReviewSection}
        ${convictionNoticeSection}
        ${bgSection}
        ${renderDrugTestSection(personId)}
        ${renderDaPolicyReceiptSection(person)}
        ${clearinghouseSection}
        ${sevenDaySection}
        ${applicationSection}
        ${isnetSection}
        ${dqScorecardSection}
        ${cdlisCardSection}
        ${requiredSection}
        ${trainingSection}
        ${dvirSection}
        <div class="compliance-detail-section">
          <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
            <div style="display:flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;color:#7c3aed;">folder</span> Driver Documents
            </div>
            ${(_compIsOwnerOrAdmin() && !_dqNonDriver) ? `
            <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.downloadFullDqFile('${escapeHtml(personId)}')" title="Download every uploaded DQ document as a ZIP (49 CFR §391.51). Contains SSN/DOB/MVR/drug-test PII — owner/admin only." style="font-size:0.72rem;padding:4px 10px;display:inline-flex;align-items:center;gap:4px;">
              <span class="material-icons" style="font-size:14px;">folder_zip</span> Download Full DQ File (ZIP)
            </button>` : ''}
          </div>
          <!-- TASK-1046 — DQ-only upload affordance (818-S2). Compliance-module
               members can add a driver-qualification doc without HR access. The
               category picker offers ONLY the 10 DQ categories; firestore.rules
               enforces the same allowlist server-side (no I-9/W-4/tax/onboarding). -->
          <div style="display:flex;align-items:flex-end;gap:8px;flex-wrap:wrap;padding:4px 0 8px;">
            <label style="display:flex;flex-direction:column;gap:2px;font-size:0.68rem;color:var(--text-secondary);">
              Document type
              <select id="compliance-dq-doc-category" style="font-size:0.75rem;padding:5px 8px;border:1px solid var(--border-light);border-radius:6px;background:#fff;color:var(--text-primary);min-width:200px;">
                <option value="">Select DQ category…</option>
                ${_cpDqCategoryOptionsHtml()}
              </select>
            </label>
            <button class="btn btn-sm btn-secondary" id="btn-compliance-dq-upload" onclick="window.ComplianceModule.uploadDqDocument('${escapeHtml(personId)}')" title="Upload a driver-qualification document (PDF/image, max 10MB) — filed to this driver's DQ file." style="font-size:0.72rem;padding:6px 12px;display:inline-flex;align-items:center;gap:4px;">
              <span class="material-icons" style="font-size:14px;">upload_file</span> Upload DQ Document
            </button>
          </div>
          <div id="compliance-person-docs" style="padding-top:6px;">
            <div style="font-size:0.75rem;color:var(--text-muted);padding:6px 0;">Loading documents…</div>
          </div>
        </div>
        <div style="display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap;padding:var(--spacing-sm) 0;">
          ${''/* TASK-9424 — ADD only; each flag clears from its own Clear… in the flag panel. */}
          <button class="btn btn-sm" onclick="window.ComplianceModule.addDriverFlag('${escJsAttr(personId)}')" style="background:transparent;border:1.5px solid #B45309;color:#B45309;font-weight:600;display:inline-flex;align-items:center;gap:6px;padding:6px 14px;" title="Flag this driver as needing attention / follow-up — a driver can carry more than one flag">
            <span class="material-icons" style="font-size:16px;">outlined_flag</span> ${person.flagged === true ? 'Add a flag' : 'Flag driver'}
          </button>
        </div>
        ${(!complianceOnly && window.HRModule?.ensureSubjectInHrState) ? `
        <div style="display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap;padding:var(--spacing-sm) 0;">
          <button class="btn btn-sm" onclick="window.ComplianceModule.generateSphResponse('${escapeHtml(personId)}')" style="background:transparent;border:1.5px solid #0F766E;color:#0F766E;font-weight:600;display:inline-flex;align-items:center;gap:6px;padding:6px 14px;" title="Another carrier asked you for this driver's records? Generates a pre-filled §391.23/§40.25 verification DRAFT to review and sign. (Not the §382.601(d) D&amp;A policy receipt — that's under Policy Receipt.)">
            <span class="material-icons" style="font-size:16px;">assignment_turned_in</span> Employment/Safety Verification
          </button>
          ${isCdl ? `
          <button class="btn btn-sm" onclick="window.ComplianceModule.openDqPacketViewer('${escapeHtml(personId)}')" style="background:transparent;border:1.5px solid #0F766E;color:#0F766E;font-weight:600;display:inline-flex;align-items:center;gap:6px;padding:6px 14px;" title="Generate insurance-ready driver qualification packet">
            <span class="material-icons" style="font-size:16px;">folder_special</span> Generate DQ Packet
          </button>` : ''}
        </div>` : ''}
        ${complianceOnly ? '' : `
        <div style="padding:var(--spacing-sm) 0;text-align:center;">
          <a href="#/hr" class="compliance-source-link" onclick="window.ComplianceModule.closeDetail(); setTimeout(() => window.HRModule?.openEmployeeDetail?.('${personId}'), 500);">
            <span class="material-icons" style="font-size:16px;">edit</span> Edit in HR
          </a>
        </div>`}
      </div>
    </div>
  `;

  modal.classList.remove('hidden');

  // TASK-4605 Slice 2 — wire the shared licence card's Upload/Remove/View
  // affordances via real functions (bindLicenseImages contract; nothing is
  // stringified so nothing can be parsed as code). Idempotent per DOM element,
  // but this modal's innerHTML is freshly rebuilt above on every call, so a
  // fresh, unbound card is always what gets bound here.
  window.PersonSections.bindLicenseImages(modal, {
    onOpen: (path) => window.viewComplianceDoc(path, '', 'drivers-license'),
    onUpload: (credType) => uploadDlDocument(personId, credType),
    onDelete: (credType) => removeDlDocument(personId, credType),
  });

  // TASK-1207 — fill the flag-reason callout (owner/admin only; fire-and-forget,
  // never blocks the modal).
  _hydrateCompFlagReason(person);

  // TASK-832 — async-hydrate the "View report" button + BG section once the modal
  // is shown (fire-and-forget; fails closed to no button on denial).
  // TASK-1096 — skip for classified Non-Drivers: the BG/MVR placeholders it
  // injects into are not rendered for that tier.
  if (!_dqNonDriver) hydrateComplianceBgReport(personId);

  // TASK-970 — lazy-load this driver's uploaded documents (people/{id}/documents)
  // into the fixed-category browser. Fire-and-forget; failure leaves a graceful
  // "could not load" message, never blocks the modal.
  loadCompliancePersonDocs(personId);
}

// TASK-970 — load + render the fixed-category driver-document browser for the
// compliance driver detail. Read-only view (View on click; no re-tag/delete —
// those stay in HR). Groups by the SAME fixed regulatory taxonomy as hr.js via
// window.HRModule.renderDqDocBrowserHtml (statically imported, so it exists even
// for compliance-only carriers — no ?v= cross-import, Pitfall #1).
//
// PII SCOPE (COD Adversary/Blast 🔴): the FETCH is category-filtered at the QUERY
// layer (where('category','in', DQ categories)) so HR onboarding (I-9/W-4/direct-
// deposit = 'general'), OSHA-medical/exposure, and untyped/legacy docs NEVER enter
// the browser on the compliance surface — not just hidden at render. Only the FMCSA
// 391.x / 49 CFR 382 driver-qualification categories are pulled. The HR surface
// (owner/admin/hr-directory) still shows every category incl. Miscellaneous.
// TASK-1623 — `conviction_notice` (the driver's signed §383.31 notification) is
// in the FETCH list so the filed record is visible here, but deliberately NOT in
// the LABELS map below, which is the upload picker. It is SERVER-AUTHORED ONLY:
// the only writer is submitConvictionNotice, after a driver signed it on a
// tokenized link. Leaving it out of the picker (and out of the firestore.rules
// client-create allowlist, which is unchanged) means no COMPLIANCE-module member
// can hand-tag an arbitrary file as a driver-signed federal notification.
// ⚠️ It is not yet a hard boundary for admin/HR members: the other legs of that
// rule carry no category fence (a pre-existing gap shared by every DQ category
// — see the note on hr.js DQ_DOC_CATEGORIES). Convention here, not proof.
// 🔴 It is NOT `mvr_annual_review`: that is the CARRIER's §391.25(c)(2) review
// note, a different record with a different signer.
const _CP_DQ_DOC_CATEGORIES = [
  'cdl', 'mec', 'mvr', 'mvr_annual_review', 'employment_app',
  'road_test', 'prev_employer_safety', 'training', 'drug_alcohol', 'clearinghouse', 'on_duty_hours',
  'conviction_notice',
  // TASK-1458 — the partner upload picker's "Other: <label>" filing. Fetch-and-
  // display only (same shape as conviction_notice): server-written by
  // uploadPersonComplianceDoc, deliberately absent from the LABELS map (picker)
  // and from the firestore.rules compliance create allowlist.
  'other',
];
// TASK-1046 — picker labels for the DQ-only upload affordance. Keys are the
// SINGLE SOURCE (_CP_DQ_DOC_CATEGORIES above); labels mirror hr.js
// DQ_DOC_CATEGORIES (hr.js:8342-8351) so a doc uploaded here reads identically
// in the HR Documents tab and this browser. These 10 keys ARE the server-side
// allowlist in firestore.rules (people/{id}/documents create) — a compliance
// member can create ONLY these categories, never an I-9/W-4/tax/onboarding doc.
const _CP_DQ_DOC_CATEGORY_LABELS = {
  cdl: 'CDL / License',
  mec: "Medical Examiner's Certificate",
  mvr: 'Motor Vehicle Record',
  mvr_annual_review: 'Annual MVR Review',
  employment_app: 'Employment Application',
  road_test: 'Road Test / Certificate',
  prev_employer_safety: 'Prev. Employer Safety History',
  training: 'Training Certificates',
  drug_alcohol: 'Drug / Alcohol Records',
  clearinghouse: 'Clearinghouse Query',
  on_duty_hours: '7-Day On-Duty Statement',
};
// TASK-1623 — the picker now iterates the LABELS map, not the fetch list. Those
// two used to be the same set; they no longer are, because `conviction_notice`
// is fetch-and-display only (server-authored). Driving the picker off the fetch
// list would have rendered a raw-key option that firestore.rules then denies.
function _cpDqCategoryOptionsHtml() {
  return Object.keys(_CP_DQ_DOC_CATEGORY_LABELS)
    .map(k => `<option value="${k}">${escapeHtml(_CP_DQ_DOC_CATEGORY_LABELS[k] || k)}</option>`)
    .join('');
}
async function loadCompliancePersonDocs(personId) {
  const container = document.getElementById('compliance-person-docs');
  if (!container) return;
  try {
    const tenantId = ComplianceState.tenantId;
    // Query-level category filter — non-DQ docs are never fetched to the client.
    // No orderBy (would need a composite index with the 'in' filter); sort below.
    const snap = await getDocs(query(
      collection(db, 'tenants', tenantId, 'people', personId, 'documents'),
      where('category', 'in', _CP_DQ_DOC_CATEGORIES)
    ));
    // Guard against a stale render if the user already moved to another driver.
    if (ComplianceState.selectedPersonId !== personId) return;
    const c2 = document.getElementById('compliance-person-docs');
    if (!c2) return;
    const _ms = (t) => {
      try {
        if (t && typeof t.toMillis === 'function') return t.toMillis();
        if (t && typeof t.toDate === 'function') return t.toDate().getTime();
        if (t instanceof Date) return t.getTime();
        const n = new Date(t).getTime();
        return isNaN(n) ? 0 : n;
      } catch (_) { return 0; }
    };
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => _ms(b.uploadedAt) - _ms(a.uploadedAt));
    if (!docs.length) {
      c2.innerHTML = `<div style="font-size:0.75rem;color:var(--text-muted);padding:6px 0;">No driver-qualification documents on file. Add DQ documents from this driver's HR file.</div>`;
      return;
    }
    const person = ComplianceState.people.find(p => p.id === personId) || null;
    // dqOnly:true — scope to FMCSA/382 DQ-file categories only (no HR onboarding /
    // OSHA-medical / untyped PII docs on the compliance surface).
    const html = window.HRModule?.renderDqDocBrowserHtml?.(docs, person, { dqOnly: true });
    if (html) {
      c2.innerHTML = html;
    } else {
      // Fallback (HRModule not yet loaded) — TASK-1029: rows are PATH-ONLY now
      // (no persisted bearer url), so open via the HR/compliance-gated
      // getComplianceDocBytes byte-stream (data attribute + delegated handler —
      // never interpolate a stored value into a JS string). Legacy url rows
      // (backfill-unresolvable only) keep the scheme-validated href.
      const safeHref = u => {
        const s = String(u || '').trim();
        return (/^https?:\/\//i.test(s) && !/["'<>`\s]/.test(s)) ? s : '';
      };
      const escAttr = s => String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      c2.innerHTML = docs.map(d => {
        const p = (typeof d.storagePath === 'string' && d.storagePath.trim() && !d.storagePath.includes('..')) ? d.storagePath.trim() : '';
        if (p) {
          return `<div style="padding:6px 0;font-size:0.8rem;"><a href="#" data-doc-path="${escAttr(p)}" onclick="event.preventDefault(); window.ComplianceModule.openDocRowBytes(this)" style="color:var(--brand-blue);">${escapeHtml(d.fileName || 'Document')}</a></div>`;
        }
        const h = safeHref(d.url);
        return `<div style="padding:6px 0;font-size:0.8rem;">${h ? `<a href="${escapeHtml(h)}" target="_blank" rel="noopener" style="color:var(--brand-blue);">${escapeHtml(d.fileName || 'Document')}</a>` : escapeHtml(d.fileName || 'Document')}</div>`;
      }).join('');
    }
  } catch (err) {
    console.error('[Compliance] Error loading person documents:', err);
    const c3 = document.getElementById('compliance-person-docs');
    if (c3) c3.innerHTML = `<div style="font-size:0.75rem;color:var(--text-muted);padding:6px 0;">Could not load documents.</div>`;
  }
}

// TASK-1046 (818-S2) — upload a DQ document from the compliance doc browser.
// Compliance-MODULE members previously had READ-only access here (TASK-970);
// this lets them ADD a driver-qualification doc without HR access. The category
// is constrained to the 10 DQ categories both client-side (the picker only
// offers _CP_DQ_DOC_CATEGORIES) and server-side (firestore.rules people/{id}/
// documents create allowlist) — never an I-9/W-4/tax/onboarding doc.
//
// Storage: uploads to the SAME hr/{tid}/{pid}/... path hr.js uploadEmployeeDocument
// uses (hr.js:8643) — governed by storage.rules /hr/{tid}/{eid}/ (canAccessHrFile,
// which any logged-in non-driver member passes, so NO storage rule change needed).
// The doc field shape MIRRORS hr.js EXACTLY (path-only per TASK-1029, no bearer
// url) so the row renders identically in the HR Documents tab and this browser.
//
// ORPHAN-BLOB (COD lesson TASK-1048): storage upload happens BEFORE the addDoc.
// The compliance member now HAS the create leg so the addDoc won't be denied; if
// it fails for another reason we best-effort deleteObject the just-uploaded blob
// (the /hr/{tid}/{eid}/ path allows client delete via canAccessHrFile, so this is
// a REAL cleanup, not a non-functional guard).
let _cpDqUploadInFlight = false;
function _cpInferContentType(file) {
  if (file && file.type) return file.type;
  const ext = String(file?.name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  return 'application/octet-stream';
}
// TASK-9449 — a managing compliance partner inside this carrier's app. Its
// uploads and Clearinghouse saves go through the server paths hr.js owns
// (window.HRModule.partner* — uploadPersonComplianceDoc / clearinghouse.logQuery),
// never a client Storage write. null for every carrier member (unchanged path).
function _compPartnerTenantId() {
  try {
    if (typeof window.isPartnerClientSession !== 'function' || !window.isPartnerClientSession()) return null;
    const t = window.AppState?.partnerClientContext?.tenantId;
    return (typeof t === 'string' && t) ? t : null;
  } catch (_) { return null; }
}
// ── TASK-10154 — partner Identity & License on this carrier view ───────────
// A managing partner has no rules grant on peopleIdentity (TASK-9359), so
// window.PersonIdentity reports "cannot know" and the CDL section used to print
// "Restricted". Chris 2026-10-27: the partner sees DOB / DL number / state /
// expiry here, masked with an eye to reveal, and the Edit form saves them —
// all through the server callable partnerPersonIdentity (firm fence + audit
// row per call; the reveal and every save are audited). Nothing below reads or
// writes the vault directly, and nothing runs for a carrier member
// (_compPartnerTenantId() is null for them).
//   byPerson: personId -> { status: 'loading'|'ready'|'error', masked }
//   edit:     personId -> full values fetched (audited, purpose 'edit') when the
//             partner opens the Edit form; dropped on save / cancel.
const _CompPartnerId = { tenantId: null, byPerson: new Map(), edit: new Map(), editGen: 0, editPending: false };
function _compPartnerIdCall(payload) {
  return httpsCallable(functions, 'partnerPersonIdentity')(Object.assign({ tenantId: _compPartnerTenantId() }, payload));
}
function _compPartnerIdReset(tid) {
  if (_CompPartnerId.tenantId !== tid) {
    _CompPartnerId.tenantId = tid;
    _CompPartnerId.byPerson = new Map();
    _CompPartnerId.edit = new Map();
  }
}
// The masked entry for one person, fetching it once on first need. The
// re-render after the fetch only happens when that person's detail is still open.
function _compPartnerIdEntry(personId) {
  const tid = _compPartnerTenantId();
  if (!tid || !personId) return null;
  _compPartnerIdReset(tid);
  const have = _CompPartnerId.byPerson.get(personId);
  // A failed read is retried on a later render, but not in a tight loop (the
  // re-render below would otherwise refetch immediately on every failure).
  if (have && !(have.status === 'error' && Date.now() - (have.at || 0) > 30000)) return have;
  const entry = { status: 'loading', masked: null, at: Date.now() };
  _CompPartnerId.byPerson.set(String(personId), entry);
  _compPartnerIdCall({ personId, op: 'read' }).then((res) => {
    if (_CompPartnerId.tenantId !== tid) return;
    entry.status = 'ready';
    entry.masked = (res && res.data && res.data.masked) || null;
  }).catch((err) => {
    if (_CompPartnerId.tenantId !== tid) return;
    console.warn('[Compliance] partner identity read failed:', (err && err.code) || err);
    entry.status = 'error';
    entry.at = Date.now();
  }).finally(() => {
    const modal = document.getElementById('compliance-detail-modal');
    // Only repaint the person detail itself — never over another form (Add
    // Test etc.) that reused the modal while this read ran (COD r3).
    // …and never while that person's Edit form is open (typed values would be
    // wiped; the display rows are not on screen then anyway) — COD r4.
    if (ComplianceState.selectedPersonId === personId && modal && !modal.classList.contains('hidden')
        && ComplianceState.editingDriverDetailsId !== personId
        && modal.querySelector('.modal-content[data-detail="person"]')) showPersonDetail(personId);
  });
  return entry;
}
async function _compPartnerIdReveal(personId, field, purpose) {
  const tid = _compPartnerTenantId();
  if (!tid) return null;
  try {
    const res = await _compPartnerIdCall({ personId, op: 'reveal', purpose: purpose || 'view' });
    if (_compPartnerTenantId() !== tid) return null;
    const v = (res && res.data && res.data.values) || null;
    if (!v) return null;
    return field ? (v[field] || null) : v;
  } catch (err) {
    console.warn('[Compliance] partner identity reveal failed:', (err && err.code) || err);
    window.showToast?.(_compPartnerErrText(err, 'Could not show that — try again.'), 'error');
    return null;
  }
}
// The four detail rows for a partner session (display mode).
function _compPartnerIdRows(personId) {
  const e = _compPartnerIdEntry(personId);
  if (!e || e.status === 'loading') {
    return ['Date of Birth', 'DL Number', 'DL State', 'DL Expiry'].map((l) => detailRow(l, '<span style="color:var(--text-secondary);">Loading…</span>')).join('');
  }
  if (e.status === 'error' || !e.masked) {
    return ['Date of Birth', 'DL Number', 'DL State', 'DL Expiry'].map((l) => detailRow(l, '<span style="color:var(--text-secondary);">Unavailable — reopen shortly to retry</span>')).join('');
  }
  const m = e.masked;
  const sv = window.SensitiveValue;
  const maskedRow = (label, field, noun) => {
    if (!m[field]) return detailRow(label, 'Not on file');
    if (!sv) return detailRow(label, escapeHtml(m[field])); // module missing: mask only, never the value
    return detailRow(label, sv.html({ masked: m[field], kind: 'id', noun: noun + ' (recorded in the audit log)', fetchFull: () => _compPartnerIdReveal(personId, field, 'view') }));
  };
  return maskedRow('Date of Birth', 'dob', 'date of birth')
    + maskedRow('DL Number', 'dlNumber', 'licence number')
    + detailRow('DL State', m.dlState ? escapeHtml(m.dlState) : 'N/A')
    // formatDateShort returns a non-date string RAW, and the vault rules allow any
    // string ≤40 — escape it (COD 10154 r1: an HTML sink otherwise).
    + detailRow('DL Expiry', escapeHtml(formatDateShort(m.dlExpiryDate)) || 'N/A')
    + (m.dlExpiryDate ? detailRow('DL Status', getExpiryStatusText(m.dlExpiryDate)) : '');
}
// Partner Edit: fetch the full values first (audited, purpose 'edit') so the
// form opens pre-filled; on failure the identity inputs stay locked. Returns
// false when the fetch was superseded (closed / another Edit) — the caller must
// then NOT open the form (COD 10154 r1: a late reveal reopened a closed file).
async function _compPartnerIdBeginEdit(personId) {
  _CompPartnerId.edit.delete(personId);
  const gen = ++_CompPartnerId.editGen;
  const v = await _compPartnerIdReveal(personId, null, 'edit');
  if (gen !== _CompPartnerId.editGen) return false;
  if (v && typeof v === 'object') _CompPartnerId.edit.set(String(personId), v);
  return true;
}
// A value an <input type="date"> can display: a REAL calendar day as YYYY-MM-DD
// (2020-02-31 matches the shape but the control blanks it — COD r2).
function _compIsInputDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() + 1 === +m[2] && d.getUTCDate() === +m[3];
}
// Save the identity half of the partner's Edit form. Returns true when nothing
// failed. `next` holds only the keys the form sent (null = cleared).
async function _compPartnerIdSave(personId, next) {
  const before = _CompPartnerId.edit.get(personId);
  if (!before) return true; // inputs were locked — nothing to save
  const updates = {};
  const expected = {};
  ['dob', 'dlNumber', 'dlState', 'dlExpiryDate'].forEach((k) => {
    if (!Object.prototype.hasOwnProperty.call(next, k)) return;
    const a = String(before[k] == null ? '' : before[k]);
    const b = String(next[k] == null ? '' : next[k]);
    // A stored date that is not YYYY-MM-DD cannot show in a date input, so its
    // blank is not a clear (COD 10154 r1) — leave it alone.
    if ((k === 'dob' || k === 'dlExpiryDate') && a && !_compIsInputDate(a) && !b) return;
    // dlState is saved upper-cased; 'tx' → 'TX' alone is not a change (COD r3).
    const same = k === 'dlState' ? a.toUpperCase() === b.toUpperCase() : a === b;
    if (!same) { updates[k] = b; expected[k] = a; }
  });
  if (!Object.keys(updates).length) return true;
  // `expected` = what the form opened with; the server refuses if the carrier
  // changed it meanwhile (no silent overwrite of a newer value).
  const res = await _compPartnerIdCall({ personId, op: 'write', updates, expected });
  const m = res && res.data && res.data.masked;
  if (m) _CompPartnerId.byPerson.set(String(personId), { status: 'ready', masked: m });
  else _CompPartnerId.byPerson.delete(personId);
  return true;
}

function _compPartnerApi(name) {
  const fn = window.HRModule && window.HRModule[name];
  if (typeof fn !== 'function') throw new Error('This screen needs a refresh before you can save — reload the page and try again.');
  return fn;
}
// TASK-4748 — who may LOG a Clearinghouse query from this screen: 'partner' |
// 'carrier' | 'readonly'. The table lives in hr.js (clearinghouseLogMode) so both
// carrier surfaces draw the section from ONE mapping; reached via
// window.HRModule, never a cross-import (Pitfall #1). If the helper is missing
// (stale bundle) the answer is 'readonly' — never draw a form we cannot save;
// the save path already refuses on the same missing-helper condition.
function _compClearinghouseLogMode() {
  const fn = window.HRModule && window.HRModule.clearinghouseLogMode;
  if (typeof fn !== 'function') return 'readonly';
  const S = window.AppState || {};
  return fn(S.memberAccess, _compPartnerTenantId(), S.isDemo === true || S.tenant === 'demo');
}
// TASK-1320 — Break-in-Service work history. Missing helper (stale bundle) →
// no button, never a form we cannot save.
function _compCanRecordBisGap() {
  const fn = window.HRModule && window.HRModule.hrCanRecordBisGap;
  return typeof fn === 'function' ? fn() === true : false;
}
async function openBisGapAccount(personId) {
  const fn = window.HRModule && window.HRModule.openBisGapAccount;
  if (typeof fn !== 'function') { window.showToast?.('This screen needs a refresh — reload the page and try again.', 'error'); return; }
  await fn(personId, ComplianceState.people, (id) => showPersonDetail(id), _compPartnerTenantId() || ComplianceState.tenantId);
}
function _compClearinghouseReadonlyMsg() {
  return (window.HRModule && window.HRModule.CLEARINGHOUSE_READONLY_MSG) || 'View only — ask an owner or admin to log this query.';
}
function _compPartnerErrText(err, fallback) {
  return (_compPartnerTenantId() && err?.message) ? String(err.message).replace(/^internal:\s*/, '') : fallback;
}
async function uploadDqDocument(personId) {
  if (_cpDqUploadInFlight) return;
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) { window.showToast?.('Open a driver first.', 'error'); return; }

  const categorySel = document.getElementById('compliance-dq-doc-category');
  const category = categorySel?.value || '';
  // Client-side guard mirrors the server allowlist — never send a non-DQ category.
  if (!_CP_DQ_DOC_CATEGORIES.includes(category)) {
    window.showToast?.('Pick a document type first.', 'error');
    return;
  }

  let input = document.getElementById('compliance-dq-doc-file-input');
  if (!input) {
    input = document.createElement('input');
    input.type = 'file';
    input.id = 'compliance-dq-doc-file-input';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(input);
  }
  input.value = '';

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) { window.showToast?.('File too large. Max 10MB.', 'error'); return; }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
      window.showToast?.('Only PDF, JPG, and PNG files allowed.', 'error');
      return;
    }

    _cpDqUploadInFlight = true;
    const btn = document.getElementById('btn-compliance-dq-upload');
    if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Uploading…'; }

    let storageRef = null;
    try {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      // SAME convention as hr.js uploadEmployeeDocument (hr.js:8643).
      const storagePath = `hr/${tenantId}/${personId}/${timestamp}_${safeName}`;
      storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: _cpInferContentType(file) });

      const userName = window.AppState?.userName || 'Unknown';
      try {
        // addDoc field shape MIRRORS hr.js uploadEmployeeDocument EXACTLY
        // (path-only, no bearer url — TASK-1029). category is a picked DQ value.
        await addDoc(
          collection(db, 'tenants', tenantId, 'people', personId, 'documents'),
          {
            fileName: file.name,
            storagePath, // TASK-1029 — path-only; view via getComplianceDocBytes
            mimeType: file.type,
            isPdf: file.type === 'application/pdf',
            sizeBytes: file.size,
            category,
            uploadedAt: Timestamp.now(),
            uploadedBy: userName
          }
        );
      } catch (addErr) {
        // Prevent an orphan blob: the /hr/{tid}/{pid}/ path allows client delete
        // (canAccessHrFile), so this cleanup actually runs.
        try { await deleteObject(storageRef); } catch (_) { /* best-effort */ }
        throw addErr;
      }

      await loadCompliancePersonDocs(personId);
      if (categorySel) categorySel.value = '';
      window.showToast?.('DQ document uploaded', 'success');
    } catch (err) {
      console.error('[Compliance] DQ document upload failed:', err);
      window.showToast?.(err?.message || 'Upload failed. Try again.', 'error');
    } finally {
      _cpDqUploadInFlight = false;
      const b = document.getElementById('btn-compliance-dq-upload');
      if (b) { b.disabled = false; b.innerHTML = '<span class="material-icons" style="font-size:14px;">upload_file</span> Upload DQ Document'; }
    }
  };

  input.click();
}

// TASK-970 — carrier-side "Download Full DQ File (ZIP)" from the compliance
// driver detail. Same EXISTING generateDqFilePacket callable as the partner
// portal + HR surface (server-gated by requireTenantAdminOrPartner). Full ZIP =
// SSN/DOB/MVR/drug-test PII, so OWNER/ADMIN-only client-side (_compIsOwnerOrAdmin),
// matching the server gate — never module-based. In-flight guard prevents a
// double-generate. Mirrors compliance-partner.js generateDqFileFromPartner.
let _cpDqFileInFlightCompliance = false;
async function downloadFullDqFileCompliance(personId) {
  if (!_compIsOwnerOrAdmin()) { showToast('Owner or admin access required.', 'error'); return; }
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) { showToast('Open a driver first.', 'error'); return; }
  if (_cpDqFileInFlightCompliance) return;
  _cpDqFileInFlightCompliance = true;

  const person = ComplianceState.people.find(p => p.id === personId) || {};
  let modal = document.getElementById('compliance-dqfile-modal');
  if (!modal) { modal = document.createElement('div'); modal.id = 'compliance-dqfile-modal'; document.body.appendChild(modal); }
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:10050;display:flex;align-items:center;justify-content:center;padding:16px;';
  const close = () => { if (modal && modal.parentNode) modal.parentNode.removeChild(modal); _cpDqFileInFlightCompliance = false; };
  modal.innerHTML = `
    <div style="background:#fff;border-radius:var(--radius-lg,12px);max-width:460px;width:100%;max-height:90dvh;overflow-y:auto;box-shadow:0 10px 40px rgba(0,0,0,0.25);">
      <div style="padding:14px 16px;border-bottom:1px solid var(--border-light);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="color:var(--brand-blue);">folder_zip</span>
        <h3 style="margin:0;font-size:1.05rem;font-weight:700;flex:1;color:var(--text-primary);">Full DQ File</h3>
        <button class="btn-icon" id="compliance-dqfile-x" title="Close" style="padding:2px;"><span class="material-icons">close</span></button>
      </div>
      <div id="compliance-dqfile-body" style="padding:16px;color:var(--text-secondary);font-size:0.85rem;line-height:1.5;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">hourglass_empty</span> Assembling ${escapeHtml(person.name || 'this driver')}'s complete §391.51 file — this can take up to a minute…
      </div>
    </div>`;
  const body = () => document.getElementById('compliance-dqfile-body');
  document.getElementById('compliance-dqfile-x')?.addEventListener('click', close);

  try {
    const res = await httpsCallable(functions, 'generateDqFilePacket')({ tenantId, personId });
    const out = (res && res.data) || {};
    // scheme-validate the callable's download URL (same guard as doc URLs)
    const dlUrl = /^https?:\/\//i.test(String(out.downloadUrl || '').trim()) ? String(out.downloadUrl).trim() : '';
    const scoreLine = (out.checklistScore != null)
      ? `<div style="color:#047857;margin-top:6px;">Part 391 + 382 completeness: ${out.checklistScore}%${out.checklistMissing ? ` — ${out.checklistMissing} item${out.checklistMissing === 1 ? '' : 's'} missing` : ' — complete'}</div>` : '';
    if (body()) body().innerHTML = `
      <div style="font-weight:700;color:#065F46;margin-bottom:6px;">DQ file ready</div>
      <div style="color:#047857;margin-bottom:10px;">${out.docCount || 0} document${out.docCount === 1 ? '' : 's'} bundled, ${Math.round((out.sizeBytes || 0) / 1024)} KB</div>
      ${scoreLine}
      ${dlUrl ? `<a href="${escapeHtml(dlUrl)}" target="_blank" rel="noopener" class="btn btn-primary" style="margin-top:10px;display:inline-flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:18px;">download</span> Download ZIP</a>` : `<div style="color:var(--danger,#DC2626);margin-top:6px;">Download link unavailable — please retry.</div>`}
      <div style="color:var(--text-secondary);margin-top:8px;font-size:0.75rem;">Link valid for 1 hour. Includes SSN/DOB/MVR/drug-test records — handle per your retention policy.</div>
      <div style="margin-top:14px;text-align:right;"><button class="btn btn-secondary btn-sm" id="compliance-dqfile-done">Close</button></div>`;
    document.getElementById('compliance-dqfile-done')?.addEventListener('click', close);
    if (dlUrl) { try { window.open(dlUrl, '_blank', 'noopener'); } catch (_e) {} }
  } catch (err) {
    if (body()) body().innerHTML = `<div style="color:var(--danger,#DC2626);">Error: ${escapeHtml((err && err.message) || 'unknown')}</div>
      <div style="margin-top:14px;text-align:right;"><button class="btn btn-secondary btn-sm" id="compliance-dqfile-done">Close</button></div>`;
    document.getElementById('compliance-dqfile-done')?.addEventListener('click', close);
  } finally {
    _cpDqFileInFlightCompliance = false;
  }
}

function detailRow(label, value) {
  return `
    <div class="compliance-detail-row">
      <span class="compliance-detail-label">${label}</span>
      <span class="compliance-detail-value">${value || '—'}</span>
    </div>
  `;
}

function getExpiryStatusText(dateStr) {
  const days = daysUntilExpiry(dateStr);
  if (days === null) return 'N/A';
  if (days < 0) return `<span style="color:#dc2626;font-weight:600;">Expired ${Math.abs(days)} days ago</span>`;
  if (days <= 30) return `<span style="color:#d97706;font-weight:600;">Expires in ${days} days</span>`;
  // TASK-9300 (4) — NEVER a positive verdict. This used to render a green
  // "Valid (N days remaining)" from a stored expiry date alone, under "DL Status"
  // and "CDL Status" on a §391.51 surface. A state can suspend or downgrade a
  // licence without its printed expiry moving, and DD only learns that from a
  // fresh MVR — so "Valid" was a claim nobody had checked (Kolsen, Crown
  // Compliance, 9Is2WpTzIa7MyQIYeWA1; Domain lane agreed). The expired /
  // expiring warnings above are the only reason the DL row exists (nothing else
  // in this module alerts on dlExpiryDate), so the CLAIM changes rather than the
  // row going away: a neutral date fact, no colour, no "Valid". Applies equally
  // to the med-card and training callers.
  return `<span style="color:var(--text-secondary);">Expires in ${days} days</span>`;
}

// TASK-9300 (5) — CDL restrictions arrive as a STRING from the carrier forms (this
// module's DQ editor + the roster form) and as an ARRAY from older partner-side
// writes (compliance-partner.js `arr()` passes both through). Render either; treat
// [] / '' / null as "none". Measured 2026-09-22: only 2 people platform-wide carry
// the field and both are `[]`.
function _cdlRestrText(v) {
  if (Array.isArray(v)) return v.filter(Boolean).join(', ');
  return v == null ? '' : String(v).trim();
}

function maskSSN(ssn) {
  if (!ssn || ssn.length < 4) return ssn || '';
  return `***-**-${ssn.replace(/\D/g, '').slice(-4)}`;
}

// ============================================
// DVIR / JSA ACCOUNTABILITY (moved from HR)
// ============================================
function buildDvirJsaSection(emp) {
  const missedDvirs = emp.missedDvirCount || 0;
  const missedJsas = emp.missedJsaCount || 0;
  const dvirFlagged = ComplianceState.dvirFlags.filter(f => f.driverId === emp.id && f.status === 'flagged');
  const dvirDismissed = ComplianceState.dvirFlags.filter(f => f.driverId === emp.id && f.status === 'dismissed');
  const jsaFlagged = ComplianceState.jsaFlags.filter(f => f.driverId === emp.id && f.status === 'flagged');
  const jsaDismissed = ComplianceState.jsaFlags.filter(f => f.driverId === emp.id && f.status === 'dismissed');
  const hasAnything = missedDvirs > 0 || missedJsas > 0 || dvirFlagged.length > 0 || dvirDismissed.length > 0 || jsaFlagged.length > 0 || jsaDismissed.length > 0;

  let html = `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title">
        <span class="material-icons" style="font-size:18px;color:${missedDvirs > 0 || missedJsas > 0 ? '#dc2626' : '#059669'};">checklist</span> DVIR & JSA Accountability
      </div>`;

  if (!hasAnything) {
    html += `<div style="padding:12px;color:#059669;font-size:0.85rem;text-align:center;"><span class="material-icons" style="font-size:16px;vertical-align:middle;">check_circle</span> No accountability issues</div>`;
  } else {
    // DVIR counter
    if (missedDvirs > 0) {
      html += `
        <div style="display:flex;align-items:center;gap:10px;padding:10px 12px;background:#fef2f2;border-radius:8px;margin-bottom:8px;border:1px solid #fecaca;">
          <span class="material-icons" style="font-size:20px;color:#dc2626;">report_problem</span>
          <div style="flex:1;">
            <div style="font-weight:600;font-size:0.9rem;color:#0F172A;">${missedDvirs} Missed DVIR${missedDvirs !== 1 ? 's' : ''}</div>
            ${emp.lastMissedDvirDate ? `<div style="font-size:0.75rem;color:#64748B;">Last: ${formatDateShort(emp.lastMissedDvirDate)}</div>` : ''}
          </div>
          <button onclick="window.ComplianceModule.resetMissedDvirCount('${emp.id}')" class="btn btn-sm" style="color:#dc2626;border-color:#dc2626;font-size:0.75rem;padding:4px 10px;">
            <span class="material-icons" style="font-size:14px;">restart_alt</span> Reset
          </button>
        </div>`;
    }

    // JSA counter
    if (missedJsas > 0) {
      html += `
        <div style="display:flex;align-items:center;gap:10px;padding:10px 12px;background:#fef9c3;border-radius:8px;margin-bottom:8px;border:1px solid #fde68a;">
          <span class="material-icons" style="font-size:20px;color:#D97706;">health_and_safety</span>
          <div style="flex:1;">
            <div style="font-weight:600;font-size:0.9rem;color:#0F172A;">${missedJsas} Missed JSA${missedJsas !== 1 ? 's' : ''}</div>
            ${emp.lastMissedJsaDate ? `<div style="font-size:0.75rem;color:#64748B;">Last: ${formatDateShort(emp.lastMissedJsaDate)}</div>` : ''}
          </div>
          <button onclick="window.ComplianceModule.resetMissedJsaCount('${emp.id}')" class="btn btn-sm" style="color:#D97706;border-color:#D97706;font-size:0.75rem;padding:4px 10px;">
            <span class="material-icons" style="font-size:14px;">restart_alt</span> Reset
          </button>
        </div>`;
    }

    // Flagged DVIR records
    if (dvirFlagged.length > 0) {
      html += `<div style="font-size:0.78rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin:12px 0 6px;">Flagged DVIRs</div>`;
      dvirFlagged.forEach(flag => {
        const displayDate = flag.date ? formatDateShort(flag.date) : 'Unknown';
        html += `
          <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:#fef2f2;border-radius:8px;margin-bottom:4px;border:1px solid #fecaca;">
            <span class="material-icons" style="font-size:16px;color:#dc2626;">warning</span>
            <div style="flex:1;font-size:0.85rem;">
              <span style="font-weight:600;">Unit ${escapeHtml(flag.truckUnit || flag.truckId || '?')}</span>
              <span style="color:#64748B;"> — ${displayDate}${flag.jobTitle ? ' · ' + escapeHtml(flag.jobTitle) : ''}</span>
            </div>
            <button onclick="window.ComplianceModule.deleteDvirFlag('${flag.id}', '${emp.id}')" style="flex-shrink:0;background:#dc2626;color:#fff;border:none;border-radius:6px;padding:3px 8px;font-size:0.72rem;cursor:pointer;">
              <span class="material-icons" style="font-size:13px;vertical-align:middle;">delete</span>
            </button>
          </div>`;
      });
    }

    // Dismissed DVIR records
    if (dvirDismissed.length > 0) {
      html += `<div style="font-size:0.78rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin:12px 0 6px;">Dismissed DVIRs</div>`;
      dvirDismissed.forEach(flag => {
        const displayDate = flag.date ? formatDateShort(flag.date) : 'Unknown';
        html += `
          <div style="display:flex;align-items:center;gap:8px;padding:8px 10px;background:#f1f5f9;border-radius:8px;margin-bottom:4px;border:1px solid #e2e8f0;">
            <span class="material-icons" style="font-size:16px;color:#94A3B8;">warning</span>
            <div style="flex:1;font-size:0.83rem;color:#64748B;">
              Unit ${escapeHtml(flag.truckUnit || flag.truckId || '?')} — ${displayDate} <em>· dismissed</em>
            </div>
            <button onclick="window.ComplianceModule.deleteDvirFlag('${flag.id}', '${emp.id}')" style="flex-shrink:0;background:#64748B;color:#fff;border:none;border-radius:6px;padding:3px 8px;font-size:0.72rem;cursor:pointer;">
              <span class="material-icons" style="font-size:13px;vertical-align:middle;">delete</span>
            </button>
          </div>`;
      });
    }

    // Flagged JSA records
    if (jsaFlagged.length > 0) {
      html += `<div style="font-size:0.78rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin:12px 0 6px;">Flagged JSAs</div>`;
      jsaFlagged.forEach(flag => {
        const displayDate = flag.date ? formatDateShort(flag.date) : 'Unknown';
        html += `
          <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:#fef9c3;border-radius:8px;margin-bottom:4px;border:1px solid #fde68a;">
            <span class="material-icons" style="font-size:16px;color:#D97706;">health_and_safety</span>
            <div style="flex:1;font-size:0.85rem;">
              <span style="font-weight:600;">${escapeHtml(flag.jobTitle || 'Unknown Job')}</span>
              <span style="color:#64748B;"> — ${displayDate}</span>
            </div>
            <button onclick="window.ComplianceModule.deleteJsaFlag('${flag.id}', '${emp.id}')" style="flex-shrink:0;background:#D97706;color:#fff;border:none;border-radius:6px;padding:3px 8px;font-size:0.72rem;cursor:pointer;">
              <span class="material-icons" style="font-size:13px;vertical-align:middle;">delete</span>
            </button>
          </div>`;
      });
    }

    // Dismissed JSA records
    if (jsaDismissed.length > 0) {
      html += `<div style="font-size:0.78rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin:12px 0 6px;">Dismissed JSAs</div>`;
      jsaDismissed.forEach(flag => {
        const displayDate = flag.date ? formatDateShort(flag.date) : 'Unknown';
        html += `
          <div style="display:flex;align-items:center;gap:8px;padding:8px 10px;background:#f1f5f9;border-radius:8px;margin-bottom:4px;border:1px solid #e2e8f0;">
            <span class="material-icons" style="font-size:16px;color:#94A3B8;">health_and_safety</span>
            <div style="flex:1;font-size:0.83rem;color:#64748B;">
              ${escapeHtml(flag.jobTitle || 'Unknown Job')} — ${displayDate} <em>· dismissed</em>
            </div>
            <button onclick="window.ComplianceModule.deleteJsaFlag('${flag.id}', '${emp.id}')" style="flex-shrink:0;background:#64748B;color:#fff;border:none;border-radius:6px;padding:3px 8px;font-size:0.72rem;cursor:pointer;">
              <span class="material-icons" style="font-size:13px;vertical-align:middle;">delete</span>
            </button>
          </div>`;
      });
    }

    // Reset all button
    html += `
      <div style="margin-top:12px;padding-top:10px;border-top:1px solid var(--border-light);">
        ${/* TASK-4838 — emp.name in a JS string literal. escapeHtml was NOT protecting this:
             it turns the apostrophe into &#39;, the HTML parser turns it back into an
             apostrophe, and only THEN is the handler compiled — so every driver named
             O'Brien / D'Angelo had a dead "Reset All Accountability Data" button, and a
             crafted name executed. escJsAttr escapes for the JS layer first. */''}
        <button onclick="window.ComplianceModule.resetAllDvirData('${emp.id}', '${escJsAttr(emp.name)}')" class="btn btn-sm" style="color:#dc2626;border-color:#dc2626;font-size:0.78rem;">
          <span class="material-icons" style="font-size:14px;">delete_sweep</span> Reset All Accountability Data
        </button>
      </div>`;
  }

  html += `</div>`;
  return html;
}

// DVIR/JSA action functions
async function resetMissedDvirCount(personId) {
  if (!confirm('Reset missed DVIR count to zero?')) return;
  try {
    await updateDoc(doc(db, 'tenants', ComplianceState.tenantId, 'people', personId), {
      missedDvirCount: 0, lastMissedDvirDate: null
    });
    const person = ComplianceState.people.find(p => p.id === personId);
    if (person) { person.missedDvirCount = 0; person.lastMissedDvirDate = null; }
    window.showToast('DVIR counter reset', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] Error resetting DVIR count:', err);
    window.showToast('Error resetting counter', 'error');
  }
}

async function resetMissedJsaCount(personId) {
  if (!confirm('Reset missed JSA count to zero?')) return;
  try {
    await updateDoc(doc(db, 'tenants', ComplianceState.tenantId, 'people', personId), {
      missedJsaCount: 0, lastMissedJsaDate: null
    });
    const person = ComplianceState.people.find(p => p.id === personId);
    if (person) { person.missedJsaCount = 0; person.lastMissedJsaDate = null; }
    window.showToast('JSA counter reset', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] Error resetting JSA count:', err);
    window.showToast('Error resetting counter', 'error');
  }
}

async function deleteDvirFlag(flagId, personId) {
  if (!confirm('Delete this DVIR flag?')) return;
  try {
    await deleteDoc(doc(db, 'tenants', ComplianceState.tenantId, 'dvirAccountability', flagId));
    window.showToast('DVIR flag deleted', 'success');
    if (personId) showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] Error deleting DVIR flag:', err);
    window.showToast('Error deleting flag', 'error');
  }
}

async function deleteJsaFlag(flagId, personId) {
  if (!confirm('Delete this JSA flag?')) return;
  try {
    await deleteDoc(doc(db, 'tenants', ComplianceState.tenantId, 'jsaAccountability', flagId));
    window.showToast('JSA flag deleted', 'success');
    if (personId) showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] Error deleting JSA flag:', err);
    window.showToast('Error deleting flag', 'error');
  }
}

async function resetAllDvirData(employeeId, employeeName) {
  if (!confirm(`WARNING: Delete ALL DVIR/JSA accountability records for ${employeeName} and reset counters to zero?\n\nThis cannot be undone.`)) return;
  try {
    const tenantId = ComplianceState.tenantId;
    // Delete DVIR flags
    const dvirSnap = await getDocs(query(collection(db, 'tenants', tenantId, 'dvirAccountability'), where('driverId', '==', employeeId)));
    for (const d of dvirSnap.docs) await deleteDoc(doc(db, 'tenants', tenantId, 'dvirAccountability', d.id));
    // Delete JSA flags
    const jsaSnap = await getDocs(query(collection(db, 'tenants', tenantId, 'jsaAccountability'), where('driverId', '==', employeeId)));
    for (const d of jsaSnap.docs) await deleteDoc(doc(db, 'tenants', tenantId, 'jsaAccountability', d.id));
    // Reset counters
    await updateDoc(doc(db, 'tenants', tenantId, 'people', employeeId), {
      missedDvirCount: 0, lastMissedDvirDate: null,
      missedJsaCount: 0, lastMissedJsaDate: null
    });
    const total = dvirSnap.docs.length + jsaSnap.docs.length;
    window.showToast(`Deleted ${total} record${total !== 1 ? 's' : ''} for ${employeeName}`, 'success');
    showPersonDetail(employeeId);
  } catch (err) {
    console.error('[Compliance] Error resetting data:', err);
    window.showToast('Error resetting data', 'error');
  }
}

// ============================================
// DRUG TESTS
// ============================================
const DRUG_TEST_TYPES = [
  { value: 'pre_employment', label: 'Pre-Employment' },
  { value: 'random', label: 'Random' },
  { value: 'post_accident', label: 'Post-Accident' },
  { value: 'reasonable_suspicion', label: 'Reasonable Suspicion' },
  { value: 'return_to_duty', label: 'Return to Duty' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'other', label: 'Other' }
];

function getPersonDrugTests(personId) {
  return ComplianceState.drugTests.filter(t => t.personId === personId);
}

function renderDrugTestBadge(personId) {
  const tests = getPersonDrugTests(personId);
  if (tests.length === 0) {
    return `<span class="compliance-badge compliance-badge-gray" title="No drug tests on file">
      <span class="material-icons" style="font-size:14px;">science</span> No Tests
    </span>`;
  }
  const latest = tests[0]; // already sorted desc by testDate
  const failed = tests.some(t => t.result === 'fail');
  const pending = latest.result === 'pending';
  if (failed) {
    return `<span class="compliance-badge compliance-badge-critical" title="Failed drug test on file">
      <span class="material-icons" style="font-size:14px;">error</span> Drug Test: FAIL
    </span>`;
  }
  if (pending) {
    return `<span class="compliance-badge" style="background:#FEF3C7;color:#92400E;border:1px solid #F59E0B40;" title="Drug test pending">
      <span class="material-icons" style="font-size:14px;">hourglass_top</span> Drug Test: Pending
    </span>`;
  }
  return `<span class="compliance-badge compliance-badge-good" title="Last test: ${formatDateShort(latest.testDate)}">
    <span class="material-icons" style="font-size:14px;">check_circle</span> Drug Test: Pass
  </span>`;
}

function renderDrugTestSection(personId) {
  const tests = getPersonDrugTests(personId);
  // TASK-1090 — the DOT D&A testing program (49 CFR §382 / §40) covers CDL
  // drivers only. For an explicit non-CDL tier: no actionable "Add Test"; any
  // existing test records stay visible read-only (never hide recorded results).
  // Unclassified resolves to 'cdl' → full section (fail-safe).
  const _dtPerson = ComplianceState.people.find(p => p.id === personId) || null;
  // TASK-9696 — "Add Test" follows testing-program COVERAGE (CDL tier → DOT-FMCSA,
  // PHMSA flag, or the company program), not the CDL-only DQ item.
  const _dtCov = _dtPerson ? window.DqMatrix.testingCoverageFor(_dtPerson) : { any: true, programs: [] };
  const _dtApplies = _dtCov.any;
  if (!_dtApplies && tests.length === 0) return '';
  const _dtNaNote = !_dtApplies ? `
      <div style="display:flex;align-items:center;gap:8px;padding:8px;background:#F1F5F9;border:1px solid #E2E8F0;border-radius:var(--radius-md);margin-top:8px;">
        <span class="material-icons" style="font-size:16px;color:#64748B;">info</span>
        <div style="font-size:0.78rem;color:#475569;">Not in a drug &amp; alcohol testing program (not a CDL driver, no PHMSA or company-program flag). Existing records are shown read-only — never deleted (49 CFR §382.401 retention).</div>
      </div>` : '';
  return `
    <div class="compliance-detail-section" id="compliance-drug-test-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div>
          <span class="material-icons" style="font-size:18px;color:${_dtApplies ? '#7c3aed' : '#94A3B8'};">science</span> Drug & Alcohol Tests
          ${tests.length > 0 ? `<span class="compliance-badge compliance-badge-info" style="margin-left:8px;">${tests.length}</span>` : ''}
        </div>
        ${_dtApplies ? `
        <button class="btn btn-primary btn-sm" onclick="window.ComplianceModule.showAddDrugTest('${personId}')" style="font-size:0.75rem;">
          <span class="material-icons" style="font-size:16px;">add</span> Add Test
        </button>` : ''}
      </div>
      ${_dtNaNote}
      ${tests.length > 0 ? `
        <div style="display:flex;flex-direction:column;gap:8px;margin-top:8px;">
          ${tests.map(t => {
            const typeLabel = DRUG_TEST_TYPES.find(dt => dt.value === t.testType)?.label || t.testType || 'Unknown';
            const resultColor = t.result === 'pass' ? '#059669' : t.result === 'fail' ? '#DC2626' : '#D97706';
            const resultIcon = t.result === 'pass' ? 'check_circle' : t.result === 'fail' ? 'cancel' : 'hourglass_top';
            const resultLabel = t.result === 'pass' ? 'Negative (Pass)' : t.result === 'fail' ? 'Positive (Fail)' : 'Pending';
            return `
            <div style="background:var(--bg-secondary);border-radius:var(--radius-md);padding:10px 12px;border-left:3px solid ${resultColor};">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
                <div style="flex:1;min-width:0;">
                  <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                    <span style="font-weight:600;font-size:0.8rem;color:var(--text-primary);">${escapeHtml(typeLabel)}</span>
                    <span class="material-icons" style="font-size:16px;color:${resultColor};">${resultIcon}</span>
                    <span style="font-size:0.75rem;font-weight:600;color:${resultColor};">${resultLabel}</span>
                  </div>
                  <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">
                    ${formatDateShort(t.testDate)}${t.lab ? ' &bull; ' + escapeHtml(t.lab) : ''}${t.collector ? ' &bull; ' + escapeHtml(t.collector) : ''}
                    &bull; <span title="${t.program ? escapeHtml(window.DqMatrix.PROGRAMS[t.program]?.label || t.program) : 'Recorded before program tracking — the program is not inferred'}" style="font-weight:600;color:${t.program ? '#1D4ED8' : '#64748B'};">${t.program ? escapeHtml(window.DqMatrix.PROGRAMS[t.program]?.short || t.program) : 'Program not recorded'}</span>
                  </div>
                  ${t.notes ? `<div style="font-size:0.7rem;color:var(--text-muted);margin-top:2px;font-style:italic;">${escapeHtml(t.notes)}</div>` : ''}
                </div>
                <div style="display:flex;gap:2px;flex-shrink:0;">
                  ${(t.documentPath || t.documentUrl) ? `<button class="btn-icon" onclick="event.stopPropagation();window.ComplianceModule.viewDrugTestDocument('${escJsAttr(t.id)}');return false;" title="View test result / MRO report">
                    <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">description</span>
                  </button>` : ''}
                  ${(t.ccfDocumentPath || t.ccfDocumentUrl) ? `<button class="btn-icon" onclick="event.stopPropagation();window.ComplianceModule.viewDrugTestDocument('${escJsAttr(t.id)}','ccf');return false;" title="View Custody &amp; Control Form (CCF)">
                    <span class="material-icons" style="font-size:16px;color:#7c3aed;">assignment</span>
                  </button>` : ''}
                  ${_compIsOwnerOrAdmin() ? `<button class="btn-icon" onclick="event.stopPropagation();window.ComplianceModule.deleteDrugTest('${t.id}')" title="Delete">
                    <span class="material-icons" style="font-size:16px;color:#DC2626;">delete</span>
                  </button>` : ''}
                </div>
              </div>
            </div>`;
          }).join('')}
        </div>
      ` : `<div style="padding:12px;color:var(--text-muted);font-size:0.85rem;text-align:center;">No drug tests on file</div>`}
    </div>
  `;
}

// TASK-1067 — §382.601(d) Drug & Alcohol policy-receipt status + Send-Link.
// Mirrors HR's Policy Receipt section (hr.js buildComplianceTabHTML): reads the
// SAME server-authored people/{id}.drugAlcoholPolicyReceipt field and mints a
// signing link via the SAME callable (mintIncumbentDrugAlcoholReceiptToken).
// Live-refresh is free — the people onSnapshot re-renders this open modal
// (renderIfReady / TASK-879), so the status flips to "Signed <date>" when the
// driver submits, without a manual re-open.
function renderDaPolicyReceiptSection(person) {
  const rec = person.drugAlcoholPolicyReceipt;
  const signed = rec?.signedDateStr;
  // TASK-1154 — driver-signed receipts carry an immutable artifactId; legacy
  // partner_portal-RECORDED receipts (recordDaReceipt) have none → no PDF to
  // render, so the button is shown DISABLED with a tooltip (never hidden).
  const hasArtifact = !!rec?.artifactId;
  // TASK-1090 — §382.601(d) D&A policy receipt is a CDL-program item. For an
  // explicit non-CDL tier: no actionable Send Link; a receipt already signed
  // stays visible read-only. Unclassified resolves to 'cdl' (fail-safe).
  const _daApplies = window.DqMatrix.getApplicableDqItems(person).items.daPolicyReceipt;
  if (!_daApplies && !signed) return '';
  return `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="material-icons" style="font-size:18px;color:${_daApplies ? '#7c3aed' : '#94A3B8'};">assignment_turned_in</span> Policy Receipt
          <span class="compliance-cite-badge">49 CFR §382.601(d)</span>
        </div>
        ${_daApplies ? `
        <button class="btn btn-sm btn-secondary" onclick="window.ComplianceModule.sendDaReceiptLink('${escapeHtml(person.id)}')" style="font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">send</span> ${signed ? 'Resend Link' : 'Send Link'}
        </button>` : ''}
      </div>
      ${!_daApplies ? `
      <div style="display:flex;align-items:center;gap:8px;padding:8px;background:#F1F5F9;border:1px solid #E2E8F0;border-radius:var(--radius-md);margin-top:8px;">
        <span class="material-icons" style="font-size:16px;color:#64748B;">info</span>
        <div style="font-size:0.78rem;color:#475569;">The §382.601(d) D&amp;A policy receipt applies to CDL drivers only — not required for this classification. The signed receipt below is retained read-only.</div>
      </div>` : ''}
      ${signed
        ? `<div style="display:flex;align-items:center;gap:8px;padding:8px;background:#F0FDF4;border:1px solid #BBF7D0;border-radius:var(--radius-md);margin-top:8px;">
            <span class="material-icons" style="font-size:16px;color:#059669;">check_circle</span>
            <div style="font-size:0.82rem;color:#14532D;flex:1;min-width:0;">Signed ${escapeHtml(rec.signedDateStr)}${rec.policyVersion ? ` (policy v${escapeHtml(rec.policyVersion)})` : ''}</div>
            ${hasArtifact
              ? `<button class="btn btn-sm btn-secondary" onclick="window.viewSignedDaReceipt('${escapeHtml(person.id)}', this)" style="padding:4px 10px;font-size:0.72rem;flex-shrink:0;">
                  <span class="material-icons" style="font-size:14px;">picture_as_pdf</span> View signed receipt
                </button>`
              : `<button class="btn btn-sm btn-secondary" disabled title="Signed via legacy portal (PDF unavailable)" style="padding:4px 10px;font-size:0.72rem;flex-shrink:0;opacity:0.55;cursor:not-allowed;">
                  <span class="material-icons" style="font-size:14px;">picture_as_pdf</span> View signed receipt
                </button>`}
          </div>`
        : `<div style="display:flex;align-items:center;gap:8px;padding:8px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);margin-top:8px;">
            <span class="material-icons" style="font-size:16px;color:#D97706;">warning</span>
            <div style="font-size:0.82rem;color:#92400E;">No signed receipt on file. FMCSA requires this for every driver.</div>
          </div>`}
    </div>`;
}

// TASK-1067 — text the driver a one-tap link to sign the company D&A policy
// receipt. Mirrors HR's sendIncumbentDaReceiptLink (same callable + payload
// {tenantId, personId}) AND compliance's own med-card link idiom
// (sendMedUploadLinkCompliance → scheduledReminders SMS queue). The callable is
// gated requireTenantMember (any tenant member — owner/admin/staff — passes; a
// managing partner is NOT a member and is denied by design → the partner portal
// is deliberately NOT given this button, TASK-1067). The callable also emails
// the link if the driver has an email on file; we additionally text it.
async function sendDaReceiptLinkCompliance(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;

  const phone = person.phone || person.cellPhone || person.mobilePhone;
  // TASK-9145 — the server emails as well as texts now, so a phone is not required.
  if (!phone && !String(person.email || '').trim()) {
    showToast('No phone or email on file for this driver — add one first', 'error');
    return;
  }
  const _via = [phone ? `a text at ${phone}` : '', person.email ? `an email to ${person.email}` : ''].filter(Boolean).join(' and ');

  const already = !!person.drugAlcoholPolicyReceipt?.signedDateStr;
  // TASK-9696 — a receipt is owed by a person a testing program covers; say so
  // up front (the server refuses with NOT_COVERED unless the sender overrides).
  const _cov = window.DqMatrix.testingCoverageFor(person);
  const confirmMsg = _cov.any
    ? `Send ${person.name} ${_via} with a one-time link to sign the company Drug & Alcohol policy receipt (49 CFR §382.601(d))? Program: ${window.DqMatrix.coverageLabel(_cov)}.`
    : `${person.name} is not in a drug & alcohol testing program (not a CDL driver, no PHMSA or company-program flag), so no policy receipt is owed. Send the link anyway?`;
  const proceed = window.showConfirm
    ? await window.showConfirm(already ? 'Resend policy receipt link' : 'Send policy receipt link', confirmMsg, { confirmText: 'Send link', cancelText: 'Cancel' })
    : window.confirm(confirmMsg);
  if (!proceed) return;

  try {
    const mintFn = httpsCallable(functions, 'mintIncumbentDrugAlcoholReceiptToken');
    let resp;
    // TASK-9145 — the text now goes from the SERVER (sendSms:true → the tenant SMS
    // chokepoint). The old client addDoc to root scheduledReminders was denied by
    // firestore.rules (no rule = default deny), so this text never went out.
    try {
      resp = await mintFn({ tenantId: ComplianceState.tenantId, personId, sendSms: true, ...(_cov.any ? {} : { allowNotCovered: true }) });
    } catch (e) {
      // TASK-9143 - no DER on file: the server refuses; sender may override knowingly.
      if (!/DER_MISSING/.test(e?.message || '')) throw e;
      const go = window.showConfirm
        ? await window.showConfirm('No Designated Employer Representative set', 'The D&A policy notice would go out with a placeholder instead of a real contact (49 CFR §382.601(b)(1)), and a receipt signed against it cannot be fixed later.\n\nSet the DER under HR > Drug & Alcohol settings first, or send anyway.', { confirmText: 'Send anyway', cancelText: 'Cancel', danger: true })
        : window.confirm('No DER is set - the notice would carry a placeholder contact. Send anyway?');
      if (!go) return;
      resp = await mintFn({ tenantId: ComplianceState.tenantId, personId, sendSms: true, allowMissingDer: true, ...(_cov.any ? {} : { allowNotCovered: true }) });
    }
    const link = resp?.data?.link;
    if (!resp?.data?.ok || !link) {
      showToast('Could not generate link', 'error');
      return;
    }
    // TASK-9145 — say what actually happened on each leg (shared classifier).
    const row = window.DaReceiptBulk.classifySendResult(resp.data);
    if (row.status === 'sent') {
      showToast(`Policy receipt link for ${person.name}: ${row.text}`, 'success');
    } else {
      try { await navigator.clipboard?.writeText(link); } catch (_) { /* ignored */ }
      showToast(`${person.name}: ${row.text} (link copied to clipboard)`, 'error');
    }
  } catch (err) {
    console.error('[Compliance] sendDaReceiptLink error:', err);
    const denied = err?.code === 'permission-denied' || err?.code === 'functions/permission-denied';
    showToast(denied
      ? 'You do not have permission to send this link — ask an owner or admin.'
      : (err?.message || 'Could not send link'), 'error');
  }
}

// TASK-9145 — tenant-wide "send the receipt link to everyone missing one". The
// shared window.DaReceiptBulk renders; this host authorizes and passes the SAME
// callable the single Send Link uses (member path, tenantId pinned at open).
function _compDaBulkMissingCount() {
  if (!window.DaReceiptBulk) return 0;
  return window.DaReceiptBulk.missingReceiptPeople(ComplianceState.people, { isActive: p => getPersonEmploymentStatus(p) === 'active' }).length;
}
function openDaReceiptBulkSendCompliance() {
  if (!window.DaReceiptBulk) return;
  const tid = ComplianceState.tenantId;
  const td = window.AppState?.tenantData || {};
  window.DaReceiptBulk.openBulkSendModal({
    people: ComplianceState.people,
    isActive: p => getPersonEmploymentStatus(p) === 'active',
    carrierName: td.companyName || td.name || '',
    sms: { offered: true, ...window.DaReceiptBulk.smsCreditsLeft(td) },
    send: async (personId, o) => {
      const fn = httpsCallable(functions, 'mintIncumbentDrugAlcoholReceiptToken');
      const res = await fn({ tenantId: tid, personId, sendSms: !!o.sendSms, dedupeWindowMs: o.dedupeWindowMs, ...(o.allowMissingDer ? { allowMissingDer: true } : {}) });
      return res && res.data;
    },
    confirmDerOverride: (n) => (window.showConfirm
      ? window.showConfirm('No Designated Employer Representative set', 'Sending anyway applies to all ' + n + ' drivers in this send. The D&A policy notice would go out with a placeholder instead of a real contact (49 CFR §382.601(b)(1)), and a receipt signed against it cannot be fixed later.\n\nSet the DER under HR > Drug & Alcohol settings first, or send anyway.', { confirmText: 'Send anyway', cancelText: 'Cancel', danger: true })
      : Promise.resolve(window.confirm('No DER is set - the notice would carry a placeholder contact. Send anyway?'))),
  });
}

function showDrugTestModal(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;
  // TASK-1086 — clear any stale attachments from a previously-canceled modal.
  pendingDrugTestFile = null;
  pendingDrugTestCcfFile = null;
  const today = new Date().toISOString().split('T')[0];

  const modal = document.getElementById('compliance-detail-modal');
  if (!modal) return;
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;margin:auto;border-radius:var(--radius-xl);overflow:hidden;">
      <div style="padding:16px 20px;background:linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);color:#fff;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;">Add Drug Test — ${escapeHtml(person.name)}</h3>
          <button class="btn-icon" onclick="window.ComplianceModule.closeDetail()" style="color:#fff;">
            <span class="material-icons">close</span>
          </button>
        </div>
      </div>
      <div style="padding:20px;display:flex;flex-direction:column;gap:14px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Test Date *</label>
            <input type="date" id="drug-test-date" class="form-control" value="${today}" required>
          </div>
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Test Type *</label>
            <select id="drug-test-type" class="form-control" required>
              ${DRUG_TEST_TYPES.map(t => `<option value="${t.value}">${t.label}</option>`).join('')}
            </select>
          </div>
        </div>
        <!-- TASK-9696 — every test record says which program it belongs to -->
        ${(() => {
          const cov = window.DqMatrix.testingCoverageFor(person);
          const opts = window.DqMatrix.PROGRAM_KEYS.map(k => `<option value="${k}" ${cov.defaultProgram === k ? 'selected' : ''}>${escapeHtml(window.DqMatrix.PROGRAMS[k].label)}</option>`).join('');
          return `
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Testing Program *</label>
          <select id="drug-test-program" class="form-control" required>
            ${cov.defaultProgram ? '' : '<option value="" selected>— Choose the program —</option>'}${opts}
          </select>
          <div style="font-size:0.72rem;color:${cov.any ? 'var(--text-secondary)' : '#92400E'};margin-top:4px;">${cov.any ? 'This person: ' + escapeHtml(window.DqMatrix.coverageLabel(cov)) : 'This person is not in a testing program (not a CDL driver, no PHMSA / company flag). Record the test under the program it was actually run in, and fix the classification or flags.'}</div>
        </div>`;
        })()}
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Result *</label>
          <select id="drug-test-result" class="form-control" required>
            <option value="pass">Negative (Pass)</option>
            <option value="pending">Pending</option>
            <option value="fail">Positive (Fail)</option>
          </select>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Lab / Facility</label>
            <input type="text" id="drug-test-lab" class="form-control" placeholder="e.g. Quest Diagnostics">
          </div>
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Collector</label>
            <input type="text" id="drug-test-collector" class="form-control" placeholder="e.g. MRO name">
          </div>
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Test Result / MRO Report (PDF or image)</label>
          <div id="drug-test-file-preview" style="margin-bottom:6px;"></div>
          <label class="btn btn-secondary btn-sm" style="cursor:pointer;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:16px;">attach_file</span> Choose File
            <input type="file" id="drug-test-file" accept="image/*,application/pdf" style="display:none;" onchange="window.ComplianceModule.handleDrugTestFile(event)">
          </label>
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Custody &amp; Control Form (CCF) (PDF or image)</label>
          <div id="drug-test-ccf-preview" style="margin-bottom:6px;"></div>
          <label class="btn btn-secondary btn-sm" style="cursor:pointer;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:16px;">attach_file</span> Choose File
            <input type="file" id="drug-test-ccf-file" accept="image/*,application/pdf" style="display:none;" onchange="window.ComplianceModule.handleDrugTestFile(event, 'ccf')">
          </label>
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Notes</label>
          <input type="text" id="drug-test-notes" class="form-control" placeholder="Optional notes">
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom,#F8FAFC,#fff);display:flex;gap:12px;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="window.ComplianceModule.closeDetail()">Cancel</button>
        <button class="btn btn-primary" id="drug-test-save-btn" onclick="window.ComplianceModule.saveDrugTest('${personId}')">
          <span class="material-icons" style="font-size:18px;">save</span> Save
        </button>
      </div>
    </div>
  `;
}

// Temp storage for pending drug test files. TASK-1086 — a test carries TWO
// distinct docs: the verified RESULT / MRO report (pendingDrugTestFile →
// documentPath) and the Custody & Control Form (pendingDrugTestCcfFile →
// ccfDocumentPath). Both optional individually.
let pendingDrugTestFile = null;
let pendingDrugTestCcfFile = null;

function handleDrugTestFileUpload(event, kind) {
  const file = event.target.files?.[0];
  if (!file) return;
  event.target.value = '';

  const nameLower = file.name.toLowerCase();
  const isPdf = file.type === 'application/pdf' || nameLower.endsWith('.pdf');
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.tiff', '.tif'];
  const isImage = file.type.startsWith('image/') || imageExts.some(ext => nameLower.endsWith(ext));
  if (!isPdf && !isImage) {
    window.showToast?.('Only images and PDFs allowed', 'error');
    return;
  }
  if (file.size > 10 * 1024 * 1024) {
    window.showToast?.('File too large (max 10MB)', 'error');
    return;
  }

  const isCcf = kind === 'ccf';
  if (isCcf) pendingDrugTestCcfFile = file; else pendingDrugTestFile = file;
  const preview = document.getElementById(isCcf ? 'drug-test-ccf-preview' : 'drug-test-file-preview');
  if (preview) {
    preview.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;padding:6px 10px;background:var(--bg-secondary);border-radius:var(--radius-md);font-size:0.8rem;">
        <span class="material-icons" style="font-size:18px;color:${isPdf ? '#DC2626' : 'var(--brand-blue)'};">${isPdf ? 'picture_as_pdf' : 'image'}</span>
        <span style="flex:1;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(file.name)}</span>
        <button class="btn-icon" onclick="window.ComplianceModule.removeDrugTestFile(${isCcf ? "'ccf'" : ''})" title="Remove">
          <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">close</span>
        </button>
      </div>
    `;
  }
}

async function saveDrugTestRecord(personId) {
  const testDate = document.getElementById('drug-test-date')?.value?.trim();
  const testType = document.getElementById('drug-test-type')?.value?.trim();
  const result = document.getElementById('drug-test-result')?.value?.trim();
  const lab = document.getElementById('drug-test-lab')?.value?.trim() || '';
  const collector = document.getElementById('drug-test-collector')?.value?.trim() || '';
  const notes = document.getElementById('drug-test-notes')?.value?.trim() || '';
  const program = window.DqMatrix.normalizeProgram(document.getElementById('drug-test-program')?.value); // TASK-9696

  if (!testDate || !testType || !result) {
    window.showToast?.('Date, type, and result are required', 'error');
    return;
  }
  if (!program) {
    window.showToast?.('Pick the testing program this test belongs to (DOT–FMCSA, DOT–PHMSA or company)', 'error');
    return;
  }

  const person = ComplianceState.people.find(p => p.id === personId);
  const btn = document.getElementById('drug-test-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    // TASK-10081 — store the PATH only. A getDownloadURL token link is a bearer
    // credential that outlives the drugTests read gate and SEC-STORAGE-DQ, so it
    // is never fetched or saved here; viewers open the file by path.
    let documentPath = '';
    let ccfDocumentPath = '';

    // Upload files if present. TASK-1086 — documentPath = test RESULT / MRO
    // report; ccfDocumentPath = Custody & Control Form. Both leaves MUST keep
    // the `${personId}_` prefix (the server-side IDOR fence keys off it).
    if (pendingDrugTestFile) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const ext = pendingDrugTestFile.name.split('.').pop() || 'pdf';
      const fileName = `${personId}_${testDate}_${Date.now()}.${ext}`;
      const path = `tenants/${ComplianceState.tenantId}/drugTests/${year}/${month}/${fileName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, pendingDrugTestFile, { contentType: pendingDrugTestFile.type });
      documentPath = path;
    }
    if (pendingDrugTestCcfFile) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const ext = pendingDrugTestCcfFile.name.split('.').pop() || 'pdf';
      const fileName = `${personId}_${testDate}_${Date.now()}_ccf.${ext}`;
      const path = `tenants/${ComplianceState.tenantId}/drugTests/${year}/${month}/${fileName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, pendingDrugTestCcfFile, { contentType: pendingDrugTestCcfFile.type });
      ccfDocumentPath = path;
    }

    const data = {
      personId,
      personName: person?.name || '',
      testDate,
      testType,
      program, // TASK-9696 — dot_fmcsa | dot_phmsa | company
      result,
      lab,
      collector,
      notes,
      documentPath,
      ccfDocumentPath,
      createdAt: Timestamp.now()
    };

    await addDoc(collection(db, 'tenants', ComplianceState.tenantId, 'drugTests'), data);
    const _hadReceipt = !!(documentPath || ccfDocumentPath); // TASK-1274 — confirm the upload landed
    pendingDrugTestFile = null;
    pendingDrugTestCcfFile = null;
    window.showToast?.(_hadReceipt ? 'Drug test saved — receipt uploaded ✓' : 'Drug test recorded ✓', 'success');

    // TASK-1274 (Chris dogfooding) — re-render the person detail, then scroll the
    // Drug & Alcohol section into view so the new test (+ receipt) is right there
    // instead of dumping the user at the top of a long file.
    window.ComplianceModule.closeDetail();
    setTimeout(() => {
      showPersonDetail(personId);
      setTimeout(() => document.getElementById('compliance-drug-test-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }, 200);
  } catch (err) {
    console.error('[Compliance] Drug test save error:', err);
    window.showToast?.('Error saving drug test', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;">save</span> Save'; }
  }
}

async function deleteDrugTestRecord(testId) {
  if (!confirm('Delete this drug test record?')) return;
  try {
    const test = ComplianceState.drugTests.find(t => t.id === testId);
    // Delete the Firestore doc FIRST. If the rules reject it (non-admin) or the
    // DOT-result lock blocks it, we must NOT have already destroyed the Storage
    // file — that left orphaned records with a dead document link (2026-06-26).
    await deleteDoc(doc(db, 'tenants', ComplianceState.tenantId, 'drugTests', testId));
    // Doc gone — now clean up the backing files (best-effort). TASK-1086: a
    // record may carry BOTH a result doc and a CCF.
    if (test?.documentPath) {
      try { await deleteObject(ref(storage, test.documentPath)); } catch (e) { /* ok if already gone */ }
    }
    if (test?.ccfDocumentPath) {
      try { await deleteObject(ref(storage, test.ccfDocumentPath)); } catch (e) { /* ok if already gone */ }
    }
    window.showToast?.('Drug test deleted', 'success');
  } catch (err) {
    console.error('[Compliance] Drug test delete error:', err);
    window.showToast?.('Error deleting', 'error');
  }
}

// ============================================
// ASSETS TAB
// ============================================
function renderVehiclesGrid() {
  const sub = ComplianceState.assetSubTab;
  let vehicles = [];
  if (sub === 'all' || sub === 'trucks') {
    vehicles.push(...ComplianceState.trucks.filter(v => v.active !== false).filter(v => v.unit || v.nickname || v.name || v.make));
  }
  if (sub === 'all' || sub === 'trailers') {
    vehicles.push(...ComplianceState.trailers.filter(v => v.active !== false).filter(v => v.unit || v.nickname || v.name || v.make));
  }
  if (sub === 'all' || sub === 'assets') {
    vehicles.push(...ComplianceState.assets.filter(a => a.active !== false && !a.consumable).map(a => ({ ...a, _type: 'asset' })));
  }

  // Search filter
  if (ComplianceState.searchQuery) {
    const q = ComplianceState.searchQuery.trim().toLowerCase();
    vehicles = vehicles.filter(v =>
      (v.unit || '').toLowerCase().includes(q) ||
      (v.nickname || '').toLowerCase().includes(q) ||
      (v.make || '').toLowerCase().includes(q) ||
      (v.model || '').toLowerCase().includes(q)
    );
  }

  // Status filter
  if (ComplianceState.filterStatus !== 'all') {
    vehicles = vehicles.filter(v =>
      getVehicleComplianceStatus(v, ComplianceState.pretrips, ComplianceState.maintenance) === ComplianceState.filterStatus
    );
  }

  if (vehicles.length === 0) {
    return `
      <div class="empty-state" style="margin-top:var(--spacing-xl);">
        <div class="empty-state-icon"><span class="material-icons" style="font-size:48px;color:var(--text-muted);">local_shipping</span></div>
        <div class="empty-state-title">No Vehicles Found</div>
        <div class="empty-state-text">${ComplianceState.filterStatus !== 'all' ? 'Try changing your filter.' : 'No vehicles in the fleet.'}</div>
      </div>
    `;
  }

  // Sort: critical first
  const statusOrder = { critical: 0, warning: 1, good: 2 };
  vehicles.sort((a, b) =>
    statusOrder[getVehicleComplianceStatus(a, ComplianceState.pretrips, ComplianceState.maintenance)] -
    statusOrder[getVehicleComplianceStatus(b, ComplianceState.pretrips, ComplianceState.maintenance)]
  );

  return `
    <div class="compliance-grid">
      ${vehicles.map(v => renderVehicleCard(v)).join('')}
    </div>
  `;
}

function renderVehicleCard(vehicle) {
  const status = getVehicleComplianceStatus(vehicle, ComplianceState.pretrips, ComplianceState.maintenance);
  const typeLabel = vehicle._type === 'trailer' ? 'Trailer' : vehicle._type === 'asset' ? 'Equipment' : 'Truck';
  const displayName = vehicle.nickname || vehicle.name || vehicle.make || '';
  const displayModel = vehicle.model || '';

  // Get vehicle-specific pretrips
  const vehiclePretrips = ComplianceState.pretrips.filter(p =>
    p.truckId === vehicle.id || p.trailerId === vehicle.id ||
    p.truck === vehicle.unit || p.trailer === vehicle.unit
  );

  // Last DVIR
  const sortedPretrips = vehiclePretrips
    .sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(a.date || 0);
      const db2 = b.createdAt?.toDate?.() || new Date(b.date || 0);
      return db2 - da;
    });
  const lastDvir = sortedPretrips[0];
  let lastDvirDate = null;
  if (lastDvir) {
    const raw = lastDvir.createdAt?.toDate?.() || (lastDvir.date ? new Date(lastDvir.date) : null);
    lastDvirDate = raw ? raw.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : (lastDvir.date || '');
  }
  const hasDefect = _dvirHasDefects(lastDvir);

  // Open defects count
  // Same "open" definition as the dashboard/feed — a defect with a linked work
  // order is being worked and must not read as open on the vehicle card while
  // the dashboard suppresses it (COD Code Reviewer catch).
  const openDefects = vehiclePretrips.filter(_dvirIsOpenDefect).length;

  // Active maintenance
  const activeMaint = ComplianceState.maintenance.filter(m =>
    (m.truckId === vehicle.id || m.trailerId === vehicle.id ||
     m.truck === vehicle.unit || m.trailer === vehicle.unit)
  ).length;

  const statusConfig = {
    critical: { icon: 'error', color: '#DC2626', label: 'Action Required' },
    warning: { icon: 'warning', color: '#D97706', label: 'Expiring Soon' },
    good: { icon: 'check_circle', color: '#059669', label: 'Compliant' }
  };
  const sc = statusConfig[status];

  return `
    <div class="compliance-card compliance-card-${status}" onclick="window.ComplianceModule.showVehicleDetail('${vehicle.id}')">
      <div class="compliance-card-header">
        <div style="display:flex;align-items:center;justify-content:space-between;width:100%;">
          <div>
            <div style="display:flex;align-items:center;gap:8px;">
              <div class="compliance-card-name">${escapeHtml(vehicle.unit || vehicle.nickname || vehicle.name || vehicle.make || 'Unknown')}</div>
              <span class="compliance-type-badge compliance-type-${vehicle._type}">${typeLabel}</span>
            </div>
            <div class="compliance-card-role">${escapeHtml([displayName, displayModel].filter(Boolean).join(' '))}</div>
          </div>
          <div class="compliance-status-indicator" style="color:${sc.color};" title="${sc.label}">
            <span class="material-icons" style="font-size:22px;">${sc.icon}</span>
          </div>
        </div>
      </div>
      <div class="compliance-card-badges">
        ${lastDvir ? `
          <span class="compliance-badge ${hasDefect ? 'compliance-badge-critical' : 'compliance-badge-good'}" title="Last DVIR: ${lastDvirDate}${hasDefect ? ' — DEFECT' : ' — Passed'}">
            <span class="material-icons" style="font-size:14px;">${hasDefect ? 'report_problem' : 'check_circle'}</span>
            DVIR: ${lastDvirDate}${hasDefect ? ' ⚠' : ''}
          </span>
        ` : `
          <span class="compliance-badge compliance-badge-gray">
            <span class="material-icons" style="font-size:14px;">help_outline</span> No DVIRs
          </span>
        `}
        ${openDefects > 0 ? `
          <span class="compliance-badge compliance-badge-critical" title="${openDefects} open defect(s)">
            <span class="material-icons" style="font-size:14px;">error</span> ${openDefects} Defect${openDefects > 1 ? 's' : ''}
          </span>
        ` : ''}
        ${activeMaint > 0 ? `
          <span class="compliance-badge compliance-badge-warning" title="${activeMaint} active maintenance task(s)">
            <span class="material-icons" style="font-size:14px;">build</span> ${activeMaint} Task${activeMaint > 1 ? 's' : ''}
          </span>
        ` : ''}
        ${vehicle._type !== 'asset' ? `
          ${vehicle.registrationNoExpiry === true ? `<span class="compliance-badge compliance-badge-good" title="Registration marked as not expiring"><span class="material-icons" style="font-size:14px;">check_circle</span> Reg: No expiry</span>` : formatExpiryBadge(vehicle.registrationExpiry, 'Reg')}
          ${_vcRegExpiryMissing(vehicle) ? `<span class="compliance-badge compliance-badge-warning" title="A registration card is on file but no expiration date — enter it or tick Doesn't expire"><span class="material-icons" style="font-size:14px;">event_busy</span> No reg. expiry</span>` : ''}
          ${(vehicle._type !== 'trailer' && vehicle.irpExpiry) ? formatExpiryBadge(vehicle.irpExpiry, 'IRP') : ''}
          ${formatExpiryBadge(vehicle.insuranceExpiry, 'Insurance')}
          ${formatExpiryBadge(vehicle.inspectionExpiry, 'Inspection')}
          ${(Array.isArray(vehicle.customComplianceItems) ? vehicle.customComplianceItems : []).filter(it => it && it.expiryDate).map(it => formatExpiryBadge(it.expiryDate, escapeHtml(it.label || 'Custom'))).join('')}
          ${formatHvutBadge(vehicle)}
        ` : ''}
      </div>
    </div>
  `;
}

// TASK-9935 — what the "Doesn't expire" confirm weighs for this editor: the
// stored unit plus the IRP date as typed. Registered on every open (idempotent);
// window.RegNoExpiryGuard comes from roster.js, the one main-app importer.
function _registerVcRegNoExpGuard() {
  window.RegNoExpiryGuard?.register('compliance-vehicle', (cb) => {
    const vid = cb.getAttribute('data-vid') || '';
    const v = [...ComplianceState.trucks, ...ComplianceState.trailers].find(x => x.id === vid) || {};
    const irpEl = document.getElementById('vc-irpexp-' + vid);
    return { gvwr: v.gvwr, gcwr: v.gcwr, registeredWeight: v.registeredWeight, irpExpiry: irpEl ? irpEl.value : v.irpExpiry, irpDocs: v.irpDocs };
  });
}

function showVehicleDetail(vehicleId) {
  _registerVcRegNoExpGuard();
  // TASK-1147 — the vehicle detail can open from a sub-view that never
  // subscribed the asset streams (Overview health-summary / signals rows).
  // Same on-demand subscribe + spinner pattern as showPersonDetail; the retry
  // fires when the streams' first snapshots land.
  ensureStreams(VEHICLE_DETAIL_STREAMS);
  if (!_streamsLoadedAll(VEHICLE_DETAIL_STREAMS)) {
    const modalEl = document.getElementById('compliance-detail-modal');
    if (!modalEl) return;
    ComplianceState.selectedVehicleId = vehicleId;
    modalEl.innerHTML = `
      <div class="modal-content" style="max-width:560px;">
        <div style="display:flex;align-items:center;justify-content:center;padding:60px 0;">
          <div class="loading-spinner"></div>
        </div>
      </div>`;
    modalEl.classList.remove('hidden');
    _whenStreamsLoaded(VEHICLE_DETAIL_STREAMS, () => {
      if (ComplianceState.selectedVehicleId === vehicleId && !modalEl.classList.contains('hidden')) {
        showVehicleDetail(vehicleId);
      }
    });
    return;
  }

  const vehicle = [...ComplianceState.trucks, ...ComplianceState.trailers].find(v => v.id === vehicleId);
  if (!vehicle) {
    // Don't strand the TASK-1147 loading shell if the id can't be resolved
    // once the streams land (deleted vehicle / equipment-asset id).
    if (ComplianceState.selectedVehicleId === vehicleId) window.ComplianceModule?.closeDetail?.();
    return;
  }

  ComplianceState.selectedVehicleId = vehicleId;

  const modal = document.getElementById('compliance-detail-modal');
  if (!modal) return;

  const status = getVehicleComplianceStatus(vehicle, ComplianceState.pretrips, ComplianceState.maintenance);
  // TASK-749 — a compliance-only carrier has no Maintenance/DVIR module; show the
  // DVIR + maintenance sections WITH a subtle upgrade note (Chris: show-w/-note,
  // not hide) instead of the "View in Maintenance" deep-link.
  const complianceOnly = !!window.isComplianceOnlyTenant?.();
  const typeLabel = vehicle._type === 'trailer' ? 'Trailer' : 'Truck';
  const displayName = [vehicle.nickname || vehicle.make, vehicle.model].filter(Boolean).join(' ');

  // Get vehicle DVIRs sorted by date
  const vehiclePretrips = ComplianceState.pretrips.filter(p =>
    p.truckId === vehicle.id || p.trailerId === vehicle.id ||
    p.truck === vehicle.unit || p.trailer === vehicle.unit
  ).sort((a, b) => {
    const da = a.createdAt?.toDate?.() || new Date(a.date || 0);
    const db2 = b.createdAt?.toDate?.() || new Date(b.date || 0);
    return db2 - da;
  });

  // Active maintenance for this vehicle
  const vehicleMaint = ComplianceState.maintenance.filter(m =>
    m.truckId === vehicle.id || m.trailerId === vehicle.id ||
    m.truck === vehicle.unit || m.trailer === vehicle.unit
  );

  // DVIR timeline
  const dvirTimeline = vehiclePretrips.length > 0 ? `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">checklist</span> DVIR History (Last 90 Days)
      </div>
      <div class="compliance-timeline">
        ${vehiclePretrips.slice(0, 20).map(p => {
          const pRaw = p.createdAt?.toDate?.() || (p.date ? new Date(p.date) : null);
          const pDate = pRaw ? pRaw.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : (p.date || 'Unknown date');
          const isDefect = _dvirHasDefects(p);
          const driverName = p.driverName || p.driver || 'Unknown';
          const linkedWo = vehicleMaint.find(m => m.pretripId === p.id || m.dvirId === p.id);
          return `
            <div class="compliance-timeline-item">
              <div class="compliance-timeline-icon" style="color:${isDefect ? '#dc2626' : '#059669'};">
                <span class="material-icons" style="font-size:16px;">${isDefect ? 'report_problem' : 'check_circle'}</span>
              </div>
              <div class="compliance-timeline-content">
                <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                  <span style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${pDate}</span>
                  <span class="compliance-badge ${isDefect ? 'compliance-badge-critical' : 'compliance-badge-good'}" style="font-size:0.7rem;padding:1px 6px;">
                    ${isDefect ? 'Defect' : 'Pass'}
                  </span>
                  ${p.resolved ? '<span class="compliance-badge compliance-badge-good" style="font-size:0.7rem;padding:1px 6px;">Resolved</span>' : ''}
                </div>
                <div style="font-size:0.78rem;color:var(--text-secondary);">Inspector: ${escapeHtml(driverName)}</div>
                ${isDefect && p.defects ? `<div style="font-size:0.78rem;color:#dc2626;margin-top:2px;">${escapeHtml(typeof p.defects === 'string' ? p.defects : (p.defects?.length ? p.defects.join(', ') : ''))}</div>` : ''}
                ${linkedWo ? `<div style="font-size:0.75rem;color:var(--brand-blue);margin-top:2px;display:flex;align-items:center;gap:2px;">
                  <span class="material-icons" style="font-size:14px;">build</span> Linked to Work Order
                </div>` : ''}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    </div>
  ` : `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">checklist</span> DVIR History
      </div>
      <div style="padding:12px;color:var(--text-muted);font-size:0.85rem;text-align:center;">No DVIRs in the last 90 days</div>
    </div>
  `;

  // Active maintenance
  const maintSection = vehicleMaint.length > 0 ? `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title">
        <span class="material-icons" style="font-size:18px;color:#d97706;">build</span> Active Maintenance
        <span class="compliance-badge compliance-badge-warning" style="margin-left:8px;">${vehicleMaint.length}</span>
      </div>
      <div class="compliance-timeline">
        ${vehicleMaint.map(m => `
          <div class="compliance-timeline-item">
            <div class="compliance-timeline-icon" style="color:${_maintenanceIsHoldPriority(m) ? '#dc2626' : '#d97706'};">
              <span class="material-icons" style="font-size:16px;">build</span>
            </div>
            <div class="compliance-timeline-content">
              <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${escapeHtml(m.title || m.description || 'Untitled Task')}</div>
              <div style="font-size:0.78rem;color:var(--text-secondary);">
                ${m.priority ? `Priority: ${escapeHtml(m.priority)}` : ''} ${m.status ? `&bull; Status: ${escapeHtml(m.status)}` : ''}
              </div>
            </div>
          </div>
        `).join('')}
      </div>
    </div>
  ` : '';

  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;max-height:90dvh;overflow-y:auto;">
      <div class="compliance-detail-header compliance-detail-header-${status}">
        <div style="display:flex;align-items:center;justify-content:space-between;">
          <div>
            <div style="display:flex;align-items:center;gap:8px;">
              <h3 style="margin:0;font-size:1.1rem;font-weight:700;color:var(--text-primary);">${escapeHtml(vehicle.unit || vehicle.nickname || vehicle.name || vehicle.make || 'Unknown')}</h3>
              <span class="compliance-type-badge compliance-type-${vehicle._type}">${typeLabel}</span>
            </div>
            <div style="font-size:0.85rem;color:var(--text-secondary);margin-top:2px;">${escapeHtml(displayName)}</div>
          </div>
          <button class="btn-icon" onclick="window.ComplianceModule.closeDetail()" title="Close">
            <span class="material-icons">close</span>
          </button>
        </div>
      </div>
      <div style="padding:var(--spacing-md);">
        ${vehicle._type !== 'asset' ? `
        ${renderVehicleComplianceSection(vehicle)}
        ${renderHvutDetailSection(vehicle)}
        <div id="vc-permit-geometry"></div>
        ` : ''}
        ${dvirTimeline}
        ${maintSection}
        ${complianceOnly ? `
        <div style="margin:var(--spacing-sm) 0;padding:8px 12px;border:1px dashed var(--border-medium);border-radius:8px;background:var(--bg-subtle, #f8fafc);text-align:center;">
          <span style="font-size:0.8rem;color:var(--text-secondary);">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;color:var(--brand-blue);">workspace_premium</span>
            DVIR history &amp; maintenance work orders are part of the full DispatchDeck platform.
            <a href="#/billing" class="compliance-source-link" onclick="window.ComplianceModule.closeDetail()" style="font-weight:600;">Upgrade to unlock</a>.
          </span>
        </div>
        ` : `
        <div style="padding:var(--spacing-sm) 0;text-align:center;">
          <a href="#/maintenance" class="compliance-source-link" onclick="window.ComplianceModule.closeDetail()">
            <span class="material-icons" style="font-size:16px;">build</span> View in Maintenance
          </a>
        </div>
        `}
      </div>
    </div>
  `;

  modal.classList.remove('hidden');
  // TASK-9993 — Permit measurements for a Compliance login (no Roster card). LAZY;
  // the SAME url + ?v= as roster.js (pitfall #7). savePermitGeometry re-checks access.
  const pgEl = document.getElementById('vc-permit-geometry');
  if (pgEl) __ddChunkWrap(import('./rosterPermitGeometry.js?v=10304876')).then((m) => m.complianceDraw({ escapeHtml, callSave: (p) => httpsCallable(functions, 'savePermitGeometry')(p) }, pgEl, vehicle))
    .catch((e) => console.warn('[Compliance] rosterPermitGeometry:', e?.message || e));
}

// ============================================
// DRIVERS SCORECARD TAB
// ============================================
function toDateC(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (val instanceof Date) return val;
  if (typeof val === 'string') return new Date(val);
  return null;
}

function getDriverSafetyGrade(score) {
  if (score >= 95) return { letter: 'A', color: '#059669', bg: '#ECFDF5' };
  if (score >= 85) return { letter: 'B', color: '#3B82F6', bg: '#EFF6FF' };
  if (score >= 70) return { letter: 'C', color: '#D97706', bg: '#FFFBEB' };
  if (score >= 50) return { letter: 'D', color: '#EA580C', bg: '#FFF7ED' };
  return { letter: 'F', color: '#DC2626', bg: '#FEF2F2' };
}

function renderDriversScorecard() {
  const activePeople = ComplianceState.people.filter(p => p.active !== false);
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  // Build scorecard data for each person
  const scorecards = activePeople.map(person => {
    const name = person.name || 'Unknown';
    const role = person.crewType || person.role || person.roles?.[0] || '';

    // DVIRs for this driver (last 90 days — already filtered by subscription)
    const driverDvirs = ComplianceState.pretrips.filter(p => p.driverId === person.id);
    const dvirCount = driverDvirs.length;
    const passCount = driverDvirs.filter(p => p.status === 'pass' || p.result === 'pass' || p.result === 'Pass').length;
    const defectCount = dvirCount - passCount;
    const dvirPassRate = dvirCount > 0 ? (passCount / dvirCount * 100) : null;

    // Days this driver was assigned to a job (from jobDays)
    const last30Pretrips = driverDvirs.filter(p => {
      const d = toDateC(p.createdAt);
      return d && d >= thirtyDaysAgo;
    });
    const dvirDays30 = new Set(last30Pretrips.map(p => {
      const d = toDateC(p.createdAt);
      return d ? d.toISOString().slice(0, 10) : null;
    }).filter(Boolean));

    // Find days assigned via jobDays
    const assignedDays = new Set();
    ComplianceState.jobDays.forEach(jd => {
      const drivers = jd.drivers || jd.people || [];
      if (drivers.some(ref => ref === person.id || ref === person.name)) {
        const dayStr = typeof jd.date === 'string' ? jd.date : toDateC(jd.date)?.toISOString?.()?.slice(0, 10);
        if (dayStr) {
          const dayDate = new Date(dayStr);
          if (dayDate >= thirtyDaysAgo) assignedDays.add(dayStr);
        }
      }
    });

    // DVIR completion rate = DVIRs submitted / days assigned (last 30 days)
    const dvirCompletionRate = assignedDays.size > 0 ? Math.min(100, (dvirDays30.size / assignedDays.size * 100)) : (dvirCount > 0 ? 100 : null);

    // Missed DVIRs from person record
    const missedDvirs = person.missedDvirCount || 0;

    // Clock consistency (last 90 days)
    const driverClock = ComplianceState.clockEntries.filter(c => c.personId === person.id);
    const clockDays = new Set();
    driverClock.forEach(c => {
      const d = toDateC(c.clockIn);
      if (d) clockDays.add(d.toISOString().slice(0, 10));
    });
    const clockConsistency = assignedDays.size > 0 ? Math.min(100, (clockDays.size / assignedDays.size * 100)) : null;

    // Defects reported — flag drivers who never report defects as possibly not inspecting thoroughly
    const defectReportRate = dvirCount > 0 ? (defectCount / dvirCount * 100) : null;

    // Safety score: weighted composite
    // DVIR completion 40%, pass rate 30%, clock consistency 20%, no missed DVIRs 10%
    let safetyScore = 0;
    let scoreFactors = 0;
    if (dvirCompletionRate !== null) { safetyScore += dvirCompletionRate * 0.4; scoreFactors += 0.4; }
    if (dvirPassRate !== null) { safetyScore += dvirPassRate * 0.3; scoreFactors += 0.3; }
    if (clockConsistency !== null) { safetyScore += clockConsistency * 0.2; scoreFactors += 0.2; }
    safetyScore += (missedDvirs === 0 ? 100 : Math.max(0, 100 - missedDvirs * 25)) * 0.1;
    scoreFactors += 0.1;
    if (scoreFactors > 0) safetyScore = safetyScore / scoreFactors * (scoreFactors > 0 ? 1 : 0);
    safetyScore = Math.round(safetyScore);

    const hasData = dvirCount > 0 || clockDays.size > 0 || assignedDays.size > 0;

    return {
      person, name, role, dvirCount, passCount, defectCount, dvirPassRate,
      dvirCompletionRate, missedDvirs, clockConsistency, clockDays: clockDays.size,
      assignedDays: assignedDays.size, defectReportRate, safetyScore, hasData
    };
  });

  // Sort by safety score ascending (worst first) — people with no data at the end
  const withData = scorecards.filter(s => s.hasData).sort((a, b) => a.safetyScore - b.safetyScore);
  const noData = scorecards.filter(s => !s.hasData).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const sorted = [...withData, ...noData];

  // Fleet averages
  const withDvir = scorecards.filter(s => s.dvirPassRate !== null);
  const avgPassRate = withDvir.length > 0 ? withDvir.reduce((s, d) => s + d.dvirPassRate, 0) / withDvir.length : 0;
  const withCompletion = scorecards.filter(s => s.dvirCompletionRate !== null);
  const avgCompletion = withCompletion.length > 0 ? withCompletion.reduce((s, d) => s + d.dvirCompletionRate, 0) / withCompletion.length : 0;
  const totalMissed = scorecards.reduce((s, d) => s + d.missedDvirs, 0);
  const avgScore = withData.length > 0 ? Math.round(withData.reduce((s, d) => s + d.safetyScore, 0) / withData.length) : 0;
  const avgGrade = getDriverSafetyGrade(avgScore);

  return `
    <div style="margin-top:var(--spacing-md);">
      <div style="display:grid;grid-template-columns:repeat(auto-fit, minmax(130px, 1fr));gap:12px;margin-bottom:16px;">
        <div style="background:${avgGrade.bg};border-radius:12px;padding:16px;text-align:center;">
          <div style="font-size:32px;font-weight:800;color:${avgGrade.color};">${avgGrade.letter}</div>
          <div style="font-size:11px;color:var(--text-secondary);margin-top:4px;">Fleet Safety Grade</div>
        </div>
        <div style="background:#F0FDFA;border-radius:12px;padding:16px;text-align:center;">
          <div style="font-size:22px;font-weight:700;color:#059669;">${avgCompletion.toFixed(0)}%</div>
          <div style="font-size:11px;color:var(--text-secondary);margin-top:4px;">DVIR Completion</div>
        </div>
        <div style="background:#EFF6FF;border-radius:12px;padding:16px;text-align:center;">
          <div style="font-size:22px;font-weight:700;color:#3B82F6;">${avgPassRate.toFixed(0)}%</div>
          <div style="font-size:11px;color:var(--text-secondary);margin-top:4px;">Avg Pass Rate</div>
        </div>
        <div style="background:${totalMissed > 0 ? '#FEF2F2' : '#ECFDF5'};border-radius:12px;padding:16px;text-align:center;">
          <div style="font-size:22px;font-weight:700;color:${totalMissed > 0 ? '#DC2626' : '#059669'};">${totalMissed}</div>
          <div style="font-size:11px;color:var(--text-secondary);margin-top:4px;">Missed DVIRs</div>
        </div>
      </div>

      <div style="font-size:12px;color:var(--text-secondary);margin-bottom:12px;">
        ${sorted.length} crew members &middot; Last 90 days &middot; Sorted by safety score (lowest first)
      </div>

      <div class="compliance-grid">
        ${sorted.map(d => renderDriverCard(d)).join('')}
      </div>

      ${sorted.length === 0 ? `
        <div class="empty-state" style="margin-top:var(--spacing-xl);">
          <div class="empty-state-icon"><span class="material-icons" style="font-size:48px;color:var(--text-muted);">badge</span></div>
          <div class="empty-state-title">No Active Crew</div>
          <div class="empty-state-text">Add people in the Roster to see driver scorecards.</div>
        </div>
      ` : ''}
    </div>
  `;
}

function renderDriverCard(d) {
  const grade = getDriverSafetyGrade(d.safetyScore);
  const status = d.safetyScore >= 85 ? 'good' : d.safetyScore >= 70 ? 'warning' : d.hasData ? 'critical' : 'good';

  const metricRow = (label, value, color) => `
    <div style="display:flex;justify-content:space-between;align-items:center;padding:3px 0;">
      <span style="font-size:12px;color:var(--text-secondary);">${label}</span>
      <span style="font-size:12px;font-weight:600;color:${color || 'var(--text-primary)'};">${value}</span>
    </div>
  `;

  const dvirRateColor = d.dvirCompletionRate === null ? 'var(--text-secondary)' : d.dvirCompletionRate >= 90 ? '#059669' : d.dvirCompletionRate >= 70 ? '#D97706' : '#DC2626';
  const passRateColor = d.dvirPassRate === null ? 'var(--text-secondary)' : d.dvirPassRate >= 90 ? '#059669' : d.dvirPassRate >= 70 ? '#D97706' : '#DC2626';
  const clockColor = d.clockConsistency === null ? 'var(--text-secondary)' : d.clockConsistency >= 90 ? '#059669' : d.clockConsistency >= 70 ? '#D97706' : '#DC2626';

  return `
    <div class="compliance-card compliance-card-${status}" onclick="window.ComplianceModule.showPersonDetail('${d.person.id}')" style="cursor:pointer;">
      <div class="compliance-card-header">
        <div style="display:flex;align-items:center;justify-content:space-between;width:100%;">
          <div>
            <div class="compliance-card-name">${escapeHtml(d.name)}</div>
            ${d.role ? `<div style="font-size:11px;color:var(--text-secondary);margin-top:2px;">${escapeHtml(d.role)}</div>` : ''}
          </div>
          ${d.hasData ? `
          <div style="width:40px;height:40px;border-radius:10px;background:${grade.bg};display:flex;align-items:center;justify-content:center;flex-shrink:0;">
            <span style="font-size:20px;font-weight:800;color:${grade.color};">${grade.letter}</span>
          </div>` : `
          <div style="width:40px;height:40px;border-radius:10px;background:#F1F5F9;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
            <span style="font-size:14px;color:var(--text-secondary);">—</span>
          </div>`}
        </div>
      </div>
      <div style="padding:4px 0;">
        ${metricRow('DVIR Completion', d.dvirCompletionRate !== null ? d.dvirCompletionRate.toFixed(0) + '%' : 'N/A', dvirRateColor)}
        ${metricRow('Pass Rate', d.dvirPassRate !== null ? d.dvirPassRate.toFixed(0) + '%' : 'N/A', passRateColor)}
        ${metricRow('Clock Consistency', d.clockConsistency !== null ? d.clockConsistency.toFixed(0) + '%' : 'N/A', clockColor)}
        ${metricRow('Missed DVIRs', d.missedDvirs > 0 ? d.missedDvirs.toString() : '0', d.missedDvirs > 0 ? '#DC2626' : '#059669')}
        ${metricRow('Defects Reported', d.defectCount.toString(), d.defectCount > 0 ? '#D97706' : 'var(--text-primary)')}
        ${metricRow('DVIRs (90d)', d.dvirCount.toString(), 'var(--text-primary)')}
      </div>
    </div>
  `;
}

// ============================================
// IFTA TAB
// ============================================
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];
const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];
const ALL_JURISDICTIONS = [...US_STATES, ...CA_PROVINCES].sort();

const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',CT:'Connecticut',
  DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',
  KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',
  MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',
  NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',
  OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',
  WI:'Wisconsin',WY:'Wyoming',DC:'Washington DC',
  AB:'Alberta',BC:'British Columbia',MB:'Manitoba',NB:'New Brunswick',NL:'Newfoundland',NS:'Nova Scotia',
  NT:'Northwest Territories',NU:'Nunavut',ON:'Ontario',PE:'Prince Edward Island',QC:'Quebec',
  SK:'Saskatchewan',YT:'Yukon'
};

function getQuarterLabel(q) {
  return `Q${q}`;
}

function filterFuelByQuarter(entries, year, quarter) {
  // TASK-9936 (COD) — bounds as calendar strings. toISOString() on a LOCAL
  // midnight shifted both edges a day early east of UTC, so a Mar 31 row fell
  // into Q2 there. Rows store a YYYY-MM-DD calendar day; compare like with like.
  const m0 = (quarter - 1) * 3;
  const pad = (n) => String(n).padStart(2, '0');
  const startStr = `${year}-${pad(m0 + 1)}-01`;
  const endStr = `${year}-${pad(m0 + 3)}-${pad(new Date(Date.UTC(year, m0 + 3, 0)).getUTCDate())}`;
  return entries.filter(e => e.date >= startStr && e.date <= endStr);
}

// Lazy IFTA filter: trucks default true when field missing; trailers default false.
// Entries with no vehicleId are treated as legacy-IFTA (kept in reports) so we never
// silently drop historical fuel records.
function isIftaVehicleId(vehicleId) {
  if (!vehicleId) return true; // legacy / manually entered fuel — keep in report
  const truck = ComplianceState.trucks.find(v => v.id === vehicleId);
  if (truck) return truck.iftaRegistered !== false; // truck default = true
  const trailer = ComplianceState.trailers.find(v => v.id === vehicleId);
  if (trailer) return trailer.iftaRegistered === true; // trailer default = false
  return true; // unknown vehicle (deleted, etc.) — keep entry
}

// TASK-9920 — fuelPurchases now holds two kinds of row: manual IFTA-tab entries
// and READ-ONLY mirrors of approved fuel expenses (source:'expense', written by
// the onCostEntryWriteIftaMirror trigger; rule in functions/src/shared/iftaFuel.js).
// Two rows for the same fill-up (see mergeIftaFuel) are shown but counted ONCE.
const IFTA_SOURCE_LABELS = {
  manual: 'Entered on IFTA tab',
  driver: 'Driver expense',
  staff: 'Staff expense',
  admin: 'Office expense',
  fuel_card_import: 'Fuel card import',
  expense: 'Expense',
};

function iftaJurisdictionCode(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  const up = s.toUpperCase();
  if (STATE_NAMES[up]) return up;
  const f = s.toLowerCase();
  for (const code of Object.keys(STATE_NAMES)) {
    if (STATE_NAMES[code].toLowerCase() === f) return code;
  }
  return null;
}

// A row is an expense mirror only when it has the trigger's whole shape
// (source + sourceCostEntryId + an id built from it). Anything else is manual.
function iftaSourceKey(e) {
  if (!e || e.source !== 'expense' || !e.sourceCostEntryId) return 'manual';
  const id = String(e.id || '');
  if (id !== `exp_${e.sourceCostEntryId}` && !id.startsWith(`exp_${e.sourceCostEntryId}_`)) return 'manual';
  return IFTA_SOURCE_LABELS[e.expenseSource] ? e.expenseSource : 'expense';
}

// Which record wins when two describe the same fill-up: the card statement line,
// then an approved expense, then a hand entry.
const IFTA_SOURCE_RANK = { fuel_card_import: 3, manual: 1 };
function _iftaRank(r) { return IFTA_SOURCE_RANK[r._src] || 2; }
function _iftaDay(ymd) { const t = Date.parse(`${ymd}T12:00:00Z`); return Number.isFinite(t) ? Math.round(t / 864e5) : null; }
// Is `lo` (the lower-ranked row) dated close enough to `hi` to be the same fill?
// A driver / staff expense dated when it was SUBMITTED lands 0-7 days after the swipe.
function _iftaDatesMatch(lo, hi) {
  const a = _iftaDay(lo.date); const b = _iftaDay(hi.date);
  if (a == null || b == null) return false;
  if (lo.dateBasis === 'submitted') return a - b >= -1 && a - b <= 7;
  if (hi.dateBasis === 'submitted') return b - a >= -1 && b - a <= 7;
  return Math.abs(a - b) <= 1;
}

// Two records of the SAME fill-up (same truck, same state, gallons within 0.5,
// close dates) are both shown but counted ONCE. One fill-up = one counted row
// that absorbs at most ONE row of each lower rank (card <- expense <- manual), so
// manual + expense + card for the same fill count once, never twice. Run over
// ALL rows (not one quarter) so a card line on Mar 31 still pairs with the
// expense submitted Apr 2.
function mergeIftaFuel(entries) {
  const rows = (entries || []).map(e => ({ ...e, _src: iftaSourceKey(e), _st: iftaJurisdictionCode(e.jurisdiction), _dupOf: null }));
  const byKey = new Map();
  rows.forEach(r => {
    if (!r.vehicleId || !r.date || !r._st) return; // an unknown state never pairs
    const k = `${r.vehicleId}|${r._st}`;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(r);
  });
  byKey.forEach(group => {
    const byId = new Map(group.map(r => [r.id, r]));
    // The counted row a row's fill-up ends at (an expense absorbed by a card -> the card).
    const root = r => { let x = r; for (let i = 0; x && x._dupOf && i < 4; i++) x = byId.get(x._dupOf); return x || r; };
    const claimed = new Set(); // `${root id}|${absorbed rank}` — one row per rank per fill
    const gal = r => Number(r.gallons) || 0;
    // Deterministic: HIGHER-ranked losers first (expenses pair to card lines
    // before hand entries pair to either), then date, gallons, id.
    const order = group.slice().sort((a, b) => _iftaRank(b) - _iftaRank(a) || String(a.date).localeCompare(String(b.date)) || gal(a) - gal(b) || String(a.id).localeCompare(String(b.id)));
    order.forEach(lo => {
      if (_iftaRank(lo) >= 3) return; // a card line is never absorbed
      // CLOSEST candidate: gallons first, then days, then id (lexicographic).
      let best = null; let bestG = Infinity; let bestD = Infinity;
      group.forEach(x => {
        if (x === lo || _iftaRank(x) <= _iftaRank(lo)) return;
        const r = root(x);
        if (r === lo || claimed.has(`${r.id}|${_iftaRank(lo)}`)) return;
        const dg = Math.round(Math.abs(gal(x) - gal(lo)) * 1000) / 1000;
        if (dg > 0.5 || !_iftaDatesMatch(lo, x)) return;
        // Also a match for the COUNTED row, so tolerances never chain
        // (card 100 <- expense 100.5 <- manual 101 is two fills, not one).
        if (r !== x && (Math.round(Math.abs(gal(r) - gal(lo)) * 1000) / 1000 > 0.5 || !_iftaDatesMatch(lo, r))) return;
        const dd = Math.abs((_iftaDay(x.date) || 0) - (_iftaDay(lo.date) || 0));
        if (dg < bestG || (dg === bestG && (dd < bestD || (dd === bestD && String(x.id) < String(best.id))))) { best = x; bestG = dg; bestD = dd; }
      });
      if (best) { claimed.add(`${root(best).id}|${_iftaRank(lo)}`); lo._dupOf = best.id; }
    });
  });
  return rows;
}

// TASK-9936 — fuel rows with no usable state, by quarter, so the
// office sees rows that need fixing even when they sit in a quarter not on
// screen. Rows only, never a write: the office fixes the state on the expense
// (or the entry). A same-fill duplicate is skipped (its counted twin is listed).
function iftaNeedsStateByQuarter(rows) {
  const out = {};
  (rows || []).forEach(r => {
    if (!r || r._dupOf || r._st || !/^\d{4}-\d{2}-\d{2}/.test(String(r.date || ''))) return;
    const y = Number(String(r.date).slice(0, 4));
    const mo = Number(String(r.date).slice(5, 7));
    if (!(mo >= 1 && mo <= 12)) return; // COD — a malformed month never makes a Q0/Q5 button
    const q = Math.floor((mo - 1) / 3) + 1;
    const k = `${y}-Q${q}`;
    if (!out[k]) out[k] = { year: y, quarter: q, count: 0 };
    out[k].count++;
  });
  return Object.values(out).sort((a, b) => a.year - b.year || a.quarter - b.quarter);
}

// One line of jump buttons, shared by both banner shapes (COD UX: one card, not two).
// year / quarter are numbers from iftaNeedsStateByQuarter — never row text.
function iftaNeedsStateJumpsHtml(groups) {
  return `<span style="flex-basis:100%;">Other quarters also have fuel that needs a state (a quarter you already filed needs an amended return):</span>
      ${groups.map(g => `<button type="button" class="btn btn-secondary btn-sm" data-ifta-goto="${g.year}-${g.quarter}" onclick="window.ComplianceModule.iftaGoQuarter(${g.year}, ${g.quarter})">${getQuarterLabel(g.quarter)} ${g.year}: ${g.count}</button>`).join('')}`;
}

// TASK-10086-O — may this login write IFTA fuel rows? Mirror of the fuelPurchases
// rule fpW() (TASK-10086-K): demo, owner by tenants.ownerId, or an active member
// with role owner/admin, or compliance/maintenance and not view/upload-only.
// create adds a view/upload-only compliance holder (TASK-608). No member doc or a
// partner's synthetic grant -> false (the rule sees no members doc). Not
// checkModuleAccess: it previews Compliance to everyone on a complianceOnly
// carrier. Hides buttons only; the rule enforces.
function _compFuelW(create) {
  const S = window.AppState || {};
  if (S.isDemo || S.tenant === 'demo' || (S.user?.uid && S.user.uid === S.tenantData?.ownerId)) return true;
  const m = S.memberAccess;
  if (!m || m.synthetic || /^\s*(revoked|inactive|pending|removed|suspended)\s*$/i.test(m.status || '')) return false;
  const mods = Array.isArray(m.modules) ? m.modules : [];
  return m.role === 'owner' || m.role === 'admin'
    || ((mods.includes('compliance') || mods.includes('maintenance')) && m.viewUploadOnly !== true)
    || (!!create && mods.includes('compliance'));
}
const FUEL_DENIED = "You don't have permission to change fuel purchases. Ask an admin for Compliance access.";
function _fuelErr(err, fallback) { return err?.code === 'permission-denied' ? FUEL_DENIED : fallback; }

function renderIftaTab() {
  const { iftaYear, iftaQuarter } = ComplianceState;
  const canAdd = _compFuelW(1); const canEdit = _compFuelW();
  const merged = mergeIftaFuel(ComplianceState.fuelPurchases.filter(e => isIftaVehicleId(e.vehicleId)));
  const allQuarterEntries = filterFuelByQuarter(ComplianceState.fuelPurchases, iftaYear, iftaQuarter);
  const iftaRows = filterFuelByQuarter(merged, iftaYear, iftaQuarter);
  const excludedCount = allQuarterEntries.length - iftaRows.length;
  // An expense with no truck is not counted: IFTA credits only fuel put in a
  // qualified vehicle (R1010). Legacy manual rows with no truck keep counting.
  const noTruck = iftaRows.filter(e => e._src !== 'manual' && !e.vehicleId);
  const quarterEntries = iftaRows.filter(e => !e._dupOf && !(e._src !== 'manual' && !e.vehicleId)); // what the totals count
  const dupCount = iftaRows.filter(e => e._dupOf).length;
  // Every listed row with no state needs fixing — a no-truck expense too
  // (it is not counted, but setting its truck alone would still leave no state).
  const missingState = iftaRows.filter(e => !e._dupOf && !e._st);
  const missingStateGal = missingState.reduce((s, e) => s + (e.gallons || 0), 0);
  const needsStateElsewhere = iftaNeedsStateByQuarter(merged).filter(g => !(g.year === iftaYear && g.quarter === iftaQuarter));
  const missingVehicle = noTruck.length;
  const bySource = {};
  quarterEntries.forEach(e => {
    if (!bySource[e._src]) bySource[e._src] = { gallons: 0, count: 0 };
    bySource[e._src].gallons += (e.gallons || 0);
    bySource[e._src].count++;
  });

  // Summarize by jurisdiction
  const byState = {};
  quarterEntries.forEach(e => {
    const st = e._st || 'Missing state';
    if (!byState[st]) byState[st] = { gallons: 0, cost: 0, miles: 0, count: 0 };
    byState[st].gallons += (e.gallons || 0);
    byState[st].cost += (e.totalCost || 0);
    byState[st].miles += (e.miles || 0);
    byState[st].count++;
  });
  const sortedStates = Object.keys(byState).sort((a, b) => (a === 'Missing state') - (b === 'Missing state') || a.localeCompare(b));

  const totalGallons = quarterEntries.reduce((s, e) => s + (e.gallons || 0), 0);
  const totalCost = quarterEntries.reduce((s, e) => s + (e.totalCost || 0), 0);
  const totalMiles = quarterEntries.reduce((s, e) => s + (e.miles || 0), 0);
  const avgMpg = totalGallons > 0 ? (totalMiles / totalGallons).toFixed(2) : '—';

  return `
    <!-- Quarter Selector -->
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-md);flex-wrap:wrap;gap:8px;">
      <div style="display:flex;align-items:center;gap:8px;">
        <button class="btn btn-secondary btn-sm" onclick="window.ComplianceModule.iftaPrevQuarter()">
          <span class="material-icons" style="font-size:18px;">chevron_left</span>
        </button>
        <span style="font-weight:700;font-size:1rem;color:var(--text-primary);">${getQuarterLabel(iftaQuarter)} ${iftaYear}</span>
        <button class="btn btn-secondary btn-sm" onclick="window.ComplianceModule.iftaNextQuarter()">
          <span class="material-icons" style="font-size:18px;">chevron_right</span>
        </button>
      </div>
      ${canAdd ? `<button class="btn btn-primary btn-sm" onclick="window.ComplianceModule.showAddFuel()">
        <span class="material-icons" style="font-size:18px;">add</span> Add Fuel Purchase
      </button>` : `<span data-testid="ifta-fuel-view-only" style="font-size:0.8rem;color:var(--text-secondary);">${FUEL_DENIED}</span>`}
    </div>

    ${excludedCount > 0 ? `
    <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:#FEF3C7;border:1px solid #FCD34D;border-radius:var(--radius-md);margin-bottom:var(--spacing-md);font-size:0.8rem;color:#92400E;">
      <span class="material-icons" style="font-size:18px;">info</span>
      <span><strong>${excludedCount}</strong> fuel ${excludedCount === 1 ? 'entry' : 'entries'} excluded from this IFTA report (non-IFTA-registered vehicles). Toggle in Roster → Vehicle → Registration & Compliance.</span>
    </div>` : ''}

    ${missingState.length > 0 ? `
    <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:#FEE2E2;border:1px solid #FCA5A5;border-radius:var(--radius-md);margin-bottom:var(--spacing-md);font-size:0.8rem;color:#991B1B;">
      <span class="material-icons" style="font-size:18px;">error_outline</span>
      <span><strong>${missingState.length}</strong> fuel ${missingState.length === 1 ? 'purchase needs' : 'purchases need'} a state (${missingStateGal.toFixed(1)} gal) — look for the red <strong>Needs state</strong> tag below. IFTA credits fuel tax by the state it was bought in — add the state on the expense (or the entry) before you file.</span>
      ${needsStateElsewhere.length > 0 ? iftaNeedsStateJumpsHtml(needsStateElsewhere) : ''}
    </div>` : ''}

    ${missingState.length === 0 && needsStateElsewhere.length > 0 ? `
    <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:8px 12px;background:#FEE2E2;border:1px solid #FCA5A5;border-radius:var(--radius-md);margin-bottom:var(--spacing-md);font-size:0.8rem;color:#991B1B;">
      <span class="material-icons" style="font-size:18px;">error_outline</span>
      ${iftaNeedsStateJumpsHtml(needsStateElsewhere)}
    </div>` : ''}

    ${missingVehicle > 0 ? `
    <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:#FEF3C7;border:1px solid #FCD34D;border-radius:var(--radius-md);margin-bottom:var(--spacing-md);font-size:0.8rem;color:#92400E;">
      <span class="material-icons" style="font-size:18px;">info</span>
      <span><strong>${missingVehicle}</strong> fuel ${missingVehicle === 1 ? 'expense has' : 'expenses have'} no truck (${noTruck.reduce((s, e) => s + (e.gallons || 0), 0).toFixed(1)} gal) and ${missingVehicle === 1 ? 'is' : 'are'} not counted. IFTA only counts fuel put in an IFTA truck. Set the truck on the expense to count it.</span>
    </div>` : ''}

    ${quarterEntries.length > 0 ? `
    <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-lg);padding:10px 16px;margin-bottom:var(--spacing-md);font-size:0.8rem;color:var(--text-primary);">
      <div style="font-weight:700;margin-bottom:4px;">Where the gallons came from</div>
      <div style="display:flex;flex-wrap:wrap;gap:6px 16px;">
        ${Object.keys(bySource).sort().map(k => `<span>${escapeHtml(IFTA_SOURCE_LABELS[k] || k)}: <strong>${bySource[k].gallons.toFixed(1)} gal</strong> <span style="color:var(--text-secondary);">(${bySource[k].count})</span></span>`).join('')}
      </div>
      <div style="color:var(--text-secondary);margin-top:4px;">Only approved expenses count. DEF is not counted.${dupCount > 0 ? ` ${dupCount} ${dupCount === 1 ? 'row is' : 'rows are'} the same fill-up as another record and counted once.` : ''}</div>
    </div>` : ''}

    <!-- Summary Stats -->
    <div class="crm-stats-grid" style="margin-bottom:var(--spacing-md);">
      <div class="crm-stat-card bi-stat-vertical">
        <div class="crm-stat-icon" style="background:#DBEAFE;color:#3B82F6;">
          <span class="material-icons">local_gas_station</span>
        </div>
        <div class="crm-stat-value">${totalGallons.toFixed(1)}</div>
        <div class="crm-stat-label">Total Gallons</div>
      </div>
      <div class="crm-stat-card bi-stat-vertical">
        <div class="crm-stat-icon" style="background:#DCFCE7;color:#16A34A;">
          <span class="material-icons">payments</span>
        </div>
        <div class="crm-stat-value">$${totalCost.toFixed(2)}</div>
        <div class="crm-stat-label">Total Cost</div>
      </div>
      <div class="crm-stat-card bi-stat-vertical">
        <div class="crm-stat-icon" style="background:#FEF3C7;color:#D97706;">
          <span class="material-icons">straighten</span>
        </div>
        <div class="crm-stat-value">${totalMiles.toLocaleString()}</div>
        <div class="crm-stat-label">Total Miles</div>
      </div>
      <div class="crm-stat-card bi-stat-vertical">
        <div class="crm-stat-icon" style="background:#F3E8FF;color:#7C3AED;">
          <span class="material-icons">speed</span>
        </div>
        <div class="crm-stat-value">${avgMpg}</div>
        <div class="crm-stat-label">Avg MPG</div>
      </div>
    </div>

    <!-- Jurisdiction Summary -->
    ${sortedStates.length > 0 ? `
    <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-lg);overflow:hidden;margin-bottom:var(--spacing-md);">
      <div style="padding:12px 16px;background:var(--bg-secondary);border-bottom:1px solid var(--border-light);">
        <h3 style="margin:0;font-size:0.875rem;font-weight:700;color:var(--text-primary);">Jurisdiction Summary</h3>
      </div>
      <div style="overflow-x:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:0.8rem;">
          <thead>
            <tr style="background:var(--bg-secondary);">
              <th style="text-align:left;padding:8px 12px;font-weight:600;color:var(--text-secondary);">State</th>
              <th style="text-align:right;padding:8px 12px;font-weight:600;color:var(--text-secondary);">Entries</th>
              <th style="text-align:right;padding:8px 12px;font-weight:600;color:var(--text-secondary);">Gallons</th>
              <th style="text-align:right;padding:8px 12px;font-weight:600;color:var(--text-secondary);">Cost</th>
              <th style="text-align:right;padding:8px 12px;font-weight:600;color:var(--text-secondary);">Miles</th>
              <th style="text-align:right;padding:8px 12px;font-weight:600;color:var(--text-secondary);">MPG</th>
            </tr>
          </thead>
          <tbody>
            ${sortedStates.map(st => {
              const d = byState[st];
              const mpg = d.gallons > 0 ? (d.miles / d.gallons).toFixed(1) : '—';
              return `<tr style="border-bottom:1px solid var(--border-light);">
                <td style="padding:8px 12px;font-weight:600;color:var(--text-primary);">${escapeHtml(st)}${STATE_NAMES[st] ? ` <span style="color:var(--text-secondary);font-weight:400;">${STATE_NAMES[st]}</span>` : ''}</td>
                <td style="text-align:right;padding:8px 12px;color:var(--text-secondary);">${d.count}</td>
                <td style="text-align:right;padding:8px 12px;color:var(--text-primary);">${d.gallons.toFixed(1)}</td>
                <td style="text-align:right;padding:8px 12px;color:var(--text-primary);">$${d.cost.toFixed(2)}</td>
                <td style="text-align:right;padding:8px 12px;color:var(--text-primary);">${d.miles.toLocaleString()}</td>
                <td style="text-align:right;padding:8px 12px;color:var(--text-primary);">${mpg}</td>
              </tr>`;
            }).join('')}
          </tbody>
          <tfoot>
            <tr style="background:var(--bg-secondary);font-weight:700;">
              <td style="padding:8px 12px;color:var(--text-primary);">Total</td>
              <td style="text-align:right;padding:8px 12px;color:var(--text-primary);">${quarterEntries.length}</td>
              <td style="text-align:right;padding:8px 12px;color:var(--text-primary);">${totalGallons.toFixed(1)}</td>
              <td style="text-align:right;padding:8px 12px;color:var(--text-primary);">$${totalCost.toFixed(2)}</td>
              <td style="text-align:right;padding:8px 12px;color:var(--text-primary);">${totalMiles.toLocaleString()}</td>
              <td style="text-align:right;padding:8px 12px;color:var(--text-primary);">${avgMpg}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>` : ''}

    ${iftaMilesHost()}

    <!-- Recent Entries -->
    <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-lg);overflow:hidden;">
      <div style="padding:12px 16px;background:var(--bg-secondary);border-bottom:1px solid var(--border-light);display:flex;justify-content:space-between;align-items:center;">
        <h3 style="margin:0;font-size:0.875rem;font-weight:700;color:var(--text-primary);">Fuel Purchases — ${getQuarterLabel(iftaQuarter)} ${iftaYear}</h3>
        <span style="font-size:0.75rem;color:var(--text-secondary);">${iftaRows.length} entries</span>
      </div>
      ${iftaRows.length === 0 ? `
        <div style="padding:40px 20px;text-align:center;">
          <span class="material-icons" style="font-size:48px;color:var(--text-muted);margin-bottom:8px;">local_gas_station</span>
          <div style="font-size:0.875rem;color:var(--text-secondary);">No fuel purchases for this quarter</div>
          ${canAdd ? `<button class="btn btn-primary btn-sm" style="margin-top:12px;" onclick="window.ComplianceModule.showAddFuel()">
            <span class="material-icons" style="font-size:16px;">add</span> Add First Entry
          </button>` : ''}
        </div>
      ` : `
        <div style="max-height:400px;overflow-y:auto;">
          ${iftaRows.map(e => {
            const vehicle = e.vehicleUnit || 'Unknown';
            const ppg = e.gallons > 0 ? (e.totalCost / e.gallons).toFixed(3) : '—';
            const isMirror = e._src !== 'manual';
            return `
            <div style="padding:10px 16px;border-bottom:1px solid var(--border-light);display:flex;align-items:center;justify-content:space-between;gap:8px;${e._dupOf ? 'opacity:0.6;' : ''}" class="compliance-row-hover">
              <div style="flex:1;min-width:0;">
                <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                  <span style="font-weight:600;font-size:0.8rem;color:var(--text-primary);">${formatDateShort(e.date)}</span>
                  ${e._st
                    ? `<span style="background:#DBEAFE;color:#1D4ED8;padding:1px 6px;border-radius:4px;font-size:0.7rem;font-weight:600;">${escapeHtml(e._st)}</span>`
                    : `<span class="ifta-needs-state" style="background:#DC2626;color:#fff;padding:1px 6px;border-radius:4px;font-size:0.7rem;font-weight:700;display:inline-flex;align-items:center;gap:2px;" title="No state on this fuel row. Add it on the ${isMirror ? 'expense' : 'entry'} so IFTA can count it."><span class="material-icons" style="font-size:12px;">error</span>Needs state${e.jurisdiction || e.jurisdictionRaw ? ` (read as “${escapeHtml(String(e.jurisdiction || e.jurisdictionRaw))}”)` : ''}</span>`}
                  <span style="color:var(--text-secondary);font-size:0.75rem;">Truck ${escapeHtml(vehicle)}</span>
                  <span style="background:var(--bg-secondary);color:var(--text-primary);padding:1px 6px;border-radius:4px;font-size:0.7rem;">${escapeHtml(IFTA_SOURCE_LABELS[e._src] || 'Expense')}</span>
                  ${e._dupOf ? `<span style="background:#FEF3C7;color:#92400E;padding:1px 6px;border-radius:4px;font-size:0.7rem;font-weight:600;">Same fill-up as another record — counted once</span>` : ''}
                  ${isMirror && !e.vehicleId ? `<span style="background:#FEF3C7;color:#92400E;padding:1px 6px;border-radius:4px;font-size:0.7rem;font-weight:600;">No truck — not counted</span>` : ''}
                  ${e.dateBasis === 'submitted' ? `<span style="color:var(--text-secondary);font-size:0.7rem;" title="The expense has no purchase date, so this is the day it was submitted. Check it near the end of a quarter.">date submitted</span>` : ''}
                </div>
                <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">
                  ${(e.gallons || 0).toFixed(1)} gal @ $${ppg}/gal${e.miles ? ` · ${e.miles.toLocaleString()} mi` : ''}${e.odometer ? ` · Odo: ${e.odometer.toLocaleString()}` : ''}
                </div>
                ${e.notes ? `<div style="font-size:0.7rem;color:var(--text-muted);margin-top:1px;font-style:italic;">${escapeHtml(e.notes)}</div>` : ''}
              </div>
              <div style="display:flex;align-items:center;gap:4px;flex-shrink:0;">
                <span style="font-weight:700;font-size:0.875rem;color:var(--text-primary);">$${(e.totalCost || 0).toFixed(2)}</span>
                ${isMirror ? `
                <span class="material-icons" style="font-size:16px;color:var(--text-secondary);" title="From an approved expense. Change it in Expenses.">lock</span>` : !canEdit ? '' : `
                <button class="btn-icon" onclick="window.ComplianceModule.editFuel('${escJsAttr(e.id)}')" title="Edit">
                  <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">edit</span>
                </button>
                <button class="btn-icon" onclick="window.ComplianceModule.deleteFuel('${escJsAttr(e.id)}')" title="Delete">
                  <span class="material-icons" style="font-size:16px;color:#DC2626;">delete</span>
                </button>`}
              </div>
            </div>`;
          }).join('')}
        </div>
      `}
    </div>
  `;
}

function showFuelModal(entry = null) {
  const isEdit = !!entry;
  const allVehicles = [...ComplianceState.trucks, ...ComplianceState.trailers];
  // Filter to IFTA-registered for the dropdown, but always preserve the currently-selected
  // vehicle so editing an existing entry linked to a non-IFTA vehicle still works.
  const iftaVehicles = allVehicles.filter(v => {
    if (v._type === 'truck') return v.iftaRegistered !== false;
    return v.iftaRegistered === true; // trailer: opt-in only
  });
  const visibleVehicles = entry?.vehicleId && !iftaVehicles.find(v => v.id === entry.vehicleId)
    ? [...iftaVehicles, allVehicles.find(v => v.id === entry.vehicleId)].filter(Boolean)
    : iftaVehicles;
  const today = new Date().toISOString().split('T')[0];

  const modal = document.getElementById('compliance-detail-modal');
  if (!modal) return;
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;margin:auto;border-radius:var(--radius-xl);overflow:hidden;">
      <div style="padding:16px 20px;background:linear-gradient(135deg, var(--brand-blue) 0%, #2563EB 100%);color:#fff;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;">${isEdit ? 'Edit' : 'Add'} Fuel Purchase</h3>
          <button class="btn-icon" onclick="window.ComplianceModule.closeDetail()" style="color:#fff;">
            <span class="material-icons">close</span>
          </button>
        </div>
      </div>
      <div style="padding:20px;display:flex;flex-direction:column;gap:14px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Date *</label>
            <input type="date" id="fuel-date" class="form-control" value="${entry?.date || today}" required>
          </div>
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">State/Province *</label>
            <select id="fuel-jurisdiction" class="form-control" required>
              <option value="">Select...</option>
              ${ALL_JURISDICTIONS.map(j => `<option value="${j}" ${entry?.jurisdiction === j ? 'selected' : ''}>${j} — ${STATE_NAMES[j] || j}</option>`).join('')}
            </select>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Gallons *</label>
            <input type="number" id="fuel-gallons" class="form-control" step="0.1" min="0" value="${entry?.gallons || ''}" placeholder="e.g. 125.5">
          </div>
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Total Cost ($) *</label>
            <input type="number" id="fuel-cost" class="form-control" step="0.01" min="0" value="${entry?.totalCost || ''}" placeholder="e.g. 487.50">
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Vehicle</label>
            <select id="fuel-vehicle" class="form-control">
              <option value="">Select...</option>
              ${visibleVehicles.map(v => {
                const unit = v.unit || v.name || v.id;
                const selected = entry?.vehicleId === v.id ? 'selected' : '';
                const isIfta = v._type === 'truck' ? v.iftaRegistered !== false : v.iftaRegistered === true;
                return `<option value="${v.id}" data-unit="${escapeHtml(unit)}" ${selected}>${escapeHtml(unit)}${v._type === 'trailer' ? ' (Trailer)' : ''}${!isIfta ? ' — non-IFTA' : ''}</option>`;
              }).join('')}
            </select>
          </div>
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Odometer</label>
            <input type="number" id="fuel-odometer" class="form-control" min="0" value="${entry?.odometer || ''}" placeholder="Optional">
          </div>
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Miles Driven (this fill-up)</label>
          <input type="number" id="fuel-miles" class="form-control" min="0" value="${entry?.miles || ''}" placeholder="Miles since last fill-up (optional)">
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Notes</label>
          <input type="text" id="fuel-notes" class="form-control" value="${escapeHtml(entry?.notes || '')}" placeholder="Station name, receipt #, etc.">
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom,#F8FAFC,#fff);display:flex;gap:12px;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="window.ComplianceModule.closeDetail()">Cancel</button>
        <button class="btn btn-primary" id="fuel-save-btn" onclick="window.ComplianceModule.saveFuel('${entry?.id || ''}')">
          <span class="material-icons" style="font-size:18px;">save</span> ${isEdit ? 'Update' : 'Save'}
        </button>
      </div>
    </div>
  `;
}

async function saveFuelPurchase(entryId) {
  const date = document.getElementById('fuel-date')?.value?.trim();
  const jurisdiction = document.getElementById('fuel-jurisdiction')?.value?.trim();
  const gallons = parseFloat(document.getElementById('fuel-gallons')?.value) || 0;
  const totalCost = parseFloat(document.getElementById('fuel-cost')?.value) || 0;
  const vehicleSelect = document.getElementById('fuel-vehicle');
  const vehicleId = vehicleSelect?.value || '';
  const vehicleUnit = vehicleSelect?.selectedOptions?.[0]?.dataset?.unit || '';
  const odometer = parseInt(document.getElementById('fuel-odometer')?.value) || null;
  const miles = parseInt(document.getElementById('fuel-miles')?.value) || 0;
  const notes = document.getElementById('fuel-notes')?.value?.trim() || '';

  if (!date || !jurisdiction || !gallons) {
    window.showToast?.('Date, state, and gallons are required', 'error');
    return;
  }

  const btn = document.getElementById('fuel-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  const data = {
    date, jurisdiction, gallons, totalCost, vehicleId, vehicleUnit,
    odometer, miles, notes, updatedAt: Timestamp.now()
  };

  try {
    if (entryId) {
      await updateDoc(doc(db, 'tenants', ComplianceState.tenantId, 'fuelPurchases', entryId), data);
      window.showToast?.('Fuel purchase updated', 'success');
    } else {
      data.createdAt = Timestamp.now();
      await addDoc(collection(db, 'tenants', ComplianceState.tenantId, 'fuelPurchases'), data);
      window.showToast?.('Fuel purchase added', 'success');
    }
    window.ComplianceModule.closeDetail();
  } catch (err) {
    console.error('[IFTA] Save error:', err);
    window.showToast?.(_fuelErr(err, 'Error saving fuel purchase'), 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;">save</span> Save'; }
  }
}

async function deleteFuelPurchase(entryId) {
  if (!confirm('Delete this fuel purchase?')) return;
  try {
    await deleteDoc(doc(db, 'tenants', ComplianceState.tenantId, 'fuelPurchases', entryId));
    window.showToast?.('Fuel purchase deleted', 'success');
  } catch (err) {
    console.error('[IFTA] Delete error:', err);
    window.showToast?.(_fuelErr(err, 'Error deleting'), 'error');
  }
}

// ============================================
// HAZMAT TAB
// ============================================
function renderHazmatTab() {
  const activePeople = ComplianceState.people.filter(p => p.active !== false);

  // Find people with hazmat endorsements (H or X)
  const hazmatDrivers = activePeople.filter(p => {
    let endorsements = p.cdlEndorsements || p.endorsements || [];
    if (typeof endorsements === 'string') endorsements = endorsements.split(',').map(s => s.trim());
    return endorsements.some(e => {
      const code = typeof e === 'string' ? e : e.id || '';
      return code === 'H' || code === 'X';
    });
  });

  // Find people with hazmat training records
  const allWithHazmat = activePeople.filter(p => {
    let endorsements = p.cdlEndorsements || p.endorsements || [];
    if (typeof endorsements === 'string') endorsements = endorsements.split(',').map(s => s.trim());
    const hasEndorsement = endorsements.some(e => {
      const code = typeof e === 'string' ? e : e.id || '';
      return code === 'H' || code === 'X';
    });
    return hasEndorsement || p.hazmatTrainingExpiry || p.hazmatCertExpiry;
  });

  // Compliance summary
  const totalHazmat = allWithHazmat.length;
  const expiredTraining = allWithHazmat.filter(p => {
    const days = daysUntilExpiry(p.hazmatTrainingExpiry);
    return days !== null && days < 0;
  }).length;
  const expiringTraining = allWithHazmat.filter(p => {
    const days = daysUntilExpiry(p.hazmatTrainingExpiry);
    return days !== null && days >= 0 && days <= 30;
  }).length;
  const cdlHazmatExpired = allWithHazmat.filter(p => {
    const days = daysUntilExpiry(p.cdlExpiryDate);
    return days !== null && days < 0;
  }).length;

  return `
    <!-- Hazmat Summary -->
    <div class="crm-stats-grid" style="margin-bottom:var(--spacing-md);">
      <div class="crm-stat-card bi-stat-vertical">
        <div class="crm-stat-icon" style="background:#FEF3C7;color:#D97706;">
          <span class="material-icons">science</span>
        </div>
        <div class="crm-stat-value">${totalHazmat}</div>
        <div class="crm-stat-label">Hazmat Endorsed</div>
      </div>
      <div class="crm-stat-card bi-stat-vertical">
        <div class="crm-stat-icon" style="background:${cdlHazmatExpired > 0 ? '#FEE2E2' : '#ECFDF5'};color:${cdlHazmatExpired > 0 ? '#DC2626' : '#10B981'};">
          <span class="material-icons">${cdlHazmatExpired > 0 ? 'error' : 'check_circle'}</span>
        </div>
        <div class="crm-stat-value" style="color:${cdlHazmatExpired > 0 ? '#DC2626' : '#10B981'};">${cdlHazmatExpired}</div>
        <div class="crm-stat-label">CDL Expired</div>
      </div>
      <div class="crm-stat-card bi-stat-vertical">
        <div class="crm-stat-icon" style="background:${expiredTraining > 0 ? '#FEE2E2' : '#ECFDF5'};color:${expiredTraining > 0 ? '#DC2626' : '#10B981'};">
          <span class="material-icons">${expiredTraining > 0 ? 'warning' : 'verified'}</span>
        </div>
        <div class="crm-stat-value" style="color:${expiredTraining > 0 ? '#DC2626' : '#10B981'};">${expiredTraining}</div>
        <div class="crm-stat-label">Training Expired</div>
      </div>
      <div class="crm-stat-card bi-stat-vertical">
        <div class="crm-stat-icon" style="background:${expiringTraining > 0 ? '#FEF3C7' : '#ECFDF5'};color:${expiringTraining > 0 ? '#D97706' : '#10B981'};">
          <span class="material-icons">${expiringTraining > 0 ? 'schedule' : 'thumb_up'}</span>
        </div>
        <div class="crm-stat-value" style="color:${expiringTraining > 0 ? '#D97706' : '#10B981'};">${expiringTraining}</div>
        <div class="crm-stat-label">Expiring Soon</div>
      </div>
    </div>

    <!-- Driver Cards -->
    ${allWithHazmat.length === 0 ? `
      <div style="padding:40px 20px;text-align:center;">
        <span class="material-icons" style="font-size:48px;color:var(--text-muted);margin-bottom:8px;">science</span>
        <div style="font-size:0.875rem;color:var(--text-secondary);">No drivers with Hazmat endorsements</div>
        <div style="font-size:0.75rem;color:var(--text-muted);margin-top:4px;">Add H or X endorsements to drivers in Roster to see them here</div>
      </div>
    ` : `
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:var(--spacing-md);">
        ${allWithHazmat.map(p => renderHazmatDriverCard(p)).join('')}
      </div>
    `}

    <!-- Hazmat Compliance Checklist -->
    <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-lg);overflow:hidden;margin-top:var(--spacing-md);">
      <div style="padding:12px 16px;background:var(--bg-secondary);border-bottom:1px solid var(--border-light);">
        <h3 style="margin:0;font-size:0.875rem;font-weight:700;color:var(--text-primary);">
          <span class="material-icons" style="font-size:16px;vertical-align:text-bottom;">checklist</span> FMCSA Hazmat Compliance Checklist
        </h3>
      </div>
      <div style="padding:12px 16px;">
        ${renderHazmatChecklist(allWithHazmat)}
      </div>
    </div>
  `;
}

function renderHazmatDriverCard(person) {
  let endorsements = person.cdlEndorsements || person.endorsements || [];
  if (typeof endorsements === 'string') endorsements = endorsements.split(',').map(s => s.trim()).filter(Boolean);
  if (!Array.isArray(endorsements)) endorsements = [];
  const endorsementCodes = endorsements.map(e => typeof e === 'string' ? e : e.id || '').filter(Boolean);
  const hasH = endorsementCodes.includes('H');
  const hasX = endorsementCodes.includes('X');
  const hasN = endorsementCodes.includes('N');

  const cdlDays = daysUntilExpiry(person.cdlExpiryDate);
  const trainingDays = daysUntilExpiry(person.hazmatTrainingExpiry);
  const twicDays = daysUntilExpiry(person.twicExpiry);
  const medDays = daysUntilExpiry(person.medCardExpiryDate);

  // TASK-1336: for a hazmat CMV driver, a MISSING CDL or med card is a gap (critical),
  // not a pass — this card only renders for someone already holding H/X/N, so both apply.
  // TWIC + hazmat-training keep null-skip (not universally required / not provable here).
  let worstStatus = 'good';
  for (const d of [cdlDays, medDays]) {
    if (d === null || d < 0) { worstStatus = 'critical'; break; }
    if (d <= 30) worstStatus = 'warning';
  }
  if (worstStatus !== 'critical') {
    for (const d of [trainingDays, twicDays]) {
      if (d === null) continue;
      if (d < 0) { worstStatus = 'critical'; break; }
      if (d <= 30 && worstStatus !== 'critical') worstStatus = 'warning';
    }
  }

  return `
    <div class="compliance-card compliance-card-${worstStatus}" onclick="window.ComplianceModule.showPersonDetail('${person.id}')" style="cursor:pointer;">
      <div class="compliance-card-header">
        <div style="display:flex;align-items:center;justify-content:space-between;width:100%;">
          <div>
            <div class="compliance-card-name">${escapeHtml(person.name || 'Unknown')}</div>
            <div style="font-size:11px;color:var(--text-secondary);margin-top:2px;">
              ${(person.crewType || person.role || person.roles?.[0] || 'Driver')}
            </div>
          </div>
          <div style="display:flex;gap:4px;">
            ${hasH ? '<span style="background:#FEF3C7;color:#92400E;padding:2px 6px;border-radius:4px;font-size:0.65rem;font-weight:700;">H</span>' : ''}
            ${hasX ? '<span style="background:#FEE2E2;color:#991B1B;padding:2px 6px;border-radius:4px;font-size:0.65rem;font-weight:700;">X</span>' : ''}
            ${hasN ? '<span style="background:#DBEAFE;color:#1E40AF;padding:2px 6px;border-radius:4px;font-size:0.65rem;font-weight:700;">N</span>' : ''}
          </div>
        </div>
      </div>
      <div style="padding:4px 0;">
        ${formatExpiryBadge(person.cdlExpiryDate, 'CDL')}
        ${formatExpiryBadge(person.medCardExpiryDate, 'Med Card')}
        ${formatExpiryBadge(person.hazmatTrainingExpiry, 'Hazmat Training')}
        ${formatExpiryBadge(person.twicExpiry, 'TWIC')}
      </div>
      <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:4px;">
        CDL: ${person.cdlClass || 'N/A'} · #${escapeHtml(person.cdlNumber || 'N/A')}
      </div>
    </div>
  `;
}

function renderHazmatChecklist(hazmatDrivers) {
  const checks = [
    {
      label: 'All hazmat drivers have valid CDLs',
      // TASK-1336: a MISSING CDL fails this check (was `d === null || d > 0`, which
      // passed a no-CDL driver) — consistent with renderHazmatDriverCard's card color.
      pass: hazmatDrivers.every(p => { const d = daysUntilExpiry(p.cdlExpiryDate); return d !== null && d > 0; }),
      icon: 'badge'
    },
    {
      label: 'All hazmat drivers have valid medical cards',
      pass: hazmatDrivers.every(p => { const d = daysUntilExpiry(p.medCardExpiryDate); return d !== null && d > 0; }),
      icon: 'medical_services'
    },
    {
      label: 'Hazmat training up to date (recurrent every 3 years)',
      pass: hazmatDrivers.every(p => { const d = daysUntilExpiry(p.hazmatTrainingExpiry); return d === null || d > 0; }),
      icon: 'school'
    },
    {
      label: 'TWIC cards valid (if applicable)',
      pass: hazmatDrivers.every(p => { const d = daysUntilExpiry(p.twicExpiry); return d === null || d > 0; }),
      icon: 'credit_card'
    },
    {
      label: 'Security plan on file (49 CFR 172.800)',
      pass: null, // Manual check — can't determine from data
      icon: 'security'
    },
    {
      label: 'Emergency response info in vehicles (49 CFR 172.602)',
      pass: null,
      icon: 'emergency'
    }
  ];

  return checks.map(c => {
    const color = c.pass === null ? 'var(--text-secondary)' : c.pass ? '#10B981' : '#DC2626';
    const icon = c.pass === null ? 'radio_button_unchecked' : c.pass ? 'check_circle' : 'cancel';
    return `
      <div style="display:flex;align-items:center;gap:10px;padding:6px 0;border-bottom:1px solid var(--border-light);">
        <span class="material-icons" style="font-size:18px;color:${color};">${icon}</span>
        <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">${c.icon}</span>
        <span style="font-size:0.8rem;color:var(--text-primary);flex:1;">${c.label}</span>
        ${c.pass === null ? '<span style="font-size:0.65rem;background:var(--bg-secondary);padding:1px 6px;border-radius:4px;color:var(--text-secondary);font-weight:600;">MANUAL</span>' : ''}
      </div>
    `;
  }).join('');
}

// ============================================
// EVENT WIRING
// ============================================
function wireEvents() {
  const searchInput = document.getElementById('compliance-search');
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      ComplianceState.searchQuery = e.target.value;
      renderComplianceContent();
    });
    // Focus search on render if had previous query
    if (ComplianceState.searchQuery) {
      searchInput.focus();
      searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
    }
  }
}

// ============================================
// DRIVER'S LICENSE UPLOAD — TASK-4605 Slice 2: credType-parameterized so ONE
// handler serves both 'license' (front, dlDocumentPath) and 'licenseBack'
// (back, dlDocumentBackPath) — never a per-side copy (Pitfall #8).
// ============================================
const _DL_CRED_FIELDS = {
  license:     { pathField: 'dlDocumentPath',     urlField: 'dlDocumentUrl',     slug: 'dl' },
  licenseBack: { pathField: 'dlDocumentBackPath', urlField: 'dlDocumentBackUrl', slug: 'dlback' },
};
async function uploadDlDocument(personId, credType) {
  // TASK-4605 (integrated-diff COD) — defence in depth. The card now hides
  // these buttons for a member who cannot write the person doc, but HIDING A
  // BUTTON IS NOT A GATE: this function is reachable from the console and from
  // a stale render. `_compCanWriteRoster()` mirrors the authoritative server gate
  // (requireRosterWriteAccess / firestore.rules people:update), and the other
  // uploads in this file already guard the same way.
  if (!_compCanWriteRoster()) {
    window.showToast?.('You do not have permission to upload driver documents.', 'error');
    return;
  }
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;
  const fields = _DL_CRED_FIELDS[credType] || _DL_CRED_FIELDS.license;
  const isBack = fields === _DL_CRED_FIELDS.licenseBack;

  let input = document.getElementById('comp-dl-file-input');
  if (!input) {
    input = document.createElement('input');
    input.type = 'file';
    input.id = 'comp-dl-file-input';
    // TASK-4605 — a licence back is often a phone photo; accept any image plus
    // PDF (was .pdf,.jpg,.jpeg,.png, which silently rejected HEIC).
    input.accept = 'image/*,application/pdf';
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(input);
  }
  input.value = '';

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    // TASK-4609 — warn, do not block: the file is still stored and usable,
    // it just will not DISPLAY for whoever reviews it. Said at selection time,
    // while re-saving as JPEG is still cheap.
    const heicNote = window.PersonSections?.unviewableImageNote?.(file);


    if (file.size > 10 * 1024 * 1024) {
      showToast('File too large (max 10MB)', 'error');
      return;
    }

    try {
      showToast('Uploading...', 'info');
      let storagePath;
      if (_compPartnerTenantId()) {
        // TASK-9449 — managing partner: server upload (create-only) + pointer + audit.
        storagePath = (await _compPartnerApi('partnerUploadPersonDoc')(personId, isBack ? 'licenseBack' : 'license', file)).storagePath;
      } else {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      storagePath = `hr/${tenantId}/${personId}/${fields.slug}_${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
      // TASK-996 — PATH-ONLY (no bearer URL in the member-readable people doc);
      // view via the HR-gated getComplianceDocBytes byte-stream callable.
      await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
        [fields.urlField]: null,
        [fields.pathField]: storagePath
      });
      }

      // Update local state
      const person = ComplianceState.people.find(p => p.id === personId);
      if (person) {
        person[fields.urlField] = null;
        person[fields.pathField] = storagePath;
      }

      showToast(isBack ? "Driver's license (back) uploaded" : "Driver's license uploaded", 'success');
      // The file IS saved — this is only about whether a reviewer can SEE it.
      // 'warning' is a dismissible toast (8s + an X), so it survives the success
      // toast instead of being buried by it.
      if (heicNote) showToast(heicNote, 'warning');
      showPersonDetail(personId);
    } catch (err) {
      console.error('DL upload error:', err);
      showToast(_compPartnerErrText(err, 'Upload failed'), 'error'); // TASK-9449
    }
  };

  setTimeout(() => input.click(), 50);
}

async function removeDlDocument(personId, credType) {
  // TASK-4605 (integrated-diff COD) — defence in depth. The card now hides
  // these buttons for a member who cannot write the person doc, but HIDING A
  // BUTTON IS NOT A GATE: this function is reachable from the console and from
  // a stale render. `_compCanWriteRoster()` mirrors the authoritative server gate
  // (requireRosterWriteAccess / firestore.rules people:update), and the other
  // uploads in this file already guard the same way.
  if (!_compCanWriteRoster()) {
    window.showToast?.('You do not have permission to remove driver documents.', 'error');
    return;
  }
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;

  const fields = _DL_CRED_FIELDS[credType] || _DL_CRED_FIELDS.license;
  const isBack = fields === _DL_CRED_FIELDS.licenseBack;

  if (!confirm(isBack ? "Remove driver's license (back) file?" : "Remove driver's license file?")) return;

  try {
    if (person[fields.pathField]) {
      await deleteObject(ref(storage, person[fields.pathField])).catch(e => console.warn('[Compliance] Old DL delete failed:', e.message));
    }

    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
      [fields.urlField]: '',
      [fields.pathField]: ''
    });

    person[fields.urlField] = '';
    person[fields.pathField] = '';

    showToast(isBack ? "Driver's license (back) removed" : "Driver's license removed", 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('DL remove error:', err);
    showToast('Failed to remove', 'error');
  }
}

// ============================================
// MEDICAL CARD DOCUMENT UPLOAD / EXPIRY
// ============================================
async function uploadMedCardDocument(personId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  let input = document.getElementById('comp-medcard-file-input');
  if (!input) {
    input = document.createElement('input');
    input.type = 'file';
    input.id = 'comp-medcard-file-input';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(input);
  }
  input.value = '';

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showToast('File too large (max 10MB)', 'error');
      return;
    }

    try {
      showToast('Uploading...', 'info');
      let storagePath, uploadedAt;
      if (_compPartnerTenantId()) {
        // TASK-9449 — managing partner: server upload (create-only) + pointer + audit.
        const up = await _compPartnerApi('partnerUploadPersonDoc')(personId, 'medCard', file);
        storagePath = up.storagePath;
        uploadedAt = up.uploadedAt || new Date().toISOString();
      } else {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      storagePath = `hr/${tenantId}/${personId}/medcard_${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
      // TASK-996 — PATH-ONLY (no bearer URL in the member-readable people doc);
      // view via the HR-gated getComplianceDocBytes byte-stream callable.
      uploadedAt = new Date().toISOString();
      await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
        medCardDocumentUrl: null,
        medCardDocumentPath: storagePath,
        medCardDocumentUploadedAt: uploadedAt // TASK-647 — recency for getEffectiveMedCardDoc
      });
      }

      const person = ComplianceState.people.find(p => p.id === personId);
      if (person) {
        person.medCardDocumentUrl = null;
        person.medCardDocumentPath = storagePath;
        person.medCardDocumentUploadedAt = uploadedAt;
      }

      showToast('Medical card uploaded', 'success');
      showPersonDetail(personId);
    } catch (err) {
      console.error('Med card upload error:', err);
      showToast(_compPartnerErrText(err, 'Upload failed'), 'error'); // TASK-9449
    }
  };

  setTimeout(() => input.click(), 50);
}

async function removeMedCardDocument(personId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;

  if (!confirm('Remove medical card file?')) return;

  try {
    const _oldMedPath = _medDq(person).medCardDocumentPath; // TASK-1496-S6
    if (_oldMedPath) {
      await deleteObject(ref(storage, _oldMedPath)).catch(e => console.warn('[Compliance] Old med card delete failed:', e.message));
    }

    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
      medCardDocumentUrl: '',
      medCardDocumentPath: ''
    });

    person.medCardDocumentUrl = '';
    person.medCardDocumentPath = '';

    showToast('Medical card removed', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('Med card remove error:', err);
    showToast('Failed to remove', 'error');
  }
}

// TASK-647-FF — clear a cert-source (medCertPdfUrl) med card from the partner/
// compliance view. Mirror of hr.js removeMedCertPdf + the local
// removeMedCardDocument above (re-renders via showPersonDetail). Used as the
// inline fallback when HRModule.removeMedCertPdf isn't loaded.
async function removeMedCertPdf(personId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;

  if (!confirm('Remove medical card file?')) return;

  try {
    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
      medCertPdfUrl: null,
      medCertPdfUploadedAt: null,
      medCardSelfUploadedAt: null,
      medCardPendingReview: false
    });

    person.medCertPdfUrl = null;
    person.medCertPdfUploadedAt = null;
    person.medCardSelfUploadedAt = null;
    person.medCardPendingReview = false;

    showToast('Medical card removed', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('Med cert remove error:', err);
    showToast('Failed to remove', 'error');
  }
}

async function updateMedCardExpiry(personId, dateValue) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  try {
    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
      medCardExpiryDate: dateValue || ''
    });

    const person = ComplianceState.people.find(p => p.id === personId);
    if (person) {
      person.medCardExpiryDate = dateValue || '';
    }

    showToast(dateValue ? 'Med card expiry updated' : 'Med card expiry cleared', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('Med card expiry update error:', err);
    showToast('Failed to update', 'error');
  }
}

// ============================================
// TASK-812 — DRIVER QUALIFICATION DATA ENTRY (in compliance view)
// Mirrors the HR Compliance-tab edit form (saveComplianceFields). Writes the
// EXACT person-doc keys HR writes, via the compliance-local updateDoc path.
// ============================================

// Flip the CDL/identity section into edit mode and re-render.
async function editDriverDetails(personId) {
  // TASK-10154 — a managing partner's form pre-fills from an audited server
  // fetch; if it fails the identity inputs stay locked (CDL fields still edit).
  if (_compPartnerTenantId()) {
    // One audited fetch at a time: a second tap while it runs is ignored
    // (COD r2 — a double-click let the losing call wipe the winner's values).
    if (_CompPartnerId.editPending) return;
    _CompPartnerId.editPending = true;
    let current = false;
    try { current = await _compPartnerIdBeginEdit(personId); } finally { _CompPartnerId.editPending = false; }
    if (!current) return; // superseded (closed): beginEdit cached nothing
    // Closed, moved to another person, or the modal now holds another form
    // (Add Test etc.) while the fetch ran: do not (re)open the file, and drop
    // what landed.
    const modal = document.getElementById('compliance-detail-modal');
    if (ComplianceState.selectedPersonId !== personId || !modal || modal.classList.contains('hidden')
        || !modal.querySelector('.modal-content[data-detail="person"]')) {
      _CompPartnerId.edit.delete(personId);
      return;
    }
  }
  ComplianceState.editingDriverDetailsId = personId;
  showPersonDetail(personId);
}

// Leave edit mode without saving.
function cancelDriverDetailsEdit(personId) {
  ComplianceState.editingDriverDetailsId = null;
  _CompPartnerId.edit.delete(personId); // TASK-10154 — never keep full values past the form
  showPersonDetail(personId);
}

async function saveDriverDetails(personId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  const btn = document.getElementById('comp-dq-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  const val = (id) => document.getElementById(id)?.value?.trim() || '';

  // SAME keys + null-on-empty convention as HR's saveComplianceFields so the
  // three views (HR / Roster / Compliance) stay a single record with no drift.
  const updateData = {
    dob: val('comp-dq-dob') || null,
    dlState: val('comp-dq-dl-state')?.toUpperCase() || null,
    dlNumber: val('comp-dq-dl-number') || null,
    dlExpiryDate: val('comp-dq-dl-expiry') || null,
    cdlClass: val('comp-dq-cdl-class') || null,
    cdlEndorsements: val('comp-dq-cdl-endorsements') || null,
    cdlRestrictions: val('comp-dq-cdl-restrictions') || null, // TASK-9300 (5)
    cdlIssueDate: val('comp-dq-cdl-issue') || null,
    cdlExpiryDate: val('comp-dq-cdl-expiry') || null,
  };
  // TASK-9359 (2nd COD) — a LOCKED identity input (vault not readable / still
  // loading) is omitted, never saved as a clear.
  [['comp-dq-dob', 'dob'], ['comp-dq-dl-state', 'dlState'], ['comp-dq-dl-number', 'dlNumber'], ['comp-dq-dl-expiry', 'dlExpiryDate']]
    .forEach(([elId, key]) => { if (document.getElementById(elId)?.disabled) delete updateData[key]; });
  // TASK-10154 — a managing partner's identity edits go to the server callable
  // (the vault is closed to partner logins in firestore.rules); only the CDL
  // keys continue down the people-doc path below.
  const _partnerIdNext = {};
  if (_compPartnerTenantId()) {
    ['dob', 'dlState', 'dlNumber', 'dlExpiryDate'].forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(updateData, k)) { _partnerIdNext[k] = updateData[k]; delete updateData[k]; }
    });
  }
  // TASK-9422 — partner session: write only what changed (see app.js
  // partnerDropUnchangedKeys). No-op for every carrier member.
  window.partnerDropUnchangedKeys?.(updateData, ComplianceState.people.find(p => p.id === personId));

  try {
    if (Object.keys(_partnerIdNext).length) await _compPartnerIdSave(personId, _partnerIdNext);

    if (Object.keys(updateData).length) {
      // TASK-9359 — the identity kit routes to the peopleIdentity vault in the
      // same atomic batch (never onto the broadcast doc); a viewer who cannot
      // know it has it dropped (no blank-over-hidden wipe).
      const _before9359 = ComplianceState.people.find(p => p.id === personId) || { id: personId };
      await window.PersonIdentity.updatePerson(tenantId, personId, updateData, _before9359);
    }

    // TASK-934 — Driver Classification routes through the setDriverClassification
    // callable ONLY (positionType is server-owned; a direct doc write is denied
    // by firestore.rules). Fires only on an actual change.
    try {
      const personBefore = ComplianceState.people.find(p => p.id === personId);
      const pickedPt = window.DqMatrix.readClassificationControl('comp-dq-position-type');
      const currentPt = window.DqMatrix.explicitPositionType(personBefore);
      // TASK-9806 — "Drives, no CMV" asks the placarded-hazmat / passenger checks first;
      // a yes switches to the tier it requires, cancel leaves the classification alone.
      const _pick9806 = (pickedPt && pickedPt !== currentPt)
        ? await window.DqMatrix.confirmClassificationPick(pickedPt, { name: personBefore?.name })
        : null;
      if (pickedPt && pickedPt !== currentPt && !_pick9806) showToast('Classification not changed', 'info');
      if (_pick9806 && _pick9806.positionType !== currentPt) {
        const setPt = httpsCallable(functions, 'setDriverClassification');
        const ptRes = await setPt({ tenantId, personId, positionType: _pick9806.positionType, cmvChecks: _pick9806.cmvChecks });
        updateData.positionType = _pick9806.positionType;
        updateData.positionTypeSetBy = 'carrier';
        // TASK-1353 (COD UX) — surface trainings that newly apply under the new tier.
        const _nt = ptRes?.data?.newlyApplicableTrainings;
        if (Array.isArray(_nt) && _nt.length > 0) {
          const _lbls = _nt.map(t => t && t.label).filter(Boolean).join(', ');
          showToast(`Role change: ${_nt.length} required training${_nt.length > 1 ? 's' : ''} now appl${_nt.length > 1 ? 'y' : 'ies'}${_lbls ? ' — ' + _lbls : ''}`, 'warning');
        }
      }
    } catch (ptErr) {
      console.error('[Compliance] Driver classification save failed:', ptErr);
      showToast(ptErr?.message || 'Could not save driver classification', 'error');
    }

    // Keep local state in sync so the re-render shows the saved values.
    // TASK-9359 — identity fields re-overlay from the vault cache (updatePerson
    // primed it); copying them onto the object would mask the raw legacy copy.
    const person = ComplianceState.people.find(p => p.id === personId);
    if (person) {
      const { dob: _d, dlState: _s, dlNumber: _n, dlExpiryDate: _x, ...nonIdentity } = updateData;
      Object.assign(person, nonIdentity);
      try { window.PersonIdentity.overlayList([person]); } catch (_) { /* best-effort */ }
    }

    ComplianceState.editingDriverDetailsId = null;
    _CompPartnerId.edit.delete(personId); // TASK-10154 — full values leave with the form
    showToast('Driver details saved', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] Error saving driver details:', err);
    showToast(_compPartnerErrText(err, 'Failed to save driver details'), 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
  }
}

// TASK-980 / TASK-9424 — ADD a follow-up flag, or CLEAR one named flag. Routes
// through the dual-gated setDriverFlag callable ONLY (the flag cluster is
// server-owned; a direct doc write is denied by firestore.rules, and a driver
// session can never reach the callable). A driver can carry several flags; a
// clear names its flag and MUST say why — the server refuses a clear without a
// reason and keeps the reason in the flag history + auditLog.
async function addDriverFlag(personId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;
  const person = ComplianceState.people.find(p => p.id === personId);
  const name = person?.name || 'this driver';
  const reason = await window.PersonSections.openFlagReasonModal({
    title: `Flag ${name} for follow-up`,
    message: person?.flagged === true
      ? 'This driver already has a flag. This adds ANOTHER one — the existing flag stays as it is.'
      : 'Why does this driver need attention? Owners and admins see the reason; everyone with HR or Compliance access sees that the driver is flagged.',
    placeholder: 'e.g. MVR shows a 2025 speeding ticket not on the application',
    okText: 'Flag driver',
    icon: 'outlined_flag',
  });
  if (reason === null) return; // cancelled
  try {
    const fn = httpsCallable(functions, 'setDriverFlag');
    await fn({ tenantId, personId, flagged: true, reason });
    if (person) person.flagged = true; // the people listener backfills who/when
    showToast('Driver flagged for follow-up', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] flag driver failed:', err);
    showToast(err?.message || 'Could not flag the driver', 'error');
  }
}

async function clearDriverFlag(personId, flagId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;
  const person = ComplianceState.people.find(p => p.id === personId);
  const name = person?.name || 'this driver';
  // TASK-9227 — a flag set by your compliance partner is THEIRS. The panel hides
  // Clear on those; the server refuses the clear anyway (setDriverFlag resolves the
  // flag's origin and denies a carrier clearing a partner's finding).
  const reason = await window.PersonSections.openFlagReasonModal({
    title: `Clear a flag on ${name}`,
    message: 'Why is this flag being cleared? Your reason is kept with your name and the date in the flag history.\n\n'
      + 'If your compliance partner set this flag, only they can clear it — ask them to.',
    placeholder: 'e.g. Med card renewed 9/28 — copy on file',
    okText: 'Clear flag',
    minLength: 3,
    icon: 'flag_circle',
  });
  if (reason === null) return; // cancelled
  try {
    const fn = httpsCallable(functions, 'setDriverFlag');
    const res = await fn({ tenantId, personId, flagged: false, flagId: flagId || undefined, reason });
    // Optimistic local patch so the immediate repaint reflects the change. The
    // reason is NOT stored on the person (TASK-1207: server-only driverFlags doc).
    if (person && res?.data && res.data.flagged === false) {
      delete person.flagged; delete person.flaggedByUid;
      delete person.flaggedByEmail; delete person.flaggedAt;
    }
    showToast('Flag cleared — the reason is in the flag history', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] clear driver flag failed:', err);
    // TASK-9227 — the partner-origin refusal is a POLICY answer, not a fault.
    // Rendering it as a bare red "permission-denied" reads as the app being
    // broken and sends the dispatcher looking for a workaround, which on this
    // surface means deleting and re-creating the person. Say what happened and
    // what to do instead. The server message is authoritative; this only adds
    // the instruction when the refusal is the origin gate.
    const _code = String(err?.code || '');
    if (_code.endsWith('permission-denied') && /partner/i.test(String(err?.message || ''))) {
      showToast(
        `${err.message} Ask them to clear it — your attempt has been recorded so they can see it.`,
        'warning', null, 12000
      );
    } else {
      showToast(err?.message || 'Could not clear the flag', 'error');
    }
  }
}

// ============================================
// TASK-811 — MVR (in compliance view)
// Local partner-detection duplicates (HR's hrBgHandledByPartner / hrPartnerLabel
// live in HRModule and read undefined when HR isn't loaded for a compliance-only
// carrier — duplicate locally so the request copy can't silently flip). They read
// only AppState.tenantData, no HR state.
// ============================================
// TASK-1384 — per-person cache of the peopleBackgroundChecks doc fetched by
// hydrateComplianceBgReport(), so buildComplianceMvrSection can discriminate
// "the partner has the request" (no invitation exists, nothing sent to the
// driver) vs "a real invitation is out" (awaiting signature). Person-subject
// report state lives ONLY on that doc — the person doc's bgCheckRequestedAt
// stamp is deleted server-side the moment a real invitation is created.
// undefined = not fetched yet (unknown → classify from the person doc only).
// TASK-9458 — keyed by TENANT, then person: a person id alone is not unambiguous
// across tenants (this module-level cache outlives initCompliance, and a late
// hydrate for the previous tenant must not land in the next one's view).
// _compBgDocCache is always the CURRENT tenant's bucket (rebound by initCompliance),
// so every reader's `_compBgDocCache[personId]` is per-tenant; a writer takes the
// bucket of the tenant it READ from (_compBgDocBucket(tenantId)), never the binding.
// Null-prototype buckets: a person id is data, never an Object.prototype key.
const _compBgDocCacheByTenant = Object.create(null);
function _compBgDocBucket(tenantId) {
  const k = String(tenantId || '');
  return _compBgDocCacheByTenant[k] || (_compBgDocCacheByTenant[k] = Object.create(null));
}
let _compBgDocCache = _compBgDocBucket('');

function compBgHandledByPartner() {
  const td = window.AppState?.tenantData || {};
  const hasManager = !!(td.complianceManagerUid && String(td.complianceManagerUid).trim());
  if (!hasManager) return false;
  if (td.compliance?.bgHandledBy === 'carrier') return false;
  return true; // 'partner' or absent → partner-managed
}

function compPartnerLabel() {
  const td = window.AppState?.tenantData || {};
  return (typeof td.compliancePartner === 'string' && td.compliancePartner.trim())
    ? td.compliancePartner.trim()
    : 'your DOT manager';
}

// TASK-969 — MVR purpose labels (order-time server stamp: §391.23
// pre-employment / §391.25 annual / ad-hoc). Enum values are server-authored
// (functions _deriveMvrPurpose); anything else renders nothing. Kept as a
// small local map (compliance-only carriers never initialize HR state).
const COMPLIANCE_MVR_PURPOSE_LABELS = {
  pre_employment: { full: 'Pre-employment — 49 CFR §391.23', color: '#1D4ED8', bg: '#DBEAFE' },
  annual: { full: 'Annual review — 49 CFR §391.25', color: '#047857', bg: '#D1FAE5' },
  ad_hoc: { full: 'Ad-hoc / for-cause pull', color: '#475569', bg: '#F1F5F9' },
};

// ============================================================================
// TASK-1623 — §391.25(b) MVR REVIEW FLAGS + §383.31 driver notification.
//
// ⚠️ WHAT THE REGULATIONS ACTUALLY SAY (read before editing — the two are
// routinely conflated, and conflating them is the TASK-1320 attestation trap):
//
//   §391.25(a)  the carrier obtains an MVR at least once every 12 months.
//   §391.25(b)  the carrier REVIEWS it at least once every 12 months to decide
//               whether the driver still meets minimum safe-driving standards.
//   §391.25(c)  the DQ file keeps (1) a copy of that MVR and (2) "a note,
//               including the name of the person who performed the review ...
//               and the date of such review". That note is the sign-off, and
//               §391.51(b)(5) is what makes it a DQ-file requirement.
//   §383.31     is the DRIVER's duty to notify the employer in writing within
//               30 days OF THE DATE OF CONVICTION. The carrier has no §383.31
//               duty and the MVR is not its trigger or its clock.
//   §391.27     ("annual list of violations") was REMOVED from the FMCSRs
//               effective 2022-05-09 — do not build toward it.
//
// ── THE TWO FLAGS ARE DERIVED, NEVER STORED ────────────────────────────────
// A stored flag drifts the instant any of the four surfaces that can record a
// review (hr.js, this file, the partner handler, a Checkr webhook) writes
// without clearing it — and "a flag that fires on nothing" is worse than no
// flag at all. So the state is computed from data that already exists, and it
// clears automatically the moment ANY surface records a review that covers the
// MVR in question.
//
// ── AND IT REFUSES TO GUESS ────────────────────────────────────────────────
// memory/lesson-confident-wrong-parse-defeats-refusal-guards: a
// "refuse if MISSING" check never fires on a value that is present and
// confidently wrong. Every date here therefore gets the missing check AND an
// implausibility check (non-calendar / in the future / absurdly old). When
// either date is unreadable the state is `indeterminate` — it renders as
// "review status cannot be computed" and says why. It NEVER degrades into a
// confident green or a confident red.
//
// 🔴 BYTE-MIRROR: compliance-partner.js carries `_cpMvrReviewState`, the same
// logic with the same five codes. The partner portal is a SEPARATE page/realm
// (public/compliance-manager-v2.html) and cannot read anything app.js puts on
// `window`, which is why this is duplicated rather than shared — the same
// deliberate fork as `_render7DayStatementBlock` ↔ `renderSevenDayStatementFlag`.
// CHANGE ONE, CHANGE BOTH.
// ============================================================================

const _COMP_MVR_MIN_YEAR = 1990;

// Loose value → Date, tolerant of the shapes the person doc really carries
// (YYYY-MM-DD string from recordMvrPull, ISO string, Date, Firestore Timestamp)
// and strict about everything else. Returns null rather than a coerced guess.
function _compReviewParseDate(v) {
  if (!v) return null;
  try {
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v === 'object') {
      if (typeof v.toDate === 'function') { const d = v.toDate(); return isNaN(d.getTime()) ? null : d; }
      if (typeof v.seconds === 'number') { const d = new Date(v.seconds * 1000); return isNaN(d.getTime()) ? null : d; }
      return null;
    }
    if (typeof v !== 'string') return null;
    const s = v.trim();
    if (!s) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) {
      const y = +m[1], mo = +m[2], d = +m[3];
      if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
      const dt = new Date(Date.UTC(y, mo - 1, d));
      if (isNaN(dt.getTime())) return null;
      if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
      return dt;
    }
    // Anything else (US slashes, prose, an 8-digit run) is NOT accepted — a
    // permissive Date() here is exactly how a confident wrong date gets in.
    return null;
  } catch (_) { return null; }
}

// The refuse-if-IMPLAUSIBLE twin. null = usable; otherwise a human reason.
function _compReviewDateProblem(raw, whatItIs) {
  if (!raw) return null; // absence is handled by the caller — this judges PRESENT values
  const dt = _compReviewParseDate(raw);
  if (!dt) return `The ${whatItIs} on this record (${String(raw).slice(0, 40)}) is not a readable calendar date.`;
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const diffDays = Math.round((dt.getTime() - todayUtc) / 86400000);
  if (diffDays > 1) return `The ${whatItIs} on this record (${String(raw).slice(0, 40)}) is in the future.`;
  if (dt.getUTCFullYear() < _COMP_MVR_MIN_YEAR) return `The ${whatItIs} on this record (${String(raw).slice(0, 40)}) is implausibly old.`;
  return null;
}

// Does the recorded MVR result affirmatively show violations?
// Mirrors the TASK-1301 allowlist used by the partner MVR label: an unknown or
// empty result is NOT "clean" — it is unknown, and unknown still needs a human.
function _compMvrResultKind(person) {
  const res = String(person && person.mvrLastResult || '').toLowerCase().trim();
  const vio = (Number(person && person.mvrViolationCount) || 0) > 0;
  if (vio) return 'violations';
  if (/violation|hit|fail|not[ _-]?clear/.test(res)) return 'violations';
  if (/pending|ordered|processing|in[ _-]?progress|suspended|dispute/.test(res)) return 'pending';
  if (/^(clean|clear|eligible|pass)$|no[ _-]?violation|no[ _-]?record/.test(res)) return 'clean';
  return 'unknown';
}

// Does the stamped attestation ref describe the CURRENT MVR on the record?
// Compares every scalar the ref carries, not just the date. A field the ref
// never stamped (an older attestation, or a pull that had no result yet) is
// skipped rather than treated as a mismatch — a legacy ref that matches on
// everything it DID record still counts as reviewed, otherwise every review
// signed before this task would be re-flagged for no reason.
function _compMvrRefMatches(ref, p) {
  const norm = (v) => (v == null ? '' : String(v).trim().toLowerCase());
  if (norm(ref.pullDate) !== norm(p.mvrLastPullDate || p.mvrLastPulled)) return false;
  if (ref.state != null && norm(ref.state) !== norm(p.mvrLastState)) return false;
  if (ref.result != null && norm(ref.result) !== norm(p.mvrLastResult)) return false;
  if (ref.violationCount != null
      && (Number(ref.violationCount) || 0) !== (Number(p.mvrViolationCount) || 0)) return false;
  return true;
}

/**
 * Derived §391.25(b) review state for this driver's most recent MVR.
 *
 * codes:
 *   'none'          — no MVR on file; there is nothing to review yet
 *   'indeterminate' — a date on the record is unreadable/implausible. NEVER a
 *                     confident pending or a confident clear.
 *   'reviewed'      — a recorded review covers this MVR
 *   'new_mvr'       — FLAG A: "New MVR on file — motor carrier / Review required"
 *   'new_violation' — FLAG B: "New violation found on MVR — motor carrier / Review
 *                     required"  (B implies A; they are two distinct states,
 *                     not one, because Crown asks the MOTOR CARRIER to sign B)
 *                     TASK-9300 (2): Kolsen asked for "Review required" — the review is a
 *                     §391.25(b) duty the carrier owes, not something the software
 *                     requests. Partner twin in compliance-partner.js carries the same
 *                     two labels; change both or neither.
 */
function _compMvrReviewState(person) {
  const p = _mvrDq(person || {}); // TASK-1496-S7
  const mvrRaw = p.mvrLastPullDate || p.mvrLastPulled || null;
  if (!mvrRaw) {
    return { code: 'none', flagA: false, flagB: false, label: '', detail: '', reason: null,
             mvrDate: null, reviewDate: null, resultKind: null };
  }

  const mvrProblem = _compReviewDateProblem(mvrRaw, 'MVR pull date');
  const reviewRaw = p.annualReviewDate || null;
  const reviewProblem = _compReviewDateProblem(reviewRaw, 'annual review date');
  if (mvrProblem || reviewProblem) {
    return {
      code: 'indeterminate', flagA: false, flagB: false,
      label: 'Review status cannot be computed',
      detail: 'Fix the date on the record, then this will resolve on its own.',
      reason: mvrProblem || reviewProblem,
      mvrDate: mvrRaw, reviewDate: reviewRaw, resultKind: null,
    };
  }

  const mvrDt = _compReviewParseDate(mvrRaw);
  const reviewDt = _compReviewParseDate(reviewRaw);
  const resultKind = _compMvrResultKind(p);

  // Preferred binding: the attestation recorded WHICH MVR it reviewed
  // (annualReviewMvrRef, written by this file's saveAnnualReview and by the
  // partner handler). The match is over the WHOLE stamped ref, not just the
  // date — two pulls on the same day are common (a re-pull to chase a pending
  // result), and comparing pullDate alone would let a review of the FIRST one
  // silently clear the flag on the SECOND. `result` and `violationCount` are
  // exactly the fields that change between those two pulls, so including them
  // is what makes the binding meaningful.
  // Honest limit: two same-day pulls that are identical in date, state, result
  // AND violation count are indistinguishable here — but they are also the same
  // record for review purposes, so treating them as reviewed is correct.
  // 🔴 WHEN A REF EXISTS IT IS AUTHORITATIVE — the date fallback must NOT run.
  // Found by probing this function rather than reading it: with the fallback
  // reachable after a failed ref match, a same-day re-pull (review stamped
  // against the 08-01 "pending" pull, then a second 08-01 pull comes back with
  // violations) fell straight through to `reviewDt >= mvrDt`, both 2026-08-01,
  // and reported REVIEWED — silently undoing the whole point of the ref. The
  // fallback exists only for records whose signer never stamped a ref (hr.js
  // today, and every review filed before TASK-1623).
  const ref = p.annualReviewMvrRef;
  const hasRef = !!(ref && typeof ref === 'object' && ref.pullDate);
  const _reviewed = { code: 'reviewed', flagA: false, flagB: false,
                      label: 'Reviewed', detail: '', reason: null,
                      mvrDate: mvrRaw, reviewDate: reviewRaw, resultKind };
  if (hasRef) {
    if (_compMvrRefMatches(ref, p)) return _reviewed;
    // Ref present and pointing at a DIFFERENT record: this MVR is genuinely
    // unreviewed. Fall through to the flag — never to date arithmetic.
  } else if (reviewDt && mvrDt && reviewDt.getTime() >= mvrDt.getTime()) {
    return _reviewed;
  }

  if (resultKind === 'violations') {
    const n = Number(p.mvrViolationCount) || 0;
    return {
      code: 'new_violation', flagA: true, flagB: true,
      label: 'New violation found on MVR — motor carrier / Review required',
      detail: (n > 0 ? `${n} violation${n === 1 ? '' : 's'} on the ` : 'Violations on the ')
        + `MVR pulled ${String(mvrRaw).slice(0, 10)}. §391.25(b) needs a human fitness determination, and §391.25(c)(2) needs the reviewer's name and date on file.`,
      reason: null, mvrDate: mvrRaw, reviewDate: reviewRaw, resultKind,
    };
  }
  return {
    code: 'new_mvr', flagA: true, flagB: false,
    label: 'New MVR on file — motor carrier / Review required',
    detail: `MVR pulled ${String(mvrRaw).slice(0, 10)} has not been reviewed yet`
      + (resultKind === 'unknown' ? ' and no result was recorded on it.' : '.')
      + ' §391.25(b) requires the review; §391.25(c)(2) requires the reviewer\'s name and the review date in the DQ file.',
    reason: null, mvrDate: mvrRaw, reviewDate: reviewRaw, resultKind,
  };
}

// Shared banner markup for both flags + the indeterminate state. Returns '' for
// 'none' and 'reviewed' so a compliant driver's card stays quiet.
function _compMvrReviewBanner(person, opts) {
  const st = _compMvrReviewState(person);
  if (st.code === 'none' || st.code === 'reviewed') return '';
  const personId = String(person.id || '');
  const jsId = escJsAttr(personId); // TASK-10216 (COD Adversary) — JS string inside an attribute
  const cfg = st.code === 'new_violation'
    ? { bg: '#FEF2F2', border: '#FCA5A5', color: '#991B1B', icon: 'gavel' }
    : st.code === 'new_mvr'
      ? { bg: '#FFFBEB', border: '#FDE68A', color: '#92400E', icon: 'rate_review' }
      : { bg: '#F1F5F9', border: '#CBD5E1', color: '#334155', icon: 'help_outline' };
  // TASK-10216 — default ON: was `(opts && …)`, so the no-opts call in the MVR section never drew it.
  const showAction = !(opts && opts.withAction === false) && st.code !== 'indeterminate';
  // TASK-10216 — the reviewer is asked to sign a §391.25(b) determination, so the
  // banner itself opens the MVR (host-authorized onclick from hrMvrReportButton —
  // the same one the DQ scorecard gets; null = this session may not open it), or,
  // when no MVR record is on file at all (same predicate + bg doc as the
  // "MVR document" row), says so and offers the upload. "Record the review" stays
  // either way: a paper or portal MVR is a legitimate source (the modal asks which).
  let mvrExtra = '', mvrNote = '';
  if (st.code !== 'indeterminate') {
    const bgDoc = _compBgDocCache[personId];
    const vb = (window.HRModule && typeof window.HRModule.hrMvrReportButton === 'function')
      ? (window.HRModule.hrMvrReportButton(person, ComplianceState.tenantId, bgDoc) || {}) : {};
    const PS = window.PersonSections;
    if ((vb.onClick || vb.disabledReason) && PS && typeof PS.mvrViewButtonHtml === 'function') {
      mvrExtra = PS.mvrViewButtonHtml({ onClick: vb.onClick || null, disabledReason: vb.disabledReason || null });
    } else if (bgDoc !== undefined && window.DqMatrix // only once the bg doc was looked up (no false "nothing to open")
        && !window.DqMatrix.mvrRecordIsOnFile({ ...person, bgCheck: bgDoc || person.bgCheck })) {
      const canUp = _compCanWriteRoster();
      mvrNote = `<div class="comp-mvr-noreport" style="font-size:0.75rem;color:${cfg.color};margin-top:6px;font-weight:600;">No MVR report to open. ${canUp ? 'Upload it or restore the link.' : 'Ask someone with roster access to upload it.'}</div>`;
      if (canUp) mvrExtra = `<button class="btn btn-sm" onclick="window.ComplianceModule.recordMvrPull('${jsId}')" style="padding:3px 10px;font-size:0.72rem;background:#fff;color:${cfg.color};border:1px solid ${cfg.border};"><span class="material-icons" style="font-size:14px;">upload_file</span> Upload the MVR</button>`;
    }
  }
  return `
    <div data-comp-mvr-banner="${escapeHtml(personId)}" data-wa="${showAction ? 1 : 0}" style="background:${cfg.bg};border:1px solid ${cfg.border};border-radius:8px;padding:10px 12px;margin-bottom:10px;">
      <div style="display:flex;align-items:flex-start;gap:8px;">
        <span class="material-icons" style="font-size:18px;color:${cfg.color};flex-shrink:0;">${cfg.icon}</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:700;font-size:0.8rem;color:${cfg.color};">${escapeHtml(st.label)}</div>
          <div style="font-size:0.75rem;color:${cfg.color};opacity:0.9;margin-top:2px;line-height:1.45;">${escapeHtml(st.reason || st.detail)}</div>
          ${mvrNote}
          ${(showAction || mvrExtra) ? `<div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap;align-items:center;">
            ${showAction ? `<button class="btn btn-sm" onclick="window.ComplianceModule.openAnnualReviewModal('${jsId}')" style="padding:3px 10px;font-size:0.72rem;background:${cfg.color};color:#fff;border:none;">
              <span class="material-icons" style="font-size:14px;">rate_review</span> Record the review
            </button>` : ''}${mvrExtra}
          </div>` : ''}
        </div>
      </div>
    </div>`;
}

// ============================================================================
// §383.31 — the DRIVER's written notification of conviction.
//
// 🔴 This is NOT the §391.25(c)(2) review note and must never be filed as one:
// two records, two signers, and only the review note is a §391.51(b)(5) DQ-file
// requirement. The button below asks the DRIVER for their notification; the
// "Record the review" button above is what the SUPERVISOR signs.
//
// 🔴 The 30-day clock runs from the DATE OF CONVICTION, not from the MVR. By
// the time an MVR surfaces a violation the driver's window has usually already
// closed, so nothing here computes a deadline from the pull date — the driver
// supplies the conviction date on the form and the server computes from that
// (or refuses to, and says so).
// ============================================================================
function _compConvictionNoticeBlock(person) {
  const personId = String(person.id || '');
  const st = _compMvrReviewState(person);
  const filedDate = person.convictionNoticeLastFiledDate || null;
  const requestedAt = person.convictionNoticeLastRequestedAt || null;
  const delivery = (person.convictionNoticeLastDelivery && typeof person.convictionNoticeLastDelivery === 'object')
    ? person.convictionNoticeLastDelivery : null;
  const count = Number(person.convictionNoticeCount) || 0;

  // Only surface the ask where it is actually relevant: a violation flag, or a
  // notification already in flight / on file. Never nag a clean driver.
  if (st.code !== 'new_violation' && !requestedAt && !filedDate) return '';

  // DELIVERY REALITY (memory/lesson-ios-blocks-links-from-unknown-senders).
  // `linkTappable` is a server-authored TRI-state and this is the only place it
  // is rendered: true = an email landed; 'unknown' = SMS only, which Twilio will
  // call delivered while iOS silently disables the link; false = nothing went.
  // Do not collapse it to a boolean.
  let deliveryHtml = '';
  if (delivery && !filedDate) {
    if (delivery.delivered === false) {
      deliveryHtml = `<div style="background:#FEF2F2;border:1px solid #FCA5A5;border-radius:6px;padding:8px 10px;margin-top:6px;font-size:0.73rem;color:#991B1B;line-height:1.45;">
        <strong>Not delivered.</strong> ${escapeHtml((delivery.warnings || []).join(' ') || 'DispatchDeck could not reach this driver.')}
        Nothing has reached ${escapeHtml((person.name || 'the driver').split(/\s+/)[0])} — send it another way or add contact details and re-send.
      </div>`;
    } else if (delivery.linkTappable === 'unknown') {
      deliveryHtml = `<div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:6px;padding:8px 10px;margin-top:6px;font-size:0.73rem;color:#92400E;line-height:1.45;">
        <strong>Texted — delivery of the link is not confirmed.</strong> On an iPhone, links from a number the driver has never replied to are not tappable, and the carrier still reports the text as delivered. Ask ${escapeHtml((person.name || 'them').split(/\s+/)[0])} to reply <strong>START</strong> to your DispatchDeck number, or add an email address and re-send.
      </div>`;
    } else if (delivery.emailed) {
      deliveryHtml = `<div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">Emailed to ${escapeHtml(delivery.emailAddress || '')}${delivery.texted ? ' and texted' : ''}.</div>`;
    }
  }

  const btnLabel = filedDate ? 'Request another' : (requestedAt ? 'Re-send form' : 'Send §383.31 form');
  return `
    <div class="compliance-detail-section" id="comp-conviction-section-${personId}">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#B45309;">gavel</span> Conviction Notification — §383.31
        </div>
        <button class="btn btn-sm" onclick="window.ComplianceModule.requestConvictionNotice('${personId}')" style="padding:3px 8px;font-size:0.72rem;background:#B45309;color:#fff;border:none;" title="Send this driver the §383.31 notification-of-conviction form to complete and sign on their phone">
          <span class="material-icons" style="font-size:14px;">forward_to_inbox</span> ${btnLabel}
        </button>
      </div>
      <div class="compliance-detail-fields">
        <div style="font-size:0.75rem;color:var(--text-secondary);line-height:1.5;padding:2px 0 8px;">
          §383.31 puts this duty on the <strong>driver</strong>: a CDL holder must notify their employer in writing within <strong>30 days of the date of conviction</strong>. The clock runs from the conviction, not from this MVR — by now that window has often already closed. Sending the form gets the signed statement on file either way. It does <strong>not</strong> satisfy the §391.25 review above.
        </div>
        ${filedDate ? detailRow('Last notification filed', `${formatDateShort(filedDate)}${person.convictionNoticeLastConvictionDate ? ` &bull; conviction ${escapeHtml(String(person.convictionNoticeLastConvictionDate).slice(0, 10))}` : ''}${person.convictionNoticeLastFiledLate === true ? ' <span style="color:#92400E;font-weight:600;">(filed after the 30-day window)</span>' : ''}`) : ''}
        ${count > 1 ? detailRow('Notifications on file', String(count)) : ''}
        ${(!filedDate && requestedAt) ? detailRow('Status', `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:#FFFBEB;color:#D97706;border-radius:6px;font-size:0.75rem;font-weight:600;"><span class="material-icons" style="font-size:14px;">hourglass_top</span> Sent — awaiting the driver's signature</span>`) : ''}
        ${!filedDate && !requestedAt ? `<div style="padding:6px 4px;color:var(--text-secondary);font-size:0.78rem;">No §383.31 notification on file for this driver.</div>` : ''}
        ${deliveryHtml}
      </div>
    </div>`;
}

// Ask the driver for their §383.31 notification. Reports HONESTLY what landed —
// an SMS-only send is never reported as "sent", because we cannot know the link
// is tappable, and an undelivered send hands back the link so a human can pass
// it on another way.
async function requestConvictionNoticeCompliance(personId) {
  const tenantId = ComplianceState.tenantId;
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!tenantId || !person) return;
  if (!window.showConfirm) { showToast('Confirm dialog unavailable — reload and try again', 'error'); return; }

  const first = (person.name || 'this driver').split(/\s+/)[0];
  const ok = await window.showConfirm(
    'Send the §383.31 form?',
    `${first} will get a secure link to complete and sign their written notification of conviction. `
    + `49 CFR §383.31 puts that duty on the driver — DispatchDeck files the signed result on their record. `
    + `This does not record your §391.25 annual review.`,
    { confirmText: 'Send the form', cancelText: 'Not now' });
  if (!ok) return;

  try {
    const call = httpsCallable(functions, 'requestConvictionNotification');
    const res = await call({
      tenantId, personId,
      // The MVR that prompted the ask, for the record trail only — never for the
      // deadline. Scalars only.
      mvrRef: ((m) => ({ // TASK-1496-S7 — MVR detail legacy-first
        pullDate: m.mvrLastPullDate || m.mvrLastPulled || null,
        state: m.mvrLastState || null,
        result: m.mvrLastResult || null,
        violationCount: Number(m.mvrViolationCount) || 0,
      }))(_mvrDq(person)),
    });
    const d = (res && res.data) || {};
    const dl = d.delivery || {};

    // Optimistic local state so the section repaints with the real delivery
    // outcome without waiting for the person snapshot.
    person.convictionNoticeLastRequestedAt = new Date().toISOString();
    person.convictionNoticeLastRequestId = d.reqId || null;
    person.convictionNoticeLastDelivery = dl;

    if (dl.delivered === false) {
      // Never imply it went. Hand over the link so the ask isn't dead.
      const link = d.handoffLink || '';
      if (link && navigator.clipboard) { try { await navigator.clipboard.writeText(link); } catch (_) { /* clipboard denial is fine */ } }
      showToast(`Nothing was delivered to ${first}. ` + ((dl.warnings || [])[0] || '')
        + (link ? ' The link has been copied to your clipboard — send it another way.' : ''), 'error');
    } else if (dl.linkTappable === 'unknown') {
      showToast(`Texted to ${first}. DispatchDeck cannot confirm the link is tappable — if they have never replied to your DispatchDeck number, an iPhone will not let them open it. Ask them to reply START.`, 'info');
    } else {
      showToast(`§383.31 form sent to ${first}.`, 'success');
    }
    showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] requestConvictionNotice failed:', err);
    const code = String(err && err.code || '');
    showToast(
      code.includes('failed-precondition')
        ? (err.message || 'This driver has no email or mobile number on file.')
        : code.includes('permission-denied')
          ? 'You do not have permission to send this form — ask an owner or admin.'
          : ('Could not send the form: ' + (err && err.message || 'unknown error')),
      'error');
  }
}


function buildComplianceMvrSection(person) {
  person = _mvrDq(person); // TASK-1496-S7
  const personId = person.id;
  const mvrLastDate = person.mvrLastPullDate || person.mvrLastPulled || null;
  const mvrNextDue = person.mvrNextDueDate || null;
  const mvrLastState = person.mvrLastState || null;
  const mvrLastResult = person.mvrLastResult || null;
  // TASK-969 — webhook echoes the order-time purpose onto the person doc.
  const mvrPurposeCfg = COMPLIANCE_MVR_PURPOSE_LABELS[person.mvrLastPurpose] || null;
  const partnerHandles = compBgHandledByPartner();
  const partnerLabel = compPartnerLabel();
  // Paid Checkr MVR only offered when Checkr is live for the tenant; the request
  // routes to the partner. Self-managed paid initiation is intentionally NOT
  // exposed in Compliance (FCRA adverse-action wizard lives only in HR).
  const checkrActive = !!window.AppState?.tenantData?.compliancePackageDefault;

  const MVR_COLORS = {
    clean: { label: 'Clean', color: '#059669', bg: '#D1FAE5', icon: 'check_circle' },
    violations: { label: 'Violations', color: '#DC2626', bg: '#FEE2E2', icon: 'warning' },
    pending: { label: 'Pending', color: '#D97706', bg: '#FFFBEB', icon: 'hourglass_top' },
  };
  const mvrState = MVR_COLORS[mvrLastResult] || null;

  // TASK-1279 — mirror the HR MVR card: don't leave the paid "Request MVR" live while
  // an MVR order is already in flight (double-request / double-charge trap). The person
  // doc already carries the pending signal (bgCheckRequestedAt / an in-flight checkr
  // status / bgCheckPending); covers-mvr via the shared HRModule helper (Pitfall #1 —
  // no cross-import). Unknown slug ⇒ don't disable (fail-safe); a criminal-only pending
  // must not false-block an MVR request.
  const _mvrPendSlug = person.bgCheckRequestedPackageSlug || person.checkrPackageSlug || person.bgCheckPackageSlug || '';
  const _mvrPendCoversMvr = !!window.HRModule?.checkrPackageCoversMvr?.(_mvrPendSlug);
  const _mvrPendStatusLc = String(person.checkrReportStatus || person.bgCheckStatus || '').toLowerCase();
  const _mvrPendInFlight =
    (!!person.bgCheckRequestedAt && !person.checkrReportId) ||
    ['invited', 'created', 'pending', 'needs_candidate_action'].includes(_mvrPendStatusLc) ||
    person.bgCheckPending === true;
  // TASK-1384 — invitation evidence from the cached peopleBackgroundChecks doc
  // (hydrateComplianceBgReport fetches it and re-renders this section once it
  // lands). Person docs never get the webhook's checkrReportStatus dual-write
  // and creating a REAL invitation deletes bgCheckRequestedAt server-side, so
  // that doc is the only truth for "an invitation actually went to the driver".
  // Cache undefined (not fetched / read denied) ⇒ person-doc-only classification.
  const _mvrBgKind = window.HRModule?.mvrBgDocPendingKind?.(_compBgDocCache[personId]) || null;
  const _mvrInFlight = !mvrLastDate && ((_mvrPendCoversMvr && _mvrPendInFlight) || !!_mvrBgKind);
  // TASK-1384 — discriminate WHICH pending state, so the copy never claims a
  // signature step that isn't real (the 8/05 incident: two partner-routed MVR
  // requests rendered "awaiting signature" while no Checkr email existed, and
  // dispatch chased the drivers instead of the partner). Real invitation/report
  // evidence beats the request stamp.
  let _mvrPendKind = null;
  if (_mvrInFlight) {
    if (_mvrBgKind) _mvrPendKind = _mvrBgKind;
    else if (['invited', 'created', 'needs_candidate_action'].includes(_mvrPendStatusLc)) _mvrPendKind = 'signature';
    else if (_mvrPendStatusLc === 'pending') _mvrPendKind = 'processing';
    else if (person.bgCheckRequestedAt && !person.checkrReportId) _mvrPendKind = 'requested';
    else _mvrPendKind = 'consent'; // bgCheckPending two-stage — consent captured, order not yet placed
  }
  // &quot;-escape too: these land inside double-quoted title attributes, and
  // escapeHtml (div.innerHTML) does not escape quotes. Renders as " in text.
  const _mvrFirstName = escapeHtml((person.name || 'the driver').split(/\s+/)[0]).replace(/"/g, '&quot;');
  const _mvrPartnerEsc = escapeHtml(partnerLabel).replace(/"/g, '&quot;');
  // Per-state copy — mirrors hr.js's _MVR_PEND_UI (same states, compliance sizing).
  // 'requested' gets a distinct purple badge so it never scans as awaiting-signature.
  const _MVR_PEND_UI = {
    requested: {
      icon: 'forward_to_inbox', bg: '#F3E8FF', color: '#7C3AED',
      label: `MVR requested — pending ${_mvrPartnerEsc}`,
      btnLabel: 'MVR requested',
      btnTitle: `This MVR was requested from ${_mvrPartnerEsc} and is on their worklist — nothing has been sent to the driver yet. The result lands here automatically once they run it.`,
    },
    signature: {
      icon: MVR_COLORS.pending.icon, bg: MVR_COLORS.pending.bg, color: MVR_COLORS.pending.color,
      label: `MVR ordered — awaiting ${_mvrFirstName}'s signature`,
      btnLabel: 'MVR in progress',
      btnTitle: 'An MVR order is already in progress for this driver (awaiting signature). The result lands here automatically — no need to re-request.',
    },
    processing: {
      icon: MVR_COLORS.pending.icon, bg: MVR_COLORS.pending.bg, color: MVR_COLORS.pending.color,
      label: 'MVR ordered — report processing',
      btnLabel: 'MVR in progress',
      btnTitle: `An MVR order is in progress — ${_mvrFirstName} already completed their part and the report is processing. The result lands here automatically — no need to re-request.`,
    },
    consent: {
      icon: MVR_COLORS.pending.icon, bg: MVR_COLORS.pending.bg, color: MVR_COLORS.pending.color,
      label: 'Check authorized — order not yet placed',
      btnLabel: 'Check pending',
      btnTitle: 'This driver already authorized a background check that includes the MVR — the order has not been placed yet, so do not start a second one.',
    },
  };
  const _mvrPendUi = _MVR_PEND_UI[_mvrPendKind] || _MVR_PEND_UI.signature;

  const recordBtn = `<button class="btn btn-sm" onclick="window.ComplianceModule.recordMvrPull('${personId}')" style="padding:3px 8px;font-size:0.72rem;background:#0284C7;color:#fff;border:none;" title="Already have a current MVR? Log the pull date. Free — no charge, no re-pull.">
    <span class="material-icons" style="font-size:14px;">${mvrLastDate ? 'refresh' : 'edit_note'}</span> ${mvrLastDate ? 'New Pull' : 'Record existing'}
  </button>`;
  // TASK-9822 — a Canadian license can't go through a Checkr MVR: no "Request MVR" button.
  const _caMvr = (window.PersonSections && typeof window.PersonSections.canadianLicenseMvrBlock === 'function')
    ? window.PersonSections.canadianLicenseMvrBlock(person) : { blocked: false };
  const requestBtn = (_caMvr.blocked && !_mvrInFlight) ? '' : _mvrInFlight
    ? `<button class="btn btn-sm" disabled style="padding:3px 8px;font-size:0.72rem;background:#F3F4F6;color:#9CA3AF;border:1px solid #E5E7EB;cursor:not-allowed;" title="${_mvrPendUi.btnTitle}">
        <span class="material-icons" style="font-size:14px;">${_mvrPendKind === 'requested' ? 'forward_to_inbox' : 'hourglass_top'}</span> ${_mvrPendUi.btnLabel}
      </button>`
    : (checkrActive && partnerHandles)
    ? `<button class="btn btn-sm" onclick="window.ComplianceModule.requestMvrFromPartner('${personId}')" style="padding:3px 8px;font-size:0.72rem;background:transparent;color:#9333EA;border:1px solid #9333EA;" title="Request ${escapeHtml(partnerLabel)} run a new MVR via Checkr">
        <span class="material-icons" style="font-size:14px;">forward_to_inbox</span> Request MVR
      </button>`
    // TASK-1605 — the partner-routed Request MVR button above is what a package
    // would unlock here; when there is none, say so with the link instead of
    // rendering nothing. Self-managed paid initiation is (still) not exposed in
    // Compliance, so a carrier-handled account gets no stop on this surface — its
    // stops live in HR. Shared renderer in hr.js, reached through window
    // (Pitfall #1); hr.js is loaded by app.js before any view, the optional
    // chain is belt-and-braces.
    : (partnerHandles && !checkrActive)
    ? (window.HRModule?.screeningPackageStopHtml?.({ compact: true }) || '')
    : '';

  return `
    <div class="compliance-detail-section" id="comp-mvr-section-${personId}">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#0284C7;">drive_eta</span> MVR — Driving Record
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">${recordBtn}${requestBtn}<span id="comp-mvr-viewreport-${personId}" style="display:contents;"></span></div>
      </div>
      <div class="compliance-detail-fields">
        ${/* TASK-1623 — §391.25(b) review flags. Derived, so a review recorded on
              ANY surface (here, HR, or the partner portal) clears them; and
              never a confident state when a date on the record is unreadable. */
          _compMvrReviewBanner(person)}
        ${_mvrInFlight ? detailRow('Status', `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${_mvrPendUi.bg};color:${_mvrPendUi.color};border-radius:6px;font-size:0.75rem;font-weight:600;"><span class="material-icons" style="font-size:14px;">${_mvrPendUi.icon}</span> ${_mvrPendUi.label}</span>${(_mvrPendKind === 'requested' && _compIsOwnerOrAdmin()) ? ` <button class="btn btn-sm" onclick="window.ComplianceModule.cancelMvrPartnerRequest('${personId}')" style="padding:2px 8px;font-size:0.68rem;background:transparent;color:#DC2626;border:1px solid #FCA5A5;margin-left:6px;" title="Withdraw this request — removes it from ${_mvrPartnerEsc}'s worklist. You can request it again at any time."><span class="material-icons" style="font-size:12px;">close</span> Cancel request</button>` : ''}`) : ''}
        ${mvrState ? detailRow('Latest Result', `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${mvrState.bg};color:${mvrState.color};border-radius:6px;font-size:0.75rem;font-weight:600;"><span class="material-icons" style="font-size:14px;">${mvrState.icon}</span> ${mvrState.label}</span>`) : ''}
        ${detailRow('Last Pull', mvrLastDate ? formatDateShort(mvrLastDate) : 'Never pulled')}
        ${/* TASK-9429 — a pull date with no MVR record behind it (saved before the upload
              gate) is not a current MVR. Same predicate as the DQ scorecard + partner portal. */
          /* COD 1976: a Checkr MVR linked only on the hydrated bg doc (the "View MVR
             report" button's own source) is on file too — hand it in as bgCheck. */
          (mvrLastDate && window.DqMatrix && !window.DqMatrix.mvrRecordIsOnFile({ ...person, bgCheck: _compBgDocCache[personId] || person.bgCheck })) ? detailRow('MVR document', `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:#FFFBEB;color:#92400E;border:1px solid #FCD34D;border-radius:6px;font-size:0.75rem;font-weight:600;"><span class="material-icons" style="font-size:14px;">warning_amber</span> Not on file — upload the MVR; a pull date alone is not proof</span>`) : ''}
        ${mvrPurposeCfg ? detailRow('Purpose', `<span style="display:inline-flex;align-items:center;padding:2px 8px;background:${mvrPurposeCfg.bg};color:${mvrPurposeCfg.color};border-radius:6px;font-size:0.75rem;font-weight:600;">${escapeHtml(mvrPurposeCfg.full)}</span>`) : ''}
        ${mvrLastState ? detailRow('State / Province', escapeHtml(window.LicenseJurisdiction ? window.LicenseJurisdiction.jurisdictionLabel(mvrLastState) : mvrLastState)) : ''}
        ${_caMvr.blocked ? detailRow('License', `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:#FEF2F2;color:#991B1B;border:1px solid #FCA5A5;border-radius:6px;font-size:0.75rem;font-weight:600;">Canadian (${escapeHtml(_caMvr.label)}): ${escapeHtml(_caMvr.message)}</span>`) : ''}
        ${/* TASK-9822 — §391.23(b) good-faith efforts; the host authorizes with the same roster-write gate recordMvrGoodFaithEffort enforces. */
          (window.PersonSections && typeof window.PersonSections.renderMvrGoodFaith === 'function')
            ? window.PersonSections.renderMvrGoodFaith(person.mvrGoodFaithEfforts, { canRecord: _compCanWriteRoster(), recordOnClick: 'window.ComplianceModule.openMvrGoodFaith(' + JSON.stringify(String(personId)) + ')' })
            : ''}
        ${mvrNextDue ? detailRow('Next Pull Due', `${formatDateShort(mvrNextDue)} ${getExpiryStatusText(mvrNextDue)}`) : ''}
        ${(person.mvrLatestPdfPath || (person.mvrLatestPdfUrl && /^https?:\/\//i.test(String(person.mvrLatestPdfUrl)))) ? `<div style="padding:6px 0;display:flex;align-items:center;gap:10px;"><a href="#" onclick="event.preventDefault();window.viewComplianceDoc('${escJsAttr(person.mvrLatestPdfPath || '')}','${escJsAttr(person.mvrLatestPdfUrl || '')}','mvr');return false;" style="font-size:0.8rem;color:var(--brand-blue);display:inline-flex;align-items:center;gap:4px;cursor:pointer;"><span class="material-icons" style="font-size:16px;">picture_as_pdf</span> View latest MVR PDF</a><a href="#" onclick="event.preventDefault();window.viewCredDocHistory('${escapeHtml(person.id || '')}','mvr',null,this);return false;" title="All uploaded versions of this document" style="font-size:0.72rem;color:var(--text-secondary);display:inline-flex;align-items:center;gap:3px;cursor:pointer;"><span class="material-icons" style="font-size:14px;">history</span> History</a></div>` : ''}
        ${!mvrLastDate ? `<div style="padding:10px 4px;color:var(--text-secondary);font-size:0.8rem;text-align:center;">No MVR on file. FMCSR 391.25 requires a pull at least every 12 months.${partnerHandles ? ` A new paid pull is run by ${escapeHtml(partnerLabel)}.` : ''}</div>` : ''}
      </div>
    </div>
  `;
}

// TASK-832 — Background Check / criminal section for the Compliance person view.
// Renders a placeholder that hydrateComplianceBgReport() fills (async) only when
// a Checkr report actually exists on peopleBackgroundChecks/{personId}. Lets a
// partner-managed NON-PAYING carrier (HR paywalled) drill into the full MVR/
// criminal report — violations, screening detail — not just the summary badge.
// The viewer itself is the shared HRModule.openBgReportViewerCompliance (minimized:
// no adverse-action, no raw PDF, no Checkr dashboard link).
function buildComplianceBgSection(person) {
  const personId = person.id;
  return `
    <div class="compliance-detail-section" id="comp-bg-section-${escapeHtml(personId)}" style="display:none;">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#9333EA;">fact_check</span> Background Check
        </div>
        <span id="comp-bg-viewreport-${escapeHtml(personId)}" style="display:contents;"></span>
      </div>
      <div class="compliance-detail-fields" id="comp-bg-report-${escapeHtml(personId)}"></div>
    </div>
  `;
}

// Fetch the canonical peopleBackgroundChecks/{personId} doc and, IF a report
// exists, inject the "View report" button (MVR section + BG section) + the BG
// summary. One per-person getDoc, gated by the firestore.rules compliance
// person-linked read (TASK-832). A denial / missing doc leaves the placeholders
// empty (no button) — exactly the "shown only when a report exists" contract.
async function hydrateComplianceBgReport(personId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;
  const _bgBucket = _compBgDocBucket(tenantId); // TASK-9458 — the tenant this read is FOR
  let bg = null;
  try {
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'peopleBackgroundChecks', personId));
    if (snap.exists()) bg = snap.data() || {};
  } catch (e) {
    // Denied (no compliance/hr access) or transient — fail closed, no button.
    // The TASK-1384 cache stays undefined too: unknown, person-doc-only copy.
    console.warn('[Compliance] BG report lookup failed (non-fatal):', e?.message);
    return;
  }
  // TASK-9458 (COD r1) — the tenant changed while this read was in flight: the
  // linked-record pick below reads ComplianceState.people + the viewer's gates,
  // which now belong to the NEW tenant. Stop; cache nothing (unknown, not wrong).
  if (ComplianceState.tenantId !== tenantId) return;
  // TASK-9235 — hiring now writes a REFERENCE on the person-keyed doc (legacy
  // hires: a frozen copy); the report lives on the linked applicant record.
  // A member the report callable admits on its HR/admin lane may read that record
  // → show it (live, not the hire-day snapshot). A compliance-only member may not
  // (rules: person-linked only) → keep the person-keyed doc; a reference still
  // names the reports that were on file at hire, and the server opens exactly
  // those for this lane. Every button keeps passing the PERSON id — the server
  // resolves person → applicant record itself (two-way link), so no candidate id
  // is needed or exposed here.
  {
    const _p = ComplianceState.people.find(p => p.id === personId) || null;
    const _PS = window.PersonSections;
    const _linkedId = (_p && typeof _p.candidateId === 'string' && _p.candidateId && _p.candidateId !== personId) ? _p.candidateId : null;
    if (_linkedId && _PS?.pickPersonBgRecord && _compCanOpenCandidateBgReport()) {
      let _linked = null;
      try {
        const _ls = await getDoc(doc(db, 'tenants', tenantId, 'peopleBackgroundChecks', _linkedId));
        if (_ls.exists()) _linked = _ls.data() || {};
      } catch (_) { /* unreadable → keep the person-keyed doc */ }
      if (_linked) bg = _PS.pickPersonBgRecord({ personId, linkedId: _linkedId, own: bg || {}, linked: _linked }).data;
    } else if (bg && bg.bgRecordKind === 'applicant_reference' && !bg.latestReportId
      && Array.isArray(bg.inheritedReportIds) && bg.inheritedReportIds.length) {
      bg = { ...bg, latestReportId: bg.inheritedReportIds[0] };
    }
  }
  // TASK-1384 — cache the doc (or {} when none exists) so the MVR section can
  // discriminate "requested — pending partner" vs "invitation out — awaiting
  // signature", then re-render the section in place with that evidence. Runs
  // BEFORE the reportId early-return: the pending states this fixes exist
  // precisely when no report has landed yet.
  if (ComplianceState.tenantId !== tenantId) return; // TASK-9458 — …or during the linked read
  _bgBucket[personId] = bg || {};
  // Only touch the DOM if the detail modal is still showing this person (avoid
  // a stale async write into a re-rendered modal for a different driver).
  // TASK-9458 — …of this same tenant.
  if (ComplianceState.selectedPersonId !== personId || ComplianceState.tenantId !== tenantId) return;
  const _mvrSectionEl = document.getElementById(`comp-mvr-section-${personId}`);
  if (_mvrSectionEl) {
    const _p = ComplianceState.people.find(p => p.id === personId);
    if (_p) _mvrSectionEl.outerHTML = buildComplianceMvrSection(_p);
  }
  // TASK-10216 — the §391.25 section's copy of the review banner was drawn before this
  // doc landed too; redraw it so its View / "No MVR report" line reads the same doc.
  document.querySelectorAll('[data-comp-mvr-banner]').forEach((el) => {
    if (el.getAttribute('data-comp-mvr-banner') !== String(personId)) return;
    const _p = ComplianceState.people.find(p => p.id === personId);
    if (_p) el.outerHTML = _compMvrReviewBanner(_p, { withAction: el.getAttribute('data-wa') === '1' }) || '';
  });
  // TASK-9458 — the DQ scorecard was drawn before this doc landed: draw it again so
  // a Checkr MVR linked only here reads on file (same predicate + doc as the row).
  const _dqCardEl = document.getElementById(`comp-dq-scorecard-${personId}`);
  if (_dqCardEl) {
    const _p = ComplianceState.people.find(p => p.id === personId);
    if (_p) _dqCardEl.innerHTML = _compDqScorecardHtml(_p, window.DqMatrix.getApplicableDqItems(_p));
  }
  // TASK-969 — the MVR section gets its OWN opener: the standalone MVR viewer
  // (first-class, separately printable), not the full BG snapshot. Prefer the
  // MVR sub-screening's own report linkage; fall back to the parent report id
  // (the same join the single button used before this split). Purpose comes
  // from the order-time stamp on this bg doc — passed through so the viewer
  // doesn't need a second read.
  // TASK-1555 — an MVR ordered AFTER hire lands on the CANDIDATE-keyed bg doc
  // (the person-keyed doc is a one-shot promote-time mirror, so the read above
  // finds nothing), while the webhook still stamps person.mvrCheckrReportId +
  // the person carries candidateId pointing at the doc that DOES exist. Resolve
  // the button from that fallback so the carrier can open a report they paid
  // for. Two guards: candidateId must be present (the server's
  // _verifyReportScope 404s on a bare report id), and the member must be one
  // the callable's HR/admin lane admits — its compliance-module lane is
  // person-linked only, so a compliance-only member would hit a dead door.
  const safeId = String(personId).replace(/'/g, '');
  const _mvrPerson = _mvrDq(ComplianceState.people.find(p => p.id === personId) || null); // TASK-1496-S7
  let mvrJoinReportId = bg ? (bg.backgroundReports?.mvr?.checkrReportId || bg.latestReportId || null) : null;
  let mvrRecordId = personId;
  let mvrPurpose = bg
    ? ((bg.latestPackage && bg.byPackage?.[bg.latestPackage]?.mvrPurpose)
      || bg.backgroundReports?.mvr?.purpose
      || bg.mvrPurpose
      || null)
    : null;
  if (!mvrJoinReportId && _mvrPerson?.mvrCheckrReportId && _mvrPerson?.candidateId
      && _compCanOpenCandidateBgReport()) {
    mvrJoinReportId = _mvrPerson.mvrCheckrReportId;
    mvrRecordId = _mvrPerson.candidateId;
    mvrPurpose = _mvrPerson.mvrLastPurpose || null;
  }
  const mvrSlot = document.getElementById(`comp-mvr-viewreport-${personId}`);
  if (mvrSlot && mvrJoinReportId) {
    const safeMvrReport = escapeHtml(String(mvrJoinReportId));
    const safeMvrPurpose = String(mvrPurpose || '').replace(/[^a-z_]/g, '');
    const safeMvrRecord = String(mvrRecordId).replace(/'/g, '');
    mvrSlot.innerHTML = `<button class="btn btn-sm" onclick="window.ComplianceModule.openMvrReport('${safeId}','${safeMvrReport}','${safeMvrPurpose}','${safeMvrRecord}')" style="padding:3px 8px;font-size:0.72rem;background:#B45309;color:#fff;border:none;" title="Open the standalone MVR report (view + print)">
      <span class="material-icons" style="font-size:14px;">description</span> View MVR report
    </button>`;
  }

  if (!bg) return;
  // The viewer joins on a Checkr report id. Prefer the parent report id; fall
  // back to the MVR sub-screening linkage (TASK-832 capture).
  const reportId = bg.latestReportId || bg.backgroundReports?.mvr?.checkrReportId || null;
  if (!reportId) return;

  const safeReport = escapeHtml(String(reportId));
  const viewBtn = `<button class="btn btn-sm" onclick="window.ComplianceModule.openBgReport('${safeId}','${safeReport}')" style="padding:3px 8px;font-size:0.72rem;background:transparent;color:#9333EA;border:1px solid #9333EA;" title="Open the full screening detail (violations, criminal records)">
      <span class="material-icons" style="font-size:14px;">description</span> View report
    </button>`;

  const bgSection = document.getElementById(`comp-bg-section-${personId}`);
  const bgSlotBtn = document.getElementById(`comp-bg-viewreport-${personId}`);
  const bgSlot = document.getElementById(`comp-bg-report-${personId}`);
  if (bgSection && bgSlot) {
    const status = bg.latestAssessment || bg.latestResult || bg.latestStatus || null;
    const pkg = bg.latestPackage || null;
    const completedAt = bg.completedAt && typeof bg.completedAt.toDate === 'function'
      ? bg.completedAt.toDate() : null;
    const statusRow = status
      ? detailRow('Latest Result', escapeHtml(String(status)))
      : detailRow('Status', 'Report on file');
    bgSlot.innerHTML = `
      ${statusRow}
      ${pkg ? detailRow('Package', escapeHtml(String(pkg))) : ''}
      ${completedAt ? detailRow('Completed', formatDateShort(completedAt)) : ''}
    `;
    if (bgSlotBtn) bgSlotBtn.innerHTML = viewBtn;
    bgSection.style.display = '';
  }
}

// Open the shared, minimized compliance BG report viewer. Routes through
// window.HRModule (statically imported, so the export exists even when HR was
// never opened) — never reaches into HRState (TASK-832 / COD #7).
function openBgReport(personId, reportId) {
  const person = ComplianceState.people.find(p => p.id === personId) || null;
  const fn = window.HRModule?.openBgReportViewerCompliance;
  if (typeof fn !== 'function') {
    if (window.showToast) window.showToast('Report viewer unavailable — refresh and try again.', 'error');
    return;
  }
  fn({ tenantId: ComplianceState.tenantId, recordId: personId, reportId, personRecord: person });
}

// TASK-969 — open the standalone, printable MVR viewer (compliance-minimized:
// no adverse-action routing, no raw Checkr PDF). Same window.HRModule delegate
// pattern as openBgReport — never reaches into HRState (TASK-832 / COD #7).
// TASK-1555 — bgRecordId: the peopleBackgroundChecks doc key when it differs
// from the person id (post-hire Checkr order → candidate-keyed doc). The person
// still supplies identity via personRecord.
function openMvrReport(personId, reportId, mvrPurpose = null, bgRecordId = null) {
  const person = ComplianceState.people.find(p => p.id === personId) || null;
  const fn = window.HRModule?.openMvrReportViewerCompliance;
  if (typeof fn !== 'function') {
    if (window.showToast) window.showToast('MVR viewer unavailable — refresh and try again.', 'error');
    return;
  }
  fn({ tenantId: ComplianceState.tenantId, recordId: bgRecordId || personId, reportId, personRecord: person, mvrPurpose: mvrPurpose || null });
}

// Record an existing MVR pull — FREE, server-authored via the recordMvrPull
// callable (no Checkr, no charge). Compliance-local so it works for a carrier
// that never loaded HR (TASK-781 trap). Mirrors HR's saveMvrPull minimal path.
// TASK-9822 — §391.23(b) good-faith effort (a state/province sent no MVR back).
// Shared modal; recordMvrGoodFaithEffort enforces the roster-write gate server-side.
function openMvrGoodFaith(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person || !window.PersonSections || typeof window.PersonSections.openMvrGoodFaithModal !== 'function') return;
  if (!_compCanWriteRoster()) { showToast('You do not have permission to record this. Ask an owner or admin.', 'error'); return; }
  const lj = window.LicenseJurisdiction ? window.LicenseJurisdiction.licenseJurisdiction(person) : null;
  window.PersonSections.openMvrGoodFaithModal({
    personName: person.name || '',
    defaultJurisdiction: (lj && lj.province) || _mvrDq(person).mvrLastState || '', // TASK-1496-S7
    onSave: async (effort) => {
      const fn = httpsCallable(functions, 'recordMvrGoodFaithEffort');
      const res = await fn(Object.assign({ tenantId: ComplianceState.tenantId, personId }, effort));
      const row = Object.assign({}, (res && res.data && res.data.effort) || effort, { recordedBy: window.AppState?.user?.email || '', recordedSource: 'carrier' });
      const _gf = _mvrDq(person).mvrGoodFaithEfforts; // TASK-1496-S7
      person.mvrGoodFaithEfforts = (Array.isArray(_gf) ? _gf : []).concat([row]);
      showToast('Good-faith effort recorded', 'success');
      showPersonDetail(personId);
    },
  });
}

function recordMvrPull(personId) {
  const person = _mvrDq(ComplianceState.people.find(p => p.id === personId)); // TASK-1496-S7
  if (!person) return;

  const existing = document.getElementById('compliance-mvr-modal');
  if (existing) existing.remove();

  const today = new Date().toISOString().split('T')[0];
  const defaultState = person.mvrLastState || person.dlState || '';

  // TASK-1048 (818-S4) — single-source the purpose enum from HR's canonical
  // MVR_PURPOSE_DISPLAY via window.HRModule (statically exported; exists even when
  // HR was never opened). Fall back to the compliance-local COMPLIANCE_MVR_PURPOSE_LABELS
  // for a compliance-ONLY carrier whose HR bundle never loaded (same keys). Never a
  // third copy of the enum values themselves.
  const purposeCfgMap = window.HRModule?.MVR_PURPOSE_DISPLAY || COMPLIANCE_MVR_PURPOSE_LABELS;
  const purposeOptions = Object.keys(purposeCfgMap).map((key) => {
    const cfg = purposeCfgMap[key] || {};
    const label = cfg.short || cfg.full || key;
    // Default to annual — this modal frames the pull as the §391.25 annual review.
    const sel = key === 'annual' ? ' selected' : '';
    return `<option value="${escapeHtml(key)}"${sel}>${escapeHtml(label)}</option>`;
  }).join('');

  const modal = document.createElement('div');
  modal.id = 'compliance-mvr-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div class="modal-header" style="background:linear-gradient(135deg,#0EA5E9,#0284C7);color:#fff;flex-shrink:0;">
        <h3 style="color:#fff;display:flex;align-items:center;gap:8px;"><span class="material-icons">drive_eta</span> Record MVR Pull</h3>
        <button class="modal-close" onclick="document.getElementById('compliance-mvr-modal').remove()" style="color:#fff;"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:18px;overflow-y:auto;flex:1;min-height:0;">
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:14px;">Log a Motor Vehicle Record pull for <strong>${escapeHtml(person.name || '')}</strong>. Required annually per FMCSR 391.25.</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Pull Date *</label>
            <input type="date" id="comp-mvr-date" class="form-control" value="${today}">
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">State / Province *</label>
            ${/* TASK-9822 — US states + Canadian provinces (shared renderer). */
              (window.PersonSections && typeof window.PersonSections.mvrJurisdictionOptionsHtml === 'function')
                ? `<select id="comp-mvr-state" class="form-control">${window.PersonSections.mvrJurisdictionOptionsHtml(defaultState)}</select>`
                : `<input type="text" id="comp-mvr-state" class="form-control" maxlength="2" placeholder="ND" value="${escapeHtml(defaultState)}" style="text-transform:uppercase;">`}
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Result *</label>
            <select id="comp-mvr-result" class="form-control">
              <option value="">Select result...</option>
              <option value="clean">Clean — No violations</option>
              <option value="violations">Violations Found</option>
              <option value="pending">Pending — Awaiting state</option>
            </select>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Purpose *</label>
            <select id="comp-mvr-purpose" class="form-control">${purposeOptions}</select>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Violation Count</label>
            <input type="number" id="comp-mvr-violation-count" class="form-control" min="0" value="0">
          </div>
          <div class="form-group" style="grid-column:1/-1;margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Violations / Findings</label>
            <textarea id="comp-mvr-violations" class="form-control" rows="2" placeholder="List violations. Leave blank if clean."></textarea>
          </div>
          <div class="form-group" style="grid-column:1/-1;margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">MVR PDF (optional)</label>
            <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
              <input type="file" id="comp-mvr-pdf-file" accept="application/pdf" class="form-control" style="flex:1;min-width:0;" onchange="window.ComplianceModule.onMvrFilePicked(event)">
              <button type="button" id="btn-comp-mvr-autofill" class="btn btn-sm" onclick="window.ComplianceModule.autofillMvrFromPdf('${personId}')" style="background:linear-gradient(135deg,#7c3aed,#a78bfa);color:#fff;border:none;padding:6px 14px;font-size:0.78rem;display:none;align-items:center;gap:4px;">
                <span class="material-icons" style="font-size:16px;">auto_awesome</span> AI Auto-fill
              </button>
            </div>
            <div id="comp-mvr-pdf-status" style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">Max 10MB. Stored on the driver record. Click <em>AI Auto-fill</em> after picking a PDF to extract fields automatically.</div>
          </div>
        </div>
        <div style="background:#E0F2FE;border-radius:8px;padding:10px 12px;margin-top:10px;font-size:0.75rem;color:#0C4A6E;"><strong>FMCSR 391.25:</strong> The next MVR auto-schedules for 365 days from this pull date. Free — no charge, no re-pull.</div>
        <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:8px;">Canadian license? Upload the province's driver abstract here and pick the province. Asked and got nothing back? <a href="#" onclick="event.preventDefault();document.getElementById('compliance-mvr-modal')?.remove();window.ComplianceModule.openMvrGoodFaith('${escJsAttr(personId)}');return false;" style="color:var(--brand-blue);">Record a good-faith effort</a>.</div>
      </div>
      <div class="form-actions" style="padding:14px 18px;border-top:1px solid var(--border-light);display:flex;gap:8px;justify-content:flex-end;flex-shrink:0;">
        <button class="btn btn-secondary" onclick="document.getElementById('compliance-mvr-modal').remove()">Cancel</button>
        <button class="btn btn-primary" id="comp-mvr-save-btn" style="background:#0284C7;" onclick="window.ComplianceModule.saveMvrPull('${personId}')"><span class="material-icons" style="font-size:16px;">save</span> Save MVR Pull</button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  modal.classList.remove('hidden');
}

// TASK-1048 (818-S4) — read a picked file's bytes as bare base64 (data-URL prefix
// stripped). Mirrors HR's _readFileAsBase64 for the parseMvrPdf autofill call.
function _readComplianceFileAsBase64(file) {
  // TASK-1383 — guarded shared reader (settles on every path: error, abort,
  // null result, sync throw, 30s timeout). Same strip-the-prefix contract.
  return window.FileReadUtil.readAsBase64(file);
}

// TASK-1048 (818-S4) — MVR PDF picker gate. Mirrors HR's onMvrFilePicked: enforce
// the ≤10MB limit, reveal the AI Auto-fill button, and echo the selection.
function onMvrFilePicked(event) {
  const file = event?.target?.files?.[0];
  const btn = document.getElementById('btn-comp-mvr-autofill');
  const status = document.getElementById('comp-mvr-pdf-status');
  if (!file) { if (btn) btn.style.display = 'none'; return; }
  if (file.size > 10 * 1024 * 1024) {
    showToast('File too large (max 10MB)', 'error');
    if (btn) btn.style.display = 'none';
    if (event.target) event.target.value = '';
    return;
  }
  if (btn) btn.style.display = 'inline-flex';
  if (status) status.innerHTML = `Selected: <strong>${escapeHtml(file.name)}</strong> &bull; ${(file.size / 1024).toFixed(0)} KB`;
}

// TASK-1048 (818-S4) — Smart-Tools autofill. Mirrors HR's autofillMvrFromPdf EXACTLY:
// same parseMvrPdf callable + payload shape, so the TASK-1041 meter (200/day/tenant)
// counts it once via the single canonical path (NO second call path, NO bypass). A
// meter/limit rejection lands here as an HttpsError → surfaced as a graceful toast.
async function autofillMvrFromPdf(personId) {
  const fileInput = document.getElementById('comp-mvr-pdf-file');
  const file = fileInput?.files?.[0];
  if (!personId || !file) { showToast('Pick a PDF first', 'error'); return; }

  const btn = document.getElementById('btn-comp-mvr-autofill');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Reading PDF...'; }

  try {
    const pdfBase64 = await _readComplianceFileAsBase64(file);
    const parser = httpsCallable(functions, 'parseMvrPdf');
    const resp = await parser({
      tenantId: ComplianceState.tenantId,
      personId,
      pdfBase64,
      mimeType: file.type || 'application/pdf',
    });
    const data = resp?.data || {};
    if (!data.ok) { showToast('AI parse failed', 'error'); return; }
    if (data.confidence < 30) {
      showToast('AI not confident this is an MVR — please review and fill manually', 'warning');
    }

    const setVal = (id, value) => {
      const el = document.getElementById(id);
      if (el && value != null && value !== '') el.value = value;
    };
    setVal('comp-mvr-date', data.pullDate);
    setVal('comp-mvr-state', data.state ? String(data.state).toUpperCase() : data.state);
    setVal('comp-mvr-result', data.result);
    setVal('comp-mvr-violation-count', data.violationCount);
    if (data.violations) {
      const ta = document.getElementById('comp-mvr-violations');
      if (ta) ta.value = data.violations;
    }

    const status = document.getElementById('comp-mvr-pdf-status');
    if (status) {
      const matchTag = data.nameMatch
        ? '<span style="color:#059669;font-weight:600;">✓ name matches</span>'
        : data.driverName
          ? `<span style="color:#D97706;font-weight:600;">⚠ name on PDF: "${escapeHtml(data.driverName)}" — verify driver match</span>`
          : '';
      status.innerHTML = `AI extracted ${data.violationCount} violation${data.violationCount === 1 ? '' : 's'} &bull; confidence ${data.confidence}% &bull; ${matchTag}`;
    }
    showToast(`AI auto-filled MVR fields (${data.confidence}% confidence)`, 'success');
  } catch (err) {
    console.error('[Compliance] MVR auto-fill error:', err);
    // Metered (TASK-1041) — a daily-limit rejection arrives here; surface its
    // message so the user sees "daily limit reached" rather than a generic error.
    showToast(err?.message || 'AI parse failed — fill manually', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">auto_awesome</span> AI Auto-fill'; }
  }
}

async function saveMvrPull(personId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  const pullDate = document.getElementById('comp-mvr-date')?.value;
  const state = document.getElementById('comp-mvr-state')?.value?.trim()?.toUpperCase() || '';
  const result = document.getElementById('comp-mvr-result')?.value;
  const purpose = document.getElementById('comp-mvr-purpose')?.value || null;
  const violationCount = parseInt(document.getElementById('comp-mvr-violation-count')?.value, 10) || 0;
  const violations = document.getElementById('comp-mvr-violations')?.value?.trim() || '';
  const fileInput = document.getElementById('comp-mvr-pdf-file');
  const file = fileInput?.files?.[0] || null;

  if (!pullDate) { showToast('Enter the pull date', 'error'); return; }
  if (!state) { showToast('Enter a state', 'error'); return; }
  if (!result) { showToast('Select a result', 'error'); return; }
  if (file && file.size > 10 * 1024 * 1024) { showToast('File too large (max 10MB)', 'error'); return; }

  const btn = document.getElementById('comp-mvr-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  // ORPHAN-BLOB guard (COD S3/1047): the PDF is uploaded to Storage BEFORE the
  // TASK-1048 (COD ❌ fix): the MVR credential Storage path is delete-immutable
  // (§391.51 retention — storage.rules `allow delete: if false`), so a client can
  // NEVER reap an orphaned blob. The earlier "best-effort deleteObject" guard was a
  // no-op the rules always deny. Prevent the orphan at the SOURCE instead:
  //  (a) the file input is PDF-only (accept="application/pdf"), so an image can't be
  //      uploaded and then rejected by recordMvrPull's PDF check → no image orphan;
  //  (b) block the upload up front for a member who lacks roster-write access — the
  //      Storage write is gated canAccessCompliance (a compliance-only member CAN
  //      upload) but recordMvrPull is gated requireRosterWriteAccess (denies them),
  //      which is exactly the orphan case. Fail before the upload with a clear msg.
  if (file && !_compCanWriteRoster()) {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save MVR Pull'; }
    showToast('You do not have permission to attach an MVR document — ask an owner or admin to record this pull with the PDF.', 'error');
    return;
  }
  let mvrPdfPath = null;
  let recordWritten = false;
  try {
    // TASK-743 — upload to the tenant+person-scoped MVR folder and pass the storage
    // PATH (not a URL) to recordMvrPull. The server asserts the prefix, confirms the
    // object is a PDF, and persists PATH-ONLY (TASK-996) — no client URL is trusted.
    if (file) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      mvrPdfPath = `tenants/${tenantId}/people/${personId}/mvr/${Date.now()}_${safeName}`;
      const fileRef = ref(storage, mvrPdfPath);
      await uploadBytes(fileRef, file, { contentType: file.type || 'application/pdf' });
    }

    // The authoritative MVR person-doc fields + the partner-visible MVR_PULLED
    // complianceEvent are written SERVER-SIDE by recordMvrPull (client can't write
    // complianceEvents). Same callable HR uses — forgery-proof, computes next-due.
    const recordFn = httpsCallable(functions, 'recordMvrPull');
    const resp = await recordFn({
      tenantId,
      personId,
      lastPullDate: pullDate,
      pdfPath: mvrPdfPath || undefined,
      pulledBy: window.AppState?.user?.email || '',
      state,
      result,
      violationCount,
      violations: violations || null,
    });
    const recorded = resp?.data || {};
    if (!recorded.ok) throw new Error(recorded.error || 'Failed to record MVR pull');
    // Record landed server-side (scalars + mvrLatestPdfPath persisted) → the blob is
    // now referenced by the person doc; never orphan-delete it past this point.
    recordWritten = true;

    const nextDueDate = recorded.mvrNextDueDate;
    const serverPdfUrl = recorded.mvrLatestPdfUrl || null;
    const serverPdfPath = recorded.mvrLatestPdfPath || null; // TASK-996 — path-only view

    // Client-owned convenience fields (rules-allowed person-doc writes). The server
    // does NOT author mvrPullHistory or mvrLastPurpose, so the client persists them;
    // wrapped so a compliance-only member's people-write denial can't roll back the
    // (already-committed) server record or delete the referenced blob.
    const person = ComplianceState.people.find(p => p.id === personId);
    const _ph = person ? _mvrDq(person).mvrPullHistory : null; // TASK-1496-S7
    const prior = Array.isArray(_ph) ? _ph.slice(0, 23) : [];
    const entry = {
      date: pullDate, state, result, violationCount,
      violations: violations || null,
      purpose: purpose || null,
      pulledBy: window.AppState?.user?.email || 'unknown',
      pdfUrl: serverPdfUrl,
      pdfPath: serverPdfPath,
      recordedAt: new Date().toISOString(),
    };
    const newHistory = [entry, ...prior];
    try {
      await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
        mvrPullHistory: newHistory,
        // mvrLastPurpose is normally webhook-stamped at Checkr order time; for a manual
        // pull there is no server author, so record the carrier-selected purpose here
        // (drives the Purpose pill in buildComplianceMvrSection).
        ...(purpose ? { mvrLastPurpose: purpose } : {}),
      });
    } catch (histErr) {
      console.warn('[Compliance] MVR history/purpose write failed (record already saved):', histErr?.message);
    }

    if (person) {
      person.mvrLastPullDate = pullDate;
      person.mvrLastPulled = pullDate;
      person.mvrLastState = state;
      person.mvrLastResult = result;
      person.mvrLastPurpose = purpose || person.mvrLastPurpose || null;
      person.mvrViolationCount = violationCount;
      person.mvrLatestViolations = violations || null;
      person.mvrLatestPdfUrl = serverPdfUrl;
      person.mvrLatestPdfPath = serverPdfPath; // TASK-996
      person.mvrNextDueDate = nextDueDate;
      person.mvrPullHistory = newHistory;
    }

    document.getElementById('compliance-mvr-modal')?.remove();
    showToast('MVR pull recorded', 'success');
    showPersonDetail(personId);

    // TASK-1048 (818-S4) — chain the §391.25 annual-review prompt at the MVR moment,
    // reusing slice S1's openAnnualReviewModalCompliance (no duplicate write). Mirrors
    // HR's _promptAnnualReviewAfterMvr: driver-only, opens the determination form.
    _promptAnnualReviewAfterMvrCompliance(personId, pullDate, { result, violationCount });
  } catch (err) {
    console.error('[Compliance] Error recording MVR pull:', err);
    // TASK-1075 — client deleteObject is impossible here (the MVR credential path is
    // delete-immutable, storage.rules `allow delete: if false`, §391.51). Common
    // orphans are prevented at the source (PDF-only input + the roster-write gate
    // before upload). The RESIDUAL — a rare transient failure AFTER a successful
    // upload by an authorized member (recordWritten stays false) — is reaped
    // server-side. The Admin-SDK reaper re-verifies the blob is unreferenced first,
    // so a lost-response where recordMvrPull actually saved is safely refused.
    if (mvrPdfPath && !recordWritten) {
      await _reapOrphanCredBlobCompliance(tenantId, personId, mvrPdfPath);
    }
    // recordMvrPull is gated requireRosterWriteAccess — a compliance-module-ONLY member
    // is denied here BY DESIGN (TASK-818 trap; do NOT widen the gate). Surface plainly.
    const denied = err?.code === 'permission-denied';
    showToast(denied
      ? 'You do not have permission to record MVR pulls — ask an owner or admin to log this pull.'
      : (err.message || 'Failed to record MVR pull'), 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save MVR Pull'; }
  }
}

// TASK-1048 (818-S4) — carrier-side one-click §391.25 annual-review prompt after an
// MVR pull. Mirrors HR's _promptAnnualReviewAfterMvr: driver-only, and offers to open
// the EXISTING compliance annual-review modal (slice S1) — never duplicates the write.
// Guarded + never-throw so it can't break the just-succeeded MVR save.
async function _promptAnnualReviewAfterMvrCompliance(personId, reviewDate, mvrInfo) {
  try {
    if (!personId || !reviewDate || !window.showConfirm) return;
    const person = ComplianceState.people.find(p => p.id === personId);
    if (person) {
      const role = String(person.crewType || person.role || (person.roles && person.roles[0]) || '').toLowerCase();
      if (!/driver|cdl/.test(role)) return;
    }
    const result = String(mvrInfo?.result || '').toLowerCase();
    const vCount = parseInt(mvrInfo?.violationCount, 10) || 0;
    const isClean = (result === 'clean' || result === 'clear') && vCount === 0;
    const msg = isClean
      ? "You reviewed this driver's clean MVR. Record the §391.25 annual driving-record review now? Opens the review form so you can confirm the determination."
      : "You reviewed this driver's MVR — it shows violations or is pending, so the §391.25 fitness determination needs your judgment. Open the annual-review form to record the determination?";
    const openIt = await window.showConfirm(
      'Record §391.25 annual review?', msg,
      { confirmText: 'Open review form', cancelText: 'Not now' });
    if (openIt) openAnnualReviewModalCompliance(personId);
  } catch (e) {
    console.warn('[Compliance] annual-review prompt failed (non-fatal):', e);
  }
}

// Request a new (paid) MVR from the DOT partner. The full Checkr BG/MVR ordering
// modal + FCRA adverse-action wizard live ONLY in HR; for a compliance-managed
// carrier this is always the partner-request path. Delegate to HR's modal AFTER
// hydrating HRState with this person (HR's opener reads HRState.people, which is
// empty for a compliance-only carrier — TASK-781 trap), so the carrier never has
// to self-initiate a check it has no AA path to resolve (FCRA — COD Domain).
function requestMvrFromPartner(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;
  const hydrated = window.HRModule?.ensureSubjectInHrState?.(person);
  if (hydrated && window.HRModule?.openBackgroundCheckModal) {
    window.HRModule.openBackgroundCheckModal(personId, 'person', 'mvr');
  } else {
    showToast(`Contact ${compPartnerLabel()} to run a new MVR.`, 'info');
  }
}

// TASK-1384 — withdraw an open partner-routed BG/MVR request for a roster person.
// Mirror of hr.js cancelHrBgRequest (TASK-1386, candidate-typed) — the
// cancelCarrierBgRequest callable already accepts subjectType:'person'.
// Owner/admin client gate (_compIsOwnerOrAdmin shows the button); the server
// gate (requireTenantAdmin) is authoritative. Idempotent — a stale banner's
// cancel click just refreshes.
async function cancelMvrPartnerRequest(personId) {
  if (!window.showConfirm) { showToast('Confirm dialog unavailable — reload and try again', 'error'); return; }
  const ok = await window.showConfirm(
    'Cancel MVR request?',
    `This withdraws the request from ${compPartnerLabel()}'s worklist. You can request it again at any time.`,
    { confirmText: 'Cancel request', cancelText: 'Keep it', danger: true });
  if (!ok) return;
  try {
    const tenantId = ComplianceState.tenantId;
    if (!tenantId) throw new Error('Not logged in');
    const call = httpsCallable(functions, 'cancelCarrierBgRequest');
    const res = await call({ tenantId, subjectType: 'person', subjectId: personId });
    // Optimistic local clear — the requested state keys on bgCheckRequestedAt &&
    // !checkrReportId, so clearing is harmless even when the server reported
    // nothing to cancel (already run / already canceled elsewhere).
    const person = ComplianceState.people.find(p => p.id === personId);
    if (person) { delete person.bgCheckRequestedAt; delete person.bgCheckRequestedByEmail; delete person.bgCheckRequestedPackageSlug; }
    showToast(res?.data?.canceled
      ? `Request canceled — removed from ${compPartnerLabel()}'s worklist.`
      : 'No open request to cancel — refreshing.', 'success');
    // Re-render the MVR section in place (same swap the bg-doc hydrate uses).
    const secEl = document.getElementById(`comp-mvr-section-${personId}`);
    if (person && secEl) secEl.outerHTML = buildComplianceMvrSection(person);
  } catch (err) {
    console.error('[Compliance] cancelMvrPartnerRequest failed:', err);
    const denied = String(err?.code || '').includes('permission-denied');
    showToast(denied
      ? 'Owner or admin access is required to cancel a request.'
      : ('Could not cancel the request: ' + (err?.message || 'unknown error')), 'error');
  }
}

// ============================================
// TASK-1045 (818-S1) — Clearinghouse query log + §391.25 annual review
// (compliance driver detail). Mirrors HR's saveClearinghouseQuery /
// saveAnnualReview person-doc writes EXACTLY (same keys, same history-entry
// shape, same complianceTimeline rows) so HR / Compliance stay two views over
// ONE people/{id} record. The status mapping + next-due math are NOT copied —
// they're resolved at save time via window.HRModule.deriveClearinghouseStatus /
// complianceNextDueOneYear (single source, TASK-1022 anti-drift; Pitfall #1
// forbids importing hr.js). hr.js is statically imported by app.js, so those
// exports exist even when HR was never opened.
//
// PRESENTATION-ONLY label maps below (colors/icons/labels for whatever status
// string the doc holds) — NOT the enum derivation; unknown values fall back.
// ============================================
const COMP_CH_STATES = {
  not_started: { label: 'Not Started', color: '#94A3B8', icon: 'radio_button_unchecked', bg: '#F1F5F9' },
  consent_on_file: { label: 'General Consent on File', color: '#3B82F6', icon: 'description', bg: '#DBEAFE' },
  full_query_requested: { label: 'Full Query Requested', color: '#D97706', icon: 'hourglass_top', bg: '#FFFBEB' },
  consent_pending: { label: 'Driver Consent Pending', color: '#D97706', icon: 'pending', bg: '#FFFBEB' },
  clear: { label: 'Clear', color: '#059669', icon: 'verified', bg: '#D1FAE5' },
  limited_hit: { label: 'Limited Hit — Needs Full Query', color: '#DC2626', icon: 'warning', bg: '#FEE2E2' },
  refused: { label: 'Refused / Blocked', color: '#DC2626', icon: 'block', bg: '#FEE2E2' },
};

const COMP_AR_STATES = {
  qualified: { label: 'Qualified', color: '#059669', bg: '#D1FAE5', icon: 'check_circle' },
  qualified_monitoring: { label: 'Qualified w/ Monitoring', color: '#D97706', bg: '#FFFBEB', icon: 'visibility' },
  not_qualified: { label: 'Not Qualified', color: '#DC2626', bg: '#FEE2E2', icon: 'cancel' },
};

function buildComplianceClearinghouseSection(person) {
  const personId = person.id;
  const chStatus = person.clearinghouseStatus || 'not_started';
  const chState = COMP_CH_STATES[chStatus] || COMP_CH_STATES.not_started;
  const chConsent = person.clearinghouseConsentDate || null;
  const chLastQuery = person.clearinghouseLastQueryDate || null;
  const chNextDue = person.clearinghouseNextDue || null;
  const chHistory = window.HRModule?.sortClearinghouseHistoryNewestFirst?.(person.clearinghouseHistory) || (Array.isArray(person.clearinghouseHistory) ? person.clearinghouseHistory : []); // TASK-9449

  // TASK-1090 — the FMCSA Clearinghouse applies to CDL drivers only (§382.701);
  // a NonCDL person is not in the Clearinghouse and CANNOT be queried. For an
  // explicit Non-CDL Driver render a clear N/A state (no actionable Log Query);
  // any historical query data stays visible read-only. A classified Non-Driver
  // returns '' (the TASK-1096 DQ-N/A explainer covers the whole DOT set).
  // Unclassified resolves to 'cdl' (full checklist — fail-safe).
  const _chApp = window.DqMatrix.getApplicableDqItems(person);
  if (!_chApp.items.clearinghouse) {
    if (!window.DqMatrix.hasDqFile(_chApp.positionType)) return ''; // TASK-9696
    const hasChData = !!(chLastQuery || chConsent || chHistory.length || (person.clearinghouseStatus && person.clearinghouseStatus !== 'not_started'));
    return `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#94A3B8;">vaccines</span> FMCSA Clearinghouse
        </div>
        <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 10px;border-radius:999px;background:#F1F5F9;color:#64748B;border:1px solid #E2E8F0;font-size:0.75rem;font-weight:600;" title="The FMCSA Drug & Alcohol Clearinghouse applies to CDL drivers under 49 CFR §382 — a non-CDL person cannot be queried.">Non-CDL — N/A</span>
      </div>
      <div class="compliance-detail-fields">
        <div style="padding:10px 4px;color:var(--text-secondary);font-size:0.8rem;">
          Classified <strong>Non-CDL Driver</strong> — the FMCSA Clearinghouse applies to CDL drivers
          (49 CFR §382.701); a Clearinghouse query cannot be run for this person.
        </div>
        ${hasChData ? `
        <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;margin:4px 0;">Previously recorded (read-only)</div>
        ${detailRow('Last Query', chLastQuery ? formatDateShort(chLastQuery) : 'Never')}
        ${chConsent ? detailRow('General Consent Date', formatDateShort(chConsent)) : ''}` : ''}
      </div>
    </div>`;
  }

  const historyHtml = chHistory.length > 0 ? `
    <div style="margin-top:8px;">
      <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;margin-bottom:4px;">Query History</div>
      ${chHistory.slice(0, 5).map(h => `
        <div style="display:flex;align-items:center;gap:6px;padding:4px 0;font-size:0.75rem;border-bottom:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:12px;color:${h.result === 'clear' ? '#059669' : h.result === 'flagged' ? '#DC2626' : '#6B7280'};">
            ${h.result === 'clear' ? 'check_circle' : h.result === 'flagged' ? 'warning' : 'schedule'}
          </span>
          <span style="flex:1;color:var(--text-primary);">${h.type === 'full' ? 'Full Query' : h.type === 'limited' ? 'Limited Query' : h.type === 'pre_employment' ? 'Pre-Employment' : escapeHtml(h.type || 'Query')}</span>
          <span style="color:${h.result === 'clear' ? '#059669' : h.result === 'flagged' ? '#DC2626' : 'var(--text-secondary)'};font-weight:600;">${h.result === 'clear' ? 'Clear' : h.result === 'flagged' ? 'Flagged' : escapeHtml(h.result || '—')}</span>
          <span style="color:var(--text-secondary);">${escapeHtml(h.date || '')}</span>
        </div>
      `).join('')}
      ${chHistory.length > 5 ? `<div style="font-size:0.7rem;color:var(--text-secondary);padding:4px 0;">+ ${chHistory.length - 5} earlier ${chHistory.length - 5 === 1 ? 'query' : 'queries'}</div>` : ''}
    </div>` : '';

  return `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#7c3aed;">vaccines</span> FMCSA Clearinghouse
        </div>
        ${_compClearinghouseLogMode() !== 'readonly' ? `<button class="btn btn-sm" onclick="window.ComplianceModule.openClearinghouseQueryModal('${personId}')" style="padding:3px 8px;font-size:0.72rem;background:#7c3aed;color:#fff;border:none;" title="Record a Clearinghouse query (limited or full) for this driver">
          <span class="material-icons" style="font-size:14px;">add</span> Log Query
        </button>` : `<span style="display:inline-flex;align-items:center;gap:4px;font-size:0.72rem;color:var(--text-secondary);font-weight:600;" title="${escapeHtml(_compClearinghouseReadonlyMsg())}"><span class="material-icons" style="font-size:14px;">visibility</span> View only</span>`}
      </div>
      <div class="compliance-detail-fields">
        ${detailRow('Status', `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${chState.bg};color:${chState.color};border-radius:6px;font-size:0.75rem;font-weight:600;"><span class="material-icons" style="font-size:14px;">${chState.icon}</span> ${chState.label}</span>`)}
        ${detailRow('General Consent Date', chConsent ? formatDateShort(chConsent) : 'Not on file')}
        ${detailRow('Last Query', chLastQuery ? formatDateShort(chLastQuery) : 'Never')}
        ${chNextDue ? detailRow('Next Annual Query Due', `${formatDateShort(chNextDue)} ${getExpiryStatusText(chNextDue)}`) : ''}
        ${historyHtml}
        ${!chLastQuery ? `<div style="padding:10px 4px;color:var(--text-secondary);font-size:0.8rem;text-align:center;">No Clearinghouse query on file. §382.701 requires a pre-employment full query and an annual query for every CDL driver.</div>` : ''}
      </div>
    </div>
  `;
}

function buildComplianceAnnualReviewSection(person) {
  const personId = person.id;
  const arDate = person.annualReviewDate || null;
  const arNextDue = person.annualReviewNextDue || null;
  const arState = COMP_AR_STATES[person.annualReviewDetermination] || null;
  const arHistory = Array.isArray(person.annualReviewHistory) ? person.annualReviewHistory : [];

  const historyHtml = arHistory.length > 0 ? `
    <div style="margin-top:8px;">
      <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;margin-bottom:4px;">Review History</div>
      ${arHistory.slice(0, 5).map(h => {
        const hState = COMP_AR_STATES[h.determination] || { label: escapeHtml(h.determination || '—'), color: '#6B7280', icon: 'help' };
        return `
        <div style="display:flex;align-items:center;gap:6px;padding:4px 0;font-size:0.75rem;border-bottom:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:12px;color:${hState.color};">${hState.icon}</span>
          <span style="flex:1;font-weight:500;color:var(--text-primary);">${hState.label}</span>
          <span style="color:var(--text-secondary);">${escapeHtml(h.date || '')}${h.reviewedBy ? ' &bull; ' + escapeHtml(h.reviewedBy) : ''}${_compArSourceText(h.mvrReadFrom)}</span>
        </div>`;
      }).join('')}
      ${arHistory.length > 5 ? `<div style="font-size:0.7rem;color:var(--text-secondary);padding:4px 0;">+ ${arHistory.length - 5} earlier ${arHistory.length - 5 === 1 ? 'review' : 'reviews'}</div>` : ''}
    </div>` : '';

  return `
    <div class="compliance-detail-section">
      <div class="compliance-detail-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#2563EB;">rate_review</span> Annual Record Review — §391.25
        </div>
        <button class="btn btn-sm" onclick="window.ComplianceModule.openAnnualReviewModal('${personId}')" style="padding:3px 8px;font-size:0.72rem;background:#2563EB;color:#fff;border:none;" title="Record the annual driving-record review required by 49 CFR 391.25">
          <span class="material-icons" style="font-size:14px;">${arDate ? 'edit' : 'add'}</span> ${arDate ? 'New Review' : 'Record Review'}
        </button>
      </div>
      <div class="compliance-detail-fields">
        ${/* TASK-1623 — the same derived flag, shown where the reviewer acts. The
              §391.25(c)(2) note this section writes (reviewer name + review date)
              IS the sign-off; the banner is what asks for it. */
          _compMvrReviewBanner(person, { withAction: false })}
        ${arState ? detailRow('Determination', `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${arState.bg};color:${arState.color};border-radius:6px;font-size:0.75rem;font-weight:600;"><span class="material-icons" style="font-size:14px;">${arState.icon}</span> ${arState.label}</span>`) : ''}
        ${detailRow('Last Review', arDate ? formatDateShort(arDate) : 'Never conducted')}
        ${detailRow('Reviewed By', escapeHtml(person.annualReviewedBy || '—'))}
        ${arNextDue ? detailRow('Next Review Due', `${formatDateShort(arNextDue)} ${getExpiryStatusText(arNextDue)}`) : ''}
        ${historyHtml}
        ${!arDate ? `<div style="padding:10px 4px;color:var(--text-secondary);font-size:0.8rem;text-align:center;">No annual review on file. 49 CFR 391.25 requires an annual driving-record review for every driver.</div>` : ''}
      </div>
    </div>
  `;
}

// "Log Query" modal — mirrors HR's openClearinghouseModal fields exactly
// (query type / result / date / query id / consent date / notes) with
// compliance-local element ids so the two modals can never cross-read.
function openClearinghouseQueryModal(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;

  // TASK-1090 — a NonCDL person cannot be queried in the FMCSA Clearinghouse
  // (§382.701 covers CDL drivers). The section renders an N/A state for these
  // tiers, but guard the opener too so no stale button can reach the modal.
  const _chOpenApp = window.DqMatrix.getApplicableDqItems(person);
  if (!_chOpenApp.items.clearinghouse) {
    showToast(`${person.name || 'This person'} is classified ${window.DqMatrix.LABELS[_chOpenApp.positionType] || 'Non-CDL'} — the FMCSA Clearinghouse applies to CDL drivers only (§382.701).`, 'error');
    return;
  }

  // TASK-4748 — never draw a form this session cannot save. The section hides
  // the button in read-only mode; this guards a stale button / other callers.
  if (_compClearinghouseLogMode() === 'readonly') {
    showToast(_compClearinghouseReadonlyMsg(), 'error');
    return;
  }

  const existing = document.getElementById('compliance-clearinghouse-modal');
  if (existing) existing.remove();

  const chStatus = person.clearinghouseStatus || 'not_started';
  const consentDate = person.clearinghouseConsentDate || '';
  const today = new Date().toISOString().split('T')[0];

  const modal = document.createElement('div');
  modal.id = 'compliance-clearinghouse-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:520px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div class="modal-header" style="background:linear-gradient(135deg, #7c3aed, #a78bfa);color:#fff;flex-shrink:0;">
        <h3 style="color:#fff;display:flex;align-items:center;gap:8px;"><span class="material-icons">vaccines</span> Log Clearinghouse Query</h3>
        <button class="modal-close" onclick="document.getElementById('compliance-clearinghouse-modal').remove()" style="color:#fff;"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:20px;overflow-y:auto;flex:1;min-height:0;">
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
          Record a Clearinghouse query for <strong>${escapeHtml(person.name || '')}</strong>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Query Type *</label>
            <select id="comp-ch-query-type" class="form-control">
              <option value="">Select type...</option>
              <option value="pre_employment">Pre-Employment Full Query</option>
              <option value="limited">Annual Limited Query</option>
              <option value="full">Annual Full Query</option>
            </select>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Result *</label>
            <select id="comp-ch-query-result" class="form-control">
              <option value="">Select result...</option>
              <option value="clear">Clear</option>
              <option value="flagged">Flagged / Violation Found</option>
            </select>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Query Date *</label>
            <input type="date" id="comp-ch-query-date" class="form-control" value="${today}">
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Query ID</label>
            <input type="text" id="comp-ch-query-id" class="form-control" placeholder="Optional reference #">
          </div>
          <div class="form-group" style="grid-column:1/-1;margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">General Consent Date</label>
            <input type="date" id="comp-ch-consent-date" class="form-control" value="${consentDate}">
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">Date the driver signed general consent for Clearinghouse queries</div>
          </div>
          <div class="form-group" style="grid-column:1/-1;margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Notes</label>
            <textarea id="comp-ch-query-notes" class="form-control" rows="2" placeholder="Optional notes..."></textarea>
          </div>
          <div class="form-group" style="grid-column:1/-1;margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Query screenshot <span id="comp-ch-file-req" style="font-weight:500;color:#DC2626;">(required)</span></label>
            <input type="file" id="comp-ch-file" class="form-control" accept=".pdf,.jpg,.jpeg,.png" style="font-size:0.82rem;">
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">FMCSA query confirmation screenshot or PDF — filed to this driver's DQ documents (Clearinghouse Query). Max 10MB.</div>
          </div>
        </div>
        <div style="background:#F1F5F9;border-radius:8px;padding:10px 12px;margin-top:10px;font-size:0.75rem;color:var(--text-secondary);">
          <strong>Current status:</strong> ${escapeHtml(chStatus.replace(/_/g, ' '))} &bull;
          <a href="https://clearinghouse.fmcsa.dot.gov/" target="_blank" rel="noopener" style="color:var(--brand-blue);">Open FMCSA Clearinghouse</a>
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);display:flex;gap:var(--spacing-sm);justify-content:flex-end;flex-shrink:0;">
        <button class="btn btn-secondary" onclick="document.getElementById('compliance-clearinghouse-modal').remove()">Cancel</button>
        <button class="btn" id="comp-ch-save-btn" style="background:#7c3aed;color:#fff;" onclick="window.ComplianceModule.saveClearinghouseQuery('${personId}')">
          <span class="material-icons">save</span> Save Query
        </button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  modal.classList.remove('hidden');

  // TASK-1092 — async check whether a clearinghouse doc is already on file
  // (people/{id}/documents, category 'clearinghouse' — same collection the
  // TASK-1046 DQ uploader writes). Date-only saves are allowed without the
  // red warning when one exists; the label relaxes to "(optional)". A failed
  // check leaves the flag at '0' → fail toward warning, never toward silence.
  modal.dataset.chDocOnFile = '0';
  (async () => {
    try {
      const snap = await getDocs(query(
        collection(db, 'tenants', ComplianceState.tenantId, 'people', personId, 'documents'),
        where('category', '==', 'clearinghouse')
      ));
      if (!snap.empty && document.getElementById('compliance-clearinghouse-modal') === modal) {
        modal.dataset.chDocOnFile = '1';
        const req = document.getElementById('comp-ch-file-req');
        if (req) { req.textContent = '(optional — a query document is already on file)'; req.style.color = 'var(--text-secondary)'; }
      }
    } catch (_) { /* fail toward the warning */ }
  })();
}

async function saveClearinghouseQueryCompliance(personId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  // TASK-1090 — defense in depth behind the opener guard: never record a
  // Clearinghouse query for a person whose classification excludes it.
  const _chSavePerson = ComplianceState.people.find(p => p.id === personId);
  if (_chSavePerson && !window.DqMatrix.getApplicableDqItems(_chSavePerson).items.clearinghouse) {
    showToast('The FMCSA Clearinghouse applies to CDL drivers only (§382.701) — this person cannot be queried.', 'error');
    return;
  }

  const queryType = document.getElementById('comp-ch-query-type')?.value;
  const queryResult = document.getElementById('comp-ch-query-result')?.value;
  const queryDate = document.getElementById('comp-ch-query-date')?.value;
  const queryId = document.getElementById('comp-ch-query-id')?.value?.trim() || '';
  const consentDate = document.getElementById('comp-ch-consent-date')?.value || '';
  const notes = document.getElementById('comp-ch-query-notes')?.value?.trim() || '';

  if (!queryType) { showToast('Please select a query type', 'error'); return; }
  if (!queryResult) { showToast('Please select a result', 'error'); return; }
  if (!queryDate) { showToast('Please enter the query date', 'error'); return; }

  // TASK-1092 — screenshot proof of the query. Validate a selected file up
  // front; a save with NO file and NO doc on file requires an explicit RED-
  // warning acknowledgement (Chris-resolved: block-with-red-warning, not a
  // silent hard-block). All of this fires BEFORE the button disables, so a
  // declined confirm leaves the modal fully interactive.
  const chFile = document.getElementById('comp-ch-file')?.files?.[0] || null;
  if (chFile) {
    if (chFile.size > 10 * 1024 * 1024) { showToast('Screenshot too large. Max 10MB.', 'error'); return; }
    const chExt = chFile.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(chExt)) { showToast('Only PDF, JPG, and PNG files allowed.', 'error'); return; }
  } else {
    const chDocOnFile = document.getElementById('compliance-clearinghouse-modal')?.dataset?.chDocOnFile === '1';
    if (!chDocOnFile) {
      const warnMsg = 'Safety networks (TPS Alert, ISNetworld, Veriforce) demand proof of the last query date — uploading the FMCSA query screenshot is strongly recommended. Save the date-only record anyway?';
      const proceed = window.showConfirm
        ? await window.showConfirm('No Clearinghouse screenshot uploaded', warnMsg, {
          danger: true,
          icon: 'warning',
          iconColor: '#DC2626',
          confirmText: 'Save without screenshot',
          cancelText: 'Go back',
        })
        : window.confirm(warnMsg);
      if (!proceed) return;
    }
  }

  // Single-source guard (TASK-1022): status mapping + next-due come ONLY from
  // the shared hr.js helpers. If missing (stale bundle), abort — never fall
  // back to a local re-derivation; that is exactly the drift 1022 fixed.
  const deriveStatus = window.HRModule?.deriveClearinghouseStatus;
  const nextDueOneYear = window.HRModule?.complianceNextDueOneYear;
  if (typeof deriveStatus !== 'function' || typeof nextDueOneYear !== 'function') {
    showToast('Clearinghouse helpers unavailable — refresh and try again.', 'error');
    return;
  }

  // TASK-290 — Full Query / Pre-Employment Full Query consent attestation
  // (mirrors HR's soft gate; FMCSA's portal is the enforcement point, this is
  // audit-defense documentation stamped into the history entry).
  let fullConsentAttestation = null;
  if (queryType === 'full' || queryType === 'pre_employment') {
    const consentMessage =
      "Full Query requires specific driver consent (signed release on file). " +
      "FMCSA's Clearinghouse portal enforces consent uploads on their side, so by the " +
      "time you're logging it here it's already been accepted by FMCSA. DispatchDeck " +
      "records the attestation so you have audit-defense documentation.";
    const ok = window.showConfirm
      ? await window.showConfirm('Full Query consent verification', consentMessage, {
        confirmText: 'Yes — proceed and attest',
        cancelText: 'No — cancel',
      })
      : window.confirm(consentMessage);
    if (!ok) return; // user canceled — no state change, button not yet disabled
    fullConsentAttestation = {
      fullConsentAcknowledgedAt: new Date().toISOString(),
      fullConsentAcknowledgedBy: window.AppState?.user?.uid || null,
      fullConsentAcknowledgedByEmail: window.AppState?.user?.email || 'unknown',
    };
  }

  const btn = document.getElementById('comp-ch-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  // TASK-9449 — managing partner: the server records it, so the partner-portal
  // "proof required" gate applies (see hr.js _hrPartnerSaveClearinghouseQuery).
  if (_compPartnerTenantId()) {
    try {
      await _compPartnerApi('partnerSaveClearinghouseQuery')({
        personId, queryType, queryResult, queryDate, queryId, consentDate, notes,
        file: chFile, fullConsentAttested: !!fullConsentAttestation,
      });
      try {
        const fresh = await getDoc(doc(db, 'tenants', tenantId, 'people', personId));
        const person = ComplianceState.people.find(p => p.id === personId);
        if (person && fresh.exists()) Object.assign(person, fresh.data());
      } catch (_) { /* the save landed; the local copy refreshes on the next load */ }
      document.getElementById('compliance-clearinghouse-modal')?.remove();
      showToast('Clearinghouse query saved', 'success');
      showPersonDetail(personId);
    } catch (err) {
      console.error('[Compliance] partner clearinghouse save failed:', err);
      const toText = window.HRModule?.partnerChErrorText;
      showToast(typeof toText === 'function' ? toText(err) : _compPartnerErrText(err, 'Error saving query'), 'error');
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save Query'; }
    }
    return;
  }

  try {
    const personRef = doc(db, 'tenants', tenantId, 'people', personId);
    const personSnap = await getDoc(personRef);
    const existing = personSnap.exists() ? personSnap.data() : {};
    const history = (window.HRModule?.sortClearinghouseHistoryNewestFirst?.(existing.clearinghouseHistory) || existing.clearinghouseHistory || []).slice(0, 23); // TASK-9449

    // History entry — byte-identical shape to HR's saveClearinghouseQuery.
    const entry = {
      type: queryType,
      result: queryResult,
      date: queryDate,
      queryId,
      notes,
      recordedBy: window.AppState?.user?.email || 'unknown',
      recordedAt: new Date().toISOString(),
      ...(fullConsentAttestation || {})
    };
    history.unshift(entry);

    const newStatus = deriveStatus(queryType, queryResult);
    const nextDue = nextDueOneYear(queryDate);

    const updateData = {
      clearinghouseStatus: newStatus,
      clearinghouseLastQueryDate: queryDate,
      clearinghouseNextDue: nextDue,
      clearinghouseHistory: history
    };
    if (consentDate) {
      updateData.clearinghouseConsentDate = consentDate;
      // TASK-1022 — only a PURE consent capture (no query-derived status) may
      // default to 'consent_on_file'; a real query result is authoritative and
      // must never be downgraded (masking a §382-prohibited result).
      if (newStatus === 'not_started') {
        updateData.clearinghouseStatus = 'consent_on_file';
      }
    }

    await updateDoc(personRef, updateData);

    // Immutable audit trail — same row shape as HR's write; firestore.rules
    // already allows hasModule('compliance') to create complianceTimeline rows.
    await addDoc(collection(db, 'tenants', tenantId, 'complianceTimeline'), {
      personId,
      personName: existing.name || '',
      type: 'clearinghouse_query',
      description: `${queryType === 'pre_employment' ? 'Pre-Employment' : queryType === 'limited' ? 'Limited' : 'Full'} query — ${queryResult === 'clear' ? 'Clear' : 'Flagged'}`,
      queryType,
      queryResult,
      queryDate,
      queryId,
      recordedBy: window.AppState?.user?.email || 'unknown',
      timestamp: Timestamp.now(),
      createdAt: Timestamp.now()
    });

    const person = ComplianceState.people.find(p => p.id === personId);
    if (person) Object.assign(person, updateData);

    window.logAudit?.('compliance', 'CLEARINGHOUSE_QUERY_LOGGED', {
      personId, queryType, queryResult, queryDate
    });

    // TASK-1092 — attach the query screenshot to the driver's DQ documents.
    // Reuses the EXISTING TASK-1046 plumbing exactly (same hr/{tid}/{pid}/
    // storage path, same people/{id}/documents shape, category 'clearinghouse'
    // — already in the firestore.rules DQ-category allowlist; no new paths).
    // Upload runs AFTER the query record committed; a failed upload never
    // rolls back the §382.701 record — it surfaces a "attach it via Upload DQ
    // Document" toast instead.
    if (chFile) {
      let chStorageRef = null;
      try {
        const chTs = Date.now();
        const chSafeName = chFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const chStoragePath = `hr/${tenantId}/${personId}/${chTs}_${chSafeName}`;
        chStorageRef = ref(storage, chStoragePath);
        await uploadBytes(chStorageRef, chFile, { contentType: _cpInferContentType(chFile) });
        try {
          await addDoc(
            collection(db, 'tenants', tenantId, 'people', personId, 'documents'),
            {
              fileName: chFile.name,
              storagePath: chStoragePath, // TASK-1029 — path-only; view via getComplianceDocBytes
              mimeType: chFile.type,
              isPdf: chFile.type === 'application/pdf',
              sizeBytes: chFile.size,
              category: 'clearinghouse',
              uploadedAt: Timestamp.now(),
              uploadedBy: window.AppState?.userName || 'Unknown'
            }
          );
        } catch (chAddErr) {
          // Orphan-blob cleanup (TASK-1048 lesson) — the /hr/{tid}/{pid}/ path
          // allows client delete, so this is a real cleanup.
          try { await deleteObject(chStorageRef); } catch (_) { /* best-effort */ }
          throw chAddErr;
        }
      } catch (chUpErr) {
        console.error('[Compliance] clearinghouse screenshot upload failed (query record saved):', chUpErr);
        showToast('Query saved, but the screenshot upload failed — attach it via "Upload DQ Document" on this driver.', 'error');
      }
    }

    document.getElementById('compliance-clearinghouse-modal')?.remove();
    showToast('Clearinghouse query saved', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] Error saving clearinghouse query:', err);
    // The people update rule is isDispatcherOrHigher || hasModule('roster') — a
    // compliance-module-ONLY member is denied here BY DESIGN (TASK-818 trap; do
    // not widen firestore.rules). Surface it plainly instead of a generic error.
    const denied = err?.code === 'permission-denied';
    showToast(denied
      ? 'You do not have permission to update driver records — ask an owner or admin to log this query.'
      : 'Error saving query', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save Query'; }
  }
}

// "Record Review" modal — mirrors HR's openAnnualReviewModal fields exactly
// (review date / reviewed by / determination / checklist / violations /
// corrective action / signature) with compliance-local element ids.
// TASK-10216 — where the reviewer read the MVR (stored as annualReviewHistory[].mvrReadFrom).
const COMP_AR_MVR_SOURCES = [['paper', 'Paper copy'], ['state_portal', 'State DMV / DOT portal'], ['other', 'Other (describe)']];
function _compArSourceText(src) {
  if (!src || typeof src !== 'object' || !src.kind) return '';
  const hit = src.kind === 'on_file' ? 'MVR on file' : ((COMP_AR_MVR_SOURCES.find(([v]) => v === src.kind) || [])[1] || 'Other');
  return ' &bull; read from ' + escapeHtml(hit.replace(' (describe)', '') + (src.note ? ': ' + src.note : ''));
}

// Modal body — LAZY views/complianceMvrReview.js (perf budget, TASK-10216); moved verbatim.
let _arModalLoad = null;
function openAnnualReviewModalCompliance(personId) {
  _arModalLoad = _arModalLoad || __ddChunkWrap(import('./complianceMvrReview.js?v=1')).catch((e) => { _arModalLoad = null; throw e; });
  // Bound per open: _compBgDocCache is re-pointed on tenant switch.
  return _arModalLoad.then((m) => { m._bind({ ComplianceState, escapeHtml, _compBgDocCache, COMP_AR_MVR_SOURCES }); return m.openAnnualReviewModalCompliance(personId); }).catch((e) => { console.error('[Compliance] review form load failed:', e); window.showToast?.('Could not open the review form. Check your connection and try again.', 'error'); });
}

async function saveAnnualReviewCompliance(personId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  const reviewDate = document.getElementById('comp-ar-review-date')?.value;
  const reviewedBy = document.getElementById('comp-ar-reviewed-by')?.value?.trim();
  const determination = document.getElementById('comp-ar-determination')?.value;
  const violations = document.getElementById('comp-ar-violations')?.value?.trim() || '';
  const correctiveAction = document.getElementById('comp-ar-corrective-action')?.value?.trim() || '';
  const signature = document.getElementById('comp-ar-signature')?.value?.trim() || '';
  let mvrSourceKind = document.getElementById('comp-ar-mvr-source')?.value || '';
  const mvrSourceNote = (document.getElementById('comp-ar-mvr-source-note')?.value || '').trim().slice(0, 200);

  const checkMvr = document.getElementById('comp-ar-check-mvr')?.checked || false;
  const checkPsp = document.getElementById('comp-ar-check-psp')?.checked || false;
  const checkClearinghouse = document.getElementById('comp-ar-check-clearinghouse')?.checked || false;
  const checkCdl = document.getElementById('comp-ar-check-cdl')?.checked || false;
  const checkMedical = document.getElementById('comp-ar-check-medical')?.checked || false;

  if (!reviewDate) { showToast('Please enter the review date', 'error'); return; }
  if (!reviewedBy) { showToast('Please enter the reviewer name', 'error'); return; }
  if (!determination) { showToast('Please select a determination', 'error'); return; }
  if (!signature) { showToast('Please sign the review', 'error'); return; }
  // COD (Code Reviewer/Adversary): only the known kinds; "on file" re-checked against the record now.
  if (mvrSourceKind === 'on_file') {
    const _p = ComplianceState.people.find(p => p.id === personId) || {};
    if (!(window.DqMatrix && window.DqMatrix.mvrRecordIsOnFile({ ..._p, bgCheck: _compBgDocCache[personId] || _p.bgCheck }))) mvrSourceKind = '';
  } else if (!COMP_AR_MVR_SOURCES.some(([v]) => v === mvrSourceKind)) mvrSourceKind = '';
  if (!mvrSourceKind) { showToast('Say where you read the MVR', 'error'); return; }
  if (mvrSourceKind === 'other' && !mvrSourceNote) { showToast('Describe where you read the MVR', 'error'); return; }
  // TASK-10216 — nested in the history entry + attestation only (no new top-level key).
  const mvrReadFrom = { kind: mvrSourceKind, note: mvrSourceNote || null };

  // Single-source next-due (TASK-1022 anti-drift) — same guard as clearinghouse.
  const nextDueOneYear = window.HRModule?.complianceNextDueOneYear;
  if (typeof nextDueOneYear !== 'function') {
    showToast('Review helpers unavailable — refresh and try again.', 'error');
    return;
  }

  const btn = document.getElementById('comp-ar-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const personRef = doc(db, 'tenants', tenantId, 'people', personId);
    const personSnap = await getDoc(personRef);
    const existing = _mvrDq(personSnap.exists() ? { id: personId, ...personSnap.data() } : {}); // TASK-1496-S7 (mvrRef)
    const history = (existing.annualReviewHistory || []).slice(0, 23);

    // TASK-1623 — WHICH MVR this review attests to. §391.25(c)(2) requires the
    // note to carry the reviewer's name and the review date; it does not by
    // itself say which record was reviewed, so a signed review and a newer
    // un-reviewed MVR were previously indistinguishable. Stamping the record
    // this sign-off covers makes the attestation specific AND is what lets
    // `_compMvrReviewState` clear the flag on an exact match rather than on
    // date arithmetic (two pulls on the same day used to look identical).
    // Same field family as the background-report rows' attestedBy/attestedAt.
    const _mvrRefNow = {
      pullDate: existing.mvrLastPullDate || existing.mvrLastPulled || null,
      state: existing.mvrLastState || null,
      result: existing.mvrLastResult || null,
      violationCount: Number(existing.mvrViolationCount) || 0,
      pdfPath: existing.mvrLatestPdfPath || null,
    };
    const _attestation = {
      // WHO — the typed signature is the attestation; the acting account is the
      // provenance. Both are recorded because they are not always the same human.
      reviewerName: reviewedBy,
      signature,
      attestedByEmail: window.AppState?.user?.email || 'unknown',
      attestedByUid: window.AppState?.user?.uid || null,
      // WHEN — the reviewer-stated review date AND the wall-clock write time.
      reviewDate,
      attestedAt: new Date().toISOString(),
      // AGAINST WHAT — the version of the record being attested to.
      mvrRef: _mvrRefNow,
      mvrReadFrom,
      attestationReason: 'Annual driving-record review — 49 CFR 391.25(b); note retained per 391.25(c)(2).',
    };

    // History entry — HR's saveAnnualReview shape, plus the TASK-1623 binding.
    // Additive only: every pre-existing key keeps its name and position, so a
    // renderer that never heard of mvrRef is unaffected.
    const entry = {
      date: reviewDate,
      reviewedBy,
      determination,
      violations,
      correctiveAction,
      signature,
      checklist: { mvr: checkMvr, psp: checkPsp, clearinghouse: checkClearinghouse, cdl: checkCdl, medical: checkMedical },
      recordedBy: window.AppState?.user?.email || 'unknown',
      recordedAt: new Date().toISOString(),
      mvrRef: _mvrRefNow,
      mvrReadFrom,
      attestation: _attestation
    };
    history.unshift(entry);

    const nextDue = nextDueOneYear(reviewDate);

    const updateData = {
      annualReviewDate: reviewDate,
      annualReviewDetermination: determination,
      annualReviewedBy: reviewedBy,
      annualReviewNextDue: nextDue,
      annualReviewHistory: history,
      annualReviewMvrRef: _mvrRefNow,
      annualReviewAttestation: _attestation
    };

    await updateDoc(personRef, updateData);

    // Immutable audit trail — same row shape as HR's write.
    const detLabels = { qualified: 'Qualified', qualified_monitoring: 'Qualified w/ Monitoring', not_qualified: 'Not Qualified' };
    await addDoc(collection(db, 'tenants', tenantId, 'complianceTimeline'), {
      personId,
      personName: existing.name || '',
      type: 'annual_review',
      description: `Annual record review — ${detLabels[determination] || determination}`,
      determination,
      reviewDate,
      reviewedBy,
      violations: violations || null,
      correctiveAction: correctiveAction || null,
      signature,
      recordedBy: window.AppState?.user?.email || 'unknown',
      timestamp: Timestamp.now(),
      createdAt: Timestamp.now()
    });

    const person = ComplianceState.people.find(p => p.id === personId);
    if (person) Object.assign(person, updateData);

    window.logAudit?.('compliance', 'ANNUAL_REVIEW_COMPLETED', {
      personId, determination, reviewDate, reviewedBy
    });

    document.getElementById('compliance-annual-review-modal')?.remove();
    showToast('Annual review saved', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('[Compliance] Error saving annual review:', err);
    // Same person-write rule note as saveClearinghouseQueryCompliance above.
    const denied = err?.code === 'permission-denied';
    showToast(denied
      ? 'You do not have permission to update driver records — ask an owner or admin to record this review.'
      : 'Error saving review', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save Review'; }
  }
}

// ── TASK-1047 (818-S3) — Medical Exam Detail recorder + self-upload SMS link ──
// Mirrors HR's openMedExamModal / saveMedExam / sendMedUploadLink EXACTLY (same
// person-doc write keys, same medExamHistory[] shape, same complianceTimeline
// row) with compliance-local element ids so the two modals can never cross-read.
// Cert is PATH-ONLY (TASK-996/-647): certPdfUrl/medCertPdfUrl null on new upload,
// only the storage PATH is persisted (people/medExam is a canReadCompliancePII
// credential path — a bearer URL in the member-readable people doc defeats that).
function openMedExamModalCompliance(personId) {
  const person = _medDq(ComplianceState.people.find(p => p.id === personId));
  if (!person) return;

  const existing = document.getElementById('compliance-med-exam-modal');
  if (existing) existing.remove();

  const today = new Date().toISOString().split('T')[0];
  const existingRestrictions = Array.isArray(person.medRestrictions) ? person.medRestrictions : [];
  // Single-source the enum from hr.js (never a third independent copy — 1047
  // trap). Fallback array only if hr.js hasn't loaded yet (no cross-import
  // ?v= cache-buster — CLAUDE.md pitfall #1).
  const restrictionOptions = window.HRModule?.MED_RESTRICTION_OPTIONS || [
    'Wear corrective lenses',
    'Wear hearing aid',
    'Accompanied by waiver',
    'Driving within an exempt intracity zone',
    'Qualified by operation of CMV intrastate only',
    'Qualified by SPE certificate',
    'Wearing a multi-piece prosthesis',
    'Diabetes intracity only',
    'Stand-by waiver',
    'Lower limb prosthesis required',
  ];

  const modal = document.createElement('div');
  modal.id = 'compliance-med-exam-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div class="modal-header" style="background:linear-gradient(135deg, #059669, #047857);color:#fff;flex-shrink:0;">
        <h3 style="color:#fff;display:flex;align-items:center;gap:8px;"><span class="material-icons">medical_services</span> Record DOT Physical Exam</h3>
        <button class="modal-close" onclick="document.getElementById('compliance-med-exam-modal').remove()" style="color:#fff;"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:20px;overflow-y:auto;flex:1;min-height:0;">
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
          Log a DOT medical examiner's certificate for <strong>${escapeHtml(person.name || '')}</strong> per FMCSR 391.43.
        </div>
        <input type="hidden" id="comp-me-emp-id" value="${personId}">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Exam Date *</label>
            <input type="date" id="comp-me-exam-date" class="form-control" value="${today}">
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Expiration Date *</label>
            <input type="date" id="comp-me-expiry-date" class="form-control" value="${person.medCardExpiryDate || ''}">
          </div>
          <div class="form-group" style="grid-column:1/-1;margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Medical Examiner Name *</label>
            <input type="text" id="comp-me-examiner-name" class="form-control" value="${escapeHtml(person.medExaminerName || '')}" placeholder="Dr. Jane Smith">
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">NRCME Number *</label>
            <input type="text" id="comp-me-examiner-nrcme" class="form-control" value="${escapeHtml(person.medExaminerNRCME || '')}" placeholder="10-digit NRCME #" maxlength="20">
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">National Registry of Certified Medical Examiners.</div>
          </div>
          <div class="form-group" style="margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Exam Type</label>
            <select id="comp-me-exam-type" class="form-control">
              <option value="standard">Standard (24 mo)</option>
              <option value="short-term">Short-term (under 24 mo — condition-based)</option>
              <option value="exempt">Intrastate-exempt</option>
            </select>
          </div>
          <div class="form-group" style="grid-column:1/-1;margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Restrictions</label>
            <div id="comp-me-restrictions-list" style="display:flex;flex-wrap:wrap;gap:6px;margin-top:4px;">
              ${restrictionOptions.map(r => `
                <label style="display:inline-flex;align-items:center;gap:4px;padding:4px 8px;background:var(--bg-secondary,#F1F5F9);border-radius:6px;font-size:0.78rem;cursor:pointer;">
                  <input type="checkbox" class="comp-me-restriction-check" value="${escapeHtml(r)}" ${existingRestrictions.includes(r) ? 'checked' : ''}>
                  ${escapeHtml(r)}
                </label>
              `).join('')}
            </div>
            <input type="text" id="comp-me-other-restriction" class="form-control" placeholder="Other restriction (free-text, optional)" style="margin-top:6px;">
          </div>
          <div class="form-group" style="grid-column:1/-1;margin:0;">
            <label style="font-size:0.8rem;font-weight:600;">Medical Cert PDF (optional)</label>
            <input type="file" id="comp-me-cert-file" accept="application/pdf,image/*" class="form-control">
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">Max 10MB. Stored on the driver record.</div>
          </div>
        </div>
        <div style="background:#D1FAE5;border-radius:8px;padding:10px 12px;margin-top:8px;font-size:0.75rem;color:#065F46;">
          <strong>FMCSR 391.43 / 391.45:</strong> Drivers must be medically certified by an examiner listed on the National Registry. Standard certification is up to 24 months; conditional/short-term certs are issued for specific medical conditions.
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);display:flex;gap:var(--spacing-sm);justify-content:flex-end;flex-shrink:0;">
        <button class="btn btn-secondary" onclick="document.getElementById('compliance-med-exam-modal').remove()">Cancel</button>
        <button class="btn" id="comp-me-save-btn" style="background:#047857;color:#fff;" onclick="window.ComplianceModule.saveMedExam('${personId}')">
          <span class="material-icons">save</span> Save Exam
        </button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  modal.classList.remove('hidden');
}

async function saveMedExamCompliance(personId) {
  const tenantId = ComplianceState.tenantId;
  const empId = document.getElementById('comp-me-emp-id')?.value || personId;
  if (!tenantId || !empId) return;

  const examDate = document.getElementById('comp-me-exam-date')?.value;
  const expiryDate = document.getElementById('comp-me-expiry-date')?.value;
  const examinerName = document.getElementById('comp-me-examiner-name')?.value?.trim() || '';
  const examinerNRCME = document.getElementById('comp-me-examiner-nrcme')?.value?.trim() || '';
  const examType = document.getElementById('comp-me-exam-type')?.value || 'standard';
  const otherRestriction = document.getElementById('comp-me-other-restriction')?.value?.trim() || '';
  const checkedRestrictions = Array.from(document.querySelectorAll('.comp-me-restriction-check:checked')).map(c => c.value);
  const restrictions = otherRestriction ? [...checkedRestrictions, otherRestriction] : checkedRestrictions;
  const fileInput = document.getElementById('comp-me-cert-file');
  const file = fileInput?.files?.[0] || null;

  if (!examDate) { showToast('Enter exam date', 'error'); return; }
  if (!expiryDate) { showToast('Enter expiration date', 'error'); return; }
  if (!examinerName) { showToast('Enter examiner name', 'error'); return; }
  if (!examinerNRCME) { showToast('Enter NRCME number', 'error'); return; }
  if (file && file.size > 10 * 1024 * 1024) { showToast('File too large (max 10MB)', 'error'); return; }

  const btn = document.getElementById('comp-me-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  // ORPHAN-BLOB guard (TASK-1075, mirrors the S4 MVR gate at saveMvrPull): the med
  // cert PDF is uploaded to Storage (gated canAccessCompliance — a compliance-ONLY
  // member CAN upload) BEFORE the person updateDoc, which is gated
  // isDispatcherOrHigher||roster and DENIES a compliance-only member by design. That
  // denial orphans the just-uploaded cert in the delete-immutable medExam path
  // (§391.51, storage.rules `allow delete: if false`) — the client can't reap it.
  // Prevent the orphan at the SOURCE: block the upload up front for a member who
  // lacks roster-write access, exactly the case the person-write would reject.
  if (file && !_compCanWriteRoster()) {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save Exam'; }
    showToast('You do not have permission to attach a medical certificate — ask an owner or admin to record this exam with the PDF.', 'error');
    return;
  }

  // Hoisted so the catch can clean up an orphan cert blob (COD 1047): the Storage
  // upload happens BEFORE the person updateDoc, but a compliance-ONLY member is
  // denied on that write BY DESIGN (people rule = isDispatcherOrHigher||roster) —
  // without cleanup that leaves a med-card file in the DQ credential listing with
  // no backing person record. Only delete if the PERSON write never landed.
  let pdfPath = null;
  let personWritten = false;
  try {
    const personRef = doc(db, 'tenants', tenantId, 'people', empId);
    const personSnap = await getDoc(personRef);
    const existing = _medDq(personSnap.exists() ? { id: empId, ...personSnap.data() } : {}); // TASK-1496-S6

    if (file && _compPartnerTenantId()) {
      // TASK-9449 — managing partner: the server stores the cert create-only in
      // this driver's medExam folder AND sets medCertPdfPath itself (never an orphan).
      pdfPath = (await _compPartnerApi('partnerUploadPersonDoc')(empId, 'medCert', file)).storagePath || null;
      if (!pdfPath) throw new Error('The certificate upload did not return a path.');
      personWritten = true;
    } else if (file) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `tenants/${tenantId}/people/${empId}/medExam/${Date.now()}_${safeName}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file);
      // TASK-996 — PATH-ONLY (mirrors HR saveMedExam). people/medExam is a
      // canReadCompliancePII-gated credential path; a bearer URL in the
      // member-readable people doc would defeat that gate. View via the
      // HR-gated getComplianceDocBytes callable.
      pdfPath = path;
    }

    // Build history entry, capped at 24 — byte-identical shape to HR's saveMedExam.
    const history = (Array.isArray(existing.medExamHistory) ? existing.medExamHistory : []).slice(0, 23);
    const entry = {
      examDate,
      expiryDate,
      examinerName,
      examinerNRCME,
      examType,
      restrictions,
      certPdfUrl: null,          // TASK-996 — path-only
      certPdfPath: pdfPath || null,
      recordedBy: window.AppState?.user?.email || 'unknown',
      recordedAt: new Date().toISOString()
    };
    history.unshift(entry);

    const updateData = {
      medExamLastDate: examDate,
      medExaminerName: examinerName,
      medExaminerNRCME: examinerNRCME,
      medRestrictions: restrictions,
      medExamType: examType,
      // TASK-996 — new cert attached → null the URL + set path; otherwise preserve
      // the existing pair (never re-propagate a stale legacy URL forward).
      medCertPdfUrl: pdfPath ? null : (existing.medCertPdfUrl || null),
      medCertPdfPath: pdfPath || existing.medCertPdfPath || null,
      medExamHistory: history,
      // Keep legacy field in sync so cron + medCard UI still work
      medCardExpiryDate: expiryDate,
      // TASK-647 — stamp the cert-side upload time when a NEW cert PDF is attached
      // so getEffectiveMedCardDoc's recency logic surfaces this freshly-recorded exam.
      ...(pdfPath ? { medCertPdfUploadedAt: new Date().toISOString() } : {})
    };

    await updateDoc(personRef, updateData);
    personWritten = true;   // person doc now references the blob → never orphan-delete it

    // Immutable audit trail — same row shape as HR's write; firestore.rules
    // already allows hasModule('compliance') to create complianceTimeline rows.
    await addDoc(collection(db, 'tenants', tenantId, 'complianceTimeline'), {
      personId: empId,
      personName: existing.name || '',
      type: 'med_exam',
      description: `DOT physical — ${examinerName} (NRCME ${examinerNRCME}) — expires ${expiryDate}`,
      examDate,
      expiryDate,
      examinerName,
      examinerNRCME,
      examType,
      restrictions,
      certPdfUrl: null,             // TASK-996 — path-only
      certPdfPath: pdfPath || null,
      recordedBy: window.AppState?.user?.email || 'unknown',
      timestamp: Timestamp.now(),
      createdAt: Timestamp.now()
    });

    const person = ComplianceState.people.find(p => p.id === empId);
    if (person) Object.assign(person, updateData);

    window.logAudit?.('compliance', 'MED_EXAM_RECORDED', { personId: empId, examDate, expiryDate, examinerNRCME });

    document.getElementById('compliance-med-exam-modal')?.remove();
    showToast('Medical exam saved', 'success');
    showPersonDetail(empId);
  } catch (err) {
    console.error('[Compliance] Error saving medical exam:', err);
    // TASK-1075 — if the person write never landed but we already uploaded a cert
    // blob, reap the orphan. The old client `deleteObject` here was a NON-FUNCTIONAL
    // no-op: the medExam path is delete-immutable (§391.51, storage.rules
    // `allow delete: if false`). The Admin-SDK reaper re-verifies the blob is
    // unreferenced before deleting (a lost-response where the write actually landed
    // is safely refused server-side). The upfront _compCanWriteRoster gate means we
    // only get here on a rare TRANSIENT failure by an authorized member.
    if (pdfPath && !personWritten) {
      await _reapOrphanCredBlobCompliance(tenantId, empId, pdfPath);
    }
    // The people update rule is isDispatcherOrHigher || hasModule('roster') — a
    // compliance-module-ONLY member is denied here BY DESIGN (TASK-818 trap; do
    // not widen firestore.rules). Surface it plainly instead of a generic error.
    const denied = err?.code === 'permission-denied';
    showToast(_compPartnerTenantId()
      ? _compPartnerErrText(err, 'Error saving exam') // TASK-9449 — say the server's reason
      : denied
      ? 'You do not have permission to update driver records — ask an owner or admin to record this exam.'
      : 'Error saving exam', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save Exam'; }
  }
}

// Send the driver a one-tap SMS link to self-upload their new med card photo.
// Mirrors HR's sendMedUploadLink: generateMedCardUploadLink({ sendSms:true }) mints
// the link AND texts it from the server (TASK-9903 — the old client write to the
// root scheduledReminders queue was default-denied by firestore.rules, so the text
// never went out), and says what really happened. The callable is
// gated requireTenantAdminOrPartner — owner/admin passes; a non-admin compliance
// staffer hits permission-denied, which we surface as a toast (accepted gate
// decision, TASK-1047 — do NOT widen the gate).
async function sendMedUploadLinkCompliance(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;

  const phone = person.phone || person.cellPhone || person.mobilePhone;
  if (!phone) {
    showToast('No phone on file for this driver — add one first', 'error');
    return;
  }

  const confirmMsg = `Send ${person.name} an SMS at ${phone} with a one-time upload link for their new medical card?`;
  const proceed = window.showConfirm
    ? await window.showConfirm('Send med card upload link', confirmMsg, { confirmText: 'Send link', cancelText: 'Cancel' })
    : window.confirm(confirmMsg);
  if (!proceed) return;

  // TASK-9903 (COD) — one send in flight per person: a double tap must not text twice.
  if (_medLinkInFlight9903.has(personId)) return;
  _medLinkInFlight9903.add(personId);
  try {
    const generateFn = httpsCallable(functions, 'generateMedCardUploadLink');
    const resp = await generateFn({ tenantId: ComplianceState.tenantId, personId, validDays: 30, sendSms: true });
    const url = resp?.data?.url;
    if (!url) {
      showToast('Could not generate link', 'error');
      return;
    }
    // TASK-9903 — the server sent (or did not send) the text; report its real outcome.
    const r = { ok: resp.data.smsStatus === 'sent', msg: resp.data.smsMessage || 'The upload link was created but not texted.' };
    if (!r.ok) { try { await navigator.clipboard.writeText(url); r.msg += ' The link is copied — send it another way.'; } catch (_) { /* no clipboard */ } }
    showToast(r.msg, r.ok ? 'success' : 'error');
  } catch (err) {
    console.error('[Compliance] sendMedUploadLink error:', err);
    // generateMedCardUploadLink is gated requireTenantAdminOrPartner — a
    // non-admin compliance staffer is denied BY DESIGN; surface gracefully.
    const denied = err?.code === 'permission-denied' || err?.code === 'functions/permission-denied';
    showToast(denied
      ? 'You do not have permission to send upload links — ask an owner or admin.'
      : (err?.message || 'Could not send link'), 'error');
  } finally {
    _medLinkInFlight9903.delete(personId);
  }
}
const _medLinkInFlight9903 = new Set();

// TASK-894 — Employment/Safety Verification (SPH §391.23/§40.25 response) +
// DQ Packet, generated from the Compliance person-detail modal. Both PDFs are
// authored ONLY in HR, whose openers read HRState.tenantId + HRState.people —
// unset if HR was never opened, or STALE (wrong tenant) from a prior tenant
// (TASK-781 trap). Hydrate THIS subject into HRState first (same pattern as
// requestMvrFromPartner above); only delegate if hydration succeeded.
// TASK-1103 — open the completed §391.21 application via person.candidateId.
// Delegates to HRModule (the single owner of the candidate view modal).
function viewPersonApplication(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  const cid = person && person.candidateId;
  if (!cid) { showToast('No digital application on file', 'info'); return; }
  if (window.HRModule?.openCandidateApplication) {
    window.HRModule.openCandidateApplication(cid);
  } else {
    showToast('Open this driver in HR to view the application.', 'info');
  }
}

// TASK-1099 — open the §391.23 Safety Performance History Investigation pop-out
// (shared HRModule modal) for a hired driver via person.candidateId.
function openPersonPeiInvestigation(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  const cid = person && person.candidateId;
  if (!cid) { showToast('No digital application on file — the §391.23 record lives on the application', 'info'); return; }
  if (window.HRModule?.openPeiInvestigation) {
    window.HRModule.openPeiInvestigation(cid);
  } else {
    showToast('Open this driver in HR to manage the investigation.', 'info');
  }
}

function generateSphFromCompliance(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;
  const hydrated = window.HRModule?.ensureSubjectInHrState?.(person);
  if (hydrated && window.HRModule?.generateSphResponse) {
    window.HRModule.generateSphResponse(personId);
  } else {
    showToast('Open this driver in HR to generate.', 'info');
  }
}

function generateDqPacketFromCompliance(personId) {
  const person = ComplianceState.people.find(p => p.id === personId);
  if (!person) return;
  const hydrated = window.HRModule?.ensureSubjectInHrState?.(person);
  if (hydrated && window.HRModule?.openDqPacketViewer) {
    window.HRModule.openDqPacketViewer(personId);
  } else {
    showToast('Open this driver in HR to generate.', 'info');
  }
}

// ============================================
// TRAINING CERT CRUD (in compliance view)
// ============================================
// TASK-4573b — rows pass the entry id; the index is only the fallback for a
// legacy id-less entry. Resolved against the live array at click time.
function _compTrnRow(personId, index, entryId) {
  const rows = (ComplianceState.people.find(p => p.id === personId) || {}).isnetTrainings || [];
  const i = entryId ? rows.findIndex(t => t && t.id === entryId) : (rows[index] && !rows[index].id ? index : -1); // an id-less button only ever means an id-less row
  if (rows[i]) return { training: rows[i], index: i };
  showToast('That record changed. Close this and reopen the person.', 'error');
}

// The row the open Edit modal is editing (a copy taken when it opened). The save
// finds it again by id inside a transaction (trainingAudit.js saveTrainingTx).
let _compTrnEditing = null;

function showTrainingModal(personId, editIndex, entryId) {
  const isEdit = editIndex >= 0;
  let t = {};
  if (isEdit) {
    const row = _compTrnRow(personId, editIndex, entryId);
    if (!row) return;
    editIndex = row.index;
    t = row.training;
  }
  _compTrnEditing = isEdit ? { personId, entry: { ...t }, index: editIndex } : null;

  let modal = document.getElementById('compliance-training-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'compliance-training-modal';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }

  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h3><span class="material-icons">school</span> ${isEdit ? 'Edit' : 'Add'} Training Record</h3>
        <button class="modal-close" onclick="document.getElementById('compliance-training-modal').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label for="comp-trn-name">Training / Course Name *</label>
          <input type="text" id="comp-trn-name" class="form-control" placeholder="e.g. H2S Alive" value="${escapeHtml(t.trainingName || '')}">
        </div>
        <div class="form-group">
          <label for="comp-trn-cert">Certification Name</label>
          <input type="text" id="comp-trn-cert" class="form-control" placeholder="e.g. H2S Safety Certificate" value="${escapeHtml(t.certificationName || '')}">
        </div>
        <div class="form-group">
          <label for="comp-trn-org">Organization</label>
          <input type="text" id="comp-trn-org" class="form-control" placeholder="e.g. Enform" value="${escapeHtml(t.organization || '')}">
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-sm);">
          <div class="form-group">
            <label for="comp-trn-issue">Issue Date</label>
            <input type="date" id="comp-trn-issue" class="form-control" value="${t.issueDate || ''}">
          </div>
          <div class="form-group">
            <label for="comp-trn-expiry">Expiry Date</label>
            <input type="date" id="comp-trn-expiry" class="form-control" value="${t.expiryDate || ''}">
          </div>
        </div>
        <div class="form-group">
          <label for="comp-trn-number">Cert / Course Number</label>
          <input type="text" id="comp-trn-number" class="form-control" placeholder="CERT-12345" value="${escapeHtml(t.certCourseNumber || '')}">
        </div>
        <div class="form-group" style="display:flex;align-items:flex-start;gap:8px;">
          <input type="checkbox" id="comp-trn-dot" ${t.dotRelevant === true ? 'checked' : ''} style="margin-top:3px;width:16px;height:16px;flex-shrink:0;accent-color:var(--brand-blue);">
          <label for="comp-trn-dot" style="margin:0;cursor:pointer;">DOT-relevant — include in DQ file
            <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:400;margin-top:2px;">Adds this certificate to the driver's §391.51 DQ file packet. Leave off for non-DOT site certs (H2S, confined space, fit test). If linked to a Required Training, that training's DQ-file setting wins.</div>
          </label>
        </div>
        <div class="form-group">
          <label for="comp-trn-file">Scanned Copy</label>
          <input type="file" id="comp-trn-file" class="form-control" accept="image/*,.pdf">
          ${(t.scannedCopyPath || t.scannedCopyUrl) ? `<div style="font-size:0.75rem;color:#7c3aed;margin-top:4px;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:14px;">attach_file</span> Existing file attached — <a href="#" onclick="event.preventDefault();window.viewComplianceDoc('${escJsAttr(t.scannedCopyPath || '')}','${escJsAttr(t.scannedCopyUrl || '')}','certificate');return false;" style="color:#7c3aed;cursor:pointer;">View</a></div>` : ''}
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" onclick="document.getElementById('compliance-training-modal').classList.add('hidden')">Cancel</button>
        <button class="btn btn-primary" id="btn-comp-save-trn" onclick="window.ComplianceModule.saveTraining('${personId}', ${editIndex})">
          <span class="material-icons">save</span> Save
        </button>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
}

// TASK-4573 — training audit rows live in a LAZY chunk (shared with hr.js).
// TASK-4573b — so does save/delete-by-id (the save awaits the chunk; a failed load
// fails the save loudly instead of falling back to writing by index).
const _trnLoad = () => __ddChunkWrap(import('./trainingAudit.js?v=4573005'));
const _trnLazy = (f) => _trnLoad().then(f).catch(e => console.warn('[trainingAudit]', e));

async function saveTraining(personId, editIndex) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;

  const trainingName = document.getElementById('comp-trn-name')?.value?.trim();
  if (!trainingName) { showToast('Training name is required', 'error'); return; }

  const btn = document.getElementById('btn-comp-save-trn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const certificationName = document.getElementById('comp-trn-cert')?.value?.trim() || '';
    const organization = document.getElementById('comp-trn-org')?.value?.trim() || '';
    const issueDate = document.getElementById('comp-trn-issue')?.value || '';
    const expiryDate = document.getElementById('comp-trn-expiry')?.value || '';
    const certCourseNumber = document.getElementById('comp-trn-number')?.value?.trim() || '';
    const fileInput = document.getElementById('comp-trn-file');
    const file = fileInput?.files?.[0];
    if (file && file.size > 7 * 1024 * 1024) { showToast('File too large (max 7MB)', 'error'); return; } // base64 in a 10MB callable body

    const person = ComplianceState.people.find(p => p.id === personId);

    // TASK-4573b — the row this Edit modal opened (a copy, never a bare index).
    // A lost/foreign target matches nothing in the chunk -> refused, never an add.
    const ed = _compTrnEditing; // captured BEFORE any await: a modal reopened meanwhile can't retarget this save (COD r1)
    const dotRelevant = document.getElementById('comp-trn-dot')?.checked === true; // read once, not per txn retry
    const m = await _trnLoad();

    // Step 1 — write the record BY ID inside a transaction that re-reads the person;
    // refused (nothing written, nothing uploaded) when the entry is gone. A legacy
    // id-less entry gets its id here. The cert pointers are SERVER-owned
    // (uploadSafetyCert) and carried forward from the FRESH row untouched — the DQ
    // packet reads storagePath first.
    const personRef = doc(db, 'tenants', tenantId, 'people', personId);
    const target = editIndex >= 0 ? m.trainingTarget(ed?.personId === personId && ed.entry, ed?.index) : null;
    const { next: currentTrainings, record, prev: prevRecord } = await m.saveTrainingTx({ db, runTransaction }, personRef, target, (existing) => ({
      id: existing?.id || m.newTrainingId(),
      trainingName,
      certificationName: certificationName || null,
      organization: organization || null,
      issueDate: issueDate || null,
      expiryDate: expiryDate || null,
      certCourseNumber: certCourseNumber || null,
      // TASK-1085 — per-record §391.51 DQ-file flag (default unchecked). Free-form
      // records here carry no requiredTrainingId link, so without this an entry
      // could never reach the DQ packet. Also PRESERVE an existing link on edit —
      // this modal used to rebuild the record without it, silently unlinking it
      // from the Safety Roster matrix + the config-level DQ-file resolution.
      dotRelevant,
      requiredTrainingId: existing?.requiredTrainingId || null,
      hasScannedCopy: existing?.hasScannedCopy || false,
      scannedCopyUrl: existing?.scannedCopyUrl || null,
      scannedCopyPath: existing?.scannedCopyPath || null,
      docUrl: existing?.docUrl || null,
      storagePath: existing?.storagePath || null,
      uploadedAt: existing?.uploadedAt || null,
    }));
    _trnLazy(m => m.auditTraining('compliance', personId, record, prevRecord)); // TASK-4573 audit row

    // Step 2 (TASK-4573c) — a new scan goes through the SAME server path as HR's
    // modal (uploadSafetyCert, in the lazy chunk). This modal used to write a client
    // hr/ path with storagePath empty, which the §391.51 packet's safety-certs fence
    // silently dropped. A failed upload keeps the previous cert attached.
    const up = file ? await m.uploadTrainingCert({ httpsCallable, functions, getDoc, inferType: _cpInferContentType }, personRef, { tenantId, personId, entryId: record.id, file }) : {};

    if (person) person.isnetTrainings = up.trainings || currentTrainings;

    showToast(up.err || 'Training record saved', up.err ? 'error' : 'success');
    document.getElementById('compliance-training-modal')?.classList.add('hidden');
    showPersonDetail(personId);
  } catch (err) {
    console.error('Training save error:', err);
    showToast(err?.code === 'training-gone' ? err.message : err?.code === 'permission-denied' ? 'You need the HR or Compliance module to change training records' : 'Failed to save training', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save'; }
  }
}

async function deleteTraining(personId, index, entryId) {
  const tenantId = ComplianceState.tenantId;
  if (!tenantId || !personId) return;
  // TASK-4573b — the row as shown when Delete was pressed; removed BY ID inside a
  // transaction, refused when it is no longer there (never splice a bare index).
  const row = _compTrnRow(personId, index, entryId);
  if (!row) return;
  if (!confirm('Delete this training record?')) return;

  try {
    const m = await _trnLoad();
    const personRef = doc(db, 'tenants', tenantId, 'people', personId);
    // TASK-9912 — write the record FIRST, then remove the file (a refused write
    // must not leave the row pointing at a deleted cert).
    const { next: currentTrainings, removed: training } = await m.deleteTrainingTx({ db, runTransaction }, personRef, m.trainingTarget(row.training, row.index));
    if (training) _trnLazy(m => m.auditTraining('compliance', personId, training, null, true)); // TASK-4573
    if (training?.scannedCopyPath) {
      await deleteObject(ref(storage, training.scannedCopyPath)).catch(e => console.warn('[Compliance] Old training doc delete failed:', e.message));
    }

    const person = ComplianceState.people.find(p => p.id === personId);
    if (person) person.isnetTrainings = currentTrainings;

    showToast('Training record deleted', 'success');
    showPersonDetail(personId);
  } catch (err) {
    console.error('Training delete error:', err);
    showToast(err?.code === 'training-gone' ? err.message : err?.code === 'permission-denied' ? 'You need the HR or Compliance module to change training records' : 'Failed to delete', 'error');
  }
}

// ============================================
// DOT AUDIT REPORT — LAZY views/complianceDotAudit.js (perf budget, TASK-10095 S1b).
// Bodies moved verbatim; the window.ComplianceModule shims below load it on first use.
// ============================================
let _dotAuditLoad = null;
function _dotAudit(fn, arg) {
  _dotAuditLoad = _dotAuditLoad || __ddChunkWrap(import('./complianceDotAudit.js?v=10095201'))
    .then((m) => { m._bind({ ComplianceState, escapeHtml, functions, httpsCallable }); return m; })
    .catch((e) => { _dotAuditLoad = null; throw e; });
  return _dotAuditLoad.then((m) => m[fn](arg)).catch((e) => { console.error('[Compliance] DOT audit load failed:', e); window.showToast?.('Could not open the DOT audit report. Check your connection and try again.', 'error'); });
}

// ============================================
// DRUG & ALCOHOL PROGRAM DOCS + AUDIT PACKET
// ----
// Tenant-level program docs (TPA attestation, period reports, pool roster
// snapshots, blank CCFs) sit in tenants/{t}/complianceDocs. The Audit Packet
// button posts to the generateAuditPacket Cloud Function, which assembles a
// ZIP of all D&A artifacts in a date range — replaces the multi-day email
// tag chase between consultant and TPA at audit time.
// ============================================

const D_AND_A_CATEGORIES = [
  { key: 'TPA_ATTESTATION',          label: 'TPA Attestation Letter', icon: 'verified' },
  { key: 'PERIOD_REPORT',            label: 'Period Stats Report (NCMS)', icon: 'analytics' },
  { key: 'POOL_ROSTER',              label: 'Pool Roster Snapshot', icon: 'group' },
  { key: 'BLANK_CCF',                label: 'Blank CCF Template', icon: 'description' },
  // TASK-DA-v1 slice 3 — C/TPA random-selection notice. Quarterly is C/TPA
  // program practice (supports the §382.305(k) "spread reasonably throughout
  // the year" requirement); no §382.305 subsection mandates quarterly notices.
  { key: 'RANDOM_SELECTION_NOTICE',  label: 'Random Selection Notice (C/TPA)', icon: 'shuffle' },
  // TASK-DA-v1 slice 4 — §382.403 calendar-year MIS summary, first-class
  { key: 'MIS_REPORT',               label: 'MIS Calendar-Year Summary (§382.403)', icon: 'bar_chart' },
  { key: 'OTHER',                    label: 'Other D&A Document', icon: 'attach_file' },
];

// TASK-343 Slice 5 — Quarterly random-selection coverage chip strip.
// Reads `RANDOM_SELECTION_NOTICE` docs from ComplianceState.complianceDocs,
// classifies each by quarter (using effectiveDate or metadata.periodEnd),
// and surfaces Q1-Q4 of the current calendar year as colored chips.
//
// Status colors (per dispatcher spec):
//   gray  — not yet selected (future quarter, no doc)
//   blue  — active selection period (current quarter, no doc yet)
//   green — recorded (notice doc exists for this quarter)
//   amber — overdue (past quarter, no doc on file)
//
// Click chip → opens existing Upload Selection Notice modal with the quarter
// pre-populated in the title field. Backward compat: missing
// `settings.daRandomSelections[]` doesn't break — `complianceDocs` is the
// authoritative source until that registry field is wired downstream.
function _quarterOfDate(yyyymmdd) {
  if (!yyyymmdd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return null;
  const month = parseInt(yyyymmdd.slice(5, 7), 10);
  if (month >= 1 && month <= 3) return 1;
  if (month >= 4 && month <= 6) return 2;
  if (month >= 7 && month <= 9) return 3;
  if (month >= 10 && month <= 12) return 4;
  return null;
}

function _yearOfDate(yyyymmdd) {
  if (!yyyymmdd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return null;
  return parseInt(yyyymmdd.slice(0, 4), 10);
}

function renderQuarterlyRandomCoverageStrip() {
  const year = new Date().getFullYear();
  const now = new Date();
  const currentQuarter = _quarterOfDate(now.toISOString().split('T')[0]) || 1;

  // Collect RANDOM_SELECTION_NOTICE docs for the current calendar year.
  const noticeDocs = (ComplianceState.complianceDocs || [])
    .filter(d => d.category === 'RANDOM_SELECTION_NOTICE');

  // Map quarter → array of docs (read effectiveDate first, then
  // metadata.periodEnd as fallback for older period-bracketed uploads).
  const byQuarter = { 1: [], 2: [], 3: [], 4: [] };
  for (const d of noticeDocs) {
    const dateStr = d.effectiveDate || d.metadata?.periodEnd || d.metadata?.periodStart || null;
    if (!dateStr) continue;
    const docYear = _yearOfDate(dateStr);
    const docQuarter = _quarterOfDate(dateStr);
    if (docYear === year && docQuarter && byQuarter[docQuarter]) {
      byQuarter[docQuarter].push(d);
    }
  }

  // Optional settings registry (forward-compat — TASK-343 deferred slice).
  // If a tenant's settings.daRandomSelections[] exists, treat presence of an
  // entry for a quarter as "recorded" even without a notice doc uploaded.
  const tenantSettings = window.AppState?.tenantData?.settings || {};
  const registryEntries = Array.isArray(tenantSettings.daRandomSelections)
    ? tenantSettings.daRandomSelections
    : [];
  const registryByQuarter = { 1: false, 2: false, 3: false, 4: false };
  for (const e of registryEntries) {
    if (e?.year === year && e?.quarter >= 1 && e?.quarter <= 4) {
      registryByQuarter[e.quarter] = true;
    }
  }

  const chipFor = (q) => {
    const recorded = byQuarter[q].length > 0 || registryByQuarter[q];
    let bg, color, label, sub;
    if (recorded) {
      bg = '#ECFDF5'; color = '#059669'; label = `Q${q}`;
      sub = byQuarter[q].length > 0
        ? `${byQuarter[q].length} notice${byQuarter[q].length === 1 ? '' : 's'}`
        : 'Recorded';
    } else if (q < currentQuarter) {
      bg = '#FEF3C7'; color = '#92400E'; label = `Q${q}`;
      sub = 'Overdue';
    } else if (q === currentQuarter) {
      bg = '#DBEAFE'; color = '#1D4ED8'; label = `Q${q}`;
      sub = 'Active period';
    } else {
      bg = '#F1F5F9'; color = '#64748B'; label = `Q${q}`;
      sub = 'Not yet';
    }
    return `<button onclick="window.ComplianceModule.openQuarterlyRandomSelection(${year}, ${q})" style="flex:1;min-width:88px;padding:8px 10px;border-radius:8px;border:1px solid ${color}33;background:${bg};color:${color};cursor:pointer;text-align:center;display:flex;flex-direction:column;align-items:center;gap:2px;">
      <span style="font-size:0.78rem;font-weight:700;">${label}</span>
      <span style="font-size:0.68rem;font-weight:500;opacity:0.9;">${escapeHtml(sub)}</span>
    </button>`;
  };

  return `
    <div style="margin-top:10px;padding:10px 0 4px;border-top:1px dashed var(--border-light);">
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
        <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">event_repeat</span>
        <span style="font-size:0.78rem;font-weight:700;color:var(--text-primary);">Quarterly random-roster coverage — ${year}</span>
        <span style="font-size:0.68rem;color:var(--text-muted);margin-left:auto;">Program practice · 49 CFR §382.305(k)</span>
      </div>
      <div style="display:flex;gap:6px;flex-wrap:wrap;">
        ${chipFor(1)}
        ${chipFor(2)}
        ${chipFor(3)}
        ${chipFor(4)}
      </div>
      <div style="font-size:0.68rem;color:var(--text-muted);margin-top:4px;line-height:1.4;">
        C/TPAs run selections quarterly. Click a chip to upload that quarter's notice — your audit packet auto-includes it.
      </div>
    </div>
  `;
}

// TASK-343 Slice 5 — click handler. Opens the existing Upload Selection
// Notice modal pre-scoped to the clicked quarter (prefills the title field
// with `QX YYYY Random Selection Notice`). No new modal — reuses the
// existing flow at showAddProgramDocModal.
function openQuarterlyRandomSelectionModal(year, quarter) {
  showAddProgramDocModal('RANDOM_SELECTION_NOTICE');
  // After the modal renders, prefill the title input + effective date for
  // the first month of the quarter (carrier can edit). Schedule for next
  // tick because showAddProgramDocModal appends asynchronously.
  setTimeout(() => {
    const titleInput = document.getElementById('compdoc-title');
    if (titleInput && !titleInput.value) {
      titleInput.value = `Q${quarter} ${year} Random Selection Notice`;
    }
    const effInput = document.getElementById('compdoc-effective');
    if (effInput && !effInput.value) {
      const firstMonth = String((quarter - 1) * 3 + 1).padStart(2, '0');
      effInput.value = `${year}-${firstMonth}-15`;
    }
  }, 0);
}

function renderDAndAProgramSection() {
  const docs = (ComplianceState.complianceDocs || [])
    .filter(d => D_AND_A_CATEGORIES.some(c => c.key === d.category));

  const byCat = {};
  for (const d of docs) {
    (byCat[d.category] = byCat[d.category] || []).push(d);
  }

  const today = new Date().toISOString().split('T')[0];

  const catBlocks = D_AND_A_CATEGORIES.map(cat => {
    const items = (byCat[cat.key] || []).slice().sort((a, b) => {
      const ad = a.effectiveDate || a.metadata?.periodEnd || '';
      const bd = b.effectiveDate || b.metadata?.periodEnd || '';
      return bd.localeCompare(ad);
    });

    const itemsHtml = items.length === 0
      ? `<div style="font-size:0.75rem;color:var(--text-muted);padding:6px 0;">No documents uploaded.</div>`
      : items.map(d => {
          const dateLabel = d.effectiveDate || d.metadata?.periodEnd || '';
          let badge = '';
          if (cat.key === 'TPA_ATTESTATION' && d.expiresDate) {
            const days = Math.floor((new Date(d.expiresDate) - new Date(today)) / 86400000);
            if (days < 0) badge = `<span style="font-size:0.68rem;color:#fff;background:#DC2626;padding:2px 6px;border-radius:8px;margin-left:6px;">EXPIRED</span>`;
            else if (days <= 30) badge = `<span style="font-size:0.68rem;color:#92400E;background:#FDE68A;padding:2px 6px;border-radius:8px;margin-left:6px;">${days}d to expiry</span>`;
          }
          return `
            <div style="display:flex;align-items:center;gap:8px;padding:6px 8px;background:var(--bg-secondary);border-radius:6px;margin-bottom:4px;">
              <span class="material-icons" style="font-size:16px;color:var(--text-muted);">picture_as_pdf</span>
              <div style="flex:1;min-width:0;">
                <div style="font-size:0.8rem;font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(d.title || '(untitled)')}${badge}</div>
                <div style="font-size:0.7rem;color:var(--text-secondary);">${escapeHtml(dateLabel || '')}${dateLabel && d.expiresDate ? ' → ' + escapeHtml(d.expiresDate) : ''}</div>
              </div>
              ${d.pdfUrl ? `<button class="btn-icon" onclick="window.ComplianceModule.openSafePdf('${escJsAttr(d.pdfUrl)}')" title="View"><span class="material-icons" style="font-size:16px;">open_in_new</span></button>` : ''}
              <button class="btn-icon" onclick="window.ComplianceModule.deleteProgramDoc('${escJsAttr(d.id)}')" title="Delete"><span class="material-icons" style="font-size:16px;color:#DC2626;">delete</span></button>
            </div>`;
        }).join('');

    return `
      <div style="margin-top:10px;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
          <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">${cat.icon}</span>
          <span style="font-size:0.78rem;font-weight:700;color:var(--text-primary);">${cat.label}</span>
          ${cat.key === 'MIS_REPORT' ? `
          <button class="btn btn-sm" onclick="window.ComplianceModule.showMisDraft()" style="margin-left:auto;font-size:0.7rem;padding:2px 8px;" title="Build a draft of the appendix J MIS form from the test records already in DispatchDeck">
            <span class="material-icons" style="font-size:12px;">calculate</span> Build draft
          </button>
          <button class="btn btn-sm" onclick="window.ComplianceModule.showAddProgramDoc('${cat.key}')" style="font-size:0.7rem;padding:2px 8px;">
            <span class="material-icons" style="font-size:12px;">add</span> Upload
          </button>
          ` : `
          <button class="btn btn-sm" onclick="window.ComplianceModule.showAddProgramDoc('${cat.key}')" style="margin-left:auto;font-size:0.7rem;padding:2px 8px;">
            <span class="material-icons" style="font-size:12px;">add</span> Upload
          </button>
          `}
        </div>
        ${itemsHtml}
      </div>`;
  }).join('');

  return `
    <div style="margin-top:14px;padding-top:12px;border-top:1px solid var(--border-light);">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
        <span style="font-size:0.85rem;font-weight:700;color:var(--text-primary);">D&A Program Documents</span>
        <button class="btn btn-primary btn-sm" onclick="window.ComplianceModule.showAuditPacketModal()" style="margin-left:auto;font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">archive</span> Generate Audit Packet
        </button>
      </div>
      <div style="font-size:0.72rem;color:var(--text-muted);margin-bottom:4px;">
        Upload TPA letterhead, period stats, pool rosters, and blank CCFs here. The Audit Packet button bundles everything (these docs + every drug test PDF in the date range) into a single ZIP for an auditor — no more emailing your TPA for a week.
      </div>
      ${renderQuarterlyRandomCoverageStrip()}
      ${catBlocks}
    </div>`;
}

let pendingProgramDocFile = null;

function showAddProgramDocModal(categoryKey) {
  const cat = D_AND_A_CATEGORIES.find(c => c.key === categoryKey) || D_AND_A_CATEGORIES[D_AND_A_CATEGORIES.length - 1];
  const today = new Date().toISOString().split('T')[0];
  const oneYear = new Date(Date.now() + 365 * 86400000).toISOString().split('T')[0];

  // TASK-DA-v1 slice 4 — MIS_REPORT is a period-bracketed doc category (calendar-year per §382.403)
  const isPeriod = cat.key === 'PERIOD_REPORT' || cat.key === 'MIS_REPORT';

  const modal = document.createElement('div');
  modal.id = 'compdoc-modal';
  modal.className = 'modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
  modal.innerHTML = `
    <div style="background:#fff;border-radius:var(--radius-lg);max-width:480px;width:100%;max-height:90vh;overflow-y:auto;">
      <div style="padding:14px 16px;border-bottom:1px solid var(--border-light);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">${cat.icon}</span>
        <h3 style="margin:0;font-size:1.05rem;font-weight:700;flex:1;">Upload ${cat.label}</h3>
        <button class="btn-icon" onclick="document.getElementById('compdoc-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:16px;">
        <div class="form-group">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Title / description</label>
          <input type="text" id="compdoc-title" class="form-control" placeholder="${cat.key === 'TPA_ATTESTATION' ? 'e.g. ADT 2026 attestation letter' : cat.key === 'PERIOD_REPORT' ? 'e.g. 2025 Full Year FMCSA Pool stats' : cat.key === 'POOL_ROSTER' ? 'e.g. FMCSA Pool roster as of 1/1/2026' : cat.key === 'BLANK_CCF' ? 'e.g. Blank DOT Drug CCF' : cat.key === 'RANDOM_SELECTION_NOTICE' ? 'e.g. Q1 2026 Random Selection Notice (ADT)' : cat.key === 'MIS_REPORT' ? 'e.g. 2025 Calendar-Year MIS Summary' : 'Description'}" required>
        </div>

        ${isPeriod ? `
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
            <div class="form-group">
              <label style="font-size:0.78rem;color:var(--text-secondary);">Period start</label>
              <input type="date" id="compdoc-period-start" class="form-control">
            </div>
            <div class="form-group">
              <label style="font-size:0.78rem;color:var(--text-secondary);">Period end</label>
              <input type="date" id="compdoc-period-end" class="form-control">
            </div>
          </div>
          <div class="form-group">
            <label style="font-size:0.78rem;color:var(--text-secondary);">Pool type</label>
            <select id="compdoc-pool-type" class="form-control">
              <option value="DOT">DOT (FMCSA)</option>
              <option value="NON_DOT">Non-DOT (Company)</option>
            </select>
          </div>
        ` : `
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
            <div class="form-group">
              <label style="font-size:0.78rem;color:var(--text-secondary);">${cat.key === 'POOL_ROSTER' ? 'Snapshot date' : 'Effective date'}</label>
              <input type="date" id="compdoc-effective" class="form-control" value="${today}">
            </div>
            <div class="form-group">
              <label style="font-size:0.78rem;color:var(--text-secondary);">Expires (optional)</label>
              <input type="date" id="compdoc-expires" class="form-control" value="${cat.key === 'TPA_ATTESTATION' ? oneYear : ''}">
            </div>
          </div>
        `}

        <div class="form-group">
          <label style="font-size:0.78rem;color:var(--text-secondary);">PDF or image (max 25MB)</label>
          <div id="compdoc-file-preview" style="margin-bottom:6px;"></div>
          <button type="button" class="btn btn-secondary" onclick="document.getElementById('compdoc-file').click();" style="font-size:0.8rem;">
            <span class="material-icons" style="font-size:14px;">upload</span> Choose file
          </button>
          <input type="file" id="compdoc-file" accept="application/pdf,image/*,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style="display:none;" onchange="window.ComplianceModule.handleProgramDocFile(event)">
        </div>
      </div>
      <div style="padding:12px 16px;border-top:1px solid var(--border-light);display:flex;justify-content:flex-end;gap:8px;">
        <button class="btn btn-secondary" onclick="document.getElementById('compdoc-modal').remove()">Cancel</button>
        <button class="btn btn-primary" id="compdoc-save-btn" onclick="window.ComplianceModule.saveProgramDoc('${cat.key}')">
          <span class="material-icons" style="font-size:14px;">save</span> Save
        </button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  pendingProgramDocFile = null;
}

function handleProgramDocFileUpload(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  if (file.size > 25 * 1024 * 1024) {
    window.showToast?.('File too large (max 25MB)', 'error');
    event.target.value = '';
    return;
  }
  pendingProgramDocFile = file;
  const preview = document.getElementById('compdoc-file-preview');
  if (preview) {
    preview.innerHTML = `
      <div style="display:flex;align-items:center;gap:6px;padding:6px 8px;background:var(--bg-secondary);border-radius:6px;font-size:0.78rem;">
        <span class="material-icons" style="font-size:14px;">attachment</span>
        <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(file.name)}</span>
        <span style="color:var(--text-muted);">${Math.round(file.size / 1024)}KB</span>
      </div>`;
  }
}

async function saveProgramDocRecord(categoryKey) {
  const title = document.getElementById('compdoc-title')?.value?.trim();
  if (!title) { window.showToast?.('Title required', 'error'); return; }
  if (!pendingProgramDocFile) { window.showToast?.('File required', 'error'); return; }

  // TASK-DA-v1 slice 4 — MIS_REPORT is also period-bracketed
  const isPeriod = categoryKey === 'PERIOD_REPORT' || categoryKey === 'MIS_REPORT';
  const effective = isPeriod
    ? null
    : (document.getElementById('compdoc-effective')?.value || null);
  const expires = isPeriod
    ? null
    : (document.getElementById('compdoc-expires')?.value || null);
  const periodStart = isPeriod ? (document.getElementById('compdoc-period-start')?.value || null) : null;
  const periodEnd = isPeriod ? (document.getElementById('compdoc-period-end')?.value || null) : null;
  const poolType = isPeriod ? (document.getElementById('compdoc-pool-type')?.value || null) : null;

  const btn = document.getElementById('compdoc-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const tenantId = ComplianceState.tenantId;
    const ext = (pendingProgramDocFile.name.split('.').pop() || 'pdf').toLowerCase();
    const fileName = `${categoryKey}_${Date.now()}.${ext}`;
    const path = `tenants/${tenantId}/complianceDocs/${categoryKey}/${fileName}`;
    const sRef = ref(storage, path);
    await uploadBytes(sRef, pendingProgramDocFile, { contentType: pendingProgramDocFile.type });
    const url = await getDownloadURL(sRef);

    const data = {
      category: categoryKey,
      title,
      effectiveDate: effective,
      expiresDate: expires,
      metadata: isPeriod ? { periodStart, periodEnd, poolType } : {},
      pdfPath: path,
      pdfUrl: url,
      contentType: pendingProgramDocFile.type,
      fileSize: pendingProgramDocFile.size,
      createdAt: Timestamp.now(),
    };

    await addDoc(collection(db, 'tenants', tenantId, 'complianceDocs'), data);
    pendingProgramDocFile = null;
    document.getElementById('compdoc-modal')?.remove();
    window.showToast?.('Document uploaded', 'success');
  } catch (err) {
    console.error('[Compliance] Program doc save error:', err);
    window.showToast?.(`Error saving: ${err.message}`, 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
  }
}

async function deleteProgramDocRecord(docId) {
  if (!confirm('Delete this document?')) return;
  try {
    const tenantId = ComplianceState.tenantId;
    const docRow = (ComplianceState.complianceDocs || []).find(d => d.id === docId);
    if (docRow?.pdfPath) {
      try { await deleteObject(ref(storage, docRow.pdfPath)); } catch (e) { /* ok if gone */ }
    }
    await deleteDoc(doc(db, 'tenants', tenantId, 'complianceDocs', docId));
    window.showToast?.('Deleted', 'success');
  } catch (err) {
    console.error('[Compliance] Program doc delete error:', err);
    window.showToast?.(`Error deleting: ${err.message}`, 'error');
  }
}

function showAuditPacketGenerationModal() {
  const today = new Date().toISOString().split('T')[0];
  const twoYearsAgo = new Date(Date.now() - 2 * 365 * 86400000).toISOString().split('T')[0];

  const drivers = (ComplianceState.people || []).filter(p => {
    const role = (p.crewType || p.role || (p.roles?.[0]) || '').toLowerCase();
    return role.includes('driver') || p.cdlNumber || p.hasCdlNumber === true || p.licenseNumber; // TASK-9359 marker
  });

  const driverOptions = drivers
    .slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    .map(p => `<option value="${p.id}">${escapeHtml(p.name || '(unnamed)')}</option>`).join('');

  const modal = document.createElement('div');
  modal.id = 'audit-packet-modal';
  modal.className = 'modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
  modal.innerHTML = `
    <div style="background:#fff;border-radius:var(--radius-lg);max-width:520px;width:100%;max-height:90vh;overflow-y:auto;">
      <div style="padding:14px 16px;border-bottom:1px solid var(--border-light);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">archive</span>
        <h3 style="margin:0;font-size:1.05rem;font-weight:700;flex:1;">Generate D&A Audit Packet</h3>
        <button class="btn-icon" onclick="document.getElementById('audit-packet-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:16px;">
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:12px;">
          Bundles every D&A artifact in the selected range into a single ZIP — TPA attestations, period stats reports, pool rosters, blank CCFs, and every drug test PDF. Hand the ZIP to a DOT auditor or your compliance consultant.
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
          <div class="form-group">
            <label style="font-size:0.78rem;color:var(--text-secondary);">From</label>
            <input type="date" id="ap-date-from" class="form-control" value="${twoYearsAgo}" required>
          </div>
          <div class="form-group">
            <label style="font-size:0.78rem;color:var(--text-secondary);">Through</label>
            <input type="date" id="ap-date-to" class="form-control" value="${today}" required>
          </div>
        </div>

        <div class="form-group">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Drivers</label>
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
            <input type="checkbox" id="ap-all-drivers" onchange="const s = document.getElementById('ap-driver-select'); s.disabled = this.checked; s.style.opacity = this.checked ? '0.6' : '1'; document.getElementById('ap-all-warn').style.display = this.checked ? 'block' : 'none'; if (!this.checked) document.getElementById('ap-all-ack').checked = false;">
            <label for="ap-all-drivers" style="font-size:0.8rem;cursor:pointer;">Include all drivers</label>
          </div>
          <select id="ap-driver-select" class="form-control" multiple size="6">
            ${driverOptions}
          </select>
          <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:2px;">Pick only the drivers the auditor asked for. Hold Ctrl/Cmd to select multiple.</div>
          <div id="ap-all-warn" style="display:none;margin-top:8px;padding:10px 12px;border:1px solid var(--status-danger);border-left-width:4px;border-radius:6px;background:#FEF2F2;color:var(--text-primary);font-size:0.78rem;">
            <div style="display:flex;align-items:center;gap:6px;font-weight:700;color:var(--status-danger);margin-bottom:4px;"><span class="material-icons" style="font-size:16px;">warning</span>Every driver's file</div>
            Auditors can use anything you send them. Send all drivers only if the auditor asked for all of them.
            <label style="display:flex;align-items:flex-start;gap:6px;margin-top:8px;cursor:pointer;"><input type="checkbox" id="ap-all-ack" style="margin-top:2px;"> <span>The auditor asked for every driver's drug &amp; alcohol records.</span></label>
          </div>
        </div>

        <div class="form-group" style="border-top:1px solid var(--border-light);padding-top:12px;margin-top:8px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
            <input type="checkbox" id="ap-email-toggle" onchange="document.getElementById('ap-email-fields').style.display = this.checked ? 'block' : 'none';">
            <label for="ap-email-toggle" style="font-size:0.85rem;font-weight:600;cursor:pointer;">Also email packet to auditor</label>
          </div>
          <div id="ap-email-fields" style="display:none;">
            <input type="text" id="ap-email-to" class="form-control" placeholder="auditor@example.com (or comma-separated for multiple)" style="margin-bottom:6px;">
            <textarea id="ap-email-note" class="form-control" placeholder="Optional note to include in the email" rows="2" style="resize:vertical;"></textarea>
            <div style="font-size:0.7rem;color:var(--text-muted);margin-top:2px;">Up to 5 recipients. Email contains a 24-hour download link, not the ZIP itself.</div>
          </div>
        </div>

        <div id="ap-result" style="font-size:0.8rem;"></div>
      </div>
      <div style="padding:12px 16px;border-top:1px solid var(--border-light);display:flex;justify-content:flex-end;gap:8px;">
        <button class="btn btn-secondary" onclick="document.getElementById('audit-packet-modal').remove()">Close</button>
        <button class="btn btn-primary" id="ap-generate-btn" onclick="window.ComplianceModule.generateAuditPacket()">
          <span class="material-icons" style="font-size:14px;">archive</span> Generate ZIP
        </button>
      </div>
    </div>`;
  document.body.appendChild(modal);
}

async function generateAuditPacketAction() {
  const dateFrom = document.getElementById('ap-date-from')?.value;
  const dateTo = document.getElementById('ap-date-to')?.value;
  if (!dateFrom || !dateTo) { window.showToast?.('Pick a date range', 'error'); return; }

  const allDrivers = !!document.getElementById('ap-all-drivers')?.checked;
  let personIds = null;
  if (!allDrivers) {
    const sel = document.getElementById('ap-driver-select');
    personIds = Array.from(sel?.selectedOptions || []).map(o => o.value);
    if (personIds.length === 0) { window.showToast?.('Pick at least one driver', 'error'); return; }
  } else if (!document.getElementById('ap-all-ack')?.checked) {
    // TASK-471 — every driver's file only after the explicit confirmation.
    window.showToast?.('Confirm the auditor asked for every driver, or pick drivers', 'error');
    return;
  }

  const emailToggle = document.getElementById('ap-email-toggle')?.checked;
  let emailTo = null;
  let emailNote = null;
  if (emailToggle) {
    const raw = document.getElementById('ap-email-to')?.value?.trim() || '';
    if (!raw) { window.showToast?.('Enter at least one email address', 'error'); return; }
    emailTo = raw;
    emailNote = document.getElementById('ap-email-note')?.value?.trim() || null;
  }

  const btn = document.getElementById('ap-generate-btn');
  const resultDiv = document.getElementById('ap-result');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Generating...'; }
  if (resultDiv) resultDiv.innerHTML = '<div style="color:var(--text-muted);padding:8px 0;">Assembling packet — this can take 30-60 seconds depending on size...</div>';

  try {
    const fn = httpsCallable(functions, 'generateAuditPacket');
    const res = await fn({
      tenantId: ComplianceState.tenantId,
      dateFrom,
      dateTo,
      personIds,
      includeAll: allDrivers, // TASK-471 — the server refuses "no drivers" without it
      emailTo,
      emailNote,
    });
    const out = res.data || {};
    if (resultDiv) {
      // Three states: emailed-ok / email-attempted-and-failed / email-feature-not-deployed-yet.
      // The third happens if the deployed function predates the email-to-auditor extension —
      // it returns no emailSent/emailError, so we just nudge the user to download manually.
      const emailLine = (emailToggle && out.emailSent === true)
        ? `<div style="font-size:0.75rem;color:#047857;margin-top:4px;">📧 Emailed to ${(out.emailedTo || []).join(', ')}</div>`
        : (emailToggle && out.emailError)
        ? `<div style="font-size:0.75rem;color:#92400E;margin-top:4px;">⚠ Email send failed: ${escapeHtml(out.emailError)}. ZIP is still available below.</div>`
        : (emailToggle && out.emailSent === undefined)
        ? `<div style="font-size:0.75rem;color:#92400E;margin-top:4px;">Email-to-auditor not available yet on this server — share the link below directly.</div>`
        : '';
      resultDiv.innerHTML = `
        <div style="background:#ECFDF5;border:1px solid #6EE7B7;border-radius:8px;padding:12px;margin-top:8px;">
          <div style="font-weight:700;color:#065F46;margin-bottom:4px;">Packet ready</div>
          <div style="font-size:0.78rem;color:#047857;margin-bottom:8px;">
            ${out.testCount} test record${out.testCount === 1 ? '' : 's'}, ${out.programDocCount} program doc${out.programDocCount === 1 ? '' : 's'},
            ${Math.round((out.sizeBytes || 0) / 1024)} KB
          </div>
          <a href="${out.downloadUrl}" target="_blank" rel="noopener noreferrer" class="btn btn-primary" style="font-size:0.85rem;">
            <span class="material-icons" style="font-size:14px;">download</span> Download ZIP
          </a>
          ${emailLine}
          <div style="font-size:0.7rem;color:#047857;margin-top:6px;">Link valid for 24 hours. Saved at <code>${out.zipPath}</code>.</div>
        </div>`;
    }
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">archive</span> Generate ZIP'; }
  } catch (err) {
    console.error('[Compliance] Audit packet error:', err);
    if (resultDiv) resultDiv.innerHTML = `<div style="color:#DC2626;padding:8px 0;">Error: ${escapeHtml(err.message || 'unknown')}</div>`;
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">archive</span> Generate ZIP'; }
  }
}

// ============================================
// TASK-1606 — MIS calendar-year summary DRAFT (49 CFR part 40, appendix J)
//
// Renders the draft returned by the `generateAnnualMisSummaryDraft` callable.
// Every figure is either DERIVED (click it to see the exact records behind it)
// or UNDETERMINED (shown as "—" with the reason, never as a zero).
//
// 🔴 Read this before "improving" the rendering: the whole value of this screen
// is that it distinguishes a zero from an unknown. Appendix J treats them
// differently and the printed form carries a Title 18 U.S.C. 1001 notice. Do not
// add a `|| 0`, a `?? 0`, or a "cleaner" empty-cell fallback anywhere below.
// ============================================

const MisDraftState = { data: null, year: null, loading: false, attested: false, expandedCell: null };

function _misDefaultYear() {
  // The MIS covers a COMPLETED calendar year — §382.403(b) has you filing by
  // March 15 for the year just ended. Default to last year, not this one.
  return new Date().getFullYear() - 1;
}

function showMisDraftModal() {
  MisDraftState.data = null;
  MisDraftState.year = _misDefaultYear();
  MisDraftState.attested = false;
  MisDraftState.expandedCell = null;

  const existing = document.getElementById('mis-draft-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'mis-draft-modal';
  modal.className = 'modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
  modal.innerHTML = `
    <div style="background:#fff;border-radius:var(--radius-lg);max-width:1100px;width:100%;height:100vh;height:90dvh;display:flex;flex-direction:column;overflow:hidden;">
      <div style="padding:14px 16px;border-bottom:1px solid var(--border-light);display:flex;align-items:center;gap:8px;flex-shrink:0;">
        <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">bar_chart</span>
        <div style="flex:1;min-width:0;">
          <h3 style="margin:0;font-size:1.05rem;font-weight:700;">MIS Calendar-Year Summary — draft</h3>
          <div style="font-size:0.7rem;color:var(--text-secondary);">49 CFR part 40, appendix J · Form DOT F 1385 (Rev. 4/2019) · OMB 2105-0529</div>
        </div>
        <button class="btn-icon" onclick="document.getElementById('mis-draft-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div id="mis-draft-body" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px;"></div>
    </div>`;
  document.body.appendChild(modal);
  renderMisDraftBody();
}

function renderMisDraftBody() {
  const body = document.getElementById('mis-draft-body');
  if (!body) return;
  const d = MisDraftState.data;

  const yearPicker = `
    <div style="display:flex;align-items:flex-end;gap:10px;flex-wrap:wrap;margin-bottom:14px;">
      <div>
        <label style="display:block;font-size:0.72rem;color:var(--text-secondary);margin-bottom:3px;">Calendar year covered by this report</label>
        <select id="mis-year" class="form-control" style="width:150px;" onchange="window.ComplianceModule.setMisYear(this.value)">
          ${[0, 1, 2, 3, 4].map(n => {
            const y = _misDefaultYear() - n;
            return `<option value="${y}" ${y === MisDraftState.year ? 'selected' : ''}>${y}</option>`;
          }).join('')}
        </select>
      </div>
      <button class="btn btn-primary" onclick="window.ComplianceModule.buildMisDraft()" ${MisDraftState.loading ? 'disabled' : ''}>
        <span class="material-icons" style="font-size:15px;">calculate</span> ${MisDraftState.loading ? 'Working…' : 'Build draft'}
      </button>
    </div>`;

  if (!d) {
    body.innerHTML = yearPicker + `
      <div style="background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 14px;font-size:0.78rem;color:var(--text-primary);line-height:1.55;">
        <div style="font-weight:700;margin-bottom:5px;">What this does — and what it deliberately will not do.</div>
        This builds a <strong>draft</strong> of the appendix J form from the drug and alcohol test records already in DispatchDeck.
        It shows every number with the exact records behind it, so you can check each one before anyone signs.
        <div style="margin-top:8px;">Where DispatchDeck cannot derive a figure with certainty, it prints
        <strong>—</strong> and tells you why. It never prints a zero to fill a gap: on this form a zero and an unknown are different,
        and the signed form carries a Title 18 U.S.C. 1001 notice.</div>
        <div style="margin-top:8px;">DispatchDeck does not and cannot submit anything to FMCSA. Under
        §382.403(b) you only file when FMCSA notifies you in January, and then by March 15.</div>
      </div>`;
    return;
  }

  const blockerHtml = d.blockers.length === 0 ? '' : `
    <div style="background:#FEF2F2;border:1px solid #FCA5A5;border-radius:8px;padding:12px 14px;margin-bottom:14px;">
      <div style="font-weight:700;font-size:0.82rem;color:#991B1B;display:flex;align-items:center;gap:6px;margin-bottom:7px;">
        <span class="material-icons" style="font-size:17px;">report_problem</span>
        Not ready to file — ${d.blockers.length} thing${d.blockers.length === 1 ? '' : 's'} must be resolved by a person
      </div>
      ${d.blockers.map(b => `
        <div style="font-size:0.76rem;color:var(--text-primary);line-height:1.5;padding:5px 0;border-top:1px solid #FECACA;">
          <strong>${escapeHtml(_misBlockerTitle(b.code))}</strong> — ${escapeHtml(b.message)}
          ${b.recordIds && b.recordIds.length ? `<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:3px;">Records: <code>${b.recordIds.slice(0, 25).map(escapeHtml).join(', ')}</code>${b.recordIds.length > 25 ? ` +${b.recordIds.length - 25} more` : ''}</div>` : ''}
        </div>`).join('')}
    </div>`;

  const warnHtml = !d.warnings.length ? '' : `
    <div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;padding:10px 14px;margin-bottom:14px;">
      <div style="font-weight:700;font-size:0.8rem;color:#92400E;margin-bottom:5px;">Worth a look before you sign</div>
      ${d.warnings.map(w => `<div style="font-size:0.75rem;color:var(--text-primary);line-height:1.5;padding:2px 0;">• ${escapeHtml(w)}</div>`).join('')}
    </div>`;

  body.innerHTML = yearPicker + blockerHtml + warnHtml
    + _misSectionI(d) + _misSectionII(d) + _misAttestationBox(d)
    + _misGrid('III. Drug Testing Data', d.drugTesting, 'drug')
    + _misGrid('IV. Alcohol Testing Data', d.alcoholTesting, 'alc')
    + _misFooter(d);
}

function _misBlockerTitle(code) {
  return ({
    NO_RECORDS_FOR_YEAR: 'No records for this year',
    NO_ALCOHOL_SOURCE: 'No alcohol data exists',
    UNMAPPABLE_TEST_TYPE: 'A test reason the form has no row for',
    UNPARSEABLE_TEST_DATE: 'A test date that cannot be placed in a year',
    UNKNOWN_RESULT_VALUE: 'An unrecognised result value',
    SUSPECTED_DUPLICATES: 'Possible duplicate records',
    ARITHMETIC_MISMATCH: "The form's own arithmetic check failed",
    TOTAL_MISMATCH: 'A column total disagrees with its rows',
    IMPLAUSIBLE_VOLUME: 'Implausible number of tests',
    IMPLAUSIBLE_PER_EMPLOYEE: 'Implausible tests per employee',
    NON_COUNT_VALUE: 'A computed value is not a whole count',
  })[code] || code;
}

function _misField(label, value) {
  return `<div style="padding:4px 0;font-size:0.76rem;">
    <span style="color:var(--text-secondary);">${escapeHtml(label)}:</span>
    <span style="color:var(--text-primary);font-weight:600;">${value == null || value === ''
      ? '<span style="color:#B45309;font-weight:600;">— you must enter this</span>'
      : escapeHtml(String(value))}</span>
  </div>`;
}

function _misSectionI(d) {
  const e = d.employer;
  return `
    <div style="border:1px solid var(--border-light);border-radius:8px;padding:12px 14px;margin-bottom:12px;">
      <div style="font-weight:700;font-size:0.85rem;margin-bottom:6px;">I. Employer</div>
      ${_misField('Company name', e.companyName)}
      ${_misField('Doing business as', e.dbaName)}
      ${_misField('Address', e.address)}
      ${_misField('USDOT #', e.dotNumber)}
      ${_misField('Certifying official', e.certifyingOfficial)}
      ${_misField('Date certified', e.dateCertified)}
      <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:7px;line-height:1.5;border-top:1px solid var(--border-light);padding-top:7px;">
        The certifying official and their signature are never filled in by DispatchDeck. Under §382.403(e) a company
        official must certify the report even when a C/TPA prepared it, and §382.403(c) requires everything except
        that signature to be typed.
      </div>
    </div>`;
}

function _misSectionII(d) {
  const c = d.coveredEmployees.totalSafetySensitive;
  return `
    <div style="border:1px solid var(--border-light);border-radius:8px;padding:12px 14px;margin-bottom:12px;">
      <div style="font-weight:700;font-size:0.85rem;margin-bottom:6px;">II. Covered Employees</div>
      <div style="font-size:0.78rem;">
        (A) Total safety-sensitive employees in all categories:
        <strong style="color:#B45309;">— you must enter this</strong>
      </div>
      <div style="font-size:0.73rem;color:var(--text-secondary);margin-top:6px;line-height:1.55;">
        ${escapeHtml(c.reason || '')}
      </div>
    </div>`;
}

function _misAttestationBox(d) {
  // The ONLY thing that turns an empty row from "unknown" into a real zero, and
  // it takes a deliberate human act. Default off, re-asked on every rebuild.
  return `
    <div style="background:${d.completenessAttested ? '#F0FDF4' : '#F8FAFC'};border:1px solid ${d.completenessAttested ? '#86EFAC' : 'var(--border-light)'};border-radius:8px;padding:11px 14px;margin-bottom:14px;">
      <label style="display:flex;align-items:flex-start;gap:9px;cursor:pointer;">
        <input type="checkbox" id="mis-attest" ${d.completenessAttested ? 'checked' : ''}
               onchange="window.ComplianceModule.setMisAttestation(this.checked)"
               style="margin-top:2px;flex-shrink:0;">
        <span style="font-size:0.77rem;color:var(--text-primary);line-height:1.55;">
          <strong>DispatchDeck holds every DOT drug and alcohol test for ${d.form.calendarYear}.</strong>
          Tick this only if it is true. Until you do, a test type with no records on file is shown as
          <strong>unknown</strong> rather than zero — because "nothing recorded here" is not the same as
          "no such test happened", and only you know which one it is.
        </span>
      </label>
    </div>`;
}

function _misCell(cell, cellId) {
  if (!cell || cell.status !== 'derived') {
    const reason = (cell && cell.reason) || 'Not derivable.';
    return `<td class="mis-td mis-unknown" title="${window.escAttr ? window.escAttr(reason) : ''}"
      onclick="window.ComplianceModule.toggleMisCell('${window.escAttr ? window.escAttr(cellId) : cellId}')">—</td>`;
  }
  const n = cell.value;
  return `<td class="mis-td mis-derived" onclick="window.ComplianceModule.toggleMisCell('${window.escAttr ? window.escAttr(cellId) : cellId}')"
    title="${cell.sourceIds.length} record(s) — click to show them">${n}</td>`;
}

function _misGrid(title, section, prefix) {
  const cols = section.columns;
  const rows = [...section.rows, { key: 'TOTAL', label: 'TOTAL', cells: section.total.cells }];

  const head = `<tr>
    <th class="mis-th mis-rowhead">Type of Test</th>
    ${cols.map(c => `<th class="mis-th" title="${window.escAttr ? window.escAttr(c.label) : ''}">${c.col}</th>`).join('')}
  </tr>`;

  const bodyRows = rows.map(r => `
    <tr${r.key === 'TOTAL' ? ' style="font-weight:700;background:var(--bg-secondary);"' : ''}>
      <td class="mis-td mis-rowhead">${escapeHtml(r.label)}</td>
      ${cols.map(c => _misCell(r.cells[c.key], `${prefix}|${r.key}|${c.key}`)).join('')}
    </tr>`).join('');

  const expanded = MisDraftState.expandedCell && MisDraftState.expandedCell.startsWith(prefix + '|')
    ? _misCellDetail(section, prefix) : '';

  return `
    <div style="border:1px solid var(--border-light);border-radius:8px;padding:12px 14px;margin-bottom:12px;">
      <div style="font-weight:700;font-size:0.85rem;margin-bottom:8px;">${escapeHtml(title)}</div>
      <div style="overflow-x:auto;">
        <table class="mis-table"><thead>${head}</thead><tbody>${bodyRows}</tbody></table>
      </div>
      <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:6px;line-height:1.55;">
        Columns are numbered as printed on the form — hover a number for its heading.
        <strong style="color:#B45309;">A dash is not an error.</strong> It means DispatchDeck does not hold enough
        to fill that box, so it left it for you rather than guessing. Click any cell to see the records behind a
        number, or the reason a box is blank.
      </div>
      ${expanded}
      <details style="margin-top:8px;">
        <summary style="font-size:0.72rem;color:var(--text-secondary);cursor:pointer;">Column headings</summary>
        <div style="font-size:0.7rem;color:var(--text-secondary);line-height:1.6;margin-top:5px;">
          ${cols.map(c => `<div><strong>${c.col}.</strong> ${escapeHtml(c.label)}</div>`).join('')}
        </div>
      </details>
    </div>`;
}

function _misCellDetail(section, prefix) {
  const [, rowKey, colKey] = MisDraftState.expandedCell.split('|');
  const row = rowKey === 'TOTAL'
    ? { label: 'TOTAL', cells: section.total.cells }
    : section.rows.find(r => r.key === rowKey);
  if (!row) return '';
  const cell = row.cells[colKey];
  const col = section.columns.find(c => c.key === colKey);
  if (!cell || !col) return '';

  if (cell.status !== 'derived') {
    return `<div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:7px;padding:10px 12px;margin-top:9px;font-size:0.76rem;line-height:1.55;">
      <strong>${escapeHtml(row.label)} · column ${col.col} (${escapeHtml(col.label)})</strong> — why this is blank:<br>
      ${escapeHtml(cell.reason || '')}
    </div>`;
  }

  const ids = cell.sourceIds || [];
  const byId = {};
  for (const r of (ComplianceState.drugTests || [])) byId[r.id] = r;
  const listed = ids.slice(0, 60).map(id => {
    const t = byId[id];
    if (!t) return `<div style="padding:2px 0;"><code>${escapeHtml(id)}</code> <span style="color:var(--text-secondary);">(not in the loaded page of records)</span></div>`;
    return `<div style="padding:2px 0;">${escapeHtml(t.testDate || '')} — ${escapeHtml(t.personName || t.personId || '')} — ${escapeHtml(t.testType || '')} — ${escapeHtml(t.result || '')}</div>`;
  }).join('');

  return `<div style="background:#F0F9FF;border:1px solid #BAE6FD;border-radius:7px;padding:10px 12px;margin-top:9px;font-size:0.76rem;">
    <strong>${escapeHtml(row.label)} · column ${col.col} (${escapeHtml(col.label)}) = ${cell.value}</strong>
    <div style="margin-top:5px;color:var(--text-secondary);">Built from ${ids.length} record(s):</div>
    <div style="max-height:220px;overflow-y:auto;margin-top:4px;font-family:ui-monospace,monospace;font-size:0.72rem;">
      ${listed || '<div style="color:var(--text-secondary);">No records — this is an attested zero.</div>'}
      ${ids.length > 60 ? `<div style="color:var(--text-secondary);">…and ${ids.length - 60} more</div>` : ''}
    </div>
  </div>`;
}

function _misFooter(d) {
  return `
    <div style="border:1px solid var(--border-light);border-radius:8px;padding:12px 14px;background:var(--bg-secondary);">
      <div style="font-size:0.78rem;font-weight:700;margin-bottom:5px;">
        ${d.readyToFile
          ? 'Every figure on this draft was derived.'
          : `${d.undeterminedCellCount} figure${d.undeterminedCellCount === 1 ? '' : 's'} on this form could not be derived from DispatchDeck.`}
      </div>
      <div style="font-size:0.74rem;color:var(--text-secondary);line-height:1.55;">
        ${escapeHtml(d.disclaimer || '')}
      </div>
      <div style="font-size:0.72rem;color:var(--text-secondary);line-height:1.55;margin-top:7px;">
        <strong>When this is due:</strong> ${escapeHtml(d.filing.trigger)} If notified, it is due ${escapeHtml(d.filing.dueDate)}.
      </div>
      <div style="display:flex;gap:8px;margin-top:11px;flex-wrap:wrap;">
        <button class="btn btn-secondary btn-sm" onclick="window.print()">
          <span class="material-icons" style="font-size:14px;">print</span> Print this draft
        </button>
        <button class="btn btn-secondary btn-sm" onclick="window.ComplianceModule.showAddProgramDoc('MIS_REPORT')">
          <span class="material-icons" style="font-size:14px;">upload_file</span> Upload the filed copy
        </button>
      </div>
      <div style="font-size:0.7rem;color:#B45309;margin-top:8px;">
        DispatchDeck produces no signable MIS form. Transcribe these figures onto the official form only after
        checking each one against the underlying records.
      </div>
    </div>`;
}

async function buildMisDraftAction() {
  if (MisDraftState.loading) return;
  MisDraftState.loading = true;
  MisDraftState.expandedCell = null;
  renderMisDraftBody();
  try {
    const fn = httpsCallable(functions, 'generateAnnualMisSummaryDraft');
    const res = await fn({
      tenantId: ComplianceState.tenantId || window.AppState?.tenant,
      calendarYear: MisDraftState.year,
      completenessAttested: MisDraftState.attested === true,
    });
    MisDraftState.data = res.data;
  } catch (err) {
    console.error('[Compliance] MIS draft error:', err);
    MisDraftState.data = null;
    window.showToast?.(err?.message || 'Could not build the MIS draft', 'error');
  } finally {
    MisDraftState.loading = false;
    renderMisDraftBody();
  }
}

// ============================================
// PUBLIC API
// ============================================
window.ComplianceModule = {
  // TASK-1320 — Break-in-Service work history (opener delegates to window.HRModule).
  openBisGapAccount(personId) { return openBisGapAccount(personId); },
  // Action items (partner-set tasks)
  updateActionItemStatus(itemId, newStatus) { return updateActionItemStatus(itemId, newStatus); },
  openCarrierActionNote(itemId) { return openCarrierActionNote(itemId); },
  openActionItemSubject(personId) { return openActionItemSubject(personId); }, // TASK-10154
  toggleFinishedActionItems() { return toggleFinishedActionItems(); }, // TASK-10153
  submitCarrierNote(itemId) { return submitCarrierNote(itemId); },
  // TASK-9843 — registered states (Company tab card + editor)
  openRegisteredStatesEditor() { return openRegisteredStatesEditor(); },
  rsEditorAddRow(state, kind, expiry, note) { return rsEditorAddRow(state, kind, expiry, note); },
  rsEditorRemoveRow(id) { return rsEditorRemoveRow(id); },
  saveRegisteredStates() { return saveRegisteredStates(); },

  setTab(tab) {
    ComplianceState.activeTab = tab;
    ComplianceState.actFresh = 1; // TASK-10095 S1b — entering Activity re-reads; a stream re-render does not
    ComplianceState.searchQuery = '';
    ComplianceState.filterStatus = 'all';
    if (tab !== 'people') {
      ComplianceState.peopleSubTab = 'all';
      // TASK-933 — leaving the People tab drops back to the active-only default, so the
      // next visit opens on the daily view rather than a stale "terminated" scope.
      ComplianceState.peopleEmpFilter = 'active';
    }
    renderComplianceContent();
  },
  // TASK-1201 — Company Documents (Company tab; hrResources store)
  openCompanyDocUpload() { openCompanyDocUpload(); },
  submitCompanyDocUpload() { return submitCompanyDocUpload(); },
  editCompanyDoc(resourceId) { editCompanyDoc(resourceId); },
  saveCompanyDocMeta(resourceId) { return saveCompanyDocMeta(resourceId); },
  toggleCompanyDocShare(resourceId, share) { return toggleCompanyDocShare(resourceId, share); },
  openCompanyDoc(resourceId) { return openCompanyDoc(resourceId); },
  deleteCompanyDoc(resourceId) { return deleteCompanyDoc(resourceId); },
  setPeopleSubTab(sub) {
    ComplianceState.peopleSubTab = sub;
    ComplianceState.searchQuery = '';
    // TASK-933 — the Drivers scorecard is an active-only measure and shows no employment
    // pills, so entering it snaps the scope back to active (otherwise the Drivers pill
    // count would describe a scope the scorecard doesn't honor).
    if (sub === 'drivers') ComplianceState.peopleEmpFilter = 'active';
    renderComplianceContent();
  },
  // TASK-933 — employment scope for the People tab (active / inactive / terminated / all).
  setPeopleEmpFilter(scope) {
    ComplianceState.peopleEmpFilter = ['active', 'inactive', 'terminated', 'all'].includes(scope) ? scope : 'active';
    renderComplianceContent();
  },
  setFilter(status) {
    // Toggle: clicking active filter resets to 'all'
    ComplianceState.filterStatus = ComplianceState.filterStatus === status ? 'all' : status;
    renderComplianceContent();
  },
  clearSearch() {
    ComplianceState.searchQuery = '';
    renderComplianceContent();
  },
  showPersonDetail(personId) {
    showPersonDetail(personId);
  },
  // TASK-1103 — open the completed §391.21 application (via person.candidateId).
  viewPersonApplication(personId) {
    return viewPersonApplication(personId);
  },
  // TASK-1099 — §391.23 Safety Performance History Investigation pop-out.
  openPersonPeiInvestigation(personId) {
    return openPersonPeiInvestigation(personId);
  },
  // TASK-1049 — per-employee Required-Training exemption (Mark N/A / Undo).
  toggleTrainingExemptionCompliance(personId, itemId, exempt) {
    return toggleTrainingExemptionCompliance(personId, itemId, exempt);
  },
  // TASK-980 — flag / unflag a driver for follow-up (needs-attention marker).
  // TASK-9424 — add one / clear one named flag (reason required).
  addDriverFlag(personId) {
    return addDriverFlag(personId);
  },
  clearDriverFlag(personId, flagId) {
    return clearDriverFlag(personId, flagId);
  },
  // TASK-832 — open the shared minimized BG report viewer (compliance mode).
  openBgReport(personId, reportId) {
    openBgReport(personId, reportId);
  },
  // TASK-1384 — withdraw an open partner-routed MVR/BG request (person-typed).
  cancelMvrPartnerRequest(personId) {
    return cancelMvrPartnerRequest(personId);
  },
  // TASK-969 — open the standalone printable MVR viewer (compliance mode).
  // TASK-1555 — bgRecordId routes a post-hire order to its candidate-keyed doc.
  openMvrReport(personId, reportId, mvrPurpose, bgRecordId) {
    openMvrReport(personId, reportId, mvrPurpose, bgRecordId || null);
  },
  showVehicleDetail(vehicleId) {
    showVehicleDetail(vehicleId);
  },
  setAssetSubTab(tab) {
    ComplianceState.assetSubTab = tab;
    renderComplianceContent();
  },
  // +Add handlers — reuse Roster's modals (mounted to document.body on init)
  openAddPerson() {
    if (window.RosterModule?.ensureModalsMounted) window.RosterModule.ensureModalsMounted();
    window.RosterModule?.showAddPerson?.();
  },
  openAddVehicle() {
    if (window.RosterModule?.ensureModalsMounted) window.RosterModule.ensureModalsMounted();
    window.RosterModule?.showAddTruck?.();
  },
  openAddTrailer() {
    if (window.RosterModule?.ensureModalsMounted) window.RosterModule.ensureModalsMounted();
    window.RosterModule?.showAddTrailer?.();
  },
  closeDetail() {
    const modal = document.getElementById('compliance-detail-modal');
    if (modal) modal.classList.add('hidden');
    ComplianceState.selectedPersonId = null;
    ComplianceState.selectedVehicleId = null;
    ComplianceState.editingDriverDetailsId = null; // TASK-812 — never reopen in edit mode
    // TASK-10154 — full identity values never outlive the form: drop the cache,
    // cancel any in-flight Edit fetch, and (partner session) empty the hidden
    // modal so revealed values / pre-filled inputs are not left in the DOM.
    _CompPartnerId.edit.clear();
    _CompPartnerId.editGen++;
    if (modal && _compPartnerTenantId()) modal.innerHTML = '';
  },
  // DL/med-card/training person-modal actions — use the COMPLIANCE-LOCAL handlers
  // directly (TASK-781). Do NOT delegate to HRModule here: the old
  // `window.HRModule?.fn?.(...) || localFn(...)` idiom was broken two ways —
  // (a) HR upload/remove handlers are async → return a truthy Promise, so the
  // `||` local fallback was DEAD and the HR handler ran against an uninitialized
  // HRState (HRState.tenantId/people are only set by initHR, which never runs
  // for a compliance-only carrier) → silent no-op; (b) HR modal-openers return
  // undefined → the `||` ALSO fired the local opener → TWO stacked modals, and
  // HR's save wrote HRState without refreshing the compliance view → "doesn't
  // update". The compliance-local handlers key off ComplianceState.tenantId
  // (always set in the compliance module) and refresh via showPersonDetail().
  uploadDlDocument(personId, credType) { return uploadDlDocument(personId, credType); },
  removeDlDocument(personId, credType) { return removeDlDocument(personId, credType); },
  uploadMedCardDocument(personId) { return uploadMedCardDocument(personId); },
  removeMedCardDocument(personId) { return removeMedCardDocument(personId); },
  removeMedCertPdf(personId) { return removeMedCertPdf(personId); }, // TASK-647-FF — clear a cert-source med card
  updateMedCardExpiry(personId, dateValue) { return updateMedCardExpiry(personId, dateValue); },
  // TASK-812 — driver-qualification identity/CDL data entry (compliance-local)
  editDriverDetails(personId) { return editDriverDetails(personId); },
  cancelDriverDetailsEdit(personId) { return cancelDriverDetailsEdit(personId); },
  saveDriverDetails(personId) { return saveDriverDetails(personId); },
  // TASK-811 — MVR record (free, compliance-local) + request-from-partner (HR delegate)
  recordMvrPull(personId) { return recordMvrPull(personId); },
  saveMvrPull(personId) { return saveMvrPull(personId); },
  openMvrGoodFaith(personId) { return openMvrGoodFaith(personId); }, // TASK-9822
  // TASK-1048 (818-S4) — MVR PDF upload + Smart-Tools autofill (metered parseMvrPdf)
  onMvrFilePicked(event) { return onMvrFilePicked(event); },
  autofillMvrFromPdf(personId) { return autofillMvrFromPdf(personId); },
  requestMvrFromPartner(personId) { return requestMvrFromPartner(personId); },
  // TASK-1045 (818-S1) — Clearinghouse query log + §391.25 annual review
  // (compliance-local; write shapes mirror HR, mapping/next-due via HRModule)
  openClearinghouseQueryModal(personId) { return openClearinghouseQueryModal(personId); },
  saveClearinghouseQuery(personId) { return saveClearinghouseQueryCompliance(personId); },
  openAnnualReviewModal(personId) { return openAnnualReviewModalCompliance(personId); },
  saveAnnualReview(personId) { return saveAnnualReviewCompliance(personId); },
  // TASK-1623 — §383.31 driver notification of conviction (tokenized, driver-signed).
  // NOT the §391.25(c)(2) review note: different regulation, different signer.
  requestConvictionNotice(personId) { return requestConvictionNoticeCompliance(personId); },
  // TASK-1623 — derived §391.25(b) review state, exposed so other in-bundle
  // surfaces (hr.js) can render the same flag without a second implementation.
  // Byte-mirrored in compliance-partner.js as `_cpMvrReviewState` (separate realm).
  mvrReviewState(person) { return _compMvrReviewState(person); },
  // TASK-1047 (818-S3) — Medical Exam Detail recorder + self-upload SMS link
  // (compliance-local; person-doc write shape + complianceTimeline row mirror HR)
  openMedExamModalCompliance(personId) { return openMedExamModalCompliance(personId); },
  saveMedExam(personId) { return saveMedExamCompliance(personId); },
  sendMedUploadLinkCompliance(personId) { return sendMedUploadLinkCompliance(personId); },
  // TASK-1067 — §382.601(d) Send/Resend D&A policy-receipt signing link
  sendDaReceiptLink(personId) { return sendDaReceiptLinkCompliance(personId); },
  openDaReceiptBulkSend() { return openDaReceiptBulkSendCompliance(); }, // TASK-9145
  // TASK-894 — SPH/Employment Verification + DQ Packet from the compliance modal (HR delegate, hydrated)
  generateSphResponse(personId) { return generateSphFromCompliance(personId); },
  openDqPacketViewer(personId) { return generateDqPacketFromCompliance(personId); },
  // TASK-970 — Full DQ File ZIP (owner/admin) from the compliance driver detail
  downloadFullDqFile(personId) { return downloadFullDqFileCompliance(personId); },
  // TASK-1046 (818-S2) — upload a DQ document (compliance-module members).
  uploadDqDocument(personId) { return uploadDqDocument(personId); },
  addTraining(personId) { showTrainingModal(personId, -1); },
  editTraining(personId, index, entryId) { showTrainingModal(personId, index, entryId); },
  deleteTraining(personId, index, entryId) { deleteTraining(personId, index, entryId); },
  saveTraining(personId, editIndex) { saveTraining(personId, editIndex); },
  // Drug test methods — redirected to HR
  getDrugTests() { return ComplianceState.drugTests || []; },
  showDrugTestModal(personId) { window.HRModule?.showDrugTestModal?.(personId) || showDrugTestModal(personId); },
  showAddDrugTest(personId) { window.HRModule?.showDrugTestModal?.(personId) || showDrugTestModal(personId); },
  viewDrugTestDocument(testId, kind) {
    // TASK-10081 — the carrier's OWN row first (HRState is empty unless the HR
    // view subscribed); HR delegate only for a row this view does not hold.
    const test = (ComplianceState.drugTests || []).find(t => t.id === testId);
    if (!test) { window.HRModule?.viewDrugTestDocument?.(testId, kind); return; }
    // TASK-9855 slice 1 — open by PATH through the Storage SDK (rules decide);
    // the stored token URL is never opened. Same strict opener as hr.js.
    const urlKey = kind === 'ccf' ? 'ccfDocumentUrl' : 'documentUrl';
    const fref = window.StorageFile?.fileRef?.(test, { urlKey }) || null;
    if (fref && fref.path && typeof window._hrOpenStorageStrict === 'function') {
      window._hrOpenStorageStrict({ path: fref.path, bucket: fref.bucket }).catch((e) => {
        window.showToast?.(e?.sfReason === 'denied' ? 'You do not have access to this document' : (e?.message || 'Could not open the document'), 'error');
      });
      return;
    }
    const url = fref && !fref.path ? _cpSafeUrl(fref.url) : ''; // TASK-1326 — non-Storage link only
    if (url) window.open(url, '_blank', 'noopener');
    else window.showToast?.('No document on file', 'error');
  },
  deleteDrugTestRecord(testId) { window.HRModule?.deleteDrugTestRecord?.(testId) || deleteDrugTestRecord(testId); },
  handleDrugTestFile(event, kind) { window.HRModule?.handleDrugTestFile?.(event, kind) || handleDrugTestFileUpload(event, kind); },
  removeDrugTestFile(kind) {
    if (window.HRModule?.removeDrugTestFile) { window.HRModule.removeDrugTestFile(kind); return; }
    if (kind === 'ccf') pendingDrugTestCcfFile = null; else pendingDrugTestFile = null;
    const preview = document.getElementById(kind === 'ccf' ? 'drug-test-ccf-preview' : 'drug-test-file-preview');
    if (preview) preview.innerHTML = '';
  },
  saveDrugTest(personId) { window.HRModule?.saveDrugTest?.(personId) || saveDrugTestRecord(personId); },
  deleteDrugTest(testId) { window.HRModule?.deleteDrugTestRecord?.(testId) || deleteDrugTestRecord(testId); },
  // D&A program docs + audit packet
  showAddProgramDoc(categoryKey) { showAddProgramDocModal(categoryKey); },
  handleProgramDocFile(event) { handleProgramDocFileUpload(event); },
  saveProgramDoc(categoryKey) { return saveProgramDocRecord(categoryKey); },
  deleteProgramDoc(docId) { return deleteProgramDocRecord(docId); },
  showAuditPacketModal() { showAuditPacketGenerationModal(); },
  generateAuditPacket() { return generateAuditPacketAction(); },
  // TASK-1606 — appendix J MIS calendar-year summary DRAFT
  showMisDraft() { showMisDraftModal(); },
  buildMisDraft() { return buildMisDraftAction(); },
  setMisYear(y) {
    const n = parseInt(y, 10);
    if (!Number.isInteger(n)) return;
    MisDraftState.year = n;
    // Changing the year invalidates the draft AND the attestation — an
    // attestation is about one specific year, never carried forward.
    MisDraftState.data = null;
    MisDraftState.attested = false;
    renderMisDraftBody();
  },
  setMisAttestation(on) {
    MisDraftState.attested = on === true;
    // Rebuild rather than re-render: the attestation changes what the SERVER is
    // allowed to derive, so a client-side repaint would show a stale grid.
    return buildMisDraftAction();
  },
  toggleMisCell(cellId) {
    MisDraftState.expandedCell = MisDraftState.expandedCell === cellId ? null : cellId;
    renderMisDraftBody();
  },
  // TASK-972 S4 — DOT auditor access links. Delegates to the shared modal in
  // hr.js via window.HRModule at call time (pitfall #1: no cross ?v= import).
  // Compliance-only carriers have no HR module in the nav, but hr.js is
  // statically imported by app.js, so window.HRModule is always populated.
  openDotAuditAccess() {
    if (!_compIsOwnerOrAdmin()) {
      window.showToast?.('Only the owner or an admin can manage auditor access links', 'error');
      return;
    }
    if (window.HRModule?.openDotAuditAccess) {
      window.HRModule.openDotAuditAccess({
        tenantId: ComplianceState.tenantId || window.AppState?.tenant,
        people: ComplianceState.people || [],
      });
    } else {
      window.showToast?.('Audit access tools are still loading — try again in a moment.', 'error');
    }
  },
  // TASK-343 Slice 4 — SAP/RTD category management
  setDaCategory(personId, value) { return setPersonDaCategory(personId, value); },
  setDaCategoryFilter(value) { return setDaCategoryFilter(value); },
  // TASK-343 Slice 5 — quarterly random-roster chip click handler
  openQuarterlyRandomSelection(year, quarter) { return openQuarterlyRandomSelectionModal(year, quarter); },
  // Sanitized URL opener — blocks javascript:/data:/file: schemes that could
  // execute via window.open if a Firestore-controlled URL ever gets tampered.
  openSafePdf(url) {
    // TASK-9855 slice 1 — a Storage object (D&A program docs) opens through the
    // Storage SDK on the path inside the URL (rules: canAccessCompliance), not
    // through the bearer URL. Any other http(s) link keeps the old behaviour.
    if (window.StorageFile && window.StorageFile.isStorageUrl(url)) {
      window.StorageFile.open(url);
      return;
    }
    try {
      const u = new URL(url);
      if (u.protocol !== 'https:' && u.protocol !== 'http:') {
        window.showToast?.('Blocked unsafe document URL', 'error');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      window.showToast?.('Invalid document URL', 'error');
    }
  },
  // IFTA methods
  iftaPrevQuarter() {
    ComplianceState.iftaQuarter--;
    if (ComplianceState.iftaQuarter < 1) {
      ComplianceState.iftaQuarter = 4;
      ComplianceState.iftaYear--;
    }
    renderComplianceContent();
  },
  iftaGoQuarter(year, quarter) {
    const y = Number(year); const q = Number(quarter);
    if (!Number.isInteger(y) || !Number.isInteger(q) || q < 1 || q > 4) return;
    ComplianceState.iftaYear = y;
    ComplianceState.iftaQuarter = q;
    renderComplianceContent();
  },
  iftaNextQuarter() {
    ComplianceState.iftaQuarter++;
    if (ComplianceState.iftaQuarter > 4) {
      ComplianceState.iftaQuarter = 1;
      ComplianceState.iftaYear++;
    }
    renderComplianceContent();
  },
  showAddFuel() {
    showFuelModal(null);
  },
  editFuel(id) {
    const entry = ComplianceState.fuelPurchases.find(e => e.id === id);
    if (entry && entry.source === 'expense') { window.showToast?.('This came from an approved expense. Change it in Expenses.', 'info'); return; }
    if (entry) showFuelModal(entry);
  },
  saveFuel(id) {
    const entry = id ? ComplianceState.fuelPurchases.find(e => e.id === id) : null;
    if (entry && entry.source === 'expense') { window.showToast?.('This came from an approved expense. Change it in Expenses.', 'info'); return; }
    saveFuelPurchase(id || null);
  },
  deleteFuel(id) {
    const entry = ComplianceState.fuelPurchases.find(e => e.id === id);
    if (entry && entry.source === 'expense') { window.showToast?.('This came from an approved expense. Reject or change it in Expenses.', 'info'); return; }
    deleteFuelPurchase(id);
  },
  // DVIR/JSA accountability actions
  resetMissedDvirCount(personId) { resetMissedDvirCount(personId); },
  resetMissedJsaCount(personId) { resetMissedJsaCount(personId); },
  deleteDvirFlag(flagId, personId) { deleteDvirFlag(flagId, personId); },
  deleteJsaFlag(flagId, personId) { deleteJsaFlag(flagId, personId); },
  resetAllDvirData(empId, empName) { resetAllDvirData(empId, empName); },
  showDotAuditModal() { return _dotAudit('showDotAuditModal'); },
  setDotAuditType(type) { return _dotAudit('setDotAuditType', type); },
  sendDotAuditReport() { return _dotAudit('sendDotAuditReport'); },
  syncFmcsa() { syncFmcsaData(); },
  editProfileField(fieldKey) { editProfileField(fieldKey); },
  saveProfileField(fieldKey) { saveProfileField(fieldKey); },
  handleIssueClick(action, id) {
    if (action === 'people' && id) {
      ComplianceState.activeTab = 'overview';
      renderComplianceContent();
      setTimeout(() => showPersonDetail(id), 100);
    } else if (action === 'assets') {
      ComplianceState.activeTab = 'assets';
      renderComplianceContent();
      if (id) setTimeout(() => showVehicleDetail(id), 100);
    } else if (action === 'drivers') {
      ComplianceState.activeTab = 'people';
      ComplianceState.peopleSubTab = 'drivers';
      ComplianceState.peopleEmpFilter = 'active'; // TASK-933 — scorecard is active-only
      renderComplianceContent();
    } else if (action === 'maintenance') {
      window.location.hash = '#/maintenance';
    } else if (action === 'safety') {
      ComplianceState.activeTab = 'safety';
      renderComplianceContent();
    } else if (action === 'profile') {
      // Company tab — carrier-wide credentials (UCR / MCS-150 / IRP / insurance).
      // Tab key is 'profile', not 'company' (see renderTabBar).
      ComplianceState.activeTab = 'profile';
      renderComplianceContent();
    }
  },

  // ============================================
  // HVUT / Form 2290 (tenant side)
  // ============================================
  onHvutYearChange(vehicleId) {
    // Re-render the vehicle detail modal so the form hydrates from the selected year's filing.
    showVehicleDetail(vehicleId);
  },
  onHvutCategoryChange(vehicleId) {
    const catSel = document.getElementById('hvut-t-cat-' + vehicleId);
    const amountEl = document.getElementById('hvut-t-amount-' + vehicleId);
    if (!catSel || !amountEl) return;
    const match = HVUT_CATEGORIES.find(c => c.cat === catSel.value);
    if (match && !amountEl.value) amountEl.value = match.amount.toFixed(2);
  },
  async saveHvut(vehicleId) {
    const tenantId = ComplianceState.tenantId;
    if (!tenantId) return;

    const yearEl = document.getElementById('hvut-t-year-' + vehicleId);
    const statusEl = document.getElementById('hvut-t-status-' + vehicleId);
    const catEl = document.getElementById('hvut-t-cat-' + vehicleId);
    const amountEl = document.getElementById('hvut-t-amount-' + vehicleId);
    const einEl = document.getElementById('hvut-t-ein-' + vehicleId);
    const filedEl = document.getElementById('hvut-t-filed-' + vehicleId);
    const acceptedEl = document.getElementById('hvut-t-accepted-' + vehicleId);
    const suspendedEl = document.getElementById('hvut-t-suspended-' + vehicleId);
    const schedEl = document.getElementById('hvut-t-sched-' + vehicleId);
    const notesEl = document.getElementById('hvut-t-notes-' + vehicleId);
    const msgEl = document.getElementById('hvut-t-status-msg-' + vehicleId);

    if (!yearEl) return;
    const taxYear = yearEl.value;
    const updates = {
      weightCategory: catEl.value || '',
      amountPaid: amountEl.value === '' ? null : parseFloat(amountEl.value),
      ein: einEl.value.trim(),
      filedDate: filedEl.value,
      acceptedDate: acceptedEl.value,
      status: statusEl.value || 'not_filed',
      notes: notesEl.value.trim(),
      suspended: suspendedEl.checked,
    };

    const file = schedEl.files?.[0];
    let schedule1Base64, schedule1ContentType;
    if (file) {
      if (file.type !== 'application/pdf') {
        if (msgEl) { msgEl.textContent = 'Schedule 1 must be a PDF.'; msgEl.style.color = '#DC2626'; }
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        if (msgEl) { msgEl.textContent = 'Schedule 1 exceeds 10MB.'; msgEl.style.color = '#DC2626'; }
        return;
      }
      // TASK-1383 — guarded shared reader. The await sits OUTSIDE the save's
      // try/catch below, so catch locally and land the error in the modal's
      // message line instead of an unhandled rejection.
      try {
        schedule1Base64 = await window.FileReadUtil.readAsBase64(file);
      } catch (err) {
        if (msgEl) { msgEl.textContent = err?.message || 'Could not read the Schedule 1 file.'; msgEl.style.color = '#DC2626'; }
        return;
      }
      schedule1ContentType = 'application/pdf';
    }

    if (msgEl) { msgEl.textContent = 'Saving…'; msgEl.style.color = 'var(--text-muted)'; }
    try {
      const { getFunctions, httpsCallable } = await import('https://www.gstatic.com/firebasejs/10.8.0/firebase-functions.js');
      const fn = httpsCallable(getFunctions(), 'upsertHvutFiling');
      await fn({
        tenantId,
        vehicleId,
        taxYear: String(taxYear),
        updates,
        ...(schedule1Base64 ? { schedule1Base64, schedule1ContentType } : {}),
      });
      if (msgEl) { msgEl.textContent = 'Saved ✓'; msgEl.style.color = '#059669'; }
      // onSnapshot will refresh the list automatically; re-render the modal
      setTimeout(() => showVehicleDetail(vehicleId), 500);
    } catch (err) {
      console.error('[saveHvut]', err);
      if (msgEl) { msgEl.textContent = 'Failed: ' + (err.message || 'Unknown error'); msgEl.style.color = '#DC2626'; }
    }
  },

  // TASK-1137 K5 — append a blank custom-compliance-date row in the edit modal.
  addVehicleCustomItem(vehicleId) {
    const rows = document.getElementById(`vc-cci-rows-${vehicleId}`);
    if (rows) {
      rows.insertAdjacentHTML('beforeend', _vcCciRowHtml({}));
      rows.querySelector('.vc-cci-row:last-child .vc-cci-label')?.focus();
    }
  },

  // TASK-749 — save the editable reg/ins/insp fields + upload any attached docs
  // via the updateVehicleCompliance callable (same path the partner portal uses).
  async saveVehicleCompliance(vehicleId) {
    const tenantId = ComplianceState.tenantId;
    if (!tenantId) return;
    const vehicle = [...ComplianceState.trucks, ...ComplianceState.trailers].find(v => v.id === vehicleId);
    const vehicleType = vehicle?._type === 'trailer' ? 'trailer' : 'truck';
    const g = (id) => document.getElementById(id + '-' + vehicleId);
    const msgEl = g('vc-msg');
    const updates = {
      registrationNumber: g('vc-regnum')?.value.trim() || '',
      // TASK-9814 follow-up: "Doesn't expire" clears the date (the date would otherwise keep alerting).
      registrationExpiry: g('vc-regnoexp')?.checked ? '' : (g('vc-regexp')?.value || ''),
      registrationNoExpiry: !!g('vc-regnoexp')?.checked, // TASK-9814
      ...(vehicleType === 'truck' && g('vc-irpexp') ? { irpExpiry: g('vc-irpexp').value || '' } : {}), // TASK-9813
      insuranceCompany: g('vc-inscomp')?.value.trim() || '',
      insurancePolicy: g('vc-inspol')?.value.trim() || '',
      insuranceExpiry: g('vc-insexp')?.value || '',
      inspectionExpiry: g('vc-inspexp')?.value || '',
      inspectionLocation: g('vc-insploc')?.value.trim() || '',
    };

    // TASK-1137 K5 — collect the custom compliance-date rows (label/date/alert). The
    // server validates + drops blank-label rows; we send the full edited set (replace).
    const cciRows = document.querySelectorAll(`#vc-cci-rows-${vehicleId} .vc-cci-row`);
    if (cciRows.length) {
      updates.customComplianceItems = Array.from(cciRows).map(row => ({
        id: row.getAttribute('data-cci-id') || '',
        label: (row.querySelector('.vc-cci-label')?.value || '').trim(),
        expiryDate: row.querySelector('.vc-cci-date')?.value || '',
        alertDays: parseInt(row.querySelector('.vc-cci-alert')?.value, 10) || 30,
      })).filter(it => it.label);
    } else {
      updates.customComplianceItems = []; // all rows removed → clear
    }

    // Collect at most one attached doc per save (mirrors the 2290 single-file UX).
    const OK_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];
    let docType, docBase64, docContentType, docFileName;
    for (const kind of ['registration', 'insurance', 'inspection', ...(vehicleType === 'truck' ? ['irp'] : [])]) { // TASK-9813
      const fileEl = document.getElementById('vc-' + kind + '-file-' + vehicleId);
      const file = fileEl?.files?.[0];
      if (!file) continue;
      if (!OK_TYPES.includes(file.type)) {
        if (msgEl) { msgEl.textContent = `${kind} doc must be a PDF, PNG, JPG or WebP.`; msgEl.style.color = '#DC2626'; }
        return;
      }
      if (file.size > 7 * 1024 * 1024) {
        if (msgEl) { msgEl.textContent = `${kind} doc exceeds 7MB.`; msgEl.style.color = '#DC2626'; }
        return;
      }
      docType = kind;
      docContentType = file.type;
      docFileName = file.name;
      // TASK-1383 — guarded shared reader. The await sits OUTSIDE the save's
      // try/catch below, so catch locally and land the error in the modal's
      // message line instead of an unhandled rejection.
      try {
        docBase64 = await window.FileReadUtil.readAsBase64(file);
      } catch (err) {
        if (msgEl) { msgEl.textContent = err?.message || `Could not read the ${kind} doc.`; msgEl.style.color = '#DC2626'; }
        return;
      }
      break; // one upload per Save click
    }

    if (msgEl) { msgEl.textContent = 'Saving…'; msgEl.style.color = 'var(--text-muted)'; }
    try {
      const { getFunctions, httpsCallable } = await import('https://www.gstatic.com/firebasejs/10.8.0/firebase-functions.js');
      const fn = httpsCallable(getFunctions(), 'updateVehicleCompliance');
      const res = await fn({
        tenantId, vehicleId, vehicleType, updates,
        ...(docBase64 ? { docType, docBase64, docContentType, docFileName } : {}),
      });
      if (!res || !res.data || res.data.ok !== true) throw new Error('Save not confirmed by server');
      if (msgEl) { msgEl.textContent = 'Saved ✓'; msgEl.style.color = '#059669'; }
      // onSnapshot refreshes the list; re-render the modal so the new doc + status show.
      setTimeout(() => showVehicleDetail(vehicleId), 500);
    } catch (err) {
      console.error('[saveVehicleCompliance]', err);
      if (msgEl) { msgEl.textContent = 'Failed: ' + (err.message || 'Unknown error'); msgEl.style.color = '#DC2626'; }
    }
  },

  // TASK-749 — remove one reg/ins/insp doc (drops it from the array + deletes the blob).
  // `docIndex` indexes into the vehicle's {kind}Docs[] — the URL is resolved from
  // state, never interpolated into inline JS (COD XSS fix).
  async removeVehicleComplianceDoc(vehicleId, kind, docIndex) {
    const tenantId = ComplianceState.tenantId;
    if (!tenantId) return;
    const vehicle = [...ComplianceState.trucks, ...ComplianceState.trailers].find(v => v.id === vehicleId);
    if (!vehicle) return;
    const arr = Array.isArray(vehicle[`${kind}Docs`]) ? vehicle[`${kind}Docs`] : [];
    // TASK-10086-P — the server matches by path first, then by the legacy url.
    const d = arr[docIndex];
    const path = window.StorageFile?.fileRef?.(d)?.path || '';
    const url = (d && typeof d.url === 'string') ? d.url : '';
    if (!path && !url) return;
    if (!confirm('Remove this document?')) return;
    const vehicleType = vehicle._type === 'trailer' ? 'trailer' : 'truck';
    try {
      const { getFunctions, httpsCallable } = await import('https://www.gstatic.com/firebasejs/10.8.0/firebase-functions.js');
      const fn = httpsCallable(getFunctions(), 'updateVehicleCompliance');
      await fn({ tenantId, vehicleId, vehicleType, removeDoc: { docType: kind, path, url } });
      setTimeout(() => showVehicleDetail(vehicleId), 300);
    } catch (err) {
      console.error('[removeVehicleComplianceDoc]', err);
      alert('Could not remove document: ' + (err.message || 'Unknown error'));
    }
  },

  async deleteHvut(vehicleId) {
    const tenantId = ComplianceState.tenantId;
    if (!tenantId) return;
    const yearEl = document.getElementById('hvut-t-year-' + vehicleId);
    if (!yearEl) return;
    const taxYear = yearEl.value;
    if (!confirm(`Delete the ${taxYear} 2290 record for this vehicle? This also deletes any uploaded Schedule 1.`)) return;
    try {
      const { getFunctions, httpsCallable } = await import('https://www.gstatic.com/firebasejs/10.8.0/firebase-functions.js');
      const fn = httpsCallable(getFunctions(), 'deleteHvutFiling');
      await fn({ tenantId, filingId: `${vehicleId}_${taxYear}` });
      setTimeout(() => showVehicleDetail(vehicleId), 300);
    } catch (err) {
      console.error('[deleteHvut]', err);
      alert('Delete failed: ' + (err.message || 'Unknown error'));
    }
  },
  async viewHvutSchedule1(storagePath) {
    if (!storagePath) return;
    try {
      const { getStorage, ref, getDownloadURL } = await import('https://www.gstatic.com/firebasejs/10.8.0/firebase-storage.js');
      const url = await getDownloadURL(ref(getStorage(), storagePath));
      window.open(url, '_blank');
    } catch (err) {
      console.error('[viewHvutSchedule1]', err);
      alert('Could not open Schedule 1: ' + (err.message || 'Unknown error'));
    }
  },
  // TASK-1029 — fallback opener for the DQ-doc list when window.HRModule (and
  // its viewComplianceDoc) isn't loaded: documents rows are PATH-ONLY, so fetch
  // bytes through the HR/compliance-gated getComplianceDocBytes callable and
  // open a Blob URL. Mirrors hr.js viewComplianceDoc; NO legacy-url fallback on
  // a callable error (a denial must never open a bearer URL).
  async openDocRowBytes(el) {
    const storagePath = (el && el.getAttribute ? el.getAttribute('data-doc-path') : '') || '';
    if (!storagePath) return;
    try {
      window.showToast?.('Opening document…', 'info');
      const fn = httpsCallable(functions, 'getComplianceDocBytes');
      const result = await fn({ storagePath });
      const data = result.data || {};
      if (!(data.ok && data.inline && data.base64)) throw new Error('No document data returned');
      const bin = atob(data.base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: data.contentType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('[Compliance] openDocRowBytes failed:', err);
      window.showToast?.(err?.message || 'Could not open the document', 'error');
    }
  },

  // ============================================
  // Compliance Settings modal (Wave 2 settings reorg 2026-05-19 / v1.15.693).
  // Decentralizes Liability Insurance + State MVR Credentials from main Settings.
  // Mirrors dispatch.js openDispatchSettingsModal pattern (commit 74290b4c).
  // State-key schema unchanged — same `tenant.insurance.*` + `tenant.compliance.stateMvrCreds.*`.
  // ============================================
  openComplianceSettingsModal() { return openComplianceSettingsModal(); },
  closeComplianceSettingsModal() { return closeComplianceSettingsModal(); },
  saveComplianceSettings() { return saveComplianceSettings(); },
  // TASK-515 — drug-test collection sites (repeatable rows in the modal).
  addDrugTestSiteRow() { return _addDrugTestSiteRow(); },
  removeDrugTestSiteRow(btn) { return _removeDrugTestSiteRow(btn); },
};

// ============================================
// Compliance Settings sub-modal (Wave 2 settings reorg 2026-05-19 / v1.15.693)
// ============================================
// Schema (unchanged from main Settings — Wave 1 proved this pattern works):
//   tenant.insurance = { carrier, policyNumber, effective, expiration, liabilityLimit }
//     ^ TOP-LEVEL tenant field, not under tenant.settings.*
//   tenant.compliance.stateMvrCreds.{ut|pa} = { isSet, last4, registeredAt, registeredBy }
//     ^ TASK-287: a MASK only. The full UT business account / PA access code lives
//       in the server-only _stateMvrCreds/{state} subcollection and never reaches
//       a browser — the tenant doc is readable by every member AND driver-PIN
//       session. A root still carrying businessAccountNumber / affidavitNumber is
//       legacy; it is treated as "on file" and never put into an input.
//     ^ Written through `saveStateMvrCredAsTenant` callable (TASK-287 Phase B —
//       server-side audit log + admin-only subcollection dual-write).
//
// IDs are prefixed `comp-settings-*` to namespace-isolate from any main-Settings
// vestiges and from compliance-manager.html (partner-side). Applied COD catch
// from Code Reviewer 2026-05-19 — duplicate IDs with main Settings DOM would
// make getElementById resolve unpredictably.
function _ensureComplianceSettingsModalDOM() {
  if (document.getElementById('compliance-settings-modal')) return;
  const wrap = document.createElement('div');
  wrap.id = 'compliance-settings-modal';
  wrap.className = 'modal hidden';
  wrap.setAttribute('onclick', 'if(event.target===this)window.ComplianceModule.closeComplianceSettingsModal()');
  wrap.innerHTML = `
    <div class="modal-content" style="max-width:640px;">
      <div class="modal-header">
        <h2><span class="material-icons" style="color:var(--brand-blue);">settings</span> Compliance Settings</h2>
        <button class="modal-close" onclick="window.ComplianceModule.closeComplianceSettingsModal()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:var(--spacing-lg);">
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
          Liability insurance and per-state MVR credentials live here so they sit
          next to the rest of your DOT compliance data.
        </p>

        <h3 style="font-size:1rem;margin:var(--spacing-lg) 0 var(--spacing-md);color:var(--text-secondary);">Liability Insurance</h3>

        <div class="form-group">
          <label>Insurance Carrier</label>
          <input type="text" id="comp-settings-insurance-carrier" class="form-control" placeholder="e.g. Progressive Commercial" maxlength="120">
        </div>

        <div class="form-group">
          <label>Policy Number</label>
          <input type="text" id="comp-settings-insurance-policy" class="form-control" placeholder="Policy #" maxlength="60">
        </div>

        <div class="form-group">
          <label>Effective Date</label>
          <input type="date" id="comp-settings-insurance-effective" class="form-control">
        </div>

        <div class="form-group">
          <label>Expiration Date</label>
          <input type="date" id="comp-settings-insurance-expiration" class="form-control">
        </div>

        <div class="form-group">
          <label>Liability Limit</label>
          <div style="position:relative;">
            <span style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--text-muted);font-weight:600;pointer-events:none;">$</span>
            <input type="number" id="comp-settings-insurance-limit" class="form-control" placeholder="1000000" min="0" step="1000" style="padding-left:24px;">
          </div>
          <p style="font-size:0.75rem;color:var(--text-muted);margin-top:4px;">Per-occurrence liability limit in dollars. FMCSA minimum is $750,000 for general freight, $1M+ for hazardous.</p>
        </div>

        <!-- TASK-9916 — how far ahead the 7am vehicle expiry alerts start. Stored at
             settings.vehicleExpiryAlertDays.{insurance,registration,inspection}
             (1-180; blank = default 60/30/30). Read by the daily compliance cron
             (services/vehicleExpiryAlerts.js resolveWarnDays). -->
        <h3 style="font-size:1rem;margin:var(--spacing-lg) 0 var(--spacing-sm);color:var(--text-secondary);">Vehicle Expiration Alerts</h3>
        <p style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 var(--spacing-md);line-height:1.5;">
          How many days before a truck or trailer document expires the first alert goes out. After that you get one at 30, 14, 7 and 1 days, then weekly once it has expired. Units that share an expiration date come in one message. Insurance and registration alerts go to people tagged COMPLIANCE; inspection alerts go to people tagged MAINTENANCE. Leave blank for the default.
        </p>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:8px 16px;">
          <div class="form-group" style="margin-bottom:0;">
            <label for="comp-settings-vexp-insurance">Insurance (days)</label>
            <input type="number" id="comp-settings-vexp-insurance" class="form-control" min="1" max="180" step="1" placeholder="60" inputmode="numeric">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="comp-settings-vexp-registration">Registration (days)</label>
            <input type="number" id="comp-settings-vexp-registration" class="form-control" min="1" max="180" step="1" placeholder="30" inputmode="numeric">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="comp-settings-vexp-inspection">Annual inspection (days)</label>
            <input type="number" id="comp-settings-vexp-inspection" class="form-control" min="1" max="180" step="1" placeholder="30" inputmode="numeric">
          </div>
        </div>

        <!-- TASK-261/206 v1.15.540 — State MVR Credentials. UT/PA require per-carrier
             state-issued credentials before MVRs will run. Each carrier MUST hold its
             own; DispatchDeck cannot pool one across multiple carriers (Utah DLD
             Vendor program + PennDOT DL-9005 non-share clause). Carriers paying for
             DispatchDeck themselves manage this directly; compliance-only carriers
             managed by a partner (Crown Compliance, etc.) will have their partner
             enter the credential for them via compliance-manager.html. -->
        <details id="comp-settings-state-mvr-section" class="hr-edit-section" style="margin:var(--spacing-lg) 0;border:1px solid var(--border-light);border-radius:var(--radius-md);padding:0;">
          <summary class="hr-edit-section-toggle" style="cursor:pointer;padding:var(--spacing-md);font-weight:600;font-size:0.95rem;color:var(--text-secondary);display:flex;align-items:center;gap:8px;list-style:none;">
            <span class="material-icons" style="font-size:18px;color:var(--brand-blue,#3B82F6);">badge</span>
            <span>State MVR Credentials</span>
            <span id="comp-settings-state-mvr-summary" style="font-size:0.75rem;color:var(--text-muted);font-weight:400;margin-left:auto;"></span>
            <span class="material-icons hr-edit-chevron" aria-hidden="true">expand_more</span>
          </summary>
          <div style="padding:0 var(--spacing-md) var(--spacing-md);">
            <p id="comp-settings-state-mvr-hint" style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 var(--spacing-md);line-height:1.5;">
              Utah and Pennsylvania require your carrier to be registered directly with the state DMV before MVRs will run for drivers licensed there. Each carrier must hold its own credential — we cannot run UT or PA MVRs on your behalf using a shared account. Leave blank if you don't have UT or PA drivers.
              <a href="https://github.com/chriskjrostad/dispatchdeck/blob/main/docs/runbooks/checkr-state-mvr-setup.md" target="_blank" rel="noopener" style="color:var(--brand-blue,#3B82F6);text-decoration:underline;">How do I get these numbers?</a>
            </p>

            <div class="form-group">
              <label style="display:flex;align-items:center;gap:8px;">
                Utah business account number
                <span id="comp-settings-ut-cred-badge" style="display:none;font-size:0.7rem;background:#10B981;color:#fff;padding:2px 8px;border-radius:999px;font-weight:600;">UT credential on file</span>
              </label>
              <input type="text" id="comp-settings-ut-business-account" class="form-control" placeholder="e.g. BUS-12345" maxlength="50" style="text-transform:uppercase;" autocomplete="off">
              <label id="comp-settings-ut-remove-wrap" style="display:none;align-items:center;gap:6px;font-size:0.75rem;color:var(--text-secondary);margin-top:4px;"><input type="checkbox" id="comp-settings-ut-remove"> Remove the Utah number on file</label>
              <p style="font-size:0.72rem;color:var(--text-muted);margin-top:4px;">Issued by Utah Driver License Division when you register your motor carrier as an MVR Vendor account holder. Letters, digits, dashes. Required to run MVRs for any driver with a Utah CDL.</p>
            </div>

            <div class="form-group">
              <label style="display:flex;align-items:center;gap:8px;">
                Pennsylvania PennDOT access code
                <span id="comp-settings-pa-cred-badge" style="display:none;font-size:0.7rem;background:#10B981;color:#fff;padding:2px 8px;border-radius:999px;font-weight:600;">PA credential on file</span>
              </label>
              <input type="text" id="comp-settings-pa-affidavit" class="form-control" placeholder="e.g. A1B2C3D4" maxlength="50" style="text-transform:uppercase;" autocomplete="off">
              <label id="comp-settings-pa-remove-wrap" style="display:none;align-items:center;gap:6px;font-size:0.75rem;color:var(--text-secondary);margin-top:4px;"><input type="checkbox" id="comp-settings-pa-remove"> Remove the Pennsylvania code on file</label>
              <p style="font-size:0.72rem;color:var(--text-muted);margin-top:4px;">Issued by PennDOT after you complete a notarized DL-9005 affidavit (6-8 week approval window). Letters, digits, dashes. Required to run MVRs for any driver with a Pennsylvania CDL.</p>
            </div>

            <p id="comp-settings-state-mvr-hint-active" style="display:none;font-size:0.78rem;color:#B45309;background:rgba(245,158,11,0.08);border-left:3px solid #F59E0B;padding:8px 12px;margin-top:8px;border-radius:0 6px 6px 0;">
              You have drivers licensed in UT or PA on your roster. Add the credential for each state above to enable MVR runs for those drivers.
            </p>
          </div>
        </details>

        <!-- TASK-515 — Pre-employment drug-test collection site(s). Stored as an
             ARRAY at tenant.compliance.drugTestSites = [{name, address}]. A carrier
             can have MORE THAN ONE collection site (multi-site in scope). These
             surface as sub-lines under the "Drug Test" New-Hire Checklist item so
             whoever sends a new hire for testing sees exactly where to send them.
             Same partner-managed root (tenant.compliance.*) the partner portal
             reads; tenant-admins can write it directly via the SDK (firestore.rules
             only blocks compliancePackageDefault/compliancePackageAllowed on the
             admin branch). Folded into saveComplianceSettings(). -->
        <details id="comp-settings-drug-test-section" class="hr-edit-section" style="margin:var(--spacing-lg) 0;border:1px solid var(--border-light);border-radius:var(--radius-md);padding:0;">
          <summary class="hr-edit-section-toggle" style="cursor:pointer;padding:var(--spacing-md);font-weight:600;font-size:0.95rem;color:var(--text-secondary);display:flex;align-items:center;gap:8px;list-style:none;">
            <span class="material-icons" style="font-size:18px;color:var(--brand-blue,#3B82F6);">science</span>
            <span>Drug Test Collection Sites</span>
            <span id="comp-settings-drug-test-summary" style="font-size:0.75rem;color:var(--text-muted);font-weight:400;margin-left:auto;"></span>
            <span class="material-icons hr-edit-chevron" aria-hidden="true">expand_more</span>
          </summary>
          <div style="padding:0 var(--spacing-md) var(--spacing-md);">
            <p style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 var(--spacing-md);line-height:1.5;">
              Where new hires go for their pre-employment drug test. Add each collection site your carrier uses — these show up under the <strong>Drug Test</strong> step on every New-Hire Checklist so whoever onboards knows exactly where to send the driver.
            </p>
            <div id="comp-settings-drug-test-rows"></div>
            <button type="button" class="btn btn-secondary" onclick="window.ComplianceModule.addDrugTestSiteRow()" style="margin-top:8px;font-size:0.82rem;padding:6px 12px;display:inline-flex;align-items:center;gap:4px;">
              <span class="material-icons" style="font-size:16px;">add</span> Add a site
            </button>
          </div>
        </details>

        <!-- TASK-9993 — Permit Pete settings (per-axle-group safety cushion). Its
             own Save inside (callable savePermitPeteSettings, audited); the
             footer Save also saves them when changed. Lazy views/permitPeteSettings.js. -->
        <details id="comp-settings-permit-pete-section" class="hr-edit-section" style="margin:var(--spacing-lg) 0;border:1px solid var(--border-light);border-radius:var(--radius-md);padding:0;">
          <summary class="hr-edit-section-toggle" style="cursor:pointer;padding:var(--spacing-md);font-weight:600;font-size:0.95rem;color:var(--text-secondary);display:flex;align-items:center;gap:8px;list-style:none;">
            <span class="material-icons" style="font-size:18px;color:var(--brand-blue,#3B82F6);">scale</span>
            <span>Permit Pete settings</span>
            <span class="material-icons hr-edit-chevron" aria-hidden="true" style="margin-left:auto;">expand_more</span>
          </summary>
          <div id="comp-settings-permit-pete-body" style="padding:0 var(--spacing-md) var(--spacing-md);"></div>
        </details>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" onclick="window.ComplianceModule.closeComplianceSettingsModal()">Cancel</button>
        <button type="button" class="btn btn-primary" id="btn-save-compliance-settings" onclick="window.ComplianceModule.saveComplianceSettings()">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(wrap);
}

// TASK-515 — drug-test collection-site repeatable rows. Each row is a
// name + address pair; the modal collects them into the
// tenant.compliance.drugTestSites array on save. Inputs are plain text;
// escaping happens at READ/display time (renderDrugTestSitesSubLines in
// hr.js + the partner portal), matching how the rest of this modal stores
// raw values and escapes on render.
function _addDrugTestSiteRow(name = '', address = '') {
  const wrap = document.getElementById('comp-settings-drug-test-rows');
  if (!wrap) return;
  const row = document.createElement('div');
  row.className = 'comp-settings-drug-test-row';
  row.style.cssText = 'display:flex;gap:8px;align-items:flex-start;margin-bottom:8px;';
  row.innerHTML = `
    <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:6px;">
      <input type="text" class="form-control comp-settings-drug-test-name" placeholder="Site name (e.g. Advanced Drug Testing)" maxlength="120" value="${_escAttr(name)}">
      <input type="text" class="form-control comp-settings-drug-test-address" placeholder="Address (e.g. 1502 13th Ave W, Williston, ND 58801)" maxlength="200" value="${_escAttr(address)}">
    </div>
    <button type="button" class="btn btn-secondary" title="Remove this site" onclick="window.ComplianceModule.removeDrugTestSiteRow(this)" style="padding:6px 10px;flex-shrink:0;">
      <span class="material-icons" style="font-size:18px;">delete_outline</span>
    </button>
  `;
  wrap.appendChild(row);
}

function _removeDrugTestSiteRow(btn) {
  const row = btn?.closest?.('.comp-settings-drug-test-row');
  if (row) row.remove();
}

// Minimal HTML-attribute escaper for safely seeding input values[]. Display
// surfaces (hr.js, portal) escape on render; this just protects the value="" .
function _escAttr(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Read the current rows into a clean [{name, address}] array. Drops fully-empty
// rows; trims; caps at 20 sites (defensive — UI is manual add).
function _collectDrugTestSites() {
  const wrap = document.getElementById('comp-settings-drug-test-rows');
  if (!wrap) return [];
  const out = [];
  wrap.querySelectorAll('.comp-settings-drug-test-row').forEach(row => {
    const name = (row.querySelector('.comp-settings-drug-test-name')?.value || '').trim().slice(0, 120);
    const address = (row.querySelector('.comp-settings-drug-test-address')?.value || '').trim().slice(0, 200);
    if (!name && !address) return;
    out.push({ name, address });
  });
  return out.slice(0, 20);
}

// TASK-9916 — vehicle expiry alert windows. Server twin: resolveWarnDays in
// functions/src/services/vehicleExpiryAlerts.js (same 1-180 range; null/absent =
// default 60/30/30 there, blank input here).
const VEXP_TYPES = ['insurance', 'registration', 'inspection'];
function _vexpSaved(settings) {
  const raw = (settings && typeof settings.vehicleExpiryAlertDays === 'object' && settings.vehicleExpiryAlertDays) || {};
  const out = {};
  VEXP_TYPES.forEach(t => {
    const n = Number(raw[t]);
    out[t] = (raw[t] != null && raw[t] !== '' && Number.isFinite(n)) ? Math.min(180, Math.max(1, Math.round(n))) : null;
  });
  return out;
}
// Returns { values } or { error } — blank = null (default); anything else must be a whole number 1-180.
function _vexpFromInputs() {
  const values = {};
  for (const t of VEXP_TYPES) {
    const raw = (document.getElementById(`comp-settings-vexp-${t}`)?.value || '').trim();
    if (raw === '') { values[t] = null; continue; }
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > 180) {
      return { error: `Vehicle expiration alerts: ${t} must be a whole number of days from 1 to 180, or blank for the default.`, field: t };
    }
    values[t] = n;
  }
  return { values };
}

async function openComplianceSettingsModal() {
  _ensureComplianceSettingsModalDOM();
  const m = document.getElementById('compliance-settings-modal');
  if (!m) return;

  // Hydrate from the live tenant doc — these aren't in window.AppState.settings
  // (they're top-level tenant.insurance + tenant.compliance.stateMvrCreds).
  try {
    const tid = ComplianceState.tenantId || window.AppState?.tenant;
    if (tid) {
      const tenantRef = doc(db, 'tenants', tid);
      const snap = await getDoc(tenantRef);
      const data = snap.exists() ? snap.data() : {};

      const ins = data.insurance || {};
      const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = (val == null ? '' : val); };
      set('comp-settings-insurance-carrier', ins.carrier || '');
      set('comp-settings-insurance-policy', ins.policyNumber || '');
      set('comp-settings-insurance-effective', ins.effective || '');
      set('comp-settings-insurance-expiration', ins.expiration || '');
      set('comp-settings-insurance-limit', (ins.liabilityLimit != null && ins.liabilityLimit !== '') ? ins.liabilityLimit : '');
      // TASK-9916 — blank input = the default (shown as the placeholder).
      const vexp = _vexpSaved(data.settings);
      VEXP_TYPES.forEach(t => set(`comp-settings-vexp-${t}`, vexp[t] == null ? '' : vexp[t]));

      const stateMvrCreds = (data.compliance && typeof data.compliance.stateMvrCreds === 'object' && data.compliance.stateMvrCreds) || {};
      const utCred = (stateMvrCreds.ut && typeof stateMvrCreds.ut === 'object') ? stateMvrCreds.ut : {};
      const paCred = (stateMvrCreds.pa && typeof stateMvrCreds.pa === 'object') ? stateMvrCreds.pa : {};
      const utInput = document.getElementById('comp-settings-ut-business-account');
      const paInput = document.getElementById('comp-settings-pa-affidavit');
      const utBadge = document.getElementById('comp-settings-ut-cred-badge');
      const paBadge = document.getElementById('comp-settings-pa-cred-badge');
      const stateMvrSummary = document.getElementById('comp-settings-state-mvr-summary');
      // TASK-287 — read the MASK (isSet / last4), never a full value. The input
      // always opens EMPTY: typing replaces the number on file, leaving it blank
      // keeps it, and the Remove checkbox clears it.
      const _credMask = (blob, legacyKey) => {
        const legacy = String(blob[legacyKey] || '').trim();
        // Short values (< 8 chars) never show a last-4 — it would BE the value.
        const last4 = legacy ? (legacy.length >= 8 ? legacy.slice(-4) : '') : String(blob.last4 || '').trim().slice(-4);
        return { isSet: !!legacy || blob.isSet === true, last4 };
      };
      const utMask = _credMask(utCred, 'businessAccountNumber');
      const paMask = _credMask(paCred, 'affidavitNumber');
      const utSet = utMask.isSet;
      const paSet = paMask.isSet;
      const _prepCredInput = (input, mask, emptyPlaceholder, removeId) => {
        if (input) {
          input.value = '';
          input.placeholder = mask.isSet ? `On file${mask.last4 ? `, ending ${mask.last4}` : ''} (type a new number to replace it)` : emptyPlaceholder;
        }
        const rm = document.getElementById(removeId);
        if (rm) rm.checked = false;
        const wrap = document.getElementById(removeId + '-wrap');
        if (wrap) wrap.style.display = mask.isSet ? 'flex' : 'none';
      };
      _prepCredInput(utInput, utMask, 'e.g. BUS-12345', 'comp-settings-ut-remove');
      _prepCredInput(paInput, paMask, 'e.g. A1B2C3D4', 'comp-settings-pa-remove');
      if (utBadge) { utBadge.style.display = utSet ? 'inline-flex' : 'none'; utBadge.textContent = utSet && utMask.last4 ? `UT credential on file (ending ${utMask.last4})` : 'UT credential on file'; }
      if (paBadge) { paBadge.style.display = paSet ? 'inline-flex' : 'none'; paBadge.textContent = paSet && paMask.last4 ? `PA credential on file (ending ${paMask.last4})` : 'PA credential on file'; }
      if (stateMvrSummary) {
        const parts = [];
        if (utSet) parts.push('UT ✓');
        if (paSet) parts.push('PA ✓');
        stateMvrSummary.textContent = parts.length ? parts.join(' · ') : '';
      }

      // TASK-515 — hydrate drug-test collection-site rows from
      // tenant.compliance.drugTestSites (array of {name, address}).
      const drugRowsWrap = document.getElementById('comp-settings-drug-test-rows');
      if (drugRowsWrap) {
        drugRowsWrap.innerHTML = '';
        const sites = Array.isArray(data.compliance?.drugTestSites) ? data.compliance.drugTestSites : [];
        sites.forEach(s => {
          if (s && (s.name || s.address)) _addDrugTestSiteRow(s.name || '', s.address || '');
        });
        const drugSummary = document.getElementById('comp-settings-drug-test-summary');
        if (drugSummary) {
          const n = sites.filter(s => s && (s.name || s.address)).length;
          drugSummary.textContent = n ? `${n} site${n === 1 ? '' : 's'}` : '';
        }
      }

      // Roster heuristic: any active driver with dlState UT/PA but no cred set?
      try {
        const hintEl = document.getElementById('comp-settings-state-mvr-hint-active');
        if (hintEl) {
          const people = Array.isArray(ComplianceState.people) ? ComplianceState.people : (window.HRState?.people || []);
          const states = new Set(
            people
              .filter(p => p && (p.active !== false))
              .map(p => String(p.dlState || p.driverLicenseState || p.cdlState || '').toUpperCase().slice(0, 2))
              .filter(s => s === 'UT' || s === 'PA')
          );
          const hasUtDriver = states.has('UT') && !utSet;
          const hasPaDriver = states.has('PA') && !paSet;
          hintEl.style.display = (hasUtDriver || hasPaDriver) ? 'block' : 'none';
        }
      } catch (e) {
        console.warn('[ComplianceSettings] State MVR roster heuristic skipped:', e?.message);
      }
    }
  } catch (e) {
    console.error('[ComplianceSettings] load failed', e);
    window.showToast?.('Could not load compliance settings: ' + (e?.message || 'unknown'), 'error');
  }

  // TASK-9993 — Permit Pete settings: lazy, its own callable-backed Save.
  try {
    const peteEl = document.getElementById('comp-settings-permit-pete-body');
    const tidPete = ComplianceState.tenantId || window.AppState?.tenant;
    if (peteEl && tidPete) {
      __ddChunkWrap(import('./permitPeteSettings.js?v=9993001'))
        .then((mod) => mod.mount(peteEl, { tenantId: tidPete, call: (name, p) => httpsCallable(functions, name)(p) }))
        .catch((err) => { console.warn('[ComplianceSettings] permitPeteSettings:', err?.message || err); peteEl.innerHTML = '<p class="form-hint">Could not load Permit Pete settings.</p>'; });
    }
  } catch (_) { /* the rest of the modal still opens */ }

  m.classList.remove('hidden');
}

function closeComplianceSettingsModal() {
  const m = document.getElementById('compliance-settings-modal');
  if (m) m.classList.add('hidden');
  // TASK-9993 (COD r1 UX): Permit Pete settings have their own Save; say so if
  // they were changed and not saved (the footer Save does not save them).
  const pete = document.getElementById('comp-settings-permit-pete-body');
  if (pete && pete.dataset.dirty === '1') {
    delete pete.dataset.dirty;
    window.showToast?.('Permit Pete settings were NOT saved - reopen Compliance Settings and use "Save Permit Pete settings".', 'warning');
  }
}

async function saveComplianceSettings() {
  const tid = ComplianceState.tenantId || window.AppState?.tenant;
  if (!tid) {
    window.showToast?.('Not logged in', 'error');
    return;
  }

  const saveBtn = document.getElementById('btn-save-compliance-settings');
  const saveBtnOriginal = saveBtn ? saveBtn.innerHTML : 'Save';
  if (saveBtn) {
    saveBtn.disabled = true;
    saveBtn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  try {
    const insuranceCarrier = document.getElementById('comp-settings-insurance-carrier')?.value?.trim() || '';
    const insurancePolicyNumber = document.getElementById('comp-settings-insurance-policy')?.value?.trim() || '';
    const insuranceEffective = document.getElementById('comp-settings-insurance-effective')?.value || '';
    const insuranceExpiration = document.getElementById('comp-settings-insurance-expiration')?.value || '';
    const insuranceLimitRaw = document.getElementById('comp-settings-insurance-limit')?.value?.trim() || '';
    const insuranceLimit = insuranceLimitRaw === '' ? null : Number(insuranceLimitRaw);

    const utBusinessAccountRaw = document.getElementById('comp-settings-ut-business-account')?.value?.trim() || '';
    const paAffidavitRaw = document.getElementById('comp-settings-pa-affidavit')?.value?.trim() || '';
    const utBusinessAccount = utBusinessAccountRaw ? utBusinessAccountRaw.toUpperCase() : '';
    const paAffidavit = paAffidavitRaw ? paAffidavitRaw.toUpperCase() : '';

    // Mirror validation regex from app.js (TASK-261/206 v1.15.540).
    const STATE_MVR_RE = /^[A-Z0-9\-]{1,50}$/;
    if (utBusinessAccount && !STATE_MVR_RE.test(utBusinessAccount)) {
      window.showToast?.('Utah business account: letters, digits, dashes only (1-50 chars). No spaces or other punctuation.', 'error');
      if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
      return;
    }
    if (paAffidavit && !STATE_MVR_RE.test(paAffidavit)) {
      window.showToast?.('Pennsylvania access code: letters, digits, dashes only (1-50 chars). No spaces or other punctuation.', 'error');
      if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
      return;
    }

    // TASK-9916 — validate the alert windows before any write.
    const vexpIn = _vexpFromInputs();
    if (vexpIn.error) {
      window.showToast?.(vexpIn.error, 'error');
      document.getElementById(`comp-settings-vexp-${vexpIn.field}`)?.focus();
      if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
      return;
    }

    const tenantRef = doc(db, 'tenants', tid);
    const tenantDoc = await getDoc(tenantRef);
    const existingData = tenantDoc.exists() ? tenantDoc.data() : {};

    // TASK-9916 — only the windows that changed are written (dotted keys, so
    // sibling settings survive); old -> new goes to the audit log below.
    const vexpPrev = _vexpSaved(existingData.settings);
    const vexpPatch = {};
    const vexpFrom = {};
    const vexpTo = {};
    VEXP_TYPES.forEach(t => {
      if (vexpIn.values[t] !== vexpPrev[t]) {
        vexpPatch['settings.vehicleExpiryAlertDays.' + t] = vexpIn.values[t];
        vexpFrom[t] = vexpPrev[t];
        vexpTo[t] = vexpIn.values[t];
      }
    });

    // Preserve forward-compat insurance fields not surfaced in this UI.
    const existingInsurance = (existingData && typeof existingData.insurance === 'object' && existingData.insurance) ? existingData.insurance : {};
    const insurancePayload = {
      ...existingInsurance,
      carrier: insuranceCarrier || null,
      policyNumber: insurancePolicyNumber || null,
      effective: insuranceEffective || null,
      expiration: insuranceExpiration || null,
      liabilityLimit: (insuranceLimit != null && !Number.isNaN(insuranceLimit)) ? insuranceLimit : null
    };
    const allInsuranceEmpty = !insurancePayload.carrier && !insurancePayload.policyNumber && !insurancePayload.effective && !insurancePayload.expiration && insurancePayload.liabilityLimit == null;

    // Detect UT/PA changes — use the dedicated callable for each (TASK-287 Phase B).
    // TASK-287 — the root holds a MASK, so "changed" can no longer be a value
    // compare. A typed value is a replace (the server no-ops an identical one),
    // the Remove checkbox is a delete, and an empty input keeps what is on file.
    const utRemove = !!document.getElementById('comp-settings-ut-remove')?.checked;
    const paRemove = !!document.getElementById('comp-settings-pa-remove')?.checked;
    const utChanged = utRemove || !!utBusinessAccount;
    const paChanged = paRemove || !!paAffidavit;
    const utSendValue = utRemove ? '' : utBusinessAccount;
    const paSendValue = paRemove ? '' : paAffidavit;

    // TASK-515 — collect drug-test collection sites. Dotted-path write below so
    // sibling compliance.* fields (stateMvrCreds, notifiers, bgHandledBy,
    // customReminders, der) are preserved.
    const drugTestSites = _collectDrugTestSites();

    // Write insurance (top-level field) + drug-test sites (dotted compliance
    // path). updateDoc merges by key, so this preserves other top-level + other
    // compliance.* siblings.
    await updateDoc(tenantRef, {
      insurance: allInsuranceEmpty ? null : insurancePayload,
      'compliance.drugTestSites': drugTestSites
    });
    // TASK-9916 — its OWN write: the tenant-doc rule lets a Compliance-module
    // member (not owner/admin) save insurance + drug sites, but settings.* outside
    // their module allowlist is owner/admin only. A refusal here must not undo
    // the rest of the save, so it gets its own updateDoc and its own message.
    let vexpSaved = false;
    let vexpFailed = false;
    if (Object.keys(vexpTo).length) {
      try {
        await updateDoc(tenantRef, vexpPatch);
        vexpSaved = true;
      } catch (vexpErr) {
        vexpFailed = true;
        console.error('[ComplianceSettings] vehicle expiry alert days save failed', vexpErr);
        window.showToast?.(vexpErr?.code === 'permission-denied'
          ? 'Saved, except Vehicle Expiration Alerts: only the account owner or an admin can change those.'
          : 'Saved, except Vehicle Expiration Alerts: ' + (vexpErr?.message || 'unknown'), 'error');
      }
    }
    if (vexpSaved) {
      // null = back to the default; the audit row says so explicitly.
      window.logAudit?.('settings', 'VEHICLE_EXPIRY_ALERT_DAYS_CHANGED', {
        from: vexpFrom, to: vexpTo, defaults: { insurance: 60, registration: 30, inspection: 30 },
      });
      try {
        if (window.AppState) {
          if (!window.AppState.settings) window.AppState.settings = {};
          const cur = (typeof window.AppState.settings.vehicleExpiryAlertDays === 'object' && window.AppState.settings.vehicleExpiryAlertDays) || {};
          window.AppState.settings.vehicleExpiryAlertDays = { ...cur, ...vexpTo };
        }
      } catch (_) { /* cache sync best-effort */ }
    }
    // Keep the live cached tenant doc in sync so hr.js's drug-test sub-lines
    // reflect the change without a reload.
    try {
      if (window.AppState?.tenantData) {
        if (!window.AppState.tenantData.compliance) window.AppState.tenantData.compliance = {};
        window.AppState.tenantData.compliance.drugTestSites = drugTestSites;
      }
    } catch (_) { /* cache sync best-effort */ }

    // State MVR creds — soft-fail per state so a single failure doesn't block the
    // other. Mirrors saveCompanyProfile in app.js (TASK-287 Phase B).
    let credentialSaveFailed = false;
    const credentialSaveErrors = [];
    if (utChanged) {
      try {
        const saveCredFn = httpsCallable(functions, 'saveStateMvrCredAsTenant');
        await saveCredFn({ tenantId: tid, state: 'ut', value: utSendValue });
      } catch (e) {
        console.error('[ComplianceSettings] saveStateMvrCredAsTenant ut failed', e);
        credentialSaveFailed = true;
        credentialSaveErrors.push('UT: ' + (e?.message || 'unknown'));
      }
    }
    if (paChanged) {
      try {
        const saveCredFn = httpsCallable(functions, 'saveStateMvrCredAsTenant');
        await saveCredFn({ tenantId: tid, state: 'pa', value: paSendValue });
      } catch (e) {
        console.error('[ComplianceSettings] saveStateMvrCredAsTenant pa failed', e);
        credentialSaveFailed = true;
        credentialSaveErrors.push('PA: ' + (e?.message || 'unknown'));
      }
    }

    window.logAudit?.('settings', 'SAVE_COMPLIANCE_SETTINGS', {
      keys: ['insurance', 'compliance.drugTestSites', utChanged ? 'stateMvrCreds.ut' : null, paChanged ? 'stateMvrCreds.pa' : null, vexpSaved ? 'settings.vehicleExpiryAlertDays' : null].filter(Boolean),
      drugTestSiteCount: drugTestSites.length
    });

    if (credentialSaveFailed) {
      window.showToast?.(
        'Saved insurance, but MVR credential save failed — try again: ' + credentialSaveErrors.join(' / '),
        'error'
      );
      // Keep modal open so the user can retry the credential field.
    } else if (!vexpFailed) {
      // TASK-9993 (COD r2 UX): the footer Save also saves changed Permit Pete
      // settings (their own callable + audit row); a refusal keeps the modal open.
      const peteEl = document.getElementById('comp-settings-permit-pete-body');
      const peteOk = peteEl && typeof peteEl._ppsSaveIfDirty === 'function' ? await peteEl._ppsSaveIfDirty() : true;
      if (peteOk) {
        window.showToast?.('Compliance settings saved', 'success');
        closeComplianceSettingsModal();
      } else {
        window.showToast?.('Saved, except Permit Pete settings - see the message in that section.', 'error');
      }
    }
  } catch (e) {
    console.error('[ComplianceSettings] save failed', e);
    window.showToast?.('Save failed: ' + (e?.message || 'unknown'), 'error');
  } finally {
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.innerHTML = saveBtnOriginal;
    }
  }
}

// ============================================
// INITIALIZE
// ============================================
export function initCompliance(container, tenantId) {
  console.log('[Compliance] Initializing for tenant:', tenantId);

  if (!tenantId) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon"><span class="material-icons" style="font-size:48px;color:var(--text-muted);">warning</span></div>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">Unable to load Compliance module. Please try logging out and back in.</div>
        <button class="btn btn-primary" onclick="window.handleLogout()"><span class="material-icons">logout</span> Log Out</button>
      </div>
    `;
    return () => {};
  }

  // Reset state
  ComplianceState.tenantId = tenantId;
  _compBgDocCache = _compBgDocBucket(tenantId); // TASK-9458 — this tenant's bg-doc bucket
  ComplianceState.initialized = true;
  ComplianceState.activeTab = 'overview';
  // TASK-1201 — one-shot deep-link (sidebar brand logo → Company tab). Consumed
  // here so a later plain visit to Compliance lands on Overview as always.
  if (window._complianceDeepLinkTab) {
    ComplianceState.activeTab = window._complianceDeepLinkTab;
    window._complianceDeepLinkTab = null;
  }
  ComplianceState.companyDocs = null; // TASK-1201 — never serve a prior tenant's docs
  ComplianceState.peopleSubTab = 'all';
  ComplianceState.peopleEmpFilter = 'active'; // TASK-933 — always open on the daily view
  ComplianceState.searchQuery = '';
  ComplianceState.filterStatus = 'all';
  ComplianceState.selectedPersonId = null;
  ComplianceState.selectedVehicleId = null;
  // TASK-1147 — fresh lazy-stream registry per init (stale unsubs/loaded flags
  // from a previous tenant/session must never satisfy this one's gates).
  ComplianceState.streamUnsubs = {};
  ComplianceState.streamsLoaded = {};
  _streamWaiters.length = 0;

  // TASK-4617 — pull the company EIN from its locked `complianceSecure/company`
  // sibling. Reset first so a previous tenant's value can never be served to
  // this one (the TASK-1201 companyDocs rule, same hazard). Fire-and-forget:
  // the render does not block on it — companyEin() falls back to the tenant
  // root meanwhile — and renderIfReady() repaints once the value lands so the
  // Company Identity card and the HVUT prefill fill themselves in.
  CompanySecureState.tenantId = null;
  CompanySecureState.ein = '';
  CompanySecureState.loaded = false;
  loadCompanySecure(tenantId).then(() => {
    try { renderIfReady(); } catch (e) { /* container gone / tab switched */ }
  });

  // Render shell.
  // Header is position:relative so the settings gear can absolute-position to the
  // top-right without disturbing the centered <h1>/<p>. Mirrors the Wave 1
  // dispatch.js pattern (commit 74290b4c) with a tweak for compliance's
  // already-centered header aesthetic.
  container.innerHTML = `
    <div id="compliance-module" style="padding:var(--spacing-md);margin:0 auto;">
      <div style="text-align:center;margin-bottom:var(--spacing-md);position:relative;padding:0 52px;">
        <h1 style="font-size:1.5rem;font-weight:700;margin:0;">Compliance</h1>
        <p style="color:var(--text-secondary);font-size:0.875rem;margin:4px 0 0;">Documents, certifications & safety compliance</p>
        <div style="position:absolute;top:50%;left:0;transform:translateY(-50%);">
          <!-- TASK-9349 — Permit queue. The server decides who sees what. -->
          <button class="btn btn-secondary btn-module-header" id="compliance-permit-queue-btn" onclick="window.ComplianceOpenPermitQueue && window.ComplianceOpenPermitQueue({ tenantId: window.AppState && window.AppState.tenant })" title="Permit queue: permit requests waiting on a person" aria-label="Permit queue">
            <span class="material-icons">assignment_late</span>
          </button>
        </div>
        <div style="position:absolute;top:50%;right:0;transform:translateY(-50%);">
          <button class="btn btn-secondary btn-neon-settings btn-module-header dispatcher-only" onclick="window.ComplianceModule.openComplianceSettingsModal()" title="Compliance Settings">
            <span class="material-icons">settings</span>
          </button>
        </div>
      </div>
      <div id="compliance-content">
        <div style="display:flex;align-items:center;justify-content:center;padding:60px 0;">
          <div class="loading-spinner"></div>
        </div>
      </div>
    </div>
    <div id="compliance-detail-modal" class="modal hidden" onclick="if(event.target===this)window.ComplianceModule.closeDetail()"></div>
  `;

  // TASK-1147 — subscribe ONLY the default sub-view's (Overview) streams.
  // Every other stream subscribes on first visit of a sub-view that reads it
  // (see STREAM_DEFS / VIEW_STREAMS) instead of all ~12 opening here.
  ensureStreams(_requiredStreamsForActiveView());

  // Subscribe to action items from compliance partner (separate listener — collection
  // doesn't exist for tenants without a partner, so listener fires empty + no harm)
  startActionItemsListener(tenantId);

  // Pre-mount Roster's Add/Edit modals so compliance-only users can add
  // People / Vehicles / Trailers without leaving this module.
  if (window.RosterModule?.ensureModalsMounted) {
    window.RosterModule.ensureModalsMounted();
  }

  // Return cleanup
  return () => {
    // TASK-9709 — the MVR-review arrival prompt is appended to <body>, outside
    // the view container, so it outlived a nav away until its X was clicked.
    // It is informational and once-per-session; leaving Compliance closes it.
    try { document.getElementById('comp-mvr-review-prompt')?.remove(); } catch (_) {}
    ComplianceState.unsubscribers.forEach(unsub => unsub());
    ComplianceState.unsubscribers = [];
    // TASK-1147 — tear down every lazily opened stream + reset the registry.
    Object.values(ComplianceState.streamUnsubs).forEach(unsub => { try { unsub(); } catch (_) {} });
    ComplianceState.streamUnsubs = {};
    ComplianceState.streamsLoaded = {};
    _streamWaiters.length = 0;
    stopActionItemsListener();
    ComplianceState.initialized = false;
    ComplianceState.people = [];
    ComplianceState.trucks = [];
    ComplianceState.trailers = [];
    ComplianceState.assets = [];
    ComplianceState.hvutFilings = [];
    ComplianceState.pretrips = [];
    ComplianceState.maintenance = [];
    ComplianceState.dvirRepairs = [];
    ComplianceState.dvirRepairsError = false;
    ComplianceState.clockEntries = [];
    ComplianceState.jobDays = [];
    ComplianceState.fuelPurchases = [];
    ComplianceState.drugTests = [];
    ComplianceState.complianceDocs = [];
    ComplianceState.dvirFlags = [];
    ComplianceState.jsaFlags = [];
  };
}

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
